// Documentation Parser Service
// This service parses the API_DOCUMENTATION.md file and extracts endpoint details

export interface EndpointParameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
  example?: string;
}

export interface EndpointResponse {
  code: string;
  description: string;
  schema?: string;
  example?: string;
}

export interface EndpointData {
  id: string;
  title: string;
  category: string;
  categoryId: string;
  method: string;
  path: string;
  description: string;
  headers?: EndpointParameter[];
  parameters?: EndpointParameter[];
  responses?: EndpointResponse[];
  codeExamples?: {
    [language: string]: string;
  };
}

export interface CategoryData {
  id: string;
  name: string;
  icon: string;
  endpoints: { id: string; name: string }[];
}

// Categories mapping
export const categories: CategoryData[] = [
  {
    id: 'leads',
    name: 'Leads',
    icon: 'fas fa-user-plus',
    endpoints: [
      { id: 'leads-custom-fields', name: 'Get custom fields' },
      { id: 'leads-new-lead', name: 'Create new lead' },
      { id: 'leads-edit-lead', name: 'Edit lead' },
      { id: 'leads-get-lead', name: 'Get lead by ID' },
      { id: 'leads-get-lead-phone', name: 'Get leads by phone' },
      { id: 'leads-add-call-log', name: 'Add call log' }
    ]
  },
  {
    id: 'deals',
    name: 'Deals',
    icon: 'fas fa-handshake',
    endpoints: [
      { id: 'deals-custom-fields', name: 'Get custom fields' },
      { id: 'deals-new-deal', name: 'Create new deal' },
      { id: 'deals-edit-deal', name: 'Edit deal' },
      { id: 'deals-get-deal', name: 'Get deal by ID' },
      { id: 'deals-get-deal-phone', name: 'Get deals by phone' }
    ]
  },
  {
    id: 'customers',
    name: 'Customers',
    icon: 'fas fa-users',
    endpoints: [
      { id: 'customers-custom-fields', name: 'Get custom fields' },
      { id: 'customers-create-customer', name: 'Create customer' },
      { id: 'customers-get-customer', name: 'Get customer by ID' },
      { id: 'customers-get-customer-phone', name: 'Get customers by phone' },
      { id: 'customers-edit-customer', name: 'Edit customer' },
      { id: 'customers-add-call-log', name: 'Add call log' }
    ]
  },
  {
    id: 'products',
    name: 'Products',
    icon: 'fas fa-box',
    endpoints: [
      { id: 'products-get-all', name: 'Get all products' },
      { id: 'products-get-product', name: 'Get single product' },
      { id: 'products-edit-product', name: 'Edit existing product with variations' },
      { id: 'products-add-variations', name: 'Add new variations to existing product' },
      { id: 'products-delete-variation', name: 'Delete variation' },
      { id: 'products-create-product', name: 'Create product' }
    ]
  },
  {
    id: 'calendar',
    name: 'Calendar',
    icon: 'fas fa-calendar',
    endpoints: [
      { id: 'calendar-get-break-hours', name: 'Get break hours' },
      { id: 'calendar-get-appointment-users', name: 'Get appointment users' },
      { id: 'calendar-appointment-type-details', name: 'Get appointment type details' },
      { id: 'calendar-get-appointment-types', name: 'Get appointment types' },
      { id: 'calendar-get-user-working-hours', name: 'Get working hours' },
      { id: 'calendar-get-free-time-slot', name: 'Get free time slots' },
      { id: 'calendar-get-user-appointment-by-date', name: 'Get appointments by date' },
      { id: 'calendar-get-double-free-time-slot', name: 'Get double free slots' },
      { id: 'calendar-schedule-meeting', name: 'Schedule meeting' },
      { id: 'calendar-create-event-type', name: 'Create event type' },
      { id: 'calendar-edit-event-type', name: 'Edit event type' }
    ]
  },
  {
    id: 'team',
    name: 'Team',
    icon: 'fas fa-users-gear',
    endpoints: [
      { id: 'team-list', name: 'Get team members' }
    ]
  },
  {
    id: 'tasks',
    name: 'Tasks',
    icon: 'fas fa-list-check',
    endpoints: [
      { id: 'tasks-create-task', name: 'Create task' },
      { id: 'tasks-edit-task', name: 'Edit task' },
      { id: 'tasks-delete-task', name: 'Delete task' }
    ]
  },
  {
    id: 'contacts',
    name: 'Contacts',
    icon: 'fas fa-address-book',
    endpoints: [
      { id: 'contacts-create-contact', name: 'Create contact' },
      { id: 'contacts-find-by-phone', name: 'Find by phone' },
      { id: 'contacts-find-by-name', name: 'Find by name' }
    ]
  },
  {
    id: 'chats',
    name: 'Chats',
    icon: 'fas fa-comments',
    endpoints: [
      { id: 'chats-send-message', name: 'Send message' }
    ]
  }
];

// Endpoint data mapping with real documentation data
export const endpointsData: { [key: string]: EndpointData } = {
  // Leads endpoints
  'leads-custom-fields': {
    id: 'leads-custom-fields',
    title: 'Get custom fields',
    category: 'Leads',
    categoryId: 'leads',
    method: 'GET',
    path: '/leads/custom-fields',
    description: 'Retrieves all custom fields configured for your organization\'s leads. These fields define additional data points that can be collected for each lead beyond the standard fields.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer {your_api_key}'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: false,
        description: 'application/json (Recommended)'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'OK - Request successful',
        example: JSON.stringify({
          success: true,
          data: [
            {
              key: '1750878964687_1750878975688',
              area: 'פרטי עסק',
              type: 'text',
              label: 'פרטי עסק - שם העסק',
              areaId: '1750878964687',
              required: false
            },
            {
              key: '1750878964687_1750878993373',
              area: 'פרטי עסק',
              type: 'phone',
              label: 'פרטי עסק - טלפון של בעל העסק',
              areaId: '1750878964687',
              required: false
            }
          ]
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/leads/custom-fields';

fetch(url, {
    method: 'GET',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    }
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Custom fields:', data.data);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/leads/custom-fields"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

response = requests.get(url, headers=headers)

if response.status_code == 200:
    data = response.json()
    print("Custom fields:", data['data'])
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      curl: `curl -X GET "https://dev.clickai.live/leads/custom-fields" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json"`,
      curlLinux: `curl -X GET "https://dev.clickai.live/leads/custom-fields" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/leads/custom-fields';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    echo "Custom fields: " . json_encode($data['data']);
} else {
    echo "Error $http_code: $response";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/leads/custom-fields";
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            HttpResponseMessage response = await client.GetAsync(url);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic data = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine($"Custom fields: {data.data}");
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/leads/custom-fields')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  puts "Custom fields: #{data['data']}"
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.JsonObject;
import com.google.gson.JsonParser;

public class CustomFieldsClient {
    private static final OkHttpClient client = new OkHttpClient();
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/leads/custom-fields";
        
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject data = JsonParser.parseString(responseBody).getAsJsonObject();
                System.out.println("Custom fields: " + data.get("data"));
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'leads-new-lead': {
    id: 'leads-new-lead',
    title: 'Create new lead',
    category: 'Leads',
    categoryId: 'leads',
    method: 'POST',
    path: '/leads/new-lead',
    description: 'Creates a new lead in your organization\'s database. This endpoint supports both standard lead fields and custom fields that have been configured for your organization.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer {your_api_key}'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'name',
        type: 'string',
        required: true,
        description: 'Lead\'s full name',
        example: 'John Smith'
      },
      {
        name: 'phone',
        type: 'string',
        required: true,
        description: 'Lead\'s phone number',
        example: '0501234567'
      },
      {
        name: 'status',
        type: 'string',
        required: true,
        description: 'Lead status (new, contacted, qualified)',
        example: 'new'
      },
      {
        name: 'source',
        type: 'string',
        required: true,
        description: 'Lead source (website, facebook, phone)',
        example: 'website'
      },
      {
        name: 'hasCustomFields',
        type: 'boolean',
        required: true,
        description: 'Whether custom fields are included',
        example: 'true'
      },
      {
        name: 'email',
        type: 'string',
        required: false,
        description: 'Lead\'s email address',
        example: 'john@example.com'
      },
      {
        name: 'company',
        type: 'string',
        required: false,
        description: 'Lead\'s company name',
        example: 'Smith Industries Ltd'
      },
      {
        name: 'address',
        type: 'string',
        required: false,
        description: 'Lead\'s address',
        example: '123 Main Street, Tel Aviv'
      },
      {
        name: 'assigned',
        type: 'string',
        required: false,
        description: 'Assigned user ID',
        example: 'user-uuid-here'
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Additional notes',
        example: 'Important potential client'
      },
      {
        name: 'customFields',
        type: 'array',
        required: false,
        description: 'Custom field values (required if hasCustomFields=true)',
        example: '[{"key": "1750878964687_1750878975688", "content": "Tech Company"}]'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Created - Lead created successfully',
        example: JSON.stringify({
          success: true,
          message: 'Lead created successfully',
          lead_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing required fields',
        example: JSON.stringify({
          error: 'Missing required field: name'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '409',
        description: 'Conflict - Duplicate phone number',
        example: JSON.stringify({
          error: 'Lead with this phone number already exists for this organization'
        }, null, 2)
      },
      {
        code: '422',
        description: 'Unprocessable Entity - Invalid custom field',
        example: JSON.stringify({
          error: 'Custom field key \'invalid_key\' does not exist for this organization'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/leads/new-lead';

const headers = {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
};

// Example 1: Lead without custom fields
const leadData = {
    name: 'John Smith',
    phone: '0501234567',
    status: 'new',
    source: 'website',
    hasCustomFields: false,
    email: 'john@example.com',
    company: 'Smith Industries Ltd'
};

fetch(url, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(leadData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Lead created successfully:', data.lead_id);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));

// Example 2: Lead with custom fields
const leadDataWithCustom = {
    name: 'David Johnson',
    phone: '0529876543',
    status: 'new',
    source: 'facebook',
    hasCustomFields: true,
    customFields: [
        {
            key: '1750878964687_1750878975688',
            content: 'Tech Company'
        },
        {
            key: '1750878964687_1750878993373',
            content: '0321234567'
        }
    ]
};

fetch(url, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(leadDataWithCustom)
})
.then(response => response.json())
.then(data => console.log('Response:', data))
.catch(error => console.error('Request failed:', error));`,
      python: `import requests
import json

api_key = "your_api_key_here"
url = "https://dev.clickai.live/leads/new-lead"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Example 1: Lead without custom fields
lead_data = {
    "name": "John Smith",
    "phone": "0501234567",
    "status": "new",
    "source": "website",
    "hasCustomFields": False,
    "email": "john@example.com",
    "company": "Smith Industries Ltd"
}

response = requests.post(url, headers=headers, json=lead_data)

if response.status_code == 201:
    data = response.json()
    print("Lead created successfully:", data['lead_id'])
else:
    print(f"Error {response.status_code}: {response.json()}")

# Example 2: Lead with custom fields
lead_data_with_custom = {
    "name": "David Johnson",
    "phone": "0529876543",
    "status": "new",
    "source": "facebook",
    "hasCustomFields": True,
    "customFields": [
        {
            "key": "1750878964687_1750878975688",
            "content": "Tech Company"
        },
        {
            "key": "1750878964687_1750878993373",
            "content": "0321234567"
        }
    ]
}

response = requests.post(url, headers=headers, json=lead_data_with_custom)
print(f"Response: {response.status_code}, {response.json()}")`,
      curl: `curl -X POST "https://dev.clickai.live/leads/new-lead" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"name\\":\\"John Smith\\",\\"phone\\":\\"0501234567\\",\\"status\\":\\"new\\",\\"source\\":\\"website\\",\\"hasCustomFields\\":false,\\"email\\":\\"john@example.com\\",\\"company\\":\\"Smith Industries Ltd\\"}"`,
      curlLinux: `curl -X POST "https://dev.clickai.live/leads/new-lead" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John Smith",
    "phone": "0501234567",
    "status": "new",
    "source": "website",
    "hasCustomFields": false,
    "email": "john@example.com",
    "company": "Smith Industries Ltd"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/leads/new-lead';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

// Example 1: Lead without custom fields
$lead_data = [
    'name' => 'John Smith',
    'phone' => '0501234567',
    'status' => 'new',
    'source' => 'website',
    'hasCustomFields' => false,
    'email' => 'john@example.com',
    'company' => 'Smith Industries Ltd'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($lead_data));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $data = json_decode($response, true);
    echo "Lead created successfully: " . $data['lead_id'];
} else {
    echo "Error $http_code: $response";
}

// Example 2: Lead with custom fields
$lead_data_with_custom = [
    'name' => 'David Johnson',
    'phone' => '0529876543',
    'status' => 'new',
    'source' => 'facebook',
    'hasCustomFields' => true,
    'customFields' => [
        [
            'key' => '1750878964687_1750878975688',
            'content' => 'Tech Company'
        ],
        [
            'key' => '1750878964687_1750878993373',
            'content' => '0321234567'
        ]
    ]
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($lead_data_with_custom));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

echo "Response: $http_code, $response";
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/leads/new-lead";
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            // Example 1: Lead without custom fields
            var leadData = new
            {
                name = "John Smith",
                phone = "0501234567",
                status = "new",
                source = "website",
                hasCustomFields = false,
                email = "john@example.com",
                company = "Smith Industries Ltd"
            };
            
            string jsonData = JsonConvert.SerializeObject(leadData);
            var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
            
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic data = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine($"Lead created successfully: {data.lead_id}");
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
            
            // Example 2: Lead with custom fields
            var leadDataWithCustom = new
            {
                name = "David Johnson",
                phone = "0529876543",
                status = "new",
                source = "facebook",
                hasCustomFields = true,
                customFields = new[]
                {
                    new { key = "1750878964687_1750878975688", content = "Tech Company" },
                    new { key = "1750878964687_1750878993373", content = "0321234567" }
                }
            };
            
            string jsonDataWithCustom = JsonConvert.SerializeObject(leadDataWithCustom);
            var contentWithCustom = new StringContent(jsonDataWithCustom, Encoding.UTF8, "application/json");
            
            HttpResponseMessage responseWithCustom = await client.PostAsync(url, contentWithCustom);
            string responseContentWithCustom = await responseWithCustom.Content.ReadAsStringAsync();
            
            Console.WriteLine($"Response: {responseWithCustom.StatusCode}, {responseContentWithCustom}");
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/leads/new-lead')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

# Example 1: Lead without custom fields
lead_data = {
  name: 'John Smith',
  phone: '0501234567',
  status: 'new',
  source: 'website',
  hasCustomFields: false,
  email: 'john@example.com',
  company: 'Smith Industries Ltd'
}

request.body = lead_data.to_json
response = http.request(request)

if response.code == '201'
  data = JSON.parse(response.body)
  puts "Lead created successfully: #{data['lead_id']}"
else
  puts "Error #{response.code}: #{response.body}"
end

# Example 2: Lead with custom fields
lead_data_with_custom = {
  name: 'David Johnson',
  phone: '0529876543',
  status: 'new',
  source: 'facebook',
  hasCustomFields: true,
  customFields: [
    {
      key: '1750878964687_1750878975688',
      content: 'Tech Company'
    },
    {
      key: '1750878964687_1750878993373',
      content: '0321234567'
    }
  ]
}

request_with_custom = Net::HTTP::Post.new(url)
request_with_custom['Authorization'] = "Bearer #{api_key}"
request_with_custom['Content-Type'] = 'application/json'
request_with_custom.body = lead_data_with_custom.to_json

response_with_custom = http.request(request_with_custom)
puts "Response: #{response_with_custom.code}, #{response_with_custom.body}"`,
      java: `import okhttp3.*;
import com.google.gson.JsonObject;
import com.google.gson.JsonArray;
import com.google.gson.JsonParser;
import com.google.gson.Gson;

public class CreateLeadClient {
    private static final OkHttpClient client = new OkHttpClient();
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/leads/new-lead";
        
        // Example 1: Lead without custom fields
        JsonObject leadData = new JsonObject();
        leadData.addProperty("name", "John Smith");
        leadData.addProperty("phone", "0501234567");
        leadData.addProperty("status", "new");
        leadData.addProperty("source", "website");
        leadData.addProperty("hasCustomFields", false);
        leadData.addProperty("email", "john@example.com");
        leadData.addProperty("company", "Smith Industries Ltd");
        
        RequestBody body = RequestBody.create(leadData.toString(), JSON);
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .post(body)
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject data = JsonParser.parseString(responseBody).getAsJsonObject();
                System.out.println("Lead created successfully: " + data.get("lead_id"));
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
        
        // Example 2: Lead with custom fields
        JsonObject leadDataWithCustom = new JsonObject();
        leadDataWithCustom.addProperty("name", "David Johnson");
        leadDataWithCustom.addProperty("phone", "0529876543");
        leadDataWithCustom.addProperty("status", "new");
        leadDataWithCustom.addProperty("source", "facebook");
        leadDataWithCustom.addProperty("hasCustomFields", true);
        
        JsonArray customFields = new JsonArray();
        JsonObject customField1 = new JsonObject();
        customField1.addProperty("key", "1750878964687_1750878975688");
        customField1.addProperty("content", "Tech Company");
        
        JsonObject customField2 = new JsonObject();
        customField2.addProperty("key", "1750878964687_1750878993373");
        customField2.addProperty("content", "0321234567");
        
        customFields.add(customField1);
        customFields.add(customField2);
        leadDataWithCustom.add("customFields", customFields);
        
        RequestBody bodyWithCustom = RequestBody.create(leadDataWithCustom.toString(), JSON);
        Request requestWithCustom = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .post(bodyWithCustom)
            .build();
            
        try (Response responseWithCustom = client.newCall(requestWithCustom).execute()) {
            String responseBodyWithCustom = responseWithCustom.body().string();
            System.out.println("Response: " + responseWithCustom.code() + ", " + responseBodyWithCustom);
        }
    }
}`
    }
  },

  'customers-get-customer': {
    id: 'customers-get-customer',
    title: 'Get customer by ID',
    category: 'Customers',
    categoryId: 'customers',
    method: 'POST',
    path: '/customers/get-customer',
    description: 'Retrieves complete details of a specific customer by their ID, including all personal information, contact details, address, integration IDs, and all custom fields configured for your organization. Returns detailed customer information with enriched custom field metadata.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'customer_id',
        type: 'string',
        required: true,
        description: 'UUID of the customer to retrieve',
        example: '091cd08c-5dc3-456d-9d43-8918a549cfea'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Returns complete customer details',
        example: JSON.stringify({
          "success": true,
          "data": {
            "customer_id": "091cd08c-5dc3-456d-9d43-8918a549cfea",
            "first_name": "John",
            "last_name": "Smith",
            "email": "john@example.com",
            "phone": "0501234567",
            "address": "123 Main Street",
            "city": "Tel Aviv",
            "state": "Center",
            "country": "Israel",
            "status": "active",
            "received": "website_form",
            "profileImageLink": "https://example.com/profile.jpg",
            "next_call": "2024-02-01T10:00:00Z",
            "shopify_id": "12345",
            "woocommerce_id": "67890",
            "created_at": "2024-01-01T12:00:00Z",
            "updated_at": "2024-01-15T14:30:00Z",
            "customFields": [
              {
                "key": "customer_type_1749674077124",
                "content": "Business",
                "label": "Customer Type",
                "type": "select",
                "area": "Customer Details"
              },
              {
                "key": "priority_level_1749674482288",
                "content": "High",
                "label": "Priority Level",
                "type": "text",
                "area": "Customer Details"
              },
              {
                "key": "notes_field_1749674482289",
                "content": "N/A",
                "label": "Additional Notes",
                "type": "textarea",
                "area": "Notes"
              }
            ]
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing customer_id or invalid request',
        example: JSON.stringify({
          "error": "Missing required field: customer_id"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          "error": "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          "error": "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Customer not found',
        example: JSON.stringify({
          "error": "Customer not found or does not belong to your organization"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          "error": "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const response = await fetch('https://dev.clickai.live/customers/get-customer', {
    method: 'POST',
    headers: {
        'Authorization': 'Bearer your_api_key_here',
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        customer_id: "091cd08c-5dc3-456d-9d43-8918a549cfea"
    })
});

const data = await response.json();

if (response.ok && data.success) {
    const customer = data.data;
    console.log(\`Customer: \${customer.first_name} \${customer.last_name}\`);
    console.log(\`Phone: \${customer.phone}\`);
    console.log(\`Email: \${customer.email}\`);
    console.log(\`Status: \${customer.status}\`);
    console.log(\`Address: \${customer.address}, \${customer.city}, \${customer.country}\`);
    
    // Display custom fields
    console.log('Custom Fields:');
    customer.customFields.forEach(field => {
        if (field.content !== 'N/A') {
            console.log(\`  \${field.label}: \${field.content}\`);
        }
    });
    
    // Display integration IDs if available
    if (customer.shopify_id !== 'N/A') {
        console.log(\`Shopify ID: \${customer.shopify_id}\`);
    }
    if (customer.woocommerce_id !== 'N/A') {
        console.log(\`WooCommerce ID: \${customer.woocommerce_id}\`);
    }
} else {
    console.error('Error:', data.error || 'Unknown error');
}`,
      python: `import requests
import json
from datetime import datetime

def get_customer_by_id(api_key, customer_id):
    url = "https://dev.clickai.live/customers/get-customer"
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    data = {
        "customer_id": customer_id
    }
    
    try:
        response = requests.post(url, headers=headers, json=data)
        result = response.json()
        
        if response.status_code == 200 and result.get("success"):
            customer = result["data"]
            print(f"Customer: {customer['first_name']} {customer['last_name']}")
            print(f"Phone: {customer['phone']}")
            print(f"Email: {customer['email']}")
            print(f"Status: {customer['status']}")
            print(f"Address: {customer['address']}, {customer['city']}, {customer['country']}")
            
            # Display timestamps
            created_date = datetime.fromisoformat(customer['created_at'].replace('Z', '+00:00'))
            updated_date = datetime.fromisoformat(customer['updated_at'].replace('Z', '+00:00'))
            print(f"Created: {created_date.strftime('%Y-%m-%d %H:%M')}")
            print(f"Updated: {updated_date.strftime('%Y-%m-%d %H:%M')}")
            
            # Display custom fields
            print("\\nCustom Fields:")
            for field in customer["customFields"]:
                if field["content"] != "N/A":
                    print(f"  {field['label']}: {field['content']} ({field['type']})")
            
            # Display integration IDs
            if customer.get("shopify_id") != "N/A":
                print(f"\\nShopify ID: {customer['shopify_id']}")
            if customer.get("woocommerce_id") != "N/A":
                print(f"WooCommerce ID: {customer['woocommerce_id']}")
            
            return customer
        else:
            print(f"Error {response.status_code}: {result.get('error', 'Unknown error')}")
            return None
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

# Usage
api_key = "your_api_key_here"
customer_id = "091cd08c-5dc3-456d-9d43-8918a549cfea"
customer = get_customer_by_id(api_key, customer_id)`,
      curl: `# Get customer by ID
curl -X POST "https://dev.clickai.live/customers/get-customer" \\
     -H "Authorization: Bearer your_api_key_here" \\
     -H "Content-Type: application/json" \\
     -d '{
       "customer_id": "091cd08c-5dc3-456d-9d43-8918a549cfea"
     }'`,
      powershell: `# PowerShell example for getting customer by ID
$apiKey = "your_api_key_here"
$url = "https://dev.clickai.live/customers/get-customer"

$headers = @{
    "Authorization" = "Bearer $apiKey"
    "Content-Type" = "application/json"
}

$body = @{
    customer_id = "091cd08c-5dc3-456d-9d43-8918a549cfea"
} | ConvertTo-Json

try {
    $response = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $body
    
    if ($response.success) {
        $customer = $response.data
        Write-Host "Customer: $($customer.first_name) $($customer.last_name)" -ForegroundColor Green
        Write-Host "Phone: $($customer.phone)"
        Write-Host "Email: $($customer.email)"
        Write-Host "Status: $($customer.status)"
        Write-Host "Address: $($customer.address), $($customer.city), $($customer.country)"
        
        Write-Host "\\nCustom Fields:" -ForegroundColor Yellow
        foreach ($field in $customer.customFields) {
            if ($field.content -ne "N/A") {
                Write-Host "  $($field.label): $($field.content) ($($field.type))"
            }
        }
        
        if ($customer.shopify_id -ne "N/A") {
            Write-Host "\\nShopify ID: $($customer.shopify_id)" -ForegroundColor Cyan
        }
        if ($customer.woocommerce_id -ne "N/A") {
            Write-Host "WooCommerce ID: $($customer.woocommerce_id)" -ForegroundColor Cyan
        }
    }
} catch {
    Write-Host "Error: $($_.Exception.Message)" -ForegroundColor Red
}`,
      php: `<?php
function getCustomerById($apiKey, $customerId) {
    $url = 'https://dev.clickai.live/customers/get-customer';
    
    $data = array(
        'customer_id' => $customerId
    );
    
    $curl = curl_init();
    
    curl_setopt_array($curl, array(
        CURLOPT_URL => $url,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_POST => true,
        CURLOPT_POSTFIELDS => json_encode($data),
        CURLOPT_HTTPHEADER => array(
            'Authorization: Bearer ' . $apiKey,
            'Content-Type: application/json'
        ),
    ));
    
    $response = curl_exec($curl);
    $httpCode = curl_getinfo($curl, CURLINFO_HTTP_CODE);
    curl_close($curl);
    
    $result = json_decode($response, true);
    
    if ($httpCode == 200 && isset($result['success']) && $result['success']) {
        $customer = $result['data'];
        echo "Customer: " . $customer['first_name'] . " " . $customer['last_name'] . "\\n";
        echo "Phone: " . $customer['phone'] . "\\n";
        echo "Email: " . $customer['email'] . "\\n";
        echo "Status: " . $customer['status'] . "\\n";
        echo "Address: " . $customer['address'] . ", " . $customer['city'] . ", " . $customer['country'] . "\\n";
        
        // Display timestamps
        $createdDate = new DateTime($customer['created_at']);
        $updatedDate = new DateTime($customer['updated_at']);
        echo "Created: " . $createdDate->format('Y-m-d H:i') . "\\n";
        echo "Updated: " . $updatedDate->format('Y-m-d H:i') . "\\n";
        
        // Display custom fields
        echo "\\nCustom Fields:\\n";
        foreach ($customer['customFields'] as $field) {
            if ($field['content'] !== 'N/A') {
                echo "  " . $field['label'] . ": " . $field['content'] . " (" . $field['type'] . ")\\n";
            }
        }
        
        // Display integration IDs
        if (isset($customer['shopify_id']) && $customer['shopify_id'] !== 'N/A') {
            echo "\\nShopify ID: " . $customer['shopify_id'] . "\\n";
        }
        if (isset($customer['woocommerce_id']) && $customer['woocommerce_id'] !== 'N/A') {
            echo "WooCommerce ID: " . $customer['woocommerce_id'] . "\\n";
        }
        
        return $customer;
    } else {
        echo "Error $httpCode: " . ($result['error'] ?? 'Unknown error') . "\\n";
        return null;
    }
}

// Usage
$apiKey = 'your_api_key_here';
$customerId = '091cd08c-5dc3-456d-9d43-8918a549cfea';
$customer = getCustomerById($apiKey, $customerId);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class CustomerService
{
    private readonly HttpClient _httpClient;
    private readonly string _baseUrl = "https://dev.clickai.live";

    public CustomerService()
    {
        _httpClient = new HttpClient();
    }

    public async Task<CustomerResponse> GetCustomerByIdAsync(string apiKey, string customerId)
    {
        try
        {
            _httpClient.DefaultRequestHeaders.Clear();
            _httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");

            var requestData = new { customer_id = customerId };
            var json = JsonConvert.SerializeObject(requestData);
            var content = new StringContent(json, Encoding.UTF8, "application/json");

            var response = await _httpClient.PostAsync($"{_baseUrl}/customers/get-customer", content);
            var responseContent = await response.Content.ReadAsStringAsync();

            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<CustomerResponse>(responseContent);
                
                if (result.Success)
                {
                    var customer = result.Data;
                    Console.WriteLine($"Customer: {customer.FirstName} {customer.LastName}");
                    Console.WriteLine($"Phone: {customer.Phone}");
                    Console.WriteLine($"Email: {customer.Email}");
                    Console.WriteLine($"Status: {customer.Status}");
                    Console.WriteLine($"Address: {customer.Address}, {customer.City}, {customer.Country}");
                    
                    Console.WriteLine("\\nCustom Fields:");
                    foreach (var field in customer.CustomFields)
                    {
                        if (field.Content != "N/A")
                        {
                            Console.WriteLine($"  {field.Label}: {field.Content} ({field.Type})");
                        }
                    }
                    
                    if (customer.ShopifyId != "N/A")
                        Console.WriteLine($"\\nShopify ID: {customer.ShopifyId}");
                    if (customer.WooCommerceId != "N/A")
                        Console.WriteLine($"WooCommerce ID: {customer.WooCommerceId}");
                }
                return result;
            }
            else
            {
                var errorData = JsonConvert.DeserializeObject<ErrorResponse>(responseContent);
                Console.WriteLine($"Error {(int)response.StatusCode}: {errorData.Error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
            return null;
        }
    }
}

public class CustomerResponse
{
    public bool Success { get; set; }
    public Customer Data { get; set; }
}

public class Customer
{
    [JsonProperty("customer_id")]
    public string CustomerId { get; set; }
    [JsonProperty("first_name")]
    public string FirstName { get; set; }
    [JsonProperty("last_name")]
    public string LastName { get; set; }
    public string Email { get; set; }
    public string Phone { get; set; }
    public string Address { get; set; }
    public string City { get; set; }
    public string State { get; set; }
    public string Country { get; set; }
    public string Status { get; set; }
    [JsonProperty("shopify_id")]
    public string ShopifyId { get; set; }
    [JsonProperty("woocommerce_id")]
    public string WooCommerceId { get; set; }
    [JsonProperty("customFields")]
    public CustomField[] CustomFields { get; set; }
}

public class CustomField
{
    public string Key { get; set; }
    public string Content { get; set; }
    public string Label { get; set; }
    public string Type { get; set; }
    public string Area { get; set; }
}

public class ErrorResponse
{
    public string Error { get; set; }
}`,
      ruby: `require 'net/http'
require 'json'
require 'date'

class CustomerService
  BASE_URL = 'https://dev.clickai.live'

  def self.get_customer_by_id(api_key, customer_id)
    uri = URI("#{BASE_URL}/customers/get-customer")
    
    http = Net::HTTP.new(uri.host, uri.port)
    http.use_ssl = true

    request = Net::HTTP::Post.new(uri)
    request['Authorization'] = "Bearer #{api_key}"
    request['Content-Type'] = 'application/json'

    data = { customer_id: customer_id }
    request.body = data.to_json

    begin
      response = http.request(request)
      result = JSON.parse(response.body)

      case response.code.to_i
      when 200
        if result['success']
          customer = result['data']
          puts "Customer: #{customer['first_name']} #{customer['last_name']}"
          puts "Phone: #{customer['phone']}"
          puts "Email: #{customer['email']}"
          puts "Status: #{customer['status']}"
          puts "Address: #{customer['address']}, #{customer['city']}, #{customer['country']}"
          
          # Display timestamps
          created_date = DateTime.parse(customer['created_at'])
          updated_date = DateTime.parse(customer['updated_at'])
          puts "Created: #{created_date.strftime('%Y-%m-%d %H:%M')}"
          puts "Updated: #{updated_date.strftime('%Y-%m-%d %H:%M')}"
          
          # Display custom fields
          puts "\\nCustom Fields:"
          customer['customFields'].each do |field|
            if field['content'] != 'N/A'
              puts "  #{field['label']}: #{field['content']} (#{field['type']})"
            end
          end
          
          # Display integration IDs
          if customer['shopify_id'] != 'N/A'
            puts "\\nShopify ID: #{customer['shopify_id']}"
          end
          if customer['woocommerce_id'] != 'N/A'
            puts "WooCommerce ID: #{customer['woocommerce_id']}"
          end
          
          return customer
        end
      else
        puts "Error #{response.code}: #{result['error'] || 'Unknown error'}"
        return nil
      end
    rescue StandardError => e
      puts "Request failed: #{e.message}"
      return nil
    end
  end
end

# Usage
api_key = 'your_api_key_here'
customer_id = '091cd08c-5dc3-456d-9d43-8918a549cfea'
customer = CustomerService.get_customer_by_id(api_key, customer_id)`,
      java: `import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.net.URI;
import java.time.LocalDateTime;
import java.time.format.DateTimeFormatter;
import com.google.gson.Gson;
import com.google.gson.JsonObject;
import com.google.gson.JsonArray;

public class CustomerAPI {
    private static final String BASE_URL = "https://dev.clickai.live";
    private static final HttpClient client = HttpClient.newHttpClient();
    private static final Gson gson = new Gson();

    public static CustomerData getCustomerById(String apiKey, String customerId) {
        try {
            String jsonData = String.format("{\\"customer_id\\":\\"%s\\"}", customerId);

            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(BASE_URL + "/customers/get-customer"))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(jsonData))
                .build();

            HttpResponse<String> response = client.send(request, 
                HttpResponse.BodyHandlers.ofString());

            JsonObject result = gson.fromJson(response.body(), JsonObject.class);

            if (response.statusCode() == 200 && result.get("success").getAsBoolean()) {
                JsonObject customerData = result.getAsJsonObject("data");
                
                System.out.println("Customer: " + customerData.get("first_name").getAsString() + 
                    " " + customerData.get("last_name").getAsString());
                System.out.println("Phone: " + customerData.get("phone").getAsString());
                System.out.println("Email: " + customerData.get("email").getAsString());
                System.out.println("Status: " + customerData.get("status").getAsString());
                System.out.println("Address: " + customerData.get("address").getAsString() + 
                    ", " + customerData.get("city").getAsString() + 
                    ", " + customerData.get("country").getAsString());
                
                // Display timestamps
                String createdAt = customerData.get("created_at").getAsString();
                String updatedAt = customerData.get("updated_at").getAsString();
                System.out.println("Created: " + formatTimestamp(createdAt));
                System.out.println("Updated: " + formatTimestamp(updatedAt));
                
                // Display custom fields
                System.out.println("\\nCustom Fields:");
                JsonArray customFields = customerData.getAsJsonArray("customFields");
                for (int i = 0; i < customFields.size(); i++) {
                    JsonObject field = customFields.get(i).getAsJsonObject();
                    String content = field.get("content").getAsString();
                    if (!content.equals("N/A")) {
                        System.out.println("  " + field.get("label").getAsString() + 
                            ": " + content + " (" + field.get("type").getAsString() + ")");
                    }
                }
                
                // Display integration IDs
                String shopifyId = customerData.get("shopify_id").getAsString();
                String wooCommerceId = customerData.get("woocommerce_id").getAsString();
                if (!shopifyId.equals("N/A")) {
                    System.out.println("\\nShopify ID: " + shopifyId);
                }
                if (!wooCommerceId.equals("N/A")) {
                    System.out.println("WooCommerce ID: " + wooCommerceId);
                }
                
                return new CustomerData(true, customerData.toString());
            } else {
                String error = result.has("error") ? result.get("error").getAsString() : "Unknown error";
                System.out.println("Error " + response.statusCode() + ": " + error);
                return new CustomerData(false, error);
            }

        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
            return new CustomerData(false, e.getMessage());
        }
    }
    
    private static String formatTimestamp(String timestamp) {
        // Simple timestamp formatting - you might want to use a proper date library
        return timestamp.replace("T", " ").replace("Z", "");
    }

    public static void main(String[] args) {
        String apiKey = "your_api_key_here";
        String customerId = "091cd08c-5dc3-456d-9d43-8918a549cfea";
        
        CustomerData customer = getCustomerById(apiKey, customerId);
    }

    static class CustomerData {
        boolean success;
        String data;

        CustomerData(boolean success, String data) {
            this.success = success;
            this.data = data;
        }
    }
}`
    }
  },

  // Products endpoints
  'products-get-all': {
    id: 'products-get-all',
    title: 'Get all products',
    category: 'Products',
    categoryId: 'products',
    method: 'GET',
    path: '/products/all',
    description: 'Retrieves a complete list of all products in your organization. This endpoint returns essential product information including IDs, titles, descriptions, pricing, and integration data for external platforms like Shopify and WooCommerce.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer {your_api_key}',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: false,
        description: 'application/json (Recommended)',
        example: 'application/json'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'OK - Request successful',
        example: JSON.stringify({
          "success": true,
          "data": [
            {
              "id": "03727424-3596-418b-865c-d7fee7810426",
              "title": "Example Product 1",
              "shopify_id": "12345678",
              "woocommerce_id": "N/A",
              "description": "Detailed product description",
              "product_link": "https://example.com/product1",
              "product_type": "Electronics",
              "tags": ["Technology", "New", "Sale"],
              "image_link": "https://example.com/images/product1.jpg",
              "category": "Computers",
              "price": 1299.99,
              "compare_at_price": 1599.99,
              "has_variants": true
            },
            {
              "id": "5d5f3f6b-83a2-4b2c-adfb-b93606480325",
              "title": "Example Product 2",
              "shopify_id": "N/A",
              "woocommerce_id": "WC789",
              "description": "N/A",
              "product_link": "N/A",
              "product_type": "Clothing",
              "tags": [],
              "image_link": "N/A",
              "category": "Fashion",
              "price": 89.90,
              "compare_at_price": 0,
              "has_variants": false
            }
          ],
          "count": 2
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          "error": "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          "error": "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          "error": "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/products/all';

fetch(url, {
    method: 'GET',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    }
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log(\`Found \${data.count} products:\`);
        data.data.forEach(product => {
            const priceDisplay = product.price > 0 ? \`₪\${product.price}\` : 'No price';
            console.log(\`- \${product.title} (ID: \${product.id}) - \${priceDisplay}\`);
            if (product.tags.length > 0) {
                console.log(\`  Tags: \${product.tags.join(', ')}\`);
            }
        });
    } else {
        console.log('No products found');
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/products/all"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

response = requests.get(url, headers=headers)

if response.status_code == 200:
    data = response.json()
    if data['success']:
        print(f"Found {data['count']} products:")
        for product in data['data']:
            price_display = f"₪{product['price']}" if product['price'] > 0 else "No price"
            print(f"- {product['title']} (ID: {product['id']}) - {price_display}")
            if product['tags']:
                print(f"  Tags: {', '.join(product['tags'])}")
    else:
        print("No products found")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      curl: `curl -X GET "https://dev.clickai.live/products/all" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json"`,
      curlLinux: `curl -X GET "https://dev.clickai.live/products/all" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/products/all';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    if ($data['success']) {
        echo "Found " . $data['count'] . " products:\\n";
        foreach ($data['data'] as $product) {
            $price_display = $product['price'] > 0 ? "₪" . $product['price'] : "No price";
            echo "- " . $product['title'] . " (ID: " . $product['id'] . ") - " . $price_display . "\\n";
            if (!empty($product['tags'])) {
                echo "  Tags: " . implode(', ', $product['tags']) . "\\n";
            }
        }
    } else {
        echo "No products found\\n";
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/products/all";
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            HttpResponseMessage response = await client.GetAsync(url);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic data = JsonConvert.DeserializeObject(responseContent);
                if (data.success == true)
                {
                    Console.WriteLine($"Found {data.count} products:");
                    foreach (var product in data.data)
                    {
                        string priceDisplay = product.price > 0 ? $"₪{product.price}" : "No price";
                        Console.WriteLine($"- {product.title} (ID: {product.id}) - {priceDisplay}");
                        if (product.tags != null && product.tags.Count > 0)
                        {
                            Console.WriteLine($"  Tags: {string.Join(", ", product.tags)}");
                        }
                    }
                }
                else
                {
                    Console.WriteLine("No products found");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/products/all')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  if data['success']
    puts "Found #{data['count']} products:"
    data['data'].each do |product|
      price_display = product['price'] > 0 ? "₪#{product['price']}" : "No price"
      puts "- #{product['title']} (ID: #{product['id']}) - #{price_display}"
      if product['tags'] && !product['tags'].empty?
        puts "  Tags: #{product['tags'].join(', ')}"
      end
    end
  else
    puts "No products found"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.Gson;
import com.google.gson.JsonObject;
import com.google.gson.JsonArray;
import com.google.gson.JsonElement;

public class ProductsClient {
    private static final OkHttpClient client = new OkHttpClient();
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/products/all";
        
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                Gson gson = new Gson();
                JsonObject data = gson.fromJson(responseBody, JsonObject.class);
                
                if (data.get("success").getAsBoolean()) {
                    JsonArray products = data.getAsJsonArray("data");
                    int count = data.get("count").getAsInt();
                    
                    System.out.println("Found " + count + " products:");
                    for (JsonElement productElement : products) {
                        JsonObject product = productElement.getAsJsonObject();
                        double price = product.get("price").getAsDouble();
                        String priceDisplay = price > 0 ? "₪" + price : "No price";
                        
                        System.out.println("- " + product.get("title").getAsString() + 
                            " (ID: " + product.get("id").getAsString() + ") - " + priceDisplay);
                        
                        JsonArray tags = product.getAsJsonArray("tags");
                        if (tags != null && tags.size() > 0) {
                            System.out.print("  Tags: ");
                            for (int i = 0; i < tags.size(); i++) {
                                if (i > 0) System.out.print(", ");
                                System.out.print(tags.get(i).getAsString());
                            }
                            System.out.println();
                        }
                    }
                } else {
                    System.out.println("No products found");
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  // Leads endpoints - additional
  'leads-edit-lead': {
    id: 'leads-edit-lead',
    title: 'Edit lead',
    category: 'Leads',
    categoryId: 'leads',
    method: 'POST',
    path: '/leads/edit-lead',
    description: 'Updates an existing lead in your organization\'s database. Only the lead_id is required - all other fields are optional. You can update standard fields, custom fields, or both.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json'
      }
    ],
    parameters: [
      {
        name: 'lead_id',
        type: 'string',
        required: true,
        description: 'ID of the lead to update',
        example: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
      },
      {
        name: 'name',
        type: 'string',
        required: false,
        description: 'Lead\'s full name',
        example: 'John Doe Updated'
      },
      {
        name: 'phone',
        type: 'string',
        required: false,
        description: 'Lead\'s phone number',
        example: '0509999999'
      },
      {
        name: 'email',
        type: 'string',
        required: false,
        description: 'Lead\'s email address',
        example: 'newemail@example.com'
      },
      {
        name: 'status',
        type: 'string',
        required: false,
        description: 'Lead status',
        example: 'contacted'
      },
      {
        name: 'source',
        type: 'string',
        required: false,
        description: 'Lead source',
        example: 'phone'
      },
      {
        name: 'company',
        type: 'string',
        required: false,
        description: 'Lead\'s company name',
        example: 'New Company Ltd'
      },
      {
        name: 'address',
        type: 'string',
        required: false,
        description: 'Lead\'s address',
        example: '456 New Street, Jerusalem'
      },
      {
        name: 'assigned',
        type: 'string',
        required: false,
        description: 'Assigned user ID',
        example: 'user-uuid-here'
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Additional notes',
        example: 'Important update'
      },
      {
        name: 'hasCustomFields',
        type: 'boolean',
        required: false,
        description: 'Whether to update custom fields'
      },
      {
        name: 'customFields',
        type: 'array',
        required: false,
        description: 'Custom field values (if hasCustomFields=true)',
        example: '[{"key": "1750878964687_1750878975688", "content": "Updated Business"}]'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'OK - Lead updated successfully',
        example: JSON.stringify({
          success: true,
          message: 'Lead updated successfully',
          lead_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing lead_id',
        example: JSON.stringify({
          error: 'Missing required field: lead_id'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Lead not found',
        example: JSON.stringify({
          error: 'Lead not found or does not belong to your organization'
        }, null, 2)
      },
      {
        code: '422',
        description: 'Unprocessable Entity - Invalid Custom Field',
        example: JSON.stringify({
          error: 'Custom field key \'invalid_key\' does not exist for this organization'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/leads/edit-lead';

const headers = {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
};

// Example 1: Update basic fields only
const updateData = {
    lead_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    name: 'John Doe Updated',
    phone: '0509999999',
    email: 'newemail@example.com'
};

fetch(url, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(updateData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Lead updated successfully:', data.lead_id);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));

// Example 2: Update with custom fields
const updateDataWithCustom = {
    lead_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    name: 'David Johnson Updated',
    hasCustomFields: true,
    customFields: [
        {
            key: '1750878964687_1750878975688',
            content: 'Updated Business'
        },
        {
            key: '1750878964687_1750878993373',
            content: '0321234567'
        }
    ]
};

fetch(url, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(updateDataWithCustom)
})
.then(response => response.json())
.then(data => console.log('Response:', data))
.catch(error => console.error('Request failed:', error));`,
      python: `import requests
import json

api_key = "your_api_key_here"
url = "https://dev.clickai.live/leads/edit-lead"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Example 1: Update basic fields only
update_data = {
    "lead_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "name": "John Doe Updated",
    "phone": "0509999999",
    "email": "newemail@example.com"
}

response = requests.post(url, headers=headers, json=update_data)

if response.status_code == 200:
    data = response.json()
    print("Lead updated successfully:", data['lead_id'])
else:
    print(f"Error {response.status_code}: {response.json()}")

# Example 2: Update with custom fields
update_data_with_custom = {
    "lead_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "name": "David Johnson Updated",
    "hasCustomFields": True,
    "customFields": [
        {
            "key": "1750878964687_1750878975688",
            "content": "Updated Business"
        },
        {
            "key": "1750878964687_1750878993373",
            "content": "0321234567"
        }
    ]
}

response = requests.post(url, headers=headers, json=update_data_with_custom)
print(f"Response: {response.status_code}, {response.json()}")

# Example 3: Delete all custom fields
delete_custom_data = {
    "lead_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "hasCustomFields": False
}

response = requests.post(url, headers=headers, json=delete_custom_data)
print(f"Custom fields deleted: {response.status_code}, {response.json()}")`,
      curl: `curl -X POST "https://dev.clickai.live/leads/edit-lead" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "lead_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "name": "John Doe Updated",
    "phone": "0509999999",
    "email": "newemail@example.com"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/leads/edit-lead';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

// Example 1: Update basic fields
$update_data = [
    'lead_id' => 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    'name' => 'John Doe Updated',
    'phone' => '0509999999',
    'email' => 'newemail@example.com'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($update_data));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    echo "Lead updated successfully: " . $data['lead_id'];
} else {
    echo "Error $http_code: $response";
}

// Example 2: Update with custom fields
$update_data_with_custom = [
    'lead_id' => 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    'name' => 'David Johnson Updated',
    'hasCustomFields' => true,
    'customFields' => [
        [
            'key' => '1750878964687_1750878975688',
            'content' => 'Updated Business'
        ],
        [
            'key' => '1750878964687_1750878993373',
            'content' => '0321234567'
        ]
    ]
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($update_data_with_custom));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

echo "Response: $http_code, $response";
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/leads/edit-lead";
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            // Example 1: Update basic fields
            var updateData = new
            {
                lead_id = "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                name = "John Doe Updated",
                phone = "0509999999",
                email = "newemail@example.com"
            };
            
            string jsonData = JsonConvert.SerializeObject(updateData);
            var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
            
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic data = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine($"Lead updated successfully: {data.lead_id}");
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
            
            // Example 2: Update with custom fields
            var updateDataWithCustom = new
            {
                lead_id = "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                name = "David Johnson Updated",
                hasCustomFields = true,
                customFields = new[]
                {
                    new { key = "1750878964687_1750878975688", content = "Updated Business" },
                    new { key = "1750878964687_1750878993373", content = "0321234567" }
                }
            };
            
            string jsonDataWithCustom = JsonConvert.SerializeObject(updateDataWithCustom);
            var contentWithCustom = new StringContent(jsonDataWithCustom, Encoding.UTF8, "application/json");
            
            HttpResponseMessage responseWithCustom = await client.PostAsync(url, contentWithCustom);
            string responseContentWithCustom = await responseWithCustom.Content.ReadAsStringAsync();
            
            Console.WriteLine($"Response: {responseWithCustom.StatusCode}, {responseContentWithCustom}");
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/leads/edit-lead')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

# Example 1: Update basic fields
update_data = {
  lead_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
  name: 'John Doe Updated',
  phone: '0509999999',
  email: 'newemail@example.com'
}

request.body = update_data.to_json
response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  puts "Lead updated successfully: #{data['lead_id']}"
else
  puts "Error #{response.code}: #{response.body}"
end

# Example 2: Update with custom fields
update_data_with_custom = {
  lead_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
  name: 'David Johnson Updated',
  hasCustomFields: true,
  customFields: [
    {
      key: '1750878964687_1750878975688',
      content: 'Updated Business'
    },
    {
      key: '1750878964687_1750878993373',
      content: '0321234567'
    }
  ]
}

request_with_custom = Net::HTTP::Post.new(url)
request_with_custom['Authorization'] = "Bearer #{api_key}"
request_with_custom['Content-Type'] = 'application/json'
request_with_custom.body = update_data_with_custom.to_json

response_with_custom = http.request(request_with_custom)
puts "Response: #{response_with_custom.code}, #{response_with_custom.body}"`,
      java: `import okhttp3.*;
import com.google.gson.JsonObject;
import com.google.gson.JsonArray;
import com.google.gson.JsonParser;

public class EditLeadClient {
    private static final OkHttpClient client = new OkHttpClient();
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/leads/edit-lead";
        
        // Example 1: Update basic fields
        JsonObject updateData = new JsonObject();
        updateData.addProperty("lead_id", "a1b2c3d4-e5f6-7890-abcd-ef1234567890");
        updateData.addProperty("name", "John Doe Updated");
        updateData.addProperty("phone", "0509999999");
        updateData.addProperty("email", "newemail@example.com");
        
        RequestBody body = RequestBody.create(updateData.toString(), JSON);
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .post(body)
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject data = JsonParser.parseString(responseBody).getAsJsonObject();
                System.out.println("Lead updated successfully: " + data.get("lead_id"));
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
        
        // Example 2: Update with custom fields
        JsonObject updateDataWithCustom = new JsonObject();
        updateDataWithCustom.addProperty("lead_id", "a1b2c3d4-e5f6-7890-abcd-ef1234567890");
        updateDataWithCustom.addProperty("name", "David Johnson Updated");
        updateDataWithCustom.addProperty("hasCustomFields", true);
        
        JsonArray customFields = new JsonArray();
        JsonObject customField1 = new JsonObject();
        customField1.addProperty("key", "1750878964687_1750878975688");
        customField1.addProperty("content", "Updated Business");
        
        JsonObject customField2 = new JsonObject();
        customField2.addProperty("key", "1750878964687_1750878993373");
        customField2.addProperty("content", "0321234567");
        
        customFields.add(customField1);
        customFields.add(customField2);
        updateDataWithCustom.add("customFields", customFields);
        
        RequestBody bodyWithCustom = RequestBody.create(updateDataWithCustom.toString(), JSON);
        Request requestWithCustom = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .post(bodyWithCustom)
            .build();
            
        try (Response responseWithCustom = client.newCall(requestWithCustom).execute()) {
            String responseBodyWithCustom = responseWithCustom.body().string();
            System.out.println("Response: " + responseWithCustom.code() + ", " + responseBodyWithCustom);
        }
    }
}`
    }
  },

  'leads-get-lead': {
    id: 'leads-get-lead',
    title: 'Get lead by ID',
    category: 'Leads',
    categoryId: 'leads',
    method: 'POST',
    path: '/leads/get-lead',
    description: 'Retrieves complete information for a specific lead by its ID, including all standard fields and custom fields. Empty fields are displayed as "N/A" for consistency.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json'
      }
    ],
    parameters: [
      {
        name: 'lead_id',
        type: 'string',
        required: true,
        description: 'ID of the lead to retrieve',
        example: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'OK - Lead retrieved successfully',
        example: JSON.stringify({
          success: true,
          data: {
            lead_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
            name: 'John Smith',
            phone: '0501234567',
            email: 'john@example.com',
            status: 'new',
            source: 'website',
            notes: 'N/A',
            assigned_to: 'N/A',
            company: 'Smith Industries Ltd',
            address: 'N/A',
            created_at: '2024-01-01T12:00:00Z',
            updated_at: '2024-01-01T12:00:00Z',
            customFields: [
              {
                key: '1750878964687_1750878975688',
                content: 'Tech Business',
                label: 'Business Details - Business Name',
                type: 'text',
                area: 'Business Details'
              },
              {
                key: '1750878964687_1750878993373',
                content: 'N/A',
                label: 'Business Details - Business Owner Phone',
                type: 'phone',
                area: 'Business Details'
              }
            ]
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing lead_id',
        example: JSON.stringify({
          error: 'Missing required field: lead_id'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Lead not found',
        example: JSON.stringify({
          error: 'Lead not found or does not belong to your organization'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/leads/get-lead';

const headers = {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
};

// Get lead by ID
const leadData = {
    lead_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
};

fetch(url, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(leadData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        const leadInfo = data.data;
        console.log(\`Lead Name: \${leadInfo.name}\`);
        console.log(\`Phone: \${leadInfo.phone}\`);
        console.log(\`Email: \${leadInfo.email}\`);
        console.log(\`Custom Fields: \${leadInfo.customFields.length}\`);
        
        // Print custom fields
        leadInfo.customFields.forEach(field => {
            console.log(\`  \${field.label}: \${field.content}\`);
        });
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests
import json

api_key = "your_api_key_here"
url = "https://dev.clickai.live/leads/get-lead"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Get lead by ID
lead_data = {
    "lead_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890"
}

response = requests.post(url, headers=headers, json=lead_data)

if response.status_code == 200:
    data = response.json()
    lead_info = data['data']
    print(f"Lead Name: {lead_info['name']}")
    print(f"Phone: {lead_info['phone']}")
    print(f"Email: {lead_info['email']}")
    print(f"Custom Fields: {len(lead_info['customFields'])}")
    
    # Print custom fields
    for field in lead_info['customFields']:
        print(f"  {field['label']}: {field['content']}")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      curl: `curl -X POST "https://dev.clickai.live/leads/get-lead" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "lead_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/leads/get-lead';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

// Get lead by ID
$lead_data = [
    'lead_id' => 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($lead_data));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    $lead_info = $data['data'];
    
    echo "Lead Name: " . $lead_info['name'] . "\\n";
    echo "Phone: " . $lead_info['phone'] . "\\n";
    echo "Email: " . $lead_info['email'] . "\\n";
    echo "Custom Fields: " . count($lead_info['customFields']) . "\\n";
    
    // Print custom fields
    foreach ($lead_info['customFields'] as $field) {
        echo "  " . $field['label'] . ": " . $field['content'] . "\\n";
    }
} else {
    echo "Error $http_code: $response";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/leads/get-lead";
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            // Get lead by ID
            var leadData = new
            {
                lead_id = "a1b2c3d4-e5f6-7890-abcd-ef1234567890"
            };
            
            string jsonData = JsonConvert.SerializeObject(leadData);
            var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
            
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic data = JsonConvert.DeserializeObject(responseContent);
                var leadInfo = data.data;
                
                Console.WriteLine($"Lead Name: {leadInfo.name}");
                Console.WriteLine($"Phone: {leadInfo.phone}");
                Console.WriteLine($"Email: {leadInfo.email}");
                Console.WriteLine($"Custom Fields: {leadInfo.customFields.Count}");
                
                // Print custom fields
                foreach (var field in leadInfo.customFields)
                {
                    Console.WriteLine($"  {field.label}: {field.content}");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/leads/get-lead')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

# Get lead by ID
lead_data = {
  lead_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
}

request.body = lead_data.to_json
response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  lead_info = data['data']
  
  puts "Lead Name: #{lead_info['name']}"
  puts "Phone: #{lead_info['phone']}"
  puts "Email: #{lead_info['email']}"
  puts "Custom Fields: #{lead_info['customFields'].length}"
  
  # Print custom fields
  lead_info['customFields'].each do |field|
    puts "  #{field['label']}: #{field['content']}"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.JsonObject;
import com.google.gson.JsonParser;
import com.google.gson.JsonArray;

public class GetLeadClient {
    private static final OkHttpClient client = new OkHttpClient();
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/leads/get-lead";
        
        // Get lead by ID
        JsonObject leadData = new JsonObject();
        leadData.addProperty("lead_id", "a1b2c3d4-e5f6-7890-abcd-ef1234567890");
        
        RequestBody body = RequestBody.create(leadData.toString(), JSON);
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .post(body)
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject data = JsonParser.parseString(responseBody).getAsJsonObject();
                JsonObject leadInfo = data.getAsJsonObject("data");
                
                System.out.println("Lead Name: " + leadInfo.get("name").getAsString());
                System.out.println("Phone: " + leadInfo.get("phone").getAsString());
                System.out.println("Email: " + leadInfo.get("email").getAsString());
                
                JsonArray customFields = leadInfo.getAsJsonArray("customFields");
                System.out.println("Custom Fields: " + customFields.size());
                
                // Print custom fields
                for (int i = 0; i < customFields.size(); i++) {
                    JsonObject field = customFields.get(i).getAsJsonObject();
                    System.out.println("  " + field.get("label").getAsString() + 
                                     ": " + field.get("content").getAsString());
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'leads-get-lead-phone': {
    id: 'leads-get-lead-phone',
    title: 'Get leads by phone',
    category: 'Leads',
    categoryId: 'leads',
    method: 'POST',
    path: '/leads/get-lead/phone',
    description: 'Retrieves all leads with a specific phone number in your organization. Returns an array of leads with complete information including custom fields. Empty fields are displayed as "N/A" for consistency.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json'
      }
    ],
    parameters: [
      {
        name: 'phone',
        type: 'string',
        required: true,
        description: 'Phone number to search for',
        example: '0501234567'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'OK - Leads retrieved successfully',
        example: JSON.stringify({
          success: true,
          count: 2,
          data: [
            {
              lead_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
              name: 'John Smith',
              phone: '0501234567',
              email: 'john@example.com',
              status: 'new',
              source: 'website',
              notes: 'N/A',
              assigned_to: 'N/A',
              company: 'Smith Industries Ltd',
              address: 'N/A',
              created_at: '2024-01-01T12:00:00Z',
              updated_at: '2024-01-01T12:00:00Z',
              customFields: [
                {
                  key: '1750878964687_1750878975688',
                  content: 'Tech Business',
                  label: 'Business Details - Business Name',
                  type: 'text',
                  area: 'Business Details'
                },
                {
                  key: '1750878964687_1750878993373',
                  content: 'N/A',
                  label: 'Business Details - Business Owner Phone',
                  type: 'phone',
                  area: 'Business Details'
                }
              ]
            },
            {
              lead_id: 'b2c3d4e5-f6g7-8901-bcde-f23456789012',
              name: 'David Johnson',
              phone: '0501234567',
              email: 'N/A',
              status: 'contacted',
              source: 'phone',
              notes: 'Called multiple times',
              assigned_to: 'user-123',
              company: 'N/A',
              address: 'Tel Aviv',
              created_at: '2024-01-02T10:30:00Z',
              updated_at: '2024-01-02T15:45:00Z',
              customFields: [
                {
                  key: '1750878964687_1750878975688',
                  content: 'N/A',
                  label: 'Business Details - Business Name',
                  type: 'text',
                  area: 'Business Details'
                },
                {
                  key: '1750878964687_1750878993373',
                  content: '0321234567',
                  label: 'Business Details - Business Owner Phone',
                  type: 'phone',
                  area: 'Business Details'
                }
              ]
            }
          ]
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing phone',
        example: JSON.stringify({
          error: 'Missing required field: phone'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - No leads found',
        example: JSON.stringify({
          error: 'No leads found with this phone number in your organization'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/leads/get-lead/phone';

const headers = {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
};

// Get leads by phone number
const phoneData = {
    phone: '0501234567'
};

fetch(url, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(phoneData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        const leads = data.data;
        console.log(\`Found \${data.count} leads with phone \${phoneData.phone}\`);
        
        leads.forEach((lead, index) => {
            console.log(\`\\nLead \${index + 1}:\`);
            console.log(\`  ID: \${lead.lead_id}\`);
            console.log(\`  Name: \${lead.name}\`);
            console.log(\`  Email: \${lead.email}\`);
            console.log(\`  Status: \${lead.status}\`);
            console.log(\`  Custom Fields: \${lead.customFields.length}\`);
            
            // Print custom fields with values
            lead.customFields.forEach(field => {
                if (field.content !== 'N/A') {
                    console.log(\`    \${field.label}: \${field.content}\`);
                }
            });
        });
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests
import json

api_key = "your_api_key_here"
url = "https://dev.clickai.live/leads/get-lead/phone"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Get leads by phone number
phone_data = {
    "phone": "0501234567"
}

response = requests.post(url, headers=headers, json=phone_data)

if response.status_code == 200:
    data = response.json()
    leads = data['data']
    print(f"Found {data['count']} leads with phone {phone_data['phone']}")
    
    for i, lead in enumerate(leads, 1):
        print(f"\\nLead {i}:")
        print(f"  ID: {lead['lead_id']}")
        print(f"  Name: {lead['name']}")
        print(f"  Email: {lead['email']}")
        print(f"  Status: {lead['status']}")
        print(f"  Custom Fields: {len(lead['customFields'])}")
        
        # Print custom fields
        for field in lead['customFields']:
            if field['content'] != 'N/A':
                print(f"    {field['label']}: {field['content']}")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      curl: `curl -X POST "https://dev.clickai.live/leads/get-lead/phone" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone": "0501234567"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/leads/get-lead/phone';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

// Get leads by phone number
$phone_data = [
    'phone' => '0501234567'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($phone_data));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    $leads = $data['data'];
    
    echo "Found " . $data['count'] . " leads with phone " . $phone_data['phone'] . "\\n\\n";
    
    foreach ($leads as $index => $lead) {
        echo "Lead " . ($index + 1) . ":\\n";
        echo "  ID: " . $lead['lead_id'] . "\\n";
        echo "  Name: " . $lead['name'] . "\\n";
        echo "  Email: " . $lead['email'] . "\\n";
        echo "  Status: " . $lead['status'] . "\\n";
        echo "  Custom Fields: " . count($lead['customFields']) . "\\n";
        
        // Print custom fields with values
        foreach ($lead['customFields'] as $field) {
            if ($field['content'] !== 'N/A') {
                echo "    " . $field['label'] . ": " . $field['content'] . "\\n";
            }
        }
        echo "\\n";
    }
} else {
    echo "Error $http_code: $response";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/leads/get-lead/phone";
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            // Get leads by phone number
            var phoneData = new
            {
                phone = "0501234567"
            };
            
            string jsonData = JsonConvert.SerializeObject(phoneData);
            var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
            
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic data = JsonConvert.DeserializeObject(responseContent);
                var leads = data.data;
                
                Console.WriteLine($"Found {data.count} leads with phone {phoneData.phone}");
                
                for (int i = 0; i < leads.Count; i++)
                {
                    var lead = leads[i];
                    Console.WriteLine($"\\nLead {i + 1}:");
                    Console.WriteLine($"  ID: {lead.lead_id}");
                    Console.WriteLine($"  Name: {lead.name}");
                    Console.WriteLine($"  Email: {lead.email}");
                    Console.WriteLine($"  Status: {lead.status}");
                    Console.WriteLine($"  Custom Fields: {lead.customFields.Count}");
                    
                    // Print custom fields with values
                    foreach (var field in lead.customFields)
                    {
                        if (field.content.ToString() != "N/A")
                        {
                            Console.WriteLine($"    {field.label}: {field.content}");
                        }
                    }
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/leads/get-lead/phone')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

# Get leads by phone number
phone_data = {
  phone: '0501234567'
}

request.body = phone_data.to_json
response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  leads = data['data']
  
  puts "Found #{data['count']} leads with phone #{phone_data[:phone]}"
  
  leads.each_with_index do |lead, index|
    puts "\\nLead #{index + 1}:"
    puts "  ID: #{lead['lead_id']}"
    puts "  Name: #{lead['name']}"
    puts "  Email: #{lead['email']}"
    puts "  Status: #{lead['status']}"
    puts "  Custom Fields: #{lead['customFields'].length}"
    
    # Print custom fields with values
    lead['customFields'].each do |field|
      if field['content'] != 'N/A'
        puts "    #{field['label']}: #{field['content']}"
      end
    end
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.JsonObject;
import com.google.gson.JsonParser;
import com.google.gson.JsonArray;

public class GetLeadByPhoneClient {
    private static final OkHttpClient client = new OkHttpClient();
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/leads/get-lead/phone";
        
        // Get leads by phone number
        JsonObject phoneData = new JsonObject();
        phoneData.addProperty("phone", "0501234567");
        
        RequestBody body = RequestBody.create(phoneData.toString(), JSON);
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .post(body)
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject data = JsonParser.parseString(responseBody).getAsJsonObject();
                JsonArray leads = data.getAsJsonArray("data");
                int count = data.get("count").getAsInt();
                
                System.out.println("Found " + count + " leads with phone " + 
                                 phoneData.get("phone").getAsString());
                
                for (int i = 0; i < leads.size(); i++) {
                    JsonObject lead = leads.get(i).getAsJsonObject();
                    
                    System.out.println("\\nLead " + (i + 1) + ":");
                    System.out.println("  ID: " + lead.get("lead_id").getAsString());
                    System.out.println("  Name: " + lead.get("name").getAsString());
                    System.out.println("  Email: " + lead.get("email").getAsString());
                    System.out.println("  Status: " + lead.get("status").getAsString());
                    
                    JsonArray customFields = lead.getAsJsonArray("customFields");
                    System.out.println("  Custom Fields: " + customFields.size());
                    
                    // Print custom fields with values
                    for (int j = 0; j < customFields.size(); j++) {
                        JsonObject field = customFields.get(j).getAsJsonObject();
                        String content = field.get("content").getAsString();
                        if (!content.equals("N/A")) {
                            System.out.println("    " + field.get("label").getAsString() + 
                                             ": " + content);
                        }
                    }
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'leads-add-call-log': {
    id: 'leads-add-call-log',
    title: 'Add call log to lead',
    category: 'Leads',
    categoryId: 'leads',
    method: 'POST',
    path: '/leads/add-call-log',
    description: 'Adds a call log entry for a lead. The endpoint allows recording call details including duration, description, transcription, analysis, and additional data.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json'
      }
    ],
    parameters: [
      {
        name: 'lead_id',
        type: 'string',
        required: true,
        description: 'ID of the lead to add call log to (must be valid UUID belonging to organization)',
        example: '0c6e2bd1-c554-4b56-ab04-a470ca4fc37f'
      },
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Call title/subject (must not be empty)',
        example: 'Sales Call'
      },
      {
        name: 'description',
        type: 'string',
        required: true,
        description: 'Call description (must not be empty)',
        example: 'Call with client regarding price quote'
      },
      {
        name: 'duration',
        type: 'integer',
        required: true,
        description: 'Call duration in minutes (must be valid integer)',
        example: '30'
      },
      {
        name: 'call_date',
        type: 'string',
        required: true,
        description: 'Date and time of the call (Format: YYYY-MM-DD HH:MM)',
        example: '2024-01-15 10:30'
      },
      {
        name: 'conversation_transcription',
        type: 'string',
        required: false,
        description: 'Full conversation transcript (optional text field)',
        example: 'Client expressed interest in the product'
      },
      {
        name: 'conversation_analysis',
        type: 'string',
        required: false,
        description: 'Analysis or summary of conversation (optional text field)',
        example: 'Strong potential client'
      },
      {
        name: 'created_by',
        type: 'string',
        required: false,
        description: 'User ID who created this log entry (must exist in organization if provided)',
        example: 'user-uuid-here'
      },
      {
        name: 'score',
        type: 'integer',
        required: false,
        description: 'Call quality score (1-10) - must be valid integer if provided',
        example: '8'
      },
      {
        name: 'call_record_link',
        type: 'string',
        required: false,
        description: 'URL link to call recording (optional URL)',
        example: 'https://example.com/recording.mp3'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Call log created successfully',
        example: JSON.stringify({
          success: true,
          message: 'Call log added successfully',
          data: {
            id: 'a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6',
            lead_id: '0c6e2bd1-c554-4b56-ab04-a470ca4fc37f',
            title: 'Sales Call',
            description: 'Call with client regarding price quote',
            duration: 30,
            call_date: '2024-01-15 10:30:00',
            created_at: '2024-01-15T10:35:42.123456+00:00',
            conversation_transcription: 'Client expressed interest in the product',
            conversation_analysis: 'Strong potential client',
            created_by: 'user-uuid-here',
            score: 8,
            call_record: 'https://example.com/recording.mp3'
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad request - Missing required field',
        example: JSON.stringify({
          error: "Field 'title' is required"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad request - Invalid duration',
        example: JSON.stringify({
          error: 'Duration must be a valid integer'
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad request - Invalid date format',
        example: JSON.stringify({
          error: 'Invalid call_date format. Expected format: YYYY-MM-DD HH:MM'
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad request - Invalid user',
        example: JSON.stringify({
          error: 'Created by user does not exist or does not belong to your organization'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key'
        }, null, 2)
      },
      {
        code: '404',
        description: 'Lead not found',
        example: JSON.stringify({
          error: 'Lead not found or does not belong to your organization'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal server error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `// Example 1: Basic call log with required fields
const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/leads/add-call-log';

const callData = {
    lead_id: '0c6e2bd1-c554-4b56-ab04-a470ca4fc37f',
    title: 'Sales Call',
    description: 'Call with client regarding price quote',
    duration: 30, // 30 minutes
    call_date: '2024-01-15 10:30'
};

const headers = {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
};

async function addCallLog() {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(callData)
        });
        
        const data = await response.json();
        
        if (response.status === 201) {
            console.log('Call log created successfully!');
            console.log('ID:', data.data.id);
            console.log('Duration:', data.data.duration, 'minutes');
            console.log('Score:', data.data.score);
        } else if (response.status === 400) {
            console.error('Validation error:', data.error);
        } else if (response.status === 404) {
            console.error('Lead not found or doesn\\'t belong to your organization');
        } else {
            console.error('Error:', data.error);
        }
    } catch (error) {
        console.error('Request failed:', error);
    }
}

addCallLog();

// Example 2: Complete call log with all fields
const completeCallData = {
    lead_id: '0c6e2bd1-c554-4b56-ab04-a470ca4fc37f',
    title: 'Follow-up Call',
    description: 'Follow-up regarding previous order',
    duration: 15,
    call_date: '2024-01-16 14:30',
    conversation_transcription: 'Client asked about order status',
    conversation_analysis: 'Satisfied client, interested in additional orders',
    score: 9,
    call_record_link: 'https://example.com/recording2.mp3'
};`,
      python: `# Example 1: Basic call log
import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/leads/add-call-log"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

call_data = {
    "lead_id": "0c6e2bd1-c554-4b56-ab04-a470ca4fc37f",
    "title": "Sales Call",
    "description": "Call with client regarding price quote",
    "duration": 30,  # 30 minutes
    "call_date": "2024-01-15 10:30"
}

response = requests.post(url, json=call_data, headers=headers)

if response.status_code == 201:
    result = response.json()
    print(f"Call log created successfully! ID: {result['data']['id']}")
    print(f"Duration: {result['data']['duration']} minutes")
    print(f"Score: {result['data']['score']}")
elif response.status_code == 400:
    error = response.json()
    print(f"Validation error: {error['error']}")
elif response.status_code == 404:
    print("Lead not found or doesn't belong to your organization")
else:
    error = response.json()
    print(f"Error: {error['error']}")

# Example 2: Complete call log with all fields
complete_call_data = {
    "lead_id": "0c6e2bd1-c554-4b56-ab04-a470ca4fc37f",
    "title": "Follow-up Call",
    "description": "Follow-up regarding previous order",
    "duration": 15,
    "call_date": "2024-01-16 14:30",
    "conversation_transcription": "Client asked about order status",
    "conversation_analysis": "Satisfied client, interested in additional orders",
    "score": 9,
    "call_record_link": "https://example.com/recording2.mp3"
}

response2 = requests.post(url, json=complete_call_data, headers=headers)
if response2.status_code == 201:
    result2 = response2.json()
    print(f"Complete call log created! ID: {result2['data']['id']}")`,
      curl: `# Example 1: Basic call log
curl -X POST "https://dev.clickai.live/leads/add-call-log" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "lead_id": "0c6e2bd1-c554-4b56-ab04-a470ca4fc37f",
    "title": "Sales Call",
    "description": "Call with client regarding price quote",
    "duration": 30,
    "call_date": "2024-01-15 10:30"
  }'

# Example 2: Complete call log with all fields
curl -X POST "https://dev.clickai.live/leads/add-call-log" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "lead_id": "0c6e2bd1-c554-4b56-ab04-a470ca4fc37f",
    "title": "Follow-up Call",
    "description": "Follow-up regarding previous order",
    "duration": 15,
    "call_date": "2024-01-16 14:30",
    "conversation_transcription": "Client asked about order status",
    "conversation_analysis": "Satisfied client, interested in additional orders",
    "score": 9,
    "call_record_link": "https://example.com/recording2.mp3"
  }'`,
      powershell: `# Example 1: Basic call log
$apiKey = "your_api_key_here"
$url = "https://dev.clickai.live/leads/add-call-log"

$headers = @{
    "Authorization" = "Bearer $apiKey"
    "Content-Type" = "application/json"
}

$callData = @{
    lead_id = "0c6e2bd1-c554-4b56-ab04-a470ca4fc37f"
    title = "Sales Call"
    description = "Call with client regarding price quote"
    duration = 30
    call_date = "2024-01-15 10:30"
} | ConvertTo-Json

try {
    $response = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $callData
    Write-Host "Call log created successfully!"
    Write-Host "ID: $($response.data.id)"
    Write-Host "Duration: $($response.data.duration) minutes"
    Write-Host "Score: $($response.data.score)"
} catch {
    $statusCode = $_.Exception.Response.StatusCode.value__
    switch ($statusCode) {
        400 { Write-Host "Validation error: $($_.Exception.Message)" }
        404 { Write-Host "Lead not found or doesn't belong to your organization" }
        default { Write-Host "Error: $($_.Exception.Message)" }
    }
}

# Example 2: Complete call log
$completeCallData = @{
    lead_id = "0c6e2bd1-c554-4b56-ab04-a470ca4fc37f"
    title = "Follow-up Call"
    description = "Follow-up regarding previous order"
    duration = 15
    call_date = "2024-01-16 14:30"
    conversation_transcription = "Client asked about order status"
    conversation_analysis = "Satisfied client, interested in additional orders"
    score = 9
    call_record_link = "https://example.com/recording2.mp3"
} | ConvertTo-Json`,
      php: `<?php
// Example 1: Basic call log
function addCallLog($apiKey, $leadId, $callData) {
    $url = 'https://dev.clickai.live/leads/add-call-log';
    
    $fullCallData = array_merge([
        'lead_id' => $leadId
    ], $callData);
    
    $headers = [
        'Authorization: Bearer ' . $apiKey,
        'Content-Type: application/json'
    ];
    
    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, $url);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($fullCallData));
    curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    
    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    curl_close($ch);
    
    if ($httpCode == 201) {
        $data = json_decode($response, true);
        echo "Call log created successfully!\\n";
        echo "ID: " . $data['data']['id'] . "\\n";
        echo "Duration: " . $data['data']['duration'] . " minutes\\n";
        echo "Score: " . $data['data']['score'] . "\\n";
        return $data;
    } elseif ($httpCode == 400) {
        $error = json_decode($response, true);
        echo "Validation error: " . $error['error'] . "\\n";
    } elseif ($httpCode == 404) {
        echo "Lead not found or doesn't belong to your organization\\n";
    } else {
        $error = json_decode($response, true);
        echo "Error $httpCode: " . $error['error'] . "\\n";
    }
    
    return null;
}

// Example 1: Basic fields
$apiKey = 'your_api_key_here';
$leadId = '0c6e2bd1-c554-4b56-ab04-a470ca4fc37f';
$callData = [
    'title' => 'Sales Call',
    'description' => 'Call with client regarding price quote',
    'duration' => 30,
    'call_date' => '2024-01-15 10:30'
];

addCallLog($apiKey, $leadId, $callData);

// Example 2: Complete call log with all fields
$completeCallData = [
    'title' => 'Follow-up Call',
    'description' => 'Follow-up regarding previous order',
    'duration' => 15,
    'call_date' => '2024-01-16 14:30',
    'conversation_transcription' => 'Client asked about order status',
    'conversation_analysis' => 'Satisfied client, interested in additional orders',
    'score' => 9,
    'call_record_link' => 'https://example.com/recording2.mp3'
];

addCallLog($apiKey, $leadId, $completeCallData);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class CallLogService
{
    private readonly HttpClient httpClient;
    private readonly string apiKey;
    
    public CallLogService(string apiKey)
    {
        this.apiKey = apiKey;
        this.httpClient = new HttpClient();
    }
    
    public async Task<bool> AddCallLogAsync(string leadId, CallLogData callData)
    {
        string url = "https://dev.clickai.live/leads/add-call-log";
        
        var requestData = new
        {
            lead_id = leadId,
            title = callData.Title,
            description = callData.Description,
            duration = callData.Duration,
            call_date = callData.CallDate,
            conversation_transcription = callData.ConversationTranscription,
            conversation_analysis = callData.ConversationAnalysis,
            score = callData.Score,
            call_record_link = callData.CallRecordLink
        };
        
        string json = JsonConvert.SerializeObject(requestData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        httpClient.DefaultRequestHeaders.Clear();
        httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine("Call log created successfully!");
                Console.WriteLine($"ID: {result.data.id}");
                Console.WriteLine($"Duration: {result.data.duration} minutes");
                Console.WriteLine($"Score: {result.data.score}");
                return true;
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.BadRequest)
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Validation error: {error.error}");
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.NotFound)
            {
                Console.WriteLine("Lead not found or doesn't belong to your organization");
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
            }
            
            return false;
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return false;
        }
    }
}

public class CallLogData
{
    public string Title { get; set; }
    public string Description { get; set; }
    public int Duration { get; set; }
    public string CallDate { get; set; }
    public string ConversationTranscription { get; set; }
    public string ConversationAnalysis { get; set; }
    public int? Score { get; set; }
    public string CallRecordLink { get; set; }
}

// Example 1: Basic call log
var callLogService = new CallLogService("your_api_key_here");
var callData = new CallLogData
{
    Title = "Sales Call",
    Description = "Call with client regarding price quote",
    Duration = 30,
    CallDate = "2024-01-15 10:30"
};

bool success = await callLogService.AddCallLogAsync("0c6e2bd1-c554-4b56-ab04-a470ca4fc37f", callData);

// Example 2: Complete call log
var completeCallData = new CallLogData
{
    Title = "Follow-up Call",
    Description = "Follow-up regarding previous order",
    Duration = 15,
    CallDate = "2024-01-16 14:30",
    ConversationTranscription = "Client asked about order status",
    ConversationAnalysis = "Satisfied client, interested in additional orders",
    Score = 9,
    CallRecordLink = "https://example.com/recording2.mp3"
};`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

class CallLogService
  def initialize(api_key)
    @api_key = api_key
    @base_url = 'https://dev.clickai.live'
  end
  
  def add_call_log(lead_id, call_data)
    url = URI("#{@base_url}/leads/add-call-log")
    
    http = Net::HTTP.new(url.host, url.port)
    
    request = Net::HTTP::Post.new(url)
    request['Authorization'] = "Bearer #{@api_key}"
    request['Content-Type'] = 'application/json'
    
    full_data = call_data.merge(lead_id: lead_id)
    request.body = full_data.to_json
    
    begin
      response = http.request(request)
      
      case response.code
      when '201'
        result = JSON.parse(response.body)
        puts "Call log created successfully!"
        puts "ID: #{result['data']['id']}"
        puts "Duration: #{result['data']['duration']} minutes"
        puts "Score: #{result['data']['score']}"
        true
      when '400'
        error = JSON.parse(response.body)
        puts "Validation error: #{error['error']}"
        false
      when '404'
        puts "Lead not found or doesn't belong to your organization"
        false
      else
        error = JSON.parse(response.body)
        puts "Error: #{error['error']}"
        false
      end
    rescue => e
      puts "Exception: #{e.message}"
      false
    end
  end
end

# Example 1: Basic call log
call_service = CallLogService.new('your_api_key_here')
call_data = {
  title: 'Sales Call',
  description: 'Call with client regarding price quote',
  duration: 30,
  call_date: '2024-01-15 10:30'
}

success = call_service.add_call_log('0c6e2bd1-c554-4b56-ab04-a470ca4fc37f', call_data)

# Example 2: Complete call log with all fields
complete_call_data = {
  title: 'Follow-up Call',
  description: 'Follow-up regarding previous order',
  duration: 15,
  call_date: '2024-01-16 14:30',
  conversation_transcription: 'Client asked about order status',
  conversation_analysis: 'Satisfied client, interested in additional orders',
  score: 9,
  call_record_link: 'https://example.com/recording2.mp3'
}

success2 = call_service.add_call_log('0c6e2bd1-c554-4b56-ab04-a470ca4fc37f', complete_call_data)`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.time.Duration;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;

public class CallLogService {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    private final String apiKey;
    
    public CallLogService(String apiKey) {
        this.apiKey = apiKey;
        this.httpClient = HttpClient.newBuilder()
            .connectTimeout(Duration.ofSeconds(30))
            .build();
        this.objectMapper = new ObjectMapper();
    }
    
    public boolean addCallLog(String leadId, Map<String, Object> callData) {
        try {
            String url = "https://dev.clickai.live/leads/add-call-log";
            
            Map<String, Object> requestData = new HashMap<>(callData);
            requestData.put("lead_id", leadId);
            
            String json = objectMapper.writeValueAsString(requestData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .timeout(Duration.ofSeconds(30))
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 201) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                Map<String, Object> data = (Map<String, Object>) result.get("data");
                
                System.out.println("Call log created successfully!");
                System.out.println("ID: " + data.get("id"));
                System.out.println("Duration: " + data.get("duration") + " minutes");
                System.out.println("Score: " + data.get("score"));
                return true;
            } else if (response.statusCode() == 400) {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Validation error: " + error.get("error"));
            } else if (response.statusCode() == 404) {
                System.out.println("Lead not found or doesn't belong to your organization");
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
            
            return false;
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
            return false;
        }
    }
    
    public static void main(String[] args) {
        CallLogService service = new CallLogService("your_api_key_here");
        
        // Example 1: Basic call log
        Map<String, Object> callData = new HashMap<>();
        callData.put("title", "Sales Call");
        callData.put("description", "Call with client regarding price quote");
        callData.put("duration", 30);
        callData.put("call_date", "2024-01-15 10:30");
        
        boolean success = service.addCallLog("0c6e2bd1-c554-4b56-ab04-a470ca4fc37f", callData);
        
        // Example 2: Complete call log
        Map<String, Object> completeCallData = new HashMap<>();
        completeCallData.put("title", "Follow-up Call");
        completeCallData.put("description", "Follow-up regarding previous order");
        completeCallData.put("duration", 15);
        completeCallData.put("call_date", "2024-01-16 14:30");
        completeCallData.put("conversation_transcription", "Client asked about order status");
        completeCallData.put("conversation_analysis", "Satisfied client, interested in additional orders");
        completeCallData.put("score", 9);
        completeCallData.put("call_record_link", "https://example.com/recording2.mp3");
        
        boolean success2 = service.addCallLog("0c6e2bd1-c554-4b56-ab04-a470ca4fc37f", completeCallData);
        System.out.println("Call log creation " + (success2 ? "successful" : "failed"));
    }
}`
    }
  },

  // Deals endpoints
  'deals-custom-fields': {
    id: 'deals-custom-fields',
    title: 'Get custom fields for deals',
    category: 'Deals',
    categoryId: 'deals',
    method: 'GET',
    path: '/deals/custom-fields',
    description: 'Retrieves all custom fields configured for your organization\'s deals. These fields define additional data points that can be collected for each deal beyond the standard fields.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: false,
        description: 'Recommended: application/json'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Successfully retrieved custom fields',
        example: JSON.stringify({
          success: true,
          data: [
            {
              key: 'deal_priority_1749674482286',
              area: 'Deal Details',
              type: 'select',
              label: 'Deal Priority',
              areaId: 'deal_details',
              required: false
            },
            {
              key: 'deal_source_1749674482287',
              area: 'Source',
              type: 'text',
              label: 'Deal Source',
              areaId: 'source',
              required: true
            }
          ]
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal server error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/deals/custom-fields';

async function getCustomFields() {
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': \`Bearer \${apiKey}\`,
                'Content-Type': 'application/json'
            }
        });
        
        const data = await response.json();
        
        if (response.ok) {
            console.log('Custom fields retrieved successfully:');
            data.data.forEach(field => {
                console.log(\`- \${field.label} (\${field.type}) - Required: \${field.required}\`);
            });
        } else {
            console.error('Error:', data.error);
        }
    } catch (error) {
        console.error('Request failed:', error);
    }
}

getCustomFields();`,
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/deals/custom-fields'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

try:
    response = requests.get(url, headers=headers)
    
    if response.status_code == 200:
        data = response.json()
        print("Custom fields retrieved successfully:")
        for field in data['data']:
            print(f"- {field['label']} ({field['type']}) - Required: {field['required']}")
    else:
        error = response.json()
        print(f"Error {response.status_code}: {error['error']}")
        
except requests.exceptions.RequestException as e:
    print(f"Request failed: {e}")`,
      curl: `# Get custom fields for deals
curl -X GET "https://dev.clickai.live/deals/custom-fields" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"`,
      powershell: `$apiKey = "your_api_key_here"
$url = "https://dev.clickai.live/deals/custom-fields"

$headers = @{
    "Authorization" = "Bearer $apiKey"
    "Content-Type" = "application/json"
}

try {
    $response = Invoke-RestMethod -Uri $url -Method Get -Headers $headers
    Write-Host "Custom fields retrieved successfully:"
    foreach ($field in $response.data) {
        Write-Host "- $($field.label) ($($field.type)) - Required: $($field.required)"
    }
} catch {
    $errorDetails = $_.ErrorDetails.Message | ConvertFrom-Json
    Write-Error "Error: $($errorDetails.error)"
}`,
      php: `<?php
function getCustomFields($apiKey) {
    $url = 'https://dev.clickai.live/deals/custom-fields';
    
    $headers = [
        'Authorization: Bearer ' . $apiKey,
        'Content-Type: application/json'
    ];
    
    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, $url);
    curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    
    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    curl_close($ch);
    
    if ($httpCode == 200) {
        $data = json_decode($response, true);
        echo "Custom fields retrieved successfully:\\n";
        foreach ($data['data'] as $field) {
            echo "- {$field['label']} ({$field['type']}) - Required: " . 
                 ($field['required'] ? 'Yes' : 'No') . "\\n";
        }
    } else {
        $error = json_decode($response, true);
        echo "Error $httpCode: {$error['error']}\\n";
    }
}

$apiKey = 'your_api_key_here';
getCustomFields($apiKey);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class CustomFieldsService
{
    private readonly HttpClient httpClient;
    private readonly string apiKey;
    
    public CustomFieldsService(string apiKey)
    {
        this.apiKey = apiKey;
        this.httpClient = new HttpClient();
    }
    
    public async Task GetCustomFieldsAsync()
    {
        string url = "https://dev.clickai.live/deals/custom-fields";
        
        httpClient.DefaultRequestHeaders.Clear();
        httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        httpClient.DefaultRequestHeaders.Add("Content-Type", "application/json");
        
        try
        {
            var response = await httpClient.GetAsync(url);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine("Custom fields retrieved successfully:");
                foreach (var field in result.data)
                {
                    Console.WriteLine($"- {field.label} ({field.type}) - Required: {field.required}");
                }
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}

// Usage
var service = new CustomFieldsService("your_api_key_here");
await service.GetCustomFieldsAsync();`,
      ruby: `require 'net/http'
require 'json'

class CustomFieldsService
  def initialize(api_key)
    @api_key = api_key
    @base_url = 'https://dev.clickai.live'
  end
  
  def get_custom_fields
    url = URI("#{@base_url}/deals/custom-fields")
    
    http = Net::HTTP.new(url.host, url.port)
    request = Net::HTTP::Get.new(url)
    request['Authorization'] = "Bearer #{@api_key}"
    request['Content-Type'] = 'application/json'
    
    begin
      response = http.request(request)
      
      if response.code == '200'
        result = JSON.parse(response.body)
        puts "Custom fields retrieved successfully:"
        result['data'].each do |field|
          puts "- #{field['label']} (#{field['type']}) - Required: #{field['required']}"
        end
      else
        error = JSON.parse(response.body)
        puts "Error #{response.code}: #{error['error']}"
      end
    rescue => e
      puts "Request failed: #{e.message}"
    end
  end
end

# Usage
service = CustomFieldsService.new('your_api_key_here')
service.get_custom_fields`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.time.Duration;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.List;

public class CustomFieldsService {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    private final String apiKey;
    
    public CustomFieldsService(String apiKey) {
        this.apiKey = apiKey;
        this.httpClient = HttpClient.newBuilder()
            .connectTimeout(Duration.ofSeconds(30))
            .build();
        this.objectMapper = new ObjectMapper();
    }
    
    public void getCustomFields() {
        try {
            String url = "https://dev.clickai.live/deals/custom-fields";
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .timeout(Duration.ofSeconds(30))
                .GET()
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 200) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                List<Map<String, Object>> data = (List<Map<String, Object>>) result.get("data");
                
                System.out.println("Custom fields retrieved successfully:");
                for (Map<String, Object> field : data) {
                    System.out.println("- " + field.get("label") + " (" + 
                        field.get("type") + ") - Required: " + field.get("required"));
                }
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
        }
    }
    
    public static void main(String[] args) {
        CustomFieldsService service = new CustomFieldsService("your_api_key_here");
        service.getCustomFields();
    }
}`
    }
  },

  'deals-new-deal': {
    id: 'deals-new-deal',
    title: 'Create new deal',
    category: 'Deals',
    categoryId: 'deals',
    method: 'POST',
    path: '/deals/new-deal',
    description: 'Creates a new deal in your organization with optional custom fields support.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json'
      }
    ],
    parameters: [
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Deal title/name',
        example: 'New Deal with Important Client'
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Deal description',
        example: 'Fourth quarter deal'
      },
      {
        name: 'amount',
        type: 'number',
        required: false,
        description: 'Deal monetary value',
        example: '75000'
      },
      {
        name: 'name',
        type: 'string',
        required: false,
        description: 'Contact person name',
        example: 'David Johnson'
      },
      {
        name: 'phone',
        type: 'string',
        required: false,
        description: 'Contact phone number',
        example: '0529876543'
      },
      {
        name: 'customer_id',
        type: 'string',
        required: false,
        description: 'Customer UUID from clients table',
        example: '091cd08c-5dc3-456d-9d43-8918a549cfea'
      },
      {
        name: 'products',
        type: 'array',
        required: false,
        description: 'Array of product UUIDs',
        example: '["03727424-3596-418b-865c-d7fee7810426", "5d5f3f6b-83a2-4b2c-adfb-b93606480325"]'
      },
      {
        name: 'status',
        type: 'string',
        required: false,
        description: 'Deal status (if "closed", closed_by is required)',
        example: 'new'
      },
      {
        name: 'created_by',
        type: 'string',
        required: false,
        description: 'User UUID who creates the deal (must exist in organization)',
        example: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2'
      },
      {
        name: 'closed_by',
        type: 'string',
        required: false,
        description: 'User UUID who closed the deal (requires status="closed")',
        example: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2'
      },
      {
        name: 'hasCustomFields',
        type: 'boolean',
        required: false,
        description: 'Whether custom fields are included',
        example: 'true'
      },
      {
        name: 'customFields',
        type: 'object',
        required: false,
        description: 'Key-value pairs of custom field data',
        example: '{"asd_1749674482286": "Additional deal description"}'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Deal created successfully',
        example: JSON.stringify({
          success: true,
          message: 'Deal created successfully',
          deal_id: '550e8400-e29b-41d4-a716-446655440000'
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad request - Missing required field',
        example: JSON.stringify({
          error: 'Missing required field: title'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '422',
        description: 'Unprocessable Entity - Business rule violations',
        example: JSON.stringify({
          error: 'closed_by is required when status is "closed"'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal server error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `// Example 1: Create deal with all fields
const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/deals/new-deal';

const dealData = {
    title: 'New Deal with Important Client',
    description: 'Fourth quarter deal',
    amount: 75000,
    name: 'David Johnson',
    phone: '0529876543',
    customer_id: '091cd08c-5dc3-456d-9d43-8918a549cfea',
    products: ['03727424-3596-418b-865c-d7fee7810426', '5d5f3f6b-83a2-4b2c-adfb-b93606480325'],
    created_by: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
    hasCustomFields: true,
    customFields: {
        'asd_1749674482286': 'Additional deal description'
    }
};

async function createDeal() {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': \`Bearer \${apiKey}\`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(dealData)
        });
        
        const data = await response.json();
        
        if (response.status === 201) {
            console.log('Deal created successfully!');
            console.log('Deal ID:', data.deal_id);
        } else if (response.status === 400) {
            console.error('Validation error:', data.error);
        } else if (response.status === 422) {
            console.error('Business rule violation:', data.error);
        } else {
            console.error('Error:', data.error);
        }
    } catch (error) {
        console.error('Request failed:', error);
    }
}

createDeal();

// Example 2: Create closed deal
const closedDealData = {
    title: 'Closed Deal',
    amount: 15000,
    status: 'closed',
    closed_by: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
    hasCustomFields: false
};`,
      python: `# Example 1: Create deal with all fields
import requests

def create_deal(api_key, deal_data):
    url = 'https://dev.clickai.live/deals/new-deal'
    
    headers = {
        'Authorization': f'Bearer {api_key}',
        'Content-Type': 'application/json'
    }
    
    try:
        response = requests.post(url, json=deal_data, headers=headers)
        
        if response.status_code == 201:
            result = response.json()
            print('Deal created successfully!')
            print(f'Deal ID: {result["deal_id"]}')
            return result
        elif response.status_code == 400:
            error = response.json()
            print(f'Validation error: {error["error"]}')
        elif response.status_code == 422:
            error = response.json()
            print(f'Business rule violation: {error["error"]}')
        else:
            error = response.json()
            print(f'Error {response.status_code}: {error["error"]}')
            
    except requests.exceptions.RequestException as e:
        print(f'Request failed: {e}')
    
    return None

# Usage
api_key = 'your_api_key_here'

# Example 1: Deal with all fields
deal_data = {
    'title': 'New Deal with Important Client',
    'description': 'Fourth quarter deal',
    'amount': 75000,
    'name': 'David Johnson',
    'phone': '0529876543',
    'customer_id': '091cd08c-5dc3-456d-9d43-8918a549cfea',
    'products': ['03727424-3596-418b-865c-d7fee7810426', '5d5f3f6b-83a2-4b2c-adfb-b93606480325'],
    'created_by': 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
    'hasCustomFields': True,
    'customFields': {
        'asd_1749674482286': 'Additional deal description'
    }
}

create_deal(api_key, deal_data)

# Example 2: Closed deal
closed_deal_data = {
    'title': 'Closed Deal',
    'amount': 15000,
    'status': 'closed',
    'closed_by': 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
    'hasCustomFields': False
}

create_deal(api_key, closed_deal_data)`,
      curl: `# Example 1: Create deal with all fields
curl -X POST "https://dev.clickai.live/deals/new-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "New Deal with Important Client",
    "description": "Fourth quarter deal",
    "amount": 75000,
    "name": "David Johnson",
    "phone": "0529876543",
    "customer_id": "091cd08c-5dc3-456d-9d43-8918a549cfea",
    "products": ["03727424-3596-418b-865c-d7fee7810426", "5d5f3f6b-83a2-4b2c-adfb-b93606480325"],
    "created_by": "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2",
    "hasCustomFields": true,
    "customFields": {
      "asd_1749674482286": "Additional deal description"
    }
  }'

# Example 2: Create closed deal
curl -X POST "https://dev.clickai.live/deals/new-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Closed Deal",
    "amount": 15000,
    "status": "closed",
    "closed_by": "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2",
    "hasCustomFields": false
  }'

# Example 3: Missing title (will return 400 error)
curl -X POST "https://dev.clickai.live/deals/new-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "description": "Deal without title"
  }'`,
      powershell: `function New-Deal {
    param(
        [string]$ApiKey,
        [hashtable]$DealData
    )
    
    $url = "https://dev.clickai.live/deals/new-deal"
    $headers = @{
        "Authorization" = "Bearer $ApiKey"
        "Content-Type" = "application/json"
    }
    
    $body = $DealData | ConvertTo-Json -Depth 3
    
    try {
        $response = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $body
        Write-Host "Deal created successfully!"
        Write-Host "Deal ID: $($response.deal_id)"
        return $response
    } catch {
        $statusCode = $_.Exception.Response.StatusCode.value__
        $errorDetails = $_.ErrorDetails.Message | ConvertFrom-Json
        
        switch ($statusCode) {
            400 { Write-Error "Validation error: $($errorDetails.error)" }
            422 { Write-Error "Business rule violation: $($errorDetails.error)" }
            default { Write-Error "Error $statusCode: $($errorDetails.error)" }
        }
    }
}

# Example 1: Deal with all fields
$apiKey = "your_api_key_here"
$dealData = @{
    title = "New Deal with Important Client"
    description = "Fourth quarter deal"
    amount = 75000
    name = "David Johnson"
    phone = "0529876543"
    customer_id = "091cd08c-5dc3-456d-9d43-8918a549cfea"
    products = @("03727424-3596-418b-865c-d7fee7810426", "5d5f3f6b-83a2-4b2c-adfb-b93606480325")
    created_by = "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2"
    hasCustomFields = $true
    customFields = @{
        "asd_1749674482286" = "Additional deal description"
    }
}

New-Deal -ApiKey $apiKey -DealData $dealData

# Example 2: Closed deal
$closedDealData = @{
    title = "Closed Deal"
    amount = 15000
    status = "closed"
    closed_by = "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2"
    hasCustomFields = $false
}

New-Deal -ApiKey $apiKey -DealData $closedDealData`,
      php: `<?php
function createDeal($apiKey, $dealData) {
    $url = 'https://dev.clickai.live/deals/new-deal';
    
    $headers = [
        'Authorization: Bearer ' . $apiKey,
        'Content-Type: application/json'
    ];
    
    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, $url);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($dealData));
    curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    
    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    curl_close($ch);
    
    if ($httpCode == 201) {
        $result = json_decode($response, true);
        echo "Deal created successfully!\\n";
        echo "Deal ID: {$result['deal_id']}\\n";
        return $result;
    } elseif ($httpCode == 400) {
        $error = json_decode($response, true);
        echo "Validation error: {$error['error']}\\n";
    } elseif ($httpCode == 422) {
        $error = json_decode($response, true);
        echo "Business rule violation: {$error['error']}\\n";
    } else {
        $error = json_decode($response, true);
        echo "Error $httpCode: {$error['error']}\\n";
    }
    
    return null;
}

// Example 1: Deal with all fields
$apiKey = 'your_api_key_here';
$dealData = [
    'title' => 'New Deal with Important Client',
    'description' => 'Fourth quarter deal',
    'amount' => 75000,
    'name' => 'David Johnson',
    'phone' => '0529876543',
    'customer_id' => '091cd08c-5dc3-456d-9d43-8918a549cfea',
    'products' => ['03727424-3596-418b-865c-d7fee7810426', '5d5f3f6b-83a2-4b2c-adfb-b93606480325'],
    'created_by' => 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
    'hasCustomFields' => true,
    'customFields' => [
        'asd_1749674482286' => 'Additional deal description'
    ]
];

createDeal($apiKey, $dealData);

// Example 2: Closed deal
$closedDealData = [
    'title' => 'Closed Deal',
    'amount' => 15000,
    'status' => 'closed',
    'closed_by' => 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
    'hasCustomFields' => false
];

createDeal($apiKey, $closedDealData);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class DealService
{
    private readonly HttpClient httpClient;
    private readonly string apiKey;
    
    public DealService(string apiKey)
    {
        this.apiKey = apiKey;
        this.httpClient = new HttpClient();
    }
    
    public async Task<string> CreateDealAsync(object dealData)
    {
        string url = "https://dev.clickai.live/deals/new-deal";
        
        string json = JsonConvert.SerializeObject(dealData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        httpClient.DefaultRequestHeaders.Clear();
        httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.StatusCode == System.Net.HttpStatusCode.Created)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine("Deal created successfully!");
                Console.WriteLine($"Deal ID: {result.deal_id}");
                return result.deal_id;
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.BadRequest)
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Validation error: {error.error}");
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.UnprocessableEntity)
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Business rule violation: {error.error}");
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
        
        return null;
    }
}

// Usage
var dealService = new DealService("your_api_key_here");

// Example 1: Deal with all fields
var dealData = new
{
    title = "New Deal with Important Client",
    description = "Fourth quarter deal",
    amount = 75000,
    name = "David Johnson",
    phone = "0529876543",
    customer_id = "091cd08c-5dc3-456d-9d43-8918a549cfea",
    products = new[] { "03727424-3596-418b-865c-d7fee7810426", "5d5f3f6b-83a2-4b2c-adfb-b93606480325" },
    created_by = "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2",
    hasCustomFields = true,
    customFields = new { asd_1749674482286 = "Additional deal description" }
};

await dealService.CreateDealAsync(dealData);

// Example 2: Closed deal
var closedDealData = new
{
    title = "Closed Deal",
    amount = 15000,
    status = "closed",
    closed_by = "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2",
    hasCustomFields = false
};

await dealService.CreateDealAsync(closedDealData);`,
      ruby: `require 'net/http'
require 'json'

class DealService
  def initialize(api_key)
    @api_key = api_key
    @base_url = 'https://dev.clickai.live'
  end
  
  def create_deal(deal_data)
    url = URI("#{@base_url}/deals/new-deal")
    
    http = Net::HTTP.new(url.host, url.port)
    request = Net::HTTP::Post.new(url)
    request['Authorization'] = "Bearer #{@api_key}"
    request['Content-Type'] = 'application/json'
    request.body = deal_data.to_json
    
    begin
      response = http.request(request)
      
      case response.code
      when '201'
        result = JSON.parse(response.body)
        puts "Deal created successfully!"
        puts "Deal ID: #{result['deal_id']}"
        result['deal_id']
      when '400'
        error = JSON.parse(response.body)
        puts "Validation error: #{error['error']}"
        nil
      when '422'
        error = JSON.parse(response.body)
        puts "Business rule violation: #{error['error']}"
        nil
      else
        error = JSON.parse(response.body)
        puts "Error #{response.code}: #{error['error']}"
        nil
      end
    rescue => e
      puts "Request failed: #{e.message}"
      nil
    end
  end
end

# Usage
deal_service = DealService.new('your_api_key_here')

# Example 1: Deal with all fields
deal_data = {
  title: 'New Deal with Important Client',
  description: 'Fourth quarter deal',
  amount: 75000,
  name: 'David Johnson',
  phone: '0529876543',
  customer_id: '091cd08c-5dc3-456d-9d43-8918a549cfea',
  products: ['03727424-3596-418b-865c-d7fee7810426', '5d5f3f6b-83a2-4b2c-adfb-b93606480325'],
  created_by: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
  hasCustomFields: true,
  customFields: {
    'asd_1749674482286' => 'Additional deal description'
  }
}

deal_service.create_deal(deal_data)

# Example 2: Closed deal
closed_deal_data = {
  title: 'Closed Deal',
  amount: 15000,
  status: 'closed',
  closed_by: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
  hasCustomFields: false
}

deal_service.create_deal(closed_deal_data)`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.time.Duration;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.List;

public class DealService {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    private final String apiKey;
    
    public DealService(String apiKey) {
        this.apiKey = apiKey;
        this.httpClient = HttpClient.newBuilder()
            .connectTimeout(Duration.ofSeconds(30))
            .build();
        this.objectMapper = new ObjectMapper();
    }
    
    public String createDeal(Map<String, Object> dealData) {
        try {
            String url = "https://dev.clickai.live/deals/new-deal";
            
            String json = objectMapper.writeValueAsString(dealData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .timeout(Duration.ofSeconds(30))
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 201) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Deal created successfully!");
                System.out.println("Deal ID: " + result.get("deal_id"));
                return (String) result.get("deal_id");
            } else if (response.statusCode() == 400) {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Validation error: " + error.get("error"));
            } else if (response.statusCode() == 422) {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Business rule violation: " + error.get("error"));
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
        }
        
        return null;
    }
    
    public static void main(String[] args) {
        DealService service = new DealService("your_api_key_here");
        
        // Example 1: Deal with all fields
        Map<String, Object> dealData = Map.of(
            "title", "New Deal with Important Client",
            "description", "Fourth quarter deal",
            "amount", 75000,
            "name", "David Johnson",
            "phone", "0529876543",
            "customer_id", "091cd08c-5dc3-456d-9d43-8918a549cfea",
            "products", List.of("03727424-3596-418b-865c-d7fee7810426", "5d5f3f6b-83a2-4b2c-adfb-b93606480325"),
            "created_by", "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2",
            "hasCustomFields", true,
            "customFields", Map.of("asd_1749674482286", "Additional deal description")
        );
        
        service.createDeal(dealData);
        
        // Example 2: Closed deal
        Map<String, Object> closedDealData = Map.of(
            "title", "Closed Deal",
            "amount", 15000,
            "status", "closed",
            "closed_by", "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2",
            "hasCustomFields", false
        );
        
        service.createDeal(closedDealData);
    }
}`
    }
  },

  'deals-edit-deal': {
    id: 'deals-edit-deal',
    title: 'Edit existing deal',
    category: 'Deals',
    categoryId: 'deals',
    method: 'POST',
    path: '/deals/edit-deal',
    description: 'Updates an existing deal in your organization. Only the deal_id field is required - all other fields are optional and only the fields sent in the request will be updated.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json'
      }
    ],
    parameters: [
      {
        name: 'deal_id',
        type: 'string',
        required: true,
        description: 'UUID of the deal to update',
        example: '31539430-8cd0-46bf-be0f-c3a3ab4eb460'
      },
      {
        name: 'title',
        type: 'string',
        required: false,
        description: 'Deal title/name',
        example: 'Updated Deal'
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Deal description',
        example: 'New description'
      },
      {
        name: 'amount',
        type: 'number',
        required: false,
        description: 'Deal monetary value',
        example: '45000'
      },
      {
        name: 'status',
        type: 'string',
        required: false,
        description: 'Deal status (if "closed", closed_by is required)',
        example: 'closed'
      },
      {
        name: 'name',
        type: 'string',
        required: false,
        description: 'Contact person name',
        example: 'John Smith Updated'
      },
      {
        name: 'phone',
        type: 'string',
        required: false,
        description: 'Contact phone number',
        example: '0509999999'
      },
      {
        name: 'customer_id',
        type: 'string',
        required: false,
        description: 'Customer UUID from clients table',
        example: '091cd08c-5dc3-456d-9d43-8918a549cfea'
      },
      {
        name: 'products',
        type: 'array',
        required: false,
        description: 'Array of product UUIDs',
        example: '["03727424-3596-418b-865c-d7fee7810426", "5d5f3f6b-83a2-4b2c-adfb-b93606480325"]'
      },
      {
        name: 'created_by',
        type: 'string',
        required: false,
        description: 'User UUID who creates the deal (must exist in organization)',
        example: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2'
      },
      {
        name: 'closed_by',
        type: 'string',
        required: false,
        description: 'User UUID who closed the deal (requires status="closed")',
        example: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2'
      },
      {
        name: 'hasCustomFields',
        type: 'boolean',
        required: false,
        description: 'Whether custom fields are included (false=delete all, true=replace all, omitted=unchanged)',
        example: 'true'
      },
      {
        name: 'customFields',
        type: 'object',
        required: false,
        description: 'Key-value pairs of custom field data (replaces all existing when hasCustomFields=true)',
        example: '{"asd_1749674482286": "New custom field value"}'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Deal updated successfully',
        example: JSON.stringify({
          success: true,
          message: 'Deal updated successfully',
          deal_id: '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
          updated_fields: ['title', 'description', 'amount']
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad request - Missing required field',
        example: JSON.stringify({
          error: 'Missing required field: deal_id'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not found - Deal does not exist',
        example: JSON.stringify({
          error: 'Deal not found or does not belong to your organization'
        }, null, 2)
      },
      {
        code: '422',
        description: 'Unprocessable Entity - Business rule violations',
        example: JSON.stringify({
          error: 'closed_by is required when status is "closed"'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal server error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `// Example 1: Update basic fields
const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/deals/edit-deal';

const updateBasicFields = {
    deal_id: '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    title: 'Updated Deal',
    description: 'New description',
    amount: 45000
};

async function updateDeal(dealData) {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': \`Bearer \${apiKey}\`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(dealData)
        });
        
        const data = await response.json();
        
        if (response.status === 200) {
            console.log('Deal updated successfully!');
            console.log('Updated fields:', data.updated_fields);
            console.log('Deal ID:', data.deal_id);
        } else if (response.status === 400) {
            console.error('Validation error:', data.error);
        } else if (response.status === 404) {
            console.error('Deal not found:', data.error);
        } else if (response.status === 422) {
            console.error('Business rule violation:', data.error);
        } else {
            console.error('Error:', data.error);
        }
    } catch (error) {
        console.error('Request failed:', error);
    }
}

// Update basic fields
updateDeal(updateBasicFields);

// Example 2: Close deal
const closeDeal = {
    deal_id: '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    status: 'closed',
    closed_by: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2'
};

updateDeal(closeDeal);

// Example 3: Update with custom fields
const updateWithCustomFields = {
    deal_id: '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    hasCustomFields: true,
    customFields: {
        'asd_1749674482286': 'New custom field value'
    }
};

updateDeal(updateWithCustomFields);`,
      python: `# Example 1: Update basic fields
import requests

def update_deal(api_key, deal_data):
    url = 'https://dev.clickai.live/deals/edit-deal'
    
    headers = {
        'Authorization': f'Bearer {api_key}',
        'Content-Type': 'application/json'
    }
    
    try:
        response = requests.post(url, json=deal_data, headers=headers)
        
        if response.status_code == 200:
            result = response.json()
            print('Deal updated successfully!')
            print(f'Updated fields: {result["updated_fields"]}')
            print(f'Deal ID: {result["deal_id"]}')
            return result
        elif response.status_code == 400:
            error = response.json()
            print(f'Validation error: {error["error"]}')
        elif response.status_code == 404:
            error = response.json()
            print(f'Deal not found: {error["error"]}')
        elif response.status_code == 422:
            error = response.json()
            print(f'Business rule violation: {error["error"]}')
        else:
            error = response.json()
            print(f'Error {response.status_code}: {error["error"]}')
            
    except requests.exceptions.RequestException as e:
        print(f'Request failed: {e}')
    
    return None

# Usage
api_key = 'your_api_key_here'

# Example 1: Update basic fields
basic_update = {
    'deal_id': '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    'title': 'Updated Deal',
    'description': 'New description',
    'amount': 45000
}

update_deal(api_key, basic_update)

# Example 2: Close deal
close_deal_data = {
    'deal_id': '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    'status': 'closed',
    'closed_by': 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2'
}

update_deal(api_key, close_deal_data)

# Example 3: Update with custom fields
custom_fields_update = {
    'deal_id': '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    'hasCustomFields': True,
    'customFields': {
        'asd_1749674482286': 'New custom field value'
    }
}

update_deal(api_key, custom_fields_update)

# Example 4: Remove all custom fields
remove_custom_fields = {
    'deal_id': '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    'hasCustomFields': False
}

update_deal(api_key, remove_custom_fields)`,
      curl: `# Example 1: Update basic fields
curl -X POST "https://dev.clickai.live/deals/edit-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "deal_id": "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
    "title": "Updated Deal",
    "description": "New description",
    "amount": 45000
  }'

# Example 2: Close deal
curl -X POST "https://dev.clickai.live/deals/edit-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "deal_id": "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
    "status": "closed",
    "closed_by": "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2"
  }'

# Example 3: Update customer and products
curl -X POST "https://dev.clickai.live/deals/edit-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "deal_id": "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
    "customer_id": "091cd08c-5dc3-456d-9d43-8918a549cfea",
    "products": ["03727424-3596-418b-865c-d7fee7810426", "5d5f3f6b-83a2-4b2c-adfb-b93606480325"]
  }'

# Example 4: Update custom fields
curl -X POST "https://dev.clickai.live/deals/edit-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "deal_id": "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
    "hasCustomFields": true,
    "customFields": {
      "asd_1749674482286": "New custom field value"
    }
  }'

# Example 5: Remove all custom fields
curl -X POST "https://dev.clickai.live/deals/edit-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "deal_id": "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
    "hasCustomFields": false
  }'

# Example 6: Missing deal_id (will return 400 error)
curl -X POST "https://dev.clickai.live/deals/edit-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Deal without ID"
  }'`,
      powershell: `function Update-Deal {
    param(
        [string]$ApiKey,
        [hashtable]$DealData
    )
    
    $url = "https://dev.clickai.live/deals/edit-deal"
    $headers = @{
        "Authorization" = "Bearer $ApiKey"
        "Content-Type" = "application/json"
    }
    
    $body = $DealData | ConvertTo-Json -Depth 3
    
    try {
        $response = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $body
        Write-Host "Deal updated successfully!"
        Write-Host "Updated fields: $($response.updated_fields -join ', ')"
        Write-Host "Deal ID: $($response.deal_id)"
        return $response
    } catch {
        $statusCode = $_.Exception.Response.StatusCode.value__
        $errorDetails = $_.ErrorDetails.Message | ConvertFrom-Json
        
        switch ($statusCode) {
            400 { Write-Error "Validation error: $($errorDetails.error)" }
            404 { Write-Error "Deal not found: $($errorDetails.error)" }
            422 { Write-Error "Business rule violation: $($errorDetails.error)" }
            default { Write-Error "Error $statusCode: $($errorDetails.error)" }
        }
    }
}

# Example 1: Update basic fields
$apiKey = "your_api_key_here"
$basicUpdate = @{
    deal_id = "31539430-8cd0-46bf-be0f-c3a3ab4eb460"
    title = "Updated Deal"
    description = "New description"
    amount = 45000
}

Update-Deal -ApiKey $apiKey -DealData $basicUpdate

# Example 2: Close deal
$closeDeal = @{
    deal_id = "31539430-8cd0-46bf-be0f-c3a3ab4eb460"
    status = "closed"
    closed_by = "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2"
}

Update-Deal -ApiKey $apiKey -DealData $closeDeal

# Example 3: Update with custom fields
$customFieldsUpdate = @{
    deal_id = "31539430-8cd0-46bf-be0f-c3a3ab4eb460"
    hasCustomFields = $true
    customFields = @{
        "asd_1749674482286" = "New custom field value"
    }
}

Update-Deal -ApiKey $apiKey -DealData $customFieldsUpdate`,
      php: `<?php
function updateDeal($apiKey, $dealData) {
    $url = 'https://dev.clickai.live/deals/edit-deal';
    
    $headers = [
        'Authorization: Bearer ' . $apiKey,
        'Content-Type: application/json'
    ];
    
    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, $url);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($dealData));
    curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    
    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    curl_close($ch);
    
    if ($httpCode == 200) {
        $result = json_decode($response, true);
        echo "Deal updated successfully!\\n";
        echo "Updated fields: " . implode(', ', $result['updated_fields']) . "\\n";
        echo "Deal ID: {$result['deal_id']}\\n";
        return $result;
    } elseif ($httpCode == 400) {
        $error = json_decode($response, true);
        echo "Validation error: {$error['error']}\\n";
    } elseif ($httpCode == 404) {
        $error = json_decode($response, true);
        echo "Deal not found: {$error['error']}\\n";
    } elseif ($httpCode == 422) {
        $error = json_decode($response, true);
        echo "Business rule violation: {$error['error']}\\n";
    } else {
        $error = json_decode($response, true);
        echo "Error $httpCode: {$error['error']}\\n";
    }
    
    return null;
}

// Example 1: Update basic fields
$apiKey = 'your_api_key_here';
$basicUpdate = [
    'deal_id' => '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    'title' => 'Updated Deal',
    'description' => 'New description',
    'amount' => 45000
];

updateDeal($apiKey, $basicUpdate);

// Example 2: Close deal
$closeDeal = [
    'deal_id' => '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    'status' => 'closed',
    'closed_by' => 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2'
];

updateDeal($apiKey, $closeDeal);

// Example 3: Update with custom fields
$customFieldsUpdate = [
    'deal_id' => '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    'hasCustomFields' => true,
    'customFields' => [
        'asd_1749674482286' => 'New custom field value'
    ]
];

updateDeal($apiKey, $customFieldsUpdate);

// Example 4: Remove all custom fields
$removeCustomFields = [
    'deal_id' => '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
    'hasCustomFields' => false
];

updateDeal($apiKey, $removeCustomFields);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class DealUpdateService
{
    private readonly HttpClient httpClient;
    private readonly string apiKey;
    
    public DealUpdateService(string apiKey)
    {
        this.apiKey = apiKey;
        this.httpClient = new HttpClient();
    }
    
    public async Task<bool> UpdateDealAsync(object dealData)
    {
        string url = "https://dev.clickai.live/deals/edit-deal";
        
        string json = JsonConvert.SerializeObject(dealData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        httpClient.DefaultRequestHeaders.Clear();
        httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.StatusCode == System.Net.HttpStatusCode.OK)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine("Deal updated successfully!");
                Console.WriteLine($"Updated fields: {string.Join(", ", result.updated_fields)}");
                Console.WriteLine($"Deal ID: {result.deal_id}");
                return true;
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.BadRequest)
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Validation error: {error.error}");
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.NotFound)
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Deal not found: {error.error}");
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.UnprocessableEntity)
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Business rule violation: {error.error}");
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
        
        return false;
    }
}

// Usage
var dealService = new DealUpdateService("your_api_key_here");

// Example 1: Update basic fields
var basicUpdate = new
{
    deal_id = "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
    title = "Updated Deal",
    description = "New description",
    amount = 45000
};

await dealService.UpdateDealAsync(basicUpdate);

// Example 2: Close deal
var closeDeal = new
{
    deal_id = "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
    status = "closed",
    closed_by = "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2"
};

await dealService.UpdateDealAsync(closeDeal);

// Example 3: Update with custom fields
var customFieldsUpdate = new
{
    deal_id = "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
    hasCustomFields = true,
    customFields = new { asd_1749674482286 = "New custom field value" }
};

await dealService.UpdateDealAsync(customFieldsUpdate);`,
      ruby: `require 'net/http'
require 'json'

class DealUpdateService
  def initialize(api_key)
    @api_key = api_key
    @base_url = 'https://dev.clickai.live'
  end
  
  def update_deal(deal_data)
    url = URI("#{@base_url}/deals/edit-deal")
    
    http = Net::HTTP.new(url.host, url.port)
    request = Net::HTTP::Post.new(url)
    request['Authorization'] = "Bearer #{@api_key}"
    request['Content-Type'] = 'application/json'
    request.body = deal_data.to_json
    
    begin
      response = http.request(request)
      
      case response.code
      when '200'
        result = JSON.parse(response.body)
        puts "Deal updated successfully!"
        puts "Updated fields: #{result['updated_fields'].join(', ')}"
        puts "Deal ID: #{result['deal_id']}"
        result
      when '400'
        error = JSON.parse(response.body)
        puts "Validation error: #{error['error']}"
        nil
      when '404'
        error = JSON.parse(response.body)
        puts "Deal not found: #{error['error']}"
        nil
      when '422'
        error = JSON.parse(response.body)
        puts "Business rule violation: #{error['error']}"
        nil
      else
        error = JSON.parse(response.body)
        puts "Error #{response.code}: #{error['error']}"
        nil
      end
    rescue => e
      puts "Request failed: #{e.message}"
      nil
    end
  end
end

# Usage
deal_service = DealUpdateService.new('your_api_key_here')

# Example 1: Update basic fields
basic_update = {
  deal_id: '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
  title: 'Updated Deal',
  description: 'New description',
  amount: 45000
}

deal_service.update_deal(basic_update)

# Example 2: Close deal
close_deal_data = {
  deal_id: '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
  status: 'closed',
  closed_by: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2'
}

deal_service.update_deal(close_deal_data)

# Example 3: Update with custom fields
custom_fields_update = {
  deal_id: '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
  hasCustomFields: true,
  customFields: {
    'asd_1749674482286' => 'New custom field value'
  }
}

deal_service.update_deal(custom_fields_update)

# Example 4: Remove all custom fields
remove_custom_fields = {
  deal_id: '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
  hasCustomFields: false
}

deal_service.update_deal(remove_custom_fields)`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.time.Duration;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.List;

public class DealUpdateService {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    private final String apiKey;
    
    public DealUpdateService(String apiKey) {
        this.apiKey = apiKey;
        this.httpClient = HttpClient.newBuilder()
            .connectTimeout(Duration.ofSeconds(30))
            .build();
        this.objectMapper = new ObjectMapper();
    }
    
    public boolean updateDeal(Map<String, Object> dealData) {
        try {
            String url = "https://dev.clickai.live/deals/edit-deal";
            
            String json = objectMapper.writeValueAsString(dealData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .timeout(Duration.ofSeconds(30))
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 200) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Deal updated successfully!");
                System.out.println("Updated fields: " + result.get("updated_fields"));
                System.out.println("Deal ID: " + result.get("deal_id"));
                return true;
            } else if (response.statusCode() == 400) {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Validation error: " + error.get("error"));
            } else if (response.statusCode() == 404) {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Deal not found: " + error.get("error"));
            } else if (response.statusCode() == 422) {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Business rule violation: " + error.get("error"));
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
        }
        
        return false;
    }
    
    public static void main(String[] args) {
        DealUpdateService service = new DealUpdateService("your_api_key_here");
        
        // Example 1: Update basic fields
        Map<String, Object> basicUpdate = Map.of(
            "deal_id", "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
            "title", "Updated Deal",
            "description", "New description",
            "amount", 45000
        );
        
        service.updateDeal(basicUpdate);
        
        // Example 2: Close deal
        Map<String, Object> closeDeal = Map.of(
            "deal_id", "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
            "status", "closed",
            "closed_by", "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2"
        );
        
        service.updateDeal(closeDeal);
        
        // Example 3: Update with custom fields
        Map<String, Object> customFieldsUpdate = Map.of(
            "deal_id", "31539430-8cd0-46bf-be0f-c3a3ab4eb460",
            "hasCustomFields", true,
            "customFields", Map.of("asd_1749674482286", "New custom field value")
        );
        
        service.updateDeal(customFieldsUpdate);
    }
}`
    }
  },

  'deals-get-deal': {
    id: 'deals-get-deal',
    title: 'Get deal by ID',
    category: 'Deals',
    categoryId: 'deals',
    method: 'POST',
    path: '/deals/get-deal',
    description: 'Retrieves complete details of a specific deal by its ID, including all custom fields configured for your organization. Returns detailed information about the deal with enriched data such as customer names, product names, and user names.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json'
      }
    ],
    parameters: [
      {
        name: 'deal_id',
        type: 'string',
        required: true,
        description: 'UUID of the deal to retrieve',
        example: '31539430-8cd0-46bf-be0f-c3a3ab4eb460'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Deal retrieved successfully',
        example: JSON.stringify({
          success: true,
          data: {
            deal_id: '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
            title: 'Important Deal',
            description: 'Detailed deal description',
            amount: 50000,
            status: 'new',
            name: 'John Smith',
            phone: '0501234567',
            customer_id: '091cd08c-5dc3-456d-9d43-8918a549cfea',
            customer_name: 'First Name Last Name',
            products: ['03727424-3596-418b-865c-d7fee7810426'],
            product_names: ['Product Name'],
            created_by: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
            created_by_name: 'Jessica Rowley',
            closed_by: 'N/A',
            closed_by_name: 'N/A',
            created_at: '2024-01-01T12:00:00Z',
            updated_at: '2024-01-01T12:00:00Z',
            customFields: [
              {
                key: 'asd_1749674482286',
                content: 'Custom field content',
                label: 'Field Label',
                type: 'text',
                area: 'Deal Details'
              }
            ]
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad request - Missing required field',
        example: JSON.stringify({
          error: 'Missing required field: deal_id'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not found - Deal does not exist',
        example: JSON.stringify({
          error: 'Deal not found or does not belong to your organization'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal server error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/deals/get-deal';

async function getDeal(dealId) {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': \`Bearer \${apiKey}\`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ deal_id: dealId })
        });
        
        const data = await response.json();
        
        if (response.status === 200) {
            console.log('Deal retrieved successfully!');
            console.log('Deal Title:', data.data.title);
            console.log('Amount:', data.data.amount);
            console.log('Status:', data.data.status);
            console.log('Customer:', data.data.customer_name);
            
            // Display custom fields
            if (data.data.customFields && data.data.customFields.length > 0) {
                console.log('Custom Fields:');
                data.data.customFields.forEach(field => {
                    if (field.content !== 'N/A') {
                        console.log(\`  \${field.label}: \${field.content}\`);
                    }
                });
            }
            
            return data.data;
        } else if (response.status === 404) {
            console.error('Deal not found:', data.error);
        } else {
            console.error('Error:', data.error);
        }
    } catch (error) {
        console.error('Request failed:', error);
    }
}

// Usage
getDeal('31539430-8cd0-46bf-be0f-c3a3ab4eb460');`,
      python: `import requests

def get_deal(api_key, deal_id):
    url = 'https://dev.clickai.live/deals/get-deal'
    
    headers = {
        'Authorization': f'Bearer {api_key}',
        'Content-Type': 'application/json'
    }
    
    data = {'deal_id': deal_id}
    
    try:
        response = requests.post(url, json=data, headers=headers)
        
        if response.status_code == 200:
            result = response.json()
            deal_data = result['data']
            
            print('Deal retrieved successfully!')
            print(f'Deal Title: {deal_data["title"]}')
            print(f'Amount: {deal_data["amount"]}')
            print(f'Status: {deal_data["status"]}')
            print(f'Customer: {deal_data["customer_name"]}')
            
            # Display custom fields
            if deal_data.get('customFields'):
                print('Custom Fields:')
                for field in deal_data['customFields']:
                    if field['content'] != 'N/A':
                        print(f'  {field["label"]}: {field["content"]}')
            
            return deal_data
        elif response.status_code == 404:
            error = response.json()
            print(f'Deal not found: {error["error"]}')
        else:
            error = response.json()
            print(f'Error {response.status_code}: {error["error"]}')
            
    except requests.exceptions.RequestException as e:
        print(f'Request failed: {e}')
    
    return None

# Usage
api_key = 'your_api_key_here'
deal_id = '31539430-8cd0-46bf-be0f-c3a3ab4eb460'

deal_data = get_deal(api_key, deal_id)`,
      curl: `# Get existing deal (Success - 200)
curl -X POST "https://dev.clickai.live/deals/get-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "deal_id": "31539430-8cd0-46bf-be0f-c3a3ab4eb460"
  }'

# Non-existent deal (Error - 404)
curl -X POST "https://dev.clickai.live/deals/get-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "deal_id": "00000000-0000-0000-0000-000000000000"
  }'

# Missing deal_id (Error - 400)
curl -X POST "https://dev.clickai.live/deals/get-deal" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{}'`,
      powershell: `function Get-Deal {
    param(
        [string]$ApiKey,
        [string]$DealId
    )
    
    $url = "https://dev.clickai.live/deals/get-deal"
    $headers = @{
        "Authorization" = "Bearer $ApiKey"
        "Content-Type" = "application/json"
    }
    
    $body = @{
        deal_id = $DealId
    } | ConvertTo-Json
    
    try {
        $response = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $body
        
        Write-Host "Deal retrieved successfully!"
        Write-Host "Deal Title: $($response.data.title)"
        Write-Host "Amount: $($response.data.amount)"
        Write-Host "Status: $($response.data.status)"
        Write-Host "Customer: $($response.data.customer_name)"
        
        # Display custom fields
        if ($response.data.customFields -and $response.data.customFields.Count -gt 0) {
            Write-Host "Custom Fields:"
            foreach ($field in $response.data.customFields) {
                if ($field.content -ne "N/A") {
                    Write-Host "  $($field.label): $($field.content)"
                }
            }
        }
        
        return $response.data
    } catch {
        $statusCode = $_.Exception.Response.StatusCode.value__
        $errorDetails = $_.ErrorDetails.Message | ConvertFrom-Json
        
        if ($statusCode -eq 404) {
            Write-Error "Deal not found: $($errorDetails.error)"
        } else {
            Write-Error "Error $statusCode: $($errorDetails.error)"
        }
    }
}

# Usage
$apiKey = "your_api_key_here"
$dealId = "31539430-8cd0-46bf-be0f-c3a3ab4eb460"

Get-Deal -ApiKey $apiKey -DealId $dealId`,
      php: `<?php
function getDeal($apiKey, $dealId) {
    $url = 'https://dev.clickai.live/deals/get-deal';
    
    $data = ['deal_id' => $dealId];
    $headers = [
        'Authorization: Bearer ' . $apiKey,
        'Content-Type: application/json'
    ];
    
    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, $url);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
    curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    
    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    curl_close($ch);
    
    if ($httpCode == 200) {
        $result = json_decode($response, true);
        $dealData = $result['data'];
        
        echo "Deal retrieved successfully!\\n";
        echo "Deal Title: {$dealData['title']}\\n";
        echo "Amount: {$dealData['amount']}\\n";
        echo "Status: {$dealData['status']}\\n";
        echo "Customer: {$dealData['customer_name']}\\n";
        
        // Display custom fields
        if (!empty($dealData['customFields'])) {
            echo "Custom Fields:\\n";
            foreach ($dealData['customFields'] as $field) {
                if ($field['content'] !== 'N/A') {
                    echo "  {$field['label']}: {$field['content']}\\n";
                }
            }
        }
        
        return $dealData;
    } elseif ($httpCode == 404) {
        $error = json_decode($response, true);
        echo "Deal not found: {$error['error']}\\n";
    } else {
        $error = json_decode($response, true);
        echo "Error $httpCode: {$error['error']}\\n";
    }
    
    return null;
}

// Usage
$apiKey = 'your_api_key_here';
$dealId = '31539430-8cd0-46bf-be0f-c3a3ab4eb460';

$dealData = getDeal($apiKey, $dealId);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class DealRetrievalService
{
    private readonly HttpClient httpClient;
    private readonly string apiKey;
    
    public DealRetrievalService(string apiKey)
    {
        this.apiKey = apiKey;
        this.httpClient = new HttpClient();
    }
    
    public async Task<object> GetDealAsync(string dealId)
    {
        string url = "https://dev.clickai.live/deals/get-deal";
        
        var requestData = new { deal_id = dealId };
        string json = JsonConvert.SerializeObject(requestData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        httpClient.DefaultRequestHeaders.Clear();
        httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.StatusCode == System.Net.HttpStatusCode.OK)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                var dealData = result.data;
                
                Console.WriteLine("Deal retrieved successfully!");
                Console.WriteLine($"Deal Title: {dealData.title}");
                Console.WriteLine($"Amount: {dealData.amount}");
                Console.WriteLine($"Status: {dealData.status}");
                Console.WriteLine($"Customer: {dealData.customer_name}");
                
                // Display custom fields
                if (dealData.customFields != null && dealData.customFields.Count > 0)
                {
                    Console.WriteLine("Custom Fields:");
                    foreach (var field in dealData.customFields)
                    {
                        if (field.content.ToString() != "N/A")
                        {
                            Console.WriteLine($"  {field.label}: {field.content}");
                        }
                    }
                }
                
                return dealData;
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.NotFound)
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Deal not found: {error.error}");
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
        
        return null;
    }
}

// Usage
var dealService = new DealRetrievalService("your_api_key_here");
var dealData = await dealService.GetDealAsync("31539430-8cd0-46bf-be0f-c3a3ab4eb460");`,
      ruby: `require 'net/http'
require 'json'

class DealService
  def initialize(api_key)
    @api_key = api_key
    @base_url = 'https://dev.clickai.live'
  end
  
  def get_deal(deal_id)
    url = URI("#{@base_url}/deals/get-deal")
    
    http = Net::HTTP.new(url.host, url.port)
    request = Net::HTTP::Post.new(url)
    request['Authorization'] = "Bearer #{@api_key}"
    request['Content-Type'] = 'application/json'
    request.body = { deal_id: deal_id }.to_json
    
    begin
      response = http.request(request)
      
      case response.code
      when '200'
        result = JSON.parse(response.body)
        deal_data = result['data']
        
        puts "Deal retrieved successfully!"
        puts "Deal Title: #{deal_data['title']}"
        puts "Amount: #{deal_data['amount']}"
        puts "Status: #{deal_data['status']}"
        puts "Customer: #{deal_data['customer_name']}"
        
        # Display custom fields
        if deal_data['customFields'] && deal_data['customFields'].any?
          puts "Custom Fields:"
          deal_data['customFields'].each do |field|
            puts "  #{field['label']}: #{field['content']}" if field['content'] != 'N/A'
          end
        end
        
        deal_data
      when '404'
        error = JSON.parse(response.body)
        puts "Deal not found: #{error['error']}"
        nil
      else
        error = JSON.parse(response.body)
        puts "Error #{response.code}: #{error['error']}"
        nil
      end
    rescue => e
      puts "Request failed: #{e.message}"
      nil
    end
  end
end

# Usage
deal_service = DealService.new('your_api_key_here')
deal_data = deal_service.get_deal('31539430-8cd0-46bf-be0f-c3a3ab4eb460')`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.time.Duration;
import com.fasterxml.jackson.databind.ObjectMapper;
import com.fasterxml.jackson.databind.JsonNode;
import java.util.Map;

public class DealService {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    private final String apiKey;
    
    public DealService(String apiKey) {
        this.apiKey = apiKey;
        this.httpClient = HttpClient.newBuilder()
            .connectTimeout(Duration.ofSeconds(30))
            .build();
        this.objectMapper = new ObjectMapper();
    }
    
    public JsonNode getDeal(String dealId) {
        try {
            String url = "https://dev.clickai.live/deals/get-deal";
            
            Map<String, String> requestData = Map.of("deal_id", dealId);
            String json = objectMapper.writeValueAsString(requestData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .timeout(Duration.ofSeconds(30))
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 200) {
                JsonNode result = objectMapper.readTree(response.body());
                JsonNode dealData = result.get("data");
                
                System.out.println("Deal retrieved successfully!");
                System.out.println("Deal Title: " + dealData.get("title").asText());
                System.out.println("Amount: " + dealData.get("amount").asText());
                System.out.println("Status: " + dealData.get("status").asText());
                System.out.println("Customer: " + dealData.get("customer_name").asText());
                
                // Display custom fields
                JsonNode customFields = dealData.get("customFields");
                if (customFields != null && customFields.isArray() && customFields.size() > 0) {
                    System.out.println("Custom Fields:");
                    for (JsonNode field : customFields) {
                        if (!field.get("content").asText().equals("N/A")) {
                            System.out.println("  " + field.get("label").asText() + ": " + field.get("content").asText());
                        }
                    }
                }
                
                return dealData;
            } else if (response.statusCode() == 404) {
                JsonNode error = objectMapper.readTree(response.body());
                System.out.println("Deal not found: " + error.get("error").asText());
            } else {
                JsonNode error = objectMapper.readTree(response.body());
                System.out.println("Error: " + error.get("error").asText());
            }
        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
        }
        
        return null;
    }
    
    public static void main(String[] args) {
        DealService service = new DealService("your_api_key_here");
        JsonNode dealData = service.getDeal("31539430-8cd0-46bf-be0f-c3a3ab4eb460");
    }
}`
    }
  },

  'deals-get-deal-phone': {
    id: 'deals-get-deal-phone',
    title: 'Get deals by phone number',
    category: 'Deals',
    categoryId: 'deals',
    method: 'POST',
    path: '/deals/get-deal/phone',
    description: 'Searches for all deals associated with a specific phone number. The search is performed in three ways: 1) Finds client with matching phone in clients table, 2) Searches deals with customer_id or connecttoclients matching the client ID, 3) Searches deals with matching phone directly in deals table.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json'
      }
    ],
    parameters: [
      {
        name: 'phone',
        type: 'string',
        required: true,
        description: 'Phone number to search for',
        example: '0501234567'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Successfully found deals for phone number',
        example: JSON.stringify({
          success: true,
          count: 2,
          client_found: true,
          client_name: 'John Smith',
          searched_phone: '0501234567',
          data: [
            {
              deal_id: '31539430-8cd0-46bf-be0f-c3a3ab4eb460',
              title: 'First Deal',
              description: 'Description of the first deal',
              amount: 30000,
              status: 'new',
              name: 'John Smith',
              phone: '0501234567',
              customer_id: '091cd08c-5dc3-456d-9d43-8918a549cfea',
              customer_name: 'John Smith',
              products: ['03727424-3596-418b-865c-d7fee7810426'],
              product_names: ['Product Name'],
              created_by: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
              created_by_name: 'Jessica Rowley',
              closed_by: 'N/A',
              closed_by_name: 'N/A',
              created_at: '2024-01-01T12:00:00Z',
              updated_at: '2024-01-01T12:00:00Z',
              customFields: []
            },
            {
              deal_id: '42539430-8cd0-46bf-be0f-c3a3ab4eb461',
              title: 'Second Deal',
              description: 'Description of the second deal',
              amount: 45000,
              status: 'closed',
              name: 'John Smith',
              phone: '0501234567',
              customer_id: '091cd08c-5dc3-456d-9d43-8918a549cfea',
              customer_name: 'John Smith',
              products: [],
              product_names: [],
              created_by: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
              created_by_name: 'Jessica Rowley',
              closed_by: 'a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2',
              closed_by_name: 'Jessica Rowley',
              created_at: '2024-01-01T12:00:00Z',
              updated_at: '2024-01-02T15:30:00Z',
              customFields: []
            }
          ]
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad request - Missing phone parameter',
        example: JSON.stringify({
          error: 'Missing required field: phone'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not found - No deals found for phone number',
        example: JSON.stringify({
          success: true,
          count: 0,
          client_found: false,
          client_name: 'N/A',
          searched_phone: '9999999999',
          data: []
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal server error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/deals/get-deal/phone';

async function searchDealsByPhone(phone) {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': \`Bearer \${apiKey}\`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ phone: phone })
        });
        
        const data = await response.json();
        
        if (response.status === 200) {
            console.log(\`Found \${data.count} deals for phone: \${data.searched_phone}\`);
            console.log(\`Client found: \${data.client_found ? 'Yes' : 'No'}\`);
            if (data.client_found) {
                console.log(\`Client name: \${data.client_name}\`);
            }
            
            data.data.forEach((deal, index) => {
                console.log(\`\\nDeal \${index + 1}:\`);
                console.log(\`  Title: \${deal.title}\`);
                console.log(\`  Amount: \${deal.amount}\`);
                console.log(\`  Status: \${deal.status}\`);
                console.log(\`  Customer: \${deal.customer_name}\`);
                if (deal.customFields && deal.customFields.length > 0) {
                    console.log(\`  Custom Fields: \${deal.customFields.length}\`);
                }
            });
        } else if (response.status === 404) {
            console.log(\`No deals found for phone: \${data.searched_phone}\`);
        } else {
            console.error('Error:', data.error);
        }
    } catch (error) {
        console.error('Request failed:', error);
    }
}

// Search for deals
searchDealsByPhone('0501234567');`,
      python: `import requests

def search_deals_by_phone(api_key, phone):
    url = 'https://dev.clickai.live/deals/get-deal/phone'
    
    headers = {
        'Authorization': f'Bearer {api_key}',
        'Content-Type': 'application/json'
    }
    
    data = {'phone': phone}
    
    try:
        response = requests.post(url, json=data, headers=headers)
        
        if response.status_code == 200:
            result = response.json()
            print(f"Found {result['count']} deals for phone: {result['searched_phone']}")
            print(f"Client found: {'Yes' if result['client_found'] else 'No'}")
            if result['client_found']:
                print(f"Client name: {result['client_name']}")
            
            for i, deal in enumerate(result['data'], 1):
                print(f"\\nDeal {i}:")
                print(f"  Title: {deal['title']}")
                print(f"  Amount: {deal['amount']}")
                print(f"  Status: {deal['status']}")
                print(f"  Customer: {deal['customer_name']}")
                if deal['customFields']:
                    print(f"  Custom Fields: {len(deal['customFields'])}")
                    
        elif response.status_code == 404:
            result = response.json()
            print(f"No deals found for phone: {result['searched_phone']}")
        else:
            error = response.json()
            print(f"Error {response.status_code}: {error['error']}")
            
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")

# Usage
api_key = 'your_api_key_here'
search_deals_by_phone(api_key, '0501234567')`,
      curl: `# Search for deals by existing phone number
curl -X POST "https://dev.clickai.live/deals/get-deal/phone" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"phone": "0501234567"}'

# Search for deals by non-existent phone number
curl -X POST "https://dev.clickai.live/deals/get-deal/phone" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"phone": "9999999999"}'`,
      powershell: `function Search-DealsByPhone {
    param(
        [string]$ApiKey,
        [string]$Phone
    )
    
    $url = "https://dev.clickai.live/deals/get-deal/phone"
    $headers = @{
        "Authorization" = "Bearer $ApiKey"
        "Content-Type" = "application/json"
    }
    
    $body = @{
        phone = $Phone
    } | ConvertTo-Json
    
    try {
        $response = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $body
        
        Write-Host "Found $($response.count) deals for phone: $($response.searched_phone)"
        Write-Host "Client found: $(if ($response.client_found) {'Yes'} else {'No'})"
        if ($response.client_found) {
            Write-Host "Client name: $($response.client_name)"
        }
        
        for ($i = 0; $i -lt $response.data.Count; $i++) {
            $deal = $response.data[$i]
            Write-Host ""
            Write-Host "Deal $($i + 1):"
            Write-Host "  Title: $($deal.title)"
            Write-Host "  Amount: $($deal.amount)"
            Write-Host "  Status: $($deal.status)"
            Write-Host "  Customer: $($deal.customer_name)"
            if ($deal.customFields -and $deal.customFields.Count -gt 0) {
                Write-Host "  Custom Fields: $($deal.customFields.Count)"
            }
        }
    } catch {
        $statusCode = $_.Exception.Response.StatusCode.value__
        if ($statusCode -eq 404) {
            Write-Host "No deals found for phone: $Phone"
        } else {
            Write-Error "Error $statusCode: $($_.Exception.Message)"
        }
    }
}

# Usage
$apiKey = "your_api_key_here"
Search-DealsByPhone -ApiKey $apiKey -Phone "0501234567"`,
      php: `<?php
function searchDealsByPhone($apiKey, $phone) {
    $url = 'https://dev.clickai.live/deals/get-deal/phone';
    
    $data = ['phone' => $phone];
    $headers = [
        'Authorization: Bearer ' . $apiKey,
        'Content-Type: application/json'
    ];
    
    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, $url);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
    curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    
    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    curl_close($ch);
    
    if ($httpCode == 200) {
        $result = json_decode($response, true);
        echo "Found {$result['count']} deals for phone: {$result['searched_phone']}\\n";
        echo "Client found: " . ($result['client_found'] ? 'Yes' : 'No') . "\\n";
        if ($result['client_found']) {
            echo "Client name: {$result['client_name']}\\n";
        }
        
        foreach ($result['data'] as $index => $deal) {
            $dealNum = $index + 1;
            echo "\\nDeal $dealNum:\\n";
            echo "  Title: {$deal['title']}\\n";
            echo "  Amount: {$deal['amount']}\\n";
            echo "  Status: {$deal['status']}\\n";
            echo "  Customer: {$deal['customer_name']}\\n";
            if (!empty($deal['customFields'])) {
                echo "  Custom Fields: " . count($deal['customFields']) . "\\n";
            }
        }
    } elseif ($httpCode == 404) {
        $result = json_decode($response, true);
        echo "No deals found for phone: {$result['searched_phone']}\\n";
    } else {
        $error = json_decode($response, true);
        echo "Error $httpCode: {$error['error']}\\n";
    }
}

// Usage
$apiKey = 'your_api_key_here';
searchDealsByPhone($apiKey, '0501234567');
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class DealsSearchService
{
    private readonly HttpClient httpClient;
    private readonly string apiKey;
    
    public DealsSearchService(string apiKey)
    {
        this.apiKey = apiKey;
        this.httpClient = new HttpClient();
    }
    
    public async Task SearchDealsByPhoneAsync(string phone)
    {
        string url = "https://dev.clickai.live/deals/get-deal/phone";
        
        var requestData = new { phone = phone };
        string json = JsonConvert.SerializeObject(requestData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        httpClient.DefaultRequestHeaders.Clear();
        httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Found {result.count} deals for phone: {result.searched_phone}");
                Console.WriteLine($"Client found: {(result.client_found ? "Yes" : "No")}");
                if (result.client_found)
                {
                    Console.WriteLine($"Client name: {result.client_name}");
                }
                
                for (int i = 0; i < result.data.Count; i++)
                {
                    var deal = result.data[i];
                    Console.WriteLine($"\\nDeal {i + 1}:");
                    Console.WriteLine($"  Title: {deal.title}");
                    Console.WriteLine($"  Amount: {deal.amount}");
                    Console.WriteLine($"  Status: {deal.status}");
                    Console.WriteLine($"  Customer: {deal.customer_name}");
                    if (deal.customFields != null && deal.customFields.Count > 0)
                    {
                        Console.WriteLine($"  Custom Fields: {deal.customFields.Count}");
                    }
                }
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.NotFound)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"No deals found for phone: {result.searched_phone}");
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}

// Usage
var service = new DealsSearchService("your_api_key_here");
await service.SearchDealsByPhoneAsync("0501234567");`,
      ruby: `require 'net/http'
require 'json'

class DealsSearchService
  def initialize(api_key)
    @api_key = api_key
    @base_url = 'https://dev.clickai.live'
  end
  
  def search_deals_by_phone(phone)
    url = URI("#{@base_url}/deals/get-deal/phone")
    
    http = Net::HTTP.new(url.host, url.port)
    request = Net::HTTP::Post.new(url)
    request['Authorization'] = "Bearer #{@api_key}"
    request['Content-Type'] = 'application/json'
    request.body = { phone: phone }.to_json
    
    begin
      response = http.request(request)
      
      case response.code
      when '200'
        result = JSON.parse(response.body)
        puts "Found #{result['count']} deals for phone: #{result['searched_phone']}"
        puts "Client found: #{result['client_found'] ? 'Yes' : 'No'}"
        puts "Client name: #{result['client_name']}" if result['client_found']
        
        result['data'].each_with_index do |deal, index|
          puts "\\nDeal #{index + 1}:"
          puts "  Title: #{deal['title']}"
          puts "  Amount: #{deal['amount']}"
          puts "  Status: #{deal['status']}"
          puts "  Customer: #{deal['customer_name']}"
          puts "  Custom Fields: #{deal['customFields'].length}" if deal['customFields']&.any?
        end
      when '404'
        result = JSON.parse(response.body)
        puts "No deals found for phone: #{result['searched_phone']}"
      else
        error = JSON.parse(response.body)
        puts "Error #{response.code}: #{error['error']}"
      end
    rescue => e
      puts "Request failed: #{e.message}"
    end
  end
end

# Usage
service = DealsSearchService.new('your_api_key_here')
service.search_deals_by_phone('0501234567')`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.time.Duration;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.List;

public class DealsSearchService {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    private final String apiKey;
    
    public DealsSearchService(String apiKey) {
        this.apiKey = apiKey;
        this.httpClient = HttpClient.newBuilder()
            .connectTimeout(Duration.ofSeconds(30))
            .build();
        this.objectMapper = new ObjectMapper();
    }
    
    public void searchDealsByPhone(String phone) {
        try {
            String url = "https://dev.clickai.live/deals/get-deal/phone";
            
            Map<String, String> requestData = Map.of("phone", phone);
            String json = objectMapper.writeValueAsString(requestData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .timeout(Duration.ofSeconds(30))
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 200) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Found " + result.get("count") + " deals for phone: " + result.get("searched_phone"));
                System.out.println("Client found: " + (Boolean.TRUE.equals(result.get("client_found")) ? "Yes" : "No"));
                if (Boolean.TRUE.equals(result.get("client_found"))) {
                    System.out.println("Client name: " + result.get("client_name"));
                }
                
                List<Map<String, Object>> deals = (List<Map<String, Object>>) result.get("data");
                for (int i = 0; i < deals.size(); i++) {
                    Map<String, Object> deal = deals.get(i);
                    System.out.println("\\nDeal " + (i + 1) + ":");
                    System.out.println("  Title: " + deal.get("title"));
                    System.out.println("  Amount: " + deal.get("amount"));
                    System.out.println("  Status: " + deal.get("status"));
                    System.out.println("  Customer: " + deal.get("customer_name"));
                    List<Object> customFields = (List<Object>) deal.get("customFields");
                    if (customFields != null && !customFields.isEmpty()) {
                        System.out.println("  Custom Fields: " + customFields.size());
                    }
                }
            } else if (response.statusCode() == 404) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                System.out.println("No deals found for phone: " + result.get("searched_phone"));
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
        }
    }
    
    public static void main(String[] args) {
        DealsSearchService service = new DealsSearchService("your_api_key_here");
        service.searchDealsByPhone("0501234567");
    }
}`
    }
  },

  // Customers endpoints - additional
  'customers-custom-fields': {
    id: 'customers-custom-fields',
    title: 'Get custom fields for customers',
    category: 'Customers',
    categoryId: 'customers',
    method: 'GET',
    path: '/customers/custom-fields',
    description: 'Retrieves all custom field definitions configured for customers (clients) in your organization. Returns a list of all available custom fields with their configuration details including keys, labels, types, and areas.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: false,
        description: 'Recommended for consistency',
        example: 'application/json'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Returns all custom field definitions',
        example: JSON.stringify({
          "success": true,
          "customFields": [
            {
              "key": "customer_type_1749674482287",
              "area": "Customer Details",
              "type": "select",
              "label": "Customer Type",
              "areaId": "customer_details",
              "required": true,
              "options": ["Personal", "Business", "Government"]
            },
            {
              "key": "priority_level_1749674482288",
              "area": "Customer Details",
              "type": "text",
              "label": "Priority Level",
              "areaId": "customer_details",
              "required": false
            },
            {
              "key": "notes_1749674482289",
              "area": "Notes",
              "type": "textarea",
              "label": "General Notes",
              "areaId": "notes",
              "required": false
            }
          ]
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          "error": "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          "error": "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          "error": "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const response = await fetch('https://dev.clickai.live/customers/custom-fields', {
    method: 'GET',
    headers: {
        'Authorization': 'Bearer your_api_key_here',
        'Content-Type': 'application/json'
    }
});

const data = await response.json();

if (response.ok && data.success) {
    console.log('Custom Fields Retrieved Successfully:');
    data.customFields.forEach(field => {
        console.log(\`Field: \${field.label} (\${field.key})\`);
        console.log(\`Type: \${field.type}, Required: \${field.required}\`);
        if (field.options) {
            console.log(\`Options: \${field.options.join(', ')}\`);
        }
        console.log('---');
    });
} else {
    console.error('Error:', data.error || 'Unknown error');
}`,
      python: `import requests
import json

def get_customers_custom_fields(api_key):
    url = "https://dev.clickai.live/customers/custom-fields"
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    try:
        response = requests.get(url, headers=headers)
        data = response.json()
        
        if response.status_code == 200 and data.get("success"):
            print("Custom Fields Retrieved Successfully:")
            for field in data["customFields"]:
                print(f"Field: {field['label']} ({field['key']})")
                print(f"Type: {field['type']}, Required: {field['required']}")
                if field.get('options'):
                    print(f"Options: {', '.join(field['options'])}")
                print("---")
            return data["customFields"]
        else:
            print(f"Error {response.status_code}: {data.get('error', 'Unknown error')}")
            return None
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

# Usage
api_key = "your_api_key_here"
custom_fields = get_customers_custom_fields(api_key)`,
      curl: `# Get custom fields for customers
curl -X GET "https://dev.clickai.live/customers/custom-fields" \\
     -H "Authorization: Bearer your_api_key_here" \\
     -H "Content-Type: application/json"`,
      powershell: `# PowerShell example for getting customers custom fields
$apiKey = "your_api_key_here"
$url = "https://dev.clickai.live/customers/custom-fields"

$headers = @{
    "Authorization" = "Bearer $apiKey"
    "Content-Type" = "application/json"
}

try {
    $response = Invoke-RestMethod -Uri $url -Method Get -Headers $headers
    
    if ($response.success) {
        Write-Host "Custom Fields Retrieved Successfully:" -ForegroundColor Green
        foreach ($field in $response.customFields) {
            Write-Host "Field: $($field.label) ($($field.key))" -ForegroundColor Yellow
            Write-Host "Type: $($field.type), Required: $($field.required)"
            if ($field.options) {
                Write-Host "Options: $($field.options -join ', ')"
            }
            Write-Host "---"
        }
    }
} catch {
    Write-Host "Error: $($_.Exception.Message)" -ForegroundColor Red
}`,
      php: `<?php
function getCustomersCustomFields($apiKey) {
    $url = 'https://dev.clickai.live/customers/custom-fields';
    
    $curl = curl_init();
    
    curl_setopt_array($curl, array(
        CURLOPT_URL => $url,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_HTTPHEADER => array(
            'Authorization: Bearer ' . $apiKey,
            'Content-Type: application/json'
        ),
    ));
    
    $response = curl_exec($curl);
    $httpCode = curl_getinfo($curl, CURLINFO_HTTP_CODE);
    curl_close($curl);
    
    $data = json_decode($response, true);
    
    if ($httpCode == 200 && isset($data['success']) && $data['success']) {
        echo "Custom Fields Retrieved Successfully:\\n";
        foreach ($data['customFields'] as $field) {
            echo "Field: " . $field['label'] . " (" . $field['key'] . ")\\n";
            echo "Type: " . $field['type'] . ", Required: " . ($field['required'] ? 'Yes' : 'No') . "\\n";
            if (isset($field['options'])) {
                echo "Options: " . implode(', ', $field['options']) . "\\n";
            }
            echo "---\\n";
        }
        return $data['customFields'];
    } else {
        echo "Error $httpCode: " . ($data['error'] ?? 'Unknown error') . "\\n";
        return null;
    }
}

// Usage
$apiKey = 'your_api_key_here';
$customFields = getCustomersCustomFields($apiKey);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class CustomersCustomFieldsService
{
    private readonly HttpClient _httpClient;
    private readonly string _baseUrl = "https://dev.clickai.live";

    public CustomersCustomFieldsService()
    {
        _httpClient = new HttpClient();
    }

    public async Task<CustomFieldsResponse> GetCustomFieldsAsync(string apiKey)
    {
        try
        {
            _httpClient.DefaultRequestHeaders.Clear();
            _httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
            _httpClient.DefaultRequestHeaders.Add("Content-Type", "application/json");

            var response = await _httpClient.GetAsync($"{_baseUrl}/customers/custom-fields");
            var content = await response.Content.ReadAsStringAsync();

            if (response.IsSuccessStatusCode)
            {
                var data = JsonConvert.DeserializeObject<CustomFieldsResponse>(content);
                
                if (data.Success)
                {
                    Console.WriteLine("Custom Fields Retrieved Successfully:");
                    foreach (var field in data.CustomFields)
                    {
                        Console.WriteLine($"Field: {field.Label} ({field.Key})");
                        Console.WriteLine($"Type: {field.Type}, Required: {field.Required}");
                        if (field.Options != null)
                        {
                            Console.WriteLine($"Options: {string.Join(", ", field.Options)}");
                        }
                        Console.WriteLine("---");
                    }
                }
                return data;
            }
            else
            {
                var errorData = JsonConvert.DeserializeObject<ErrorResponse>(content);
                Console.WriteLine($"Error {(int)response.StatusCode}: {errorData.Error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
            return null;
        }
    }
}

public class CustomFieldsResponse
{
    public bool Success { get; set; }
    public CustomField[] CustomFields { get; set; }
}

public class CustomField
{
    public string Key { get; set; }
    public string Area { get; set; }
    public string Type { get; set; }
    public string Label { get; set; }
    public string AreaId { get; set; }
    public bool Required { get; set; }
    public string[] Options { get; set; }
}

public class ErrorResponse
{
    public string Error { get; set; }
}`,
      ruby: `require 'net/http'
require 'json'

class CustomersCustomFieldsService
  BASE_URL = 'https://dev.clickai.live'

  def self.get_custom_fields(api_key)
    uri = URI("#{BASE_URL}/customers/custom-fields")
    
    http = Net::HTTP.new(uri.host, uri.port)
    http.use_ssl = true

    request = Net::HTTP::Get.new(uri)
    request['Authorization'] = "Bearer #{api_key}"
    request['Content-Type'] = 'application/json'

    begin
      response = http.request(request)
      data = JSON.parse(response.body)

      case response.code.to_i
      when 200
        if data['success']
          puts "Custom Fields Retrieved Successfully:"
          data['customFields'].each do |field|
            puts "Field: #{field['label']} (#{field['key']})"
            puts "Type: #{field['type']}, Required: #{field['required']}"
            if field['options']
              puts "Options: #{field['options'].join(', ')}"
            end
            puts "---"
          end
          return data['customFields']
        end
      else
        puts "Error #{response.code}: #{data['error'] || 'Unknown error'}"
        return nil
      end
    rescue StandardError => e
      puts "Request failed: #{e.message}"
      return nil
    end
  end
end

# Usage
api_key = 'your_api_key_here'
custom_fields = CustomersCustomFieldsService.get_custom_fields(api_key)`,
      java: `import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.net.URI;
import com.google.gson.Gson;
import com.google.gson.JsonObject;
import com.google.gson.JsonArray;

public class CustomersCustomFieldsAPI {
    private static final String BASE_URL = "https://dev.clickai.live";
    private static final HttpClient client = HttpClient.newHttpClient();
    private static final Gson gson = new Gson();

    public static void getCustomFields(String apiKey) {
        try {
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(BASE_URL + "/customers/custom-fields"))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .GET()
                .build();

            HttpResponse<String> response = client.send(request, 
                HttpResponse.BodyHandlers.ofString());

            JsonObject data = gson.fromJson(response.body(), JsonObject.class);

            if (response.statusCode() == 200 && data.get("success").getAsBoolean()) {
                System.out.println("Custom Fields Retrieved Successfully:");
                
                JsonArray customFields = data.getAsJsonArray("customFields");
                for (int i = 0; i < customFields.size(); i++) {
                    JsonObject field = customFields.get(i).getAsJsonObject();
                    
                    System.out.println("Field: " + field.get("label").getAsString() + 
                        " (" + field.get("key").getAsString() + ")");
                    System.out.println("Type: " + field.get("type").getAsString() + 
                        ", Required: " + field.get("required").getAsBoolean());
                    
                    if (field.has("options")) {
                        JsonArray options = field.getAsJsonArray("options");
                        StringBuilder optionsStr = new StringBuilder("Options: ");
                        for (int j = 0; j < options.size(); j++) {
                            optionsStr.append(options.get(j).getAsString());
                            if (j < options.size() - 1) optionsStr.append(", ");
                        }
                        System.out.println(optionsStr.toString());
                    }
                    System.out.println("---");
                }
            } else {
                System.out.println("Error " + response.statusCode() + ": " + 
                    (data.has("error") ? data.get("error").getAsString() : "Unknown error"));
            }

        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
        }
    }

    public static void main(String[] args) {
        String apiKey = "your_api_key_here";
        getCustomFields(apiKey);
    }
}`
    }
  },

  'customers-create-customer': {
    id: 'customers-create-customer',
    title: 'Create new customer',
    category: 'Customers',
    categoryId: 'customers',
    method: 'POST',
    path: '/customers/create-customer',
    description: 'Creates a new customer (client) in your organization with optional custom fields support. Phone number is mandatory, and at least one additional identification field (first_name, last_name, or email) is required.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'phone',
        type: 'string',
        required: true,
        description: 'Customer phone number (unique per organization)',
        example: '0501234567'
      },
      {
        name: 'hasCustomFields',
        type: 'boolean',
        required: true,
        description: 'Whether custom fields are included',
        example: 'true'
      },
      {
        name: 'first_name',
        type: 'string',
        required: false,
        description: 'Customer first name (required if last_name and email are not provided)',
        example: 'John'
      },
      {
        name: 'last_name',
        type: 'string',
        required: false,
        description: 'Customer last name (required if first_name and email are not provided)',
        example: 'Smith'
      },
      {
        name: 'email',
        type: 'string',
        required: false,
        description: 'Customer email address (required if first_name and last_name are not provided)',
        example: 'john@example.com'
      },
      {
        name: 'address',
        type: 'string',
        required: false,
        description: 'Customer address',
        example: '123 Main Street'
      },
      {
        name: 'city',
        type: 'string',
        required: false,
        description: 'Customer city',
        example: 'Tel Aviv'
      },
      {
        name: 'state',
        type: 'string',
        required: false,
        description: 'Customer state/region',
        example: 'Center'
      },
      {
        name: 'country',
        type: 'string',
        required: false,
        description: 'Customer country',
        example: 'Israel'
      },
      {
        name: 'status',
        type: 'string',
        required: false,
        description: 'Customer status (default: new)',
        example: 'active'
      },
      {
        name: 'received',
        type: 'string',
        required: false,
        description: 'How customer was received/acquired',
        example: 'website_form'
      },
      {
        name: 'profileImageLink',
        type: 'string',
        required: false,
        description: 'URL to customer profile image',
        example: 'https://example.com/image.jpg'
      },
      {
        name: 'next_call',
        type: 'string',
        required: false,
        description: 'Next call timestamp (ISO 8601 format)',
        example: '2024-12-25T10:00:00Z'
      },
      {
        name: 'shopify_id',
        type: 'string',
        required: false,
        description: 'Shopify customer ID for integration',
        example: '12345'
      },
      {
        name: 'woocommerce_id',
        type: 'string',
        required: false,
        description: 'WooCommerce customer ID for integration',
        example: '67890'
      },
      {
        name: 'customFields',
        type: 'object',
        required: false,
        description: 'Key-value pairs of custom field data (required when hasCustomFields=true)',
        example: '{"customer_type_123": "Business"}'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Created - Customer created successfully',
        example: JSON.stringify({
          "success": true,
          "customer_id": "550e8400-e29b-41d4-a716-446655440000",
          "message": "Customer created successfully"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing required fields or validation errors',
        example: JSON.stringify({
          "error": "At least one of the following fields is required (in addition to phone): first_name, last_name, email"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          "error": "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          "error": "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '409',
        description: 'Conflict - Duplicate phone number',
        example: JSON.stringify({
          "error": "Customer with this phone number already exists in your organization"
        }, null, 2)
      },
      {
        code: '422',
        description: 'Unprocessable Entity - Invalid custom fields',
        example: JSON.stringify({
          "error": "Custom field key 'invalid_key' does not exist for this organization"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          "error": "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `// Example 1: Customer with custom fields
const response = await fetch('https://dev.clickai.live/customers/create-customer', {
    method: 'POST',
    headers: {
        'Authorization': 'Bearer your_api_key_here',
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        first_name: "John",
        last_name: "Smith",
        email: "john@example.com",
        phone: "0501234567",
        address: "123 Main Street",
        city: "Tel Aviv",
        state: "Center",
        country: "Israel",
        status: "active",
        hasCustomFields: true,
        customFields: {
            "customer_type_123": "Business"
        }
    })
});

const data = await response.json();

if (response.ok && data.success) {
    console.log(\`Customer created with ID: \${data.customer_id}\`);
} else {
    console.error('Error:', data.error || 'Unknown error');
}

// Example 2: Minimal customer without custom fields
const minimalResponse = await fetch('https://dev.clickai.live/customers/create-customer', {
    method: 'POST',
    headers: {
        'Authorization': 'Bearer your_api_key_here',
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        first_name: "David",
        phone: "0529876543",
        hasCustomFields: false
    })
});`,
      python: `import requests
import json

def create_customer_with_custom_fields(api_key):
    """Example 1: Customer with custom fields"""
    url = "https://dev.clickai.live/customers/create-customer"
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    data = {
        "first_name": "John",
        "last_name": "Smith",
        "email": "john@example.com",
        "phone": "0501234567",
        "address": "123 Main Street",
        "city": "Tel Aviv",
        "state": "Center",
        "country": "Israel",
        "status": "active",
        "hasCustomFields": True,
        "customFields": {
            "customer_type_123": "Business"
        }
    }
    
    try:
        response = requests.post(url, headers=headers, json=data)
        result = response.json()
        
        if response.status_code == 201 and result.get("success"):
            print(f"Customer created with ID: {result['customer_id']}")
            return result["customer_id"]
        else:
            print(f"Error {response.status_code}: {result.get('error', 'Unknown error')}")
            return None
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

def create_minimal_customer(api_key):
    """Example 2: Minimal customer without custom fields"""
    url = "https://dev.clickai.live/customers/create-customer"
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    data = {
        "first_name": "David",
        "phone": "0529876543",
        "hasCustomFields": False
    }
    
    response = requests.post(url, headers=headers, json=data)
    return response.json()

# Usage
api_key = "your_api_key_here"
customer_id = create_customer_with_custom_fields(api_key)`,
      curl: `# Example 1: Customer with custom fields
curl -X POST "https://dev.clickai.live/customers/create-customer" \\
     -H "Authorization: Bearer your_api_key_here" \\
     -H "Content-Type: application/json" \\
     -d '{
       "first_name": "John",
       "last_name": "Smith",
       "email": "john@example.com",
       "phone": "0501234567",
       "address": "123 Main Street",
       "city": "Tel Aviv",
       "state": "Center",
       "country": "Israel",
       "status": "active",
       "hasCustomFields": true,
       "customFields": {
         "customer_type_123": "Business"
       }
     }'

# Example 2: Minimal customer
curl -X POST "https://dev.clickai.live/customers/create-customer" \\
     -H "Authorization: Bearer your_api_key_here" \\
     -H "Content-Type: application/json" \\
     -d '{
       "first_name": "David",
       "phone": "0529876543",
       "hasCustomFields": false
     }'`,
      powershell: `# PowerShell example for creating customers
$apiKey = "your_api_key_here"
$url = "https://dev.clickai.live/customers/create-customer"

$headers = @{
    "Authorization" = "Bearer $apiKey"
    "Content-Type" = "application/json"
}

# Example 1: Customer with custom fields
$customerData = @{
    first_name = "John"
    last_name = "Smith"
    email = "john@example.com"
    phone = "0501234567"
    address = "123 Main Street"
    city = "Tel Aviv"
    state = "Center"
    country = "Israel"
    status = "active"
    hasCustomFields = $true
    customFields = @{
        "customer_type_123" = "Business"
    }
} | ConvertTo-Json

try {
    $response = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $customerData
    
    if ($response.success) {
        Write-Host "Customer created with ID: $($response.customer_id)" -ForegroundColor Green
    }
} catch {
    Write-Host "Error: $($_.Exception.Message)" -ForegroundColor Red
}

# Example 2: Minimal customer
$minimalData = @{
    first_name = "David"
    phone = "0529876543"
    hasCustomFields = $false
} | ConvertTo-Json

$minimalResponse = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $minimalData`,
      php: `<?php
function createCustomerWithCustomFields($apiKey) {
    $url = 'https://dev.clickai.live/customers/create-customer';
    
    $data = array(
        'first_name' => 'John',
        'last_name' => 'Smith',
        'email' => 'john@example.com',
        'phone' => '0501234567',
        'address' => '123 Main Street',
        'city' => 'Tel Aviv',
        'state' => 'Center',
        'country' => 'Israel',
        'status' => 'active',
        'hasCustomFields' => true,
        'customFields' => array(
            'customer_type_123' => 'Business'
        )
    );
    
    $curl = curl_init();
    
    curl_setopt_array($curl, array(
        CURLOPT_URL => $url,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_POST => true,
        CURLOPT_POSTFIELDS => json_encode($data),
        CURLOPT_HTTPHEADER => array(
            'Authorization: Bearer ' . $apiKey,
            'Content-Type: application/json'
        ),
    ));
    
    $response = curl_exec($curl);
    $httpCode = curl_getinfo($curl, CURLINFO_HTTP_CODE);
    curl_close($curl);
    
    $result = json_decode($response, true);
    
    if ($httpCode == 201 && isset($result['success']) && $result['success']) {
        echo "Customer created with ID: " . $result['customer_id'] . "\\n";
        return $result['customer_id'];
    } else {
        echo "Error $httpCode: " . ($result['error'] ?? 'Unknown error') . "\\n";
        return null;
    }
}

function createMinimalCustomer($apiKey) {
    $url = 'https://dev.clickai.live/customers/create-customer';
    
    $data = array(
        'first_name' => 'David',
        'phone' => '0529876543',
        'hasCustomFields' => false
    );
    
    $curl = curl_init();
    curl_setopt_array($curl, array(
        CURLOPT_URL => $url,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_POST => true,
        CURLOPT_POSTFIELDS => json_encode($data),
        CURLOPT_HTTPHEADER => array(
            'Authorization: Bearer ' . $apiKey,
            'Content-Type: application/json'
        ),
    ));
    
    $response = curl_exec($curl);
    curl_close($curl);
    
    return json_decode($response, true);
}

// Usage
$apiKey = 'your_api_key_here';
$customerId = createCustomerWithCustomFields($apiKey);
$minimalResult = createMinimalCustomer($apiKey);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class CustomerCreationService
{
    private readonly HttpClient _httpClient;
    private readonly string _baseUrl = "https://dev.clickai.live";

    public CustomerCreationService()
    {
        _httpClient = new HttpClient();
    }

    public async Task<CustomerResponse> CreateCustomerWithCustomFieldsAsync(string apiKey)
    {
        try
        {
            _httpClient.DefaultRequestHeaders.Clear();
            _httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");

            var customerData = new
            {
                first_name = "John",
                last_name = "Smith",
                email = "john@example.com",
                phone = "0501234567",
                address = "123 Main Street",
                city = "Tel Aviv",
                state = "Center",
                country = "Israel",
                status = "active",
                hasCustomFields = true,
                customFields = new
                {
                    customer_type_123 = "Business"
                }
            };

            var json = JsonConvert.SerializeObject(customerData);
            var content = new StringContent(json, Encoding.UTF8, "application/json");

            var response = await _httpClient.PostAsync($"{_baseUrl}/customers/create-customer", content);
            var responseContent = await response.Content.ReadAsStringAsync();

            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<CustomerResponse>(responseContent);
                
                if (result.Success)
                {
                    Console.WriteLine($"Customer created with ID: {result.CustomerId}");
                }
                return result;
            }
            else
            {
                var errorData = JsonConvert.DeserializeObject<ErrorResponse>(responseContent);
                Console.WriteLine($"Error {(int)response.StatusCode}: {errorData.Error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
            return null;
        }
    }

    public async Task<CustomerResponse> CreateMinimalCustomerAsync(string apiKey)
    {
        var customerData = new
        {
            first_name = "David",
            phone = "0529876543",
            hasCustomFields = false
        };

        var json = JsonConvert.SerializeObject(customerData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");

        _httpClient.DefaultRequestHeaders.Clear();
        _httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");

        var response = await _httpClient.PostAsync($"{_baseUrl}/customers/create-customer", content);
        var responseContent = await response.Content.ReadAsStringAsync();

        return JsonConvert.DeserializeObject<CustomerResponse>(responseContent);
    }
}

public class CustomerResponse
{
    public bool Success { get; set; }
    [JsonProperty("customer_id")]
    public string CustomerId { get; set; }
    public string Message { get; set; }
}

public class ErrorResponse
{
    public string Error { get; set; }
}`,
      ruby: `require 'net/http'
require 'json'

class CustomerCreationService
  BASE_URL = 'https://dev.clickai.live'

  def self.create_customer_with_custom_fields(api_key)
    uri = URI("#{BASE_URL}/customers/create-customer")
    
    http = Net::HTTP.new(uri.host, uri.port)
    http.use_ssl = true

    request = Net::HTTP::Post.new(uri)
    request['Authorization'] = "Bearer #{api_key}"
    request['Content-Type'] = 'application/json'

    data = {
      first_name: "John",
      last_name: "Smith",
      email: "john@example.com",
      phone: "0501234567",
      address: "123 Main Street",
      city: "Tel Aviv",
      state: "Center",
      country: "Israel",
      status: "active",
      hasCustomFields: true,
      customFields: {
        "customer_type_123" => "Business"
      }
    }

    request.body = data.to_json

    begin
      response = http.request(request)
      result = JSON.parse(response.body)

      case response.code.to_i
      when 201
        if result['success']
          puts "Customer created with ID: #{result['customer_id']}"
          return result['customer_id']
        end
      else
        puts "Error #{response.code}: #{result['error'] || 'Unknown error'}"
        return nil
      end
    rescue StandardError => e
      puts "Request failed: #{e.message}"
      return nil
    end
  end

  def self.create_minimal_customer(api_key)
    uri = URI("#{BASE_URL}/customers/create-customer")
    
    http = Net::HTTP.new(uri.host, uri.port)
    http.use_ssl = true

    request = Net::HTTP::Post.new(uri)
    request['Authorization'] = "Bearer #{api_key}"
    request['Content-Type'] = 'application/json'

    data = {
      first_name: "David",
      phone: "0529876543",
      hasCustomFields: false
    }

    request.body = data.to_json
    response = http.request(request)
    JSON.parse(response.body)
  end
end

# Usage
api_key = 'your_api_key_here'
customer_id = CustomerCreationService.create_customer_with_custom_fields(api_key)
minimal_result = CustomerCreationService.create_minimal_customer(api_key)`,
      java: `import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.net.URI;
import com.google.gson.Gson;
import com.google.gson.JsonObject;

public class CustomerCreationAPI {
    private static final String BASE_URL = "https://dev.clickai.live";
    private static final HttpClient client = HttpClient.newHttpClient();
    private static final Gson gson = new Gson();

    public static CustomerResponse createCustomerWithCustomFields(String apiKey) {
        try {
            // Example 1: Customer with custom fields
            String jsonData = "{"
                + "\\"first_name\\":\\"John\\","
                + "\\"last_name\\":\\"Smith\\","
                + "\\"email\\":\\"john@example.com\\","
                + "\\"phone\\":\\"0501234567\\","
                + "\\"address\\":\\"123 Main Street\\","
                + "\\"city\\":\\"Tel Aviv\\","
                + "\\"state\\":\\"Center\\","
                + "\\"country\\":\\"Israel\\","
                + "\\"status\\":\\"active\\","
                + "\\"hasCustomFields\\":true,"
                + "\\"customFields\\":{\\"customer_type_123\\":\\"Business\\"}"
                + "}";

            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(BASE_URL + "/customers/create-customer"))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(jsonData))
                .build();

            HttpResponse<String> response = client.send(request, 
                HttpResponse.BodyHandlers.ofString());

            JsonObject result = gson.fromJson(response.body(), JsonObject.class);

            if (response.statusCode() == 201 && result.get("success").getAsBoolean()) {
                String customerId = result.get("customer_id").getAsString();
                System.out.println("Customer created with ID: " + customerId);
                return new CustomerResponse(true, customerId, "Customer created successfully");
            } else {
                String error = result.has("error") ? result.get("error").getAsString() : "Unknown error";
                System.out.println("Error " + response.statusCode() + ": " + error);
                return new CustomerResponse(false, null, error);
            }

        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
            return new CustomerResponse(false, null, e.getMessage());
        }
    }

    public static CustomerResponse createMinimalCustomer(String apiKey) {
        try {
            // Example 2: Minimal customer
            String jsonData = "{"
                + "\\"first_name\\":\\"David\\","
                + "\\"phone\\":\\"0529876543\\","
                + "\\"hasCustomFields\\":false"
                + "}";

            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(BASE_URL + "/customers/create-customer"))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(jsonData))
                .build();

            HttpResponse<String> response = client.send(request, 
                HttpResponse.BodyHandlers.ofString());

            JsonObject result = gson.fromJson(response.body(), JsonObject.class);
            
            if (result.get("success").getAsBoolean()) {
                return new CustomerResponse(true, result.get("customer_id").getAsString(), 
                    result.get("message").getAsString());
            } else {
                return new CustomerResponse(false, null, result.get("error").getAsString());
            }

        } catch (Exception e) {
            return new CustomerResponse(false, null, e.getMessage());
        }
    }

    public static void main(String[] args) {
        String apiKey = "your_api_key_here";
        
        CustomerResponse fullCustomer = createCustomerWithCustomFields(apiKey);
        CustomerResponse minimalCustomer = createMinimalCustomer(apiKey);
    }

    static class CustomerResponse {
        boolean success;
        String customerId;
        String message;

        CustomerResponse(boolean success, String customerId, String message) {
            this.success = success;
            this.customerId = customerId;
            this.message = message;
        }
    }
}`
    }
  },

  'customers-edit-customer': {
    id: 'customers-edit-customer',
    title: 'Edit customer',
    category: 'Customers',
    categoryId: 'customers',
    method: 'POST',
    path: '/customers/edit-customer',
    description: 'Updates an existing customer by ID. Only the customer_id field is required - all other fields are optional and will only be updated if provided. Supports intelligent custom field updates that preserve existing fields not included in the request.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'customer_id',
        type: 'string',
        required: true,
        description: 'UUID of the customer to update',
        example: 'e768ecd5-a2dd-41fe-8823-4846de51b9f9'
      },
      {
        name: 'first_name',
        type: 'string',
        required: false,
        description: 'Customer first name',
        example: 'John Updated'
      },
      {
        name: 'last_name',
        type: 'string',
        required: false,
        description: 'Customer last name',
        example: 'Smith Updated'
      },
      {
        name: 'email',
        type: 'string',
        required: false,
        description: 'Customer email address',
        example: 'john.updated@example.com'
      },
      {
        name: 'phone',
        type: 'string',
        required: false,
        description: 'Customer phone number',
        example: '0509999999'
      },
      {
        name: 'address',
        type: 'string',
        required: false,
        description: 'Street address',
        example: '456 Updated Street'
      },
      {
        name: 'city',
        type: 'string',
        required: false,
        description: 'City name',
        example: 'Tel Aviv'
      },
      {
        name: 'state',
        type: 'string',
        required: false,
        description: 'State/region',
        example: 'Center'
      },
      {
        name: 'country',
        type: 'string',
        required: false,
        description: 'Country name',
        example: 'Israel'
      },
      {
        name: 'status',
        type: 'string',
        required: false,
        description: 'Customer status (new, active, inactive, etc.)',
        example: 'active'
      },
      {
        name: 'received',
        type: 'string',
        required: false,
        description: 'How customer was acquired/received',
        example: 'website_form'
      },
      {
        name: 'profileImageLink',
        type: 'string',
        required: false,
        description: 'URL to customer profile image',
        example: 'https://example.com/profile-updated.jpg'
      },
      {
        name: 'next_call',
        type: 'string',
        required: false,
        description: 'Next scheduled call timestamp (ISO 8601)',
        example: '2024-03-01T15:00:00Z'
      },
      {
        name: 'shopify_id',
        type: 'string',
        required: false,
        description: 'Shopify customer ID for integration',
        example: '67890'
      },
      {
        name: 'woocommerce_id',
        type: 'string',
        required: false,
        description: 'WooCommerce customer ID for integration',
        example: '13579'
      },
      {
        name: 'hasCustomFields',
        type: 'boolean',
        required: false,
        description: 'Whether to update custom fields. false = delete all, true = update specified fields',
        example: 'true'
      },
      {
        name: 'customFields',
        type: 'object',
        required: false,
        description: 'Custom fields to update (key-value pairs). Only specified fields are changed',
        example: '{"customer_type_1749674077124": "Premium", "priority_level": "High"}'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Customer updated successfully',
        example: JSON.stringify({
          "success": true,
          "message": "Customer updated successfully",
          "customer_id": "e768ecd5-a2dd-41fe-8823-4846de51b9f9"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing customer_id or invalid data',
        example: JSON.stringify({
          "error": "Missing required field: customer_id"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          "error": "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          "error": "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Customer not found',
        example: JSON.stringify({
          "error": "Customer not found or does not belong to your organization"
        }, null, 2)
      },
      {
        code: '422',
        description: 'Unprocessable Entity - Invalid custom field key',
        example: JSON.stringify({
          "error": "Custom field key 'invalid_key' does not exist for this organization"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          "error": "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `// Example 1: Basic field updates
const response = await fetch('https://dev.clickai.live/customers/edit-customer', {
    method: 'POST',
    headers: {
        'Authorization': 'Bearer your_api_key_here',
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        customer_id: "e768ecd5-a2dd-41fe-8823-4846de51b9f9",
        first_name: "John Updated",
        email: "john.updated@example.com",
        status: "active",
        address: "456 Updated Street",
        city: "Tel Aviv"
    })
});

const data = await response.json();

if (response.ok && data.success) {
    console.log(\`Customer \${data.customer_id} updated successfully\`);
} else {
    console.error('Error:', data.error || 'Unknown error');
}

// Example 2: Update with custom fields
const responseWithCustomFields = await fetch('https://dev.clickai.live/customers/edit-customer', {
    method: 'POST',
    headers: {
        'Authorization': 'Bearer your_api_key_here',
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        customer_id: "e768ecd5-a2dd-41fe-8823-4846de51b9f9",
        first_name: "John",
        phone: "0509999999",
        hasCustomFields: true,
        customFields: {
            "customer_type_1749674077124": "Premium",
            "priority_level": "High"
        }
    })
});

const customFieldsData = await responseWithCustomFields.json();
console.log('Custom fields update:', customFieldsData);`,
      python: `import requests
import json

def update_customer_basic(api_key, customer_id, updates):
    """Update customer with basic fields only"""
    url = "https://dev.clickai.live/customers/edit-customer"
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    data = {"customer_id": customer_id, **updates}
    
    try:
        response = requests.post(url, headers=headers, json=data)
        result = response.json()
        
        if response.status_code == 200 and result.get("success"):
            print(f"Customer {result['customer_id']} updated successfully")
            return result
        else:
            print(f"Error {response.status_code}: {result.get('error', 'Unknown error')}")
            return None
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

def update_customer_with_custom_fields(api_key, customer_id, basic_updates, custom_fields):
    """Update customer with custom fields"""
    url = "https://dev.clickai.live/customers/edit-customer"
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    data = {
        "customer_id": customer_id,
        "hasCustomFields": True,
        "customFields": custom_fields,
        **basic_updates
    }
    
    try:
        response = requests.post(url, headers=headers, json=data)
        result = response.json()
        
        if response.status_code == 200 and result.get("success"):
            print(f"Customer {result['customer_id']} updated with custom fields")
            return result
        else:
            print(f"Error {response.status_code}: {result.get('error', 'Unknown error')}")
            return None
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

def delete_customer_custom_fields(api_key, customer_id):
    """Delete all custom fields for a customer"""
    url = "https://dev.clickai.live/customers/edit-customer"
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    data = {
        "customer_id": customer_id,
        "hasCustomFields": False
    }
    
    try:
        response = requests.post(url, headers=headers, json=data)
        result = response.json()
        
        if response.status_code == 200 and result.get("success"):
            print(f"All custom fields deleted for customer {result['customer_id']}")
            return result
        else:
            print(f"Error {response.status_code}: {result.get('error', 'Unknown error')}")
            return None
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

# Usage examples
api_key = "your_api_key_here"
customer_id = "e768ecd5-a2dd-41fe-8823-4846de51b9f9"

# Example 1: Basic update
basic_updates = {
    "first_name": "John Updated",
    "email": "john.updated@example.com",
    "status": "active",
    "address": "456 Updated Street"
}
update_customer_basic(api_key, customer_id, basic_updates)

# Example 2: Update with custom fields
custom_fields = {
    "customer_type_1749674077124": "Premium",
    "priority_level": "High"
}
update_customer_with_custom_fields(api_key, customer_id, {"phone": "0509999999"}, custom_fields)

# Example 3: Delete all custom fields
delete_customer_custom_fields(api_key, customer_id)`,
      curl: `# Example 1: Basic field updates
curl -X POST "https://dev.clickai.live/customers/edit-customer" \\
     -H "Authorization: Bearer your_api_key_here" \\
     -H "Content-Type: application/json" \\
     -d '{
       "customer_id": "e768ecd5-a2dd-41fe-8823-4846de51b9f9",
       "first_name": "John Updated",
       "email": "john.updated@example.com", 
       "status": "active",
       "address": "456 Updated Street",
       "city": "Tel Aviv"
     }'

# Example 2: Update with custom fields
curl -X POST "https://dev.clickai.live/customers/edit-customer" \\
     -H "Authorization: Bearer your_api_key_here" \\
     -H "Content-Type: application/json" \\
     -d '{
       "customer_id": "e768ecd5-a2dd-41fe-8823-4846de51b9f9",
       "first_name": "John",
       "phone": "0509999999",
       "hasCustomFields": true,
       "customFields": {
         "customer_type_1749674077124": "Premium",
         "priority_level": "High"
       }
     }'`,
      powershell: `# PowerShell example for editing customers
$apiKey = "your_api_key_here"
$url = "https://dev.clickai.live/customers/edit-customer"

$headers = @{
    "Authorization" = "Bearer $apiKey"
    "Content-Type" = "application/json"
}

# Example 1: Basic field updates
$basicUpdateBody = @{
    customer_id = "e768ecd5-a2dd-41fe-8823-4846de51b9f9"
    first_name = "John Updated"
    email = "john.updated@example.com"
    status = "active"
    address = "456 Updated Street"
    city = "Tel Aviv"
} | ConvertTo-Json

try {
    $response = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $basicUpdateBody
    
    if ($response.success) {
        Write-Host "Customer $($response.customer_id) updated successfully" -ForegroundColor Green
    }
} catch {
    Write-Host "Error: $($_.Exception.Message)" -ForegroundColor Red
}

# Example 2: Update with custom fields
$customFieldsBody = @{
    customer_id = "e768ecd5-a2dd-41fe-8823-4846de51b9f9"
    first_name = "John"
    phone = "0509999999"
    hasCustomFields = $true
    customFields = @{
        "customer_type_1749674077124" = "Premium"
        "priority_level" = "High"
    }
} | ConvertTo-Json -Depth 3

try {
    $customResponse = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $customFieldsBody
    
    if ($customResponse.success) {
        Write-Host "Customer $($customResponse.customer_id) updated with custom fields" -ForegroundColor Green
    }
} catch {
    Write-Host "Custom fields update error: $($_.Exception.Message)" -ForegroundColor Red
}`,
      php: `<?php
function updateCustomerBasic($apiKey, $customerId, $updates) {
    $url = 'https://dev.clickai.live/customers/edit-customer';
    
    $data = array_merge(['customer_id' => $customerId], $updates);
    
    $curl = curl_init();
    
    curl_setopt_array($curl, array(
        CURLOPT_URL => $url,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_POST => true,
        CURLOPT_POSTFIELDS => json_encode($data),
        CURLOPT_HTTPHEADER => array(
            'Authorization: Bearer ' . $apiKey,
            'Content-Type: application/json'
        ),
    ));
    
    $response = curl_exec($curl);
    $httpCode = curl_getinfo($curl, CURLINFO_HTTP_CODE);
    curl_close($curl);
    
    $result = json_decode($response, true);
    
    if ($httpCode == 200 && isset($result['success']) && $result['success']) {
        echo "Customer " . $result['customer_id'] . " updated successfully\\n";
        return $result;
    } else {
        echo "Error $httpCode: " . ($result['error'] ?? 'Unknown error') . "\\n";
        return null;
    }
}

function updateCustomerWithCustomFields($apiKey, $customerId, $basicUpdates, $customFields) {
    $url = 'https://dev.clickai.live/customers/edit-customer';
    
    $data = array_merge([
        'customer_id' => $customerId,
        'hasCustomFields' => true,
        'customFields' => $customFields
    ], $basicUpdates);
    
    $curl = curl_init();
    
    curl_setopt_array($curl, array(
        CURLOPT_URL => $url,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_POST => true,
        CURLOPT_POSTFIELDS => json_encode($data),
        CURLOPT_HTTPHEADER => array(
            'Authorization: Bearer ' . $apiKey,
            'Content-Type: application/json'
        ),
    ));
    
    $response = curl_exec($curl);
    $httpCode = curl_getinfo($curl, CURLINFO_HTTP_CODE);
    curl_close($curl);
    
    $result = json_decode($response, true);
    
    if ($httpCode == 200 && isset($result['success']) && $result['success']) {
        echo "Customer " . $result['customer_id'] . " updated with custom fields\\n";
        return $result;
    } else {
        echo "Error $httpCode: " . ($result['error'] ?? 'Unknown error') . "\\n";
        return null;
    }
}

// Usage examples
$apiKey = 'your_api_key_here';
$customerId = 'e768ecd5-a2dd-41fe-8823-4846de51b9f9';

// Example 1: Basic update
$basicUpdates = array(
    'first_name' => 'John Updated',
    'email' => 'john.updated@example.com',
    'status' => 'active',
    'address' => '456 Updated Street'
);
updateCustomerBasic($apiKey, $customerId, $basicUpdates);

// Example 2: Update with custom fields
$customFields = array(
    'customer_type_1749674077124' => 'Premium',
    'priority_level' => 'High'
);
updateCustomerWithCustomFields($apiKey, $customerId, ['phone' => '0509999999'], $customFields);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class CustomerUpdateService
{
    private readonly HttpClient _httpClient;
    private readonly string _baseUrl = "https://dev.clickai.live";

    public CustomerUpdateService()
    {
        _httpClient = new HttpClient();
    }

    public async Task<CustomerUpdateResponse> UpdateCustomerBasicAsync(string apiKey, string customerId, object updates)
    {
        try
        {
            _httpClient.DefaultRequestHeaders.Clear();
            _httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");

            var requestData = new 
            {
                customer_id = customerId,
                first_name = "John Updated",
                email = "john.updated@example.com",
                status = "active",
                address = "456 Updated Street",
                city = "Tel Aviv"
            };

            var json = JsonConvert.SerializeObject(requestData);
            var content = new StringContent(json, Encoding.UTF8, "application/json");

            var response = await _httpClient.PostAsync($"{_baseUrl}/customers/edit-customer", content);
            var responseContent = await response.Content.ReadAsStringAsync();

            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<CustomerUpdateResponse>(responseContent);
                
                if (result.Success)
                {
                    Console.WriteLine($"Customer {result.CustomerId} updated successfully");
                }
                return result;
            }
            else
            {
                var errorData = JsonConvert.DeserializeObject<ErrorResponse>(responseContent);
                Console.WriteLine($"Error {(int)response.StatusCode}: {errorData.Error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
            return null;
        }
    }

    public async Task<CustomerUpdateResponse> UpdateCustomerWithCustomFieldsAsync(string apiKey, string customerId)
    {
        try
        {
            _httpClient.DefaultRequestHeaders.Clear();
            _httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");

            var requestData = new 
            {
                customer_id = customerId,
                first_name = "John",
                phone = "0509999999",
                hasCustomFields = true,
                customFields = new
                {
                    customer_type_1749674077124 = "Premium",
                    priority_level = "High"
                }
            };

            var json = JsonConvert.SerializeObject(requestData);
            var content = new StringContent(json, Encoding.UTF8, "application/json");

            var response = await _httpClient.PostAsync($"{_baseUrl}/customers/edit-customer", content);
            var responseContent = await response.Content.ReadAsStringAsync();

            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<CustomerUpdateResponse>(responseContent);
                
                if (result.Success)
                {
                    Console.WriteLine($"Customer {result.CustomerId} updated with custom fields");
                }
                return result;
            }
            else
            {
                var errorData = JsonConvert.DeserializeObject<ErrorResponse>(responseContent);
                Console.WriteLine($"Error {(int)response.StatusCode}: {errorData.Error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
            return null;
        }
    }
}

public class CustomerUpdateResponse
{
    public bool Success { get; set; }
    public string Message { get; set; }
    [JsonProperty("customer_id")]
    public string CustomerId { get; set; }
}

public class ErrorResponse
{
    public string Error { get; set; }
}`,
      ruby: `require 'net/http'
require 'json'

class CustomerUpdateService
  BASE_URL = 'https://dev.clickai.live'

  def self.update_customer_basic(api_key, customer_id, updates)
    uri = URI("#{BASE_URL}/customers/edit-customer")
    
    http = Net::HTTP.new(uri.host, uri.port)
    http.use_ssl = true

    request = Net::HTTP::Post.new(uri)
    request['Authorization'] = "Bearer #{api_key}"
    request['Content-Type'] = 'application/json'

    data = { customer_id: customer_id }.merge(updates)
    request.body = data.to_json

    begin
      response = http.request(request)
      result = JSON.parse(response.body)

      case response.code.to_i
      when 200
        if result['success']
          puts "Customer #{result['customer_id']} updated successfully"
          return result
        end
      else
        puts "Error #{response.code}: #{result['error'] || 'Unknown error'}"
        return nil
      end
    rescue StandardError => e
      puts "Request failed: #{e.message}"
      return nil
    end
  end

  def self.update_customer_with_custom_fields(api_key, customer_id, basic_updates, custom_fields)
    uri = URI("#{BASE_URL}/customers/edit-customer")
    
    http = Net::HTTP.new(uri.host, uri.port)
    http.use_ssl = true

    request = Net::HTTP::Post.new(uri)
    request['Authorization'] = "Bearer #{api_key}"
    request['Content-Type'] = 'application/json'

    data = {
      customer_id: customer_id,
      hasCustomFields: true,
      customFields: custom_fields
    }.merge(basic_updates)
    
    request.body = data.to_json

    begin
      response = http.request(request)
      result = JSON.parse(response.body)

      case response.code.to_i
      when 200
        if result['success']
          puts "Customer #{result['customer_id']} updated with custom fields"
          return result
        end
      else
        puts "Error #{response.code}: #{result['error'] || 'Unknown error'}"
        return nil
      end
    rescue StandardError => e
      puts "Request failed: #{e.message}"
      return nil
    end
  end
end

# Usage examples
api_key = 'your_api_key_here'
customer_id = 'e768ecd5-a2dd-41fe-8823-4846de51b9f9'

# Example 1: Basic update
basic_updates = {
  first_name: 'John Updated',
  email: 'john.updated@example.com',
  status: 'active',
  address: '456 Updated Street'
}
CustomerUpdateService.update_customer_basic(api_key, customer_id, basic_updates)

# Example 2: Update with custom fields
custom_fields = {
  'customer_type_1749674077124' => 'Premium',
  'priority_level' => 'High'
}
CustomerUpdateService.update_customer_with_custom_fields(api_key, customer_id, { phone: '0509999999' }, custom_fields)`,
      java: `import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.net.URI;
import com.google.gson.Gson;
import com.google.gson.JsonObject;

public class CustomerUpdateAPI {
    private static final String BASE_URL = "https://dev.clickai.live";
    private static final HttpClient client = HttpClient.newHttpClient();
    private static final Gson gson = new Gson();

    public static CustomerUpdateResult updateCustomerBasic(String apiKey, String customerId) {
        try {
            String jsonData = "{"
                + "\\"customer_id\\":\\"" + customerId + "\\","
                + "\\"first_name\\":\\"John Updated\\","
                + "\\"email\\":\\"john.updated@example.com\\","
                + "\\"status\\":\\"active\\","
                + "\\"address\\":\\"456 Updated Street\\","
                + "\\"city\\":\\"Tel Aviv\\""
                + "}";

            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(BASE_URL + "/customers/edit-customer"))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(jsonData))
                .build();

            HttpResponse<String> response = client.send(request, 
                HttpResponse.BodyHandlers.ofString());

            JsonObject result = gson.fromJson(response.body(), JsonObject.class);

            if (response.statusCode() == 200 && result.get("success").getAsBoolean()) {
                String updatedCustomerId = result.get("customer_id").getAsString();
                System.out.println("Customer " + updatedCustomerId + " updated successfully");
                return new CustomerUpdateResult(true, updatedCustomerId);
            } else {
                String error = result.has("error") ? result.get("error").getAsString() : "Unknown error";
                System.out.println("Error " + response.statusCode() + ": " + error);
                return new CustomerUpdateResult(false, customerId);
            }

        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
            return new CustomerUpdateResult(false, customerId);
        }
    }

    public static CustomerUpdateResult updateCustomerWithCustomFields(String apiKey, String customerId) {
        try {
            String jsonData = "{"
                + "\\"customer_id\\":\\"" + customerId + "\\","
                + "\\"first_name\\":\\"John\\","
                + "\\"phone\\":\\"0509999999\\","
                + "\\"hasCustomFields\\":true,"
                + "\\"customFields\\":{"
                + "\\"customer_type_1749674077124\\":\\"Premium\\","
                + "\\"priority_level\\":\\"High\\""
                + "}"
                + "}";

            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(BASE_URL + "/customers/edit-customer"))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(jsonData))
                .build();

            HttpResponse<String> response = client.send(request, 
                HttpResponse.BodyHandlers.ofString());

            JsonObject result = gson.fromJson(response.body(), JsonObject.class);

            if (response.statusCode() == 200 && result.get("success").getAsBoolean()) {
                String updatedCustomerId = result.get("customer_id").getAsString();
                System.out.println("Customer " + updatedCustomerId + " updated with custom fields");
                return new CustomerUpdateResult(true, updatedCustomerId);
            } else {
                String error = result.has("error") ? result.get("error").getAsString() : "Unknown error";
                System.out.println("Error " + response.statusCode() + ": " + error);
                return new CustomerUpdateResult(false, customerId);
            }

        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
            return new CustomerUpdateResult(false, customerId);
        }
    }

    public static void main(String[] args) {
        String apiKey = "your_api_key_here";
        String customerId = "e768ecd5-a2dd-41fe-8823-4846de51b9f9";
        
        // Example 1: Basic update
        CustomerUpdateResult basicResult = updateCustomerBasic(apiKey, customerId);
        
        // Example 2: Update with custom fields
        CustomerUpdateResult customFieldsResult = updateCustomerWithCustomFields(apiKey, customerId);
    }

    static class CustomerUpdateResult {
        boolean success;
        String customerId;

        CustomerUpdateResult(boolean success, String customerId) {
            this.success = success;
            this.customerId = customerId;
        }
    }
}`
    }
  },

  'customers-get-customers': {
    id: 'customers-get-customers',
    title: 'Get all customers',
    category: 'Customers',
    categoryId: 'customers',
    method: 'GET',
    path: '/customers/get-customers',
    description: 'Retrieve a list of all customers in your organization.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success',
        example: JSON.stringify({
          success: true,
          data: [
            {
              id: 'cust_01H9XK2F5Z6G7ABCDEFGHI',
              first_name: 'ישראל',
              last_name: 'ישראלי',
              phone: '0501234567',
              email: 'israel@example.com',
              company: 'חברת הדוגמה בעמ'
            }
          ]
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/customers/get-customers';

fetch(url, {
  method: 'GET',
  headers: {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => console.log(data));`,
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/customers/get-customers'

response = requests.get(url, 
    headers={'Authorization': f'Bearer {api_key}'})
print(response.json())`,
      curl: `curl -X GET "https://dev.clickai.live/customers/get-customers" \\
  -H "Authorization: Bearer your_api_key_here"`,
      php: `<?php
$api_key = 'your_api_key_here';

$ch = curl_init('https://dev.clickai.live/customers/get-customers');
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Authorization: Bearer ' . $api_key
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

echo curl_exec($ch);
curl_close($ch);
?>`,
      ruby: `require 'net/http'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/customers/get-customers')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = "Bearer #{api_key}"

puts http.request(request).body`
    }
  },

  'customers-get-customer-phone': {
    id: 'customers-get-customer-phone',
    title: 'Get customers by phone number',
    category: 'Customers',
    categoryId: 'customers',
    method: 'POST',
    path: '/customers/get-customer/phone',
    description: 'Searches for customers by phone number within your organization. This endpoint can return multiple customers if they share the same phone number. Returns complete customer details including all personal information, contact details, address, integration IDs, and all custom fields configured for your organization.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'phone',
        type: 'string',
        required: true,
        description: 'Phone number to search for',
        example: '0501234567'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Returns customers found (array may be empty)',
        example: JSON.stringify({
          "success": true,
          "data": [
            {
              "customer_id": "091cd08c-5dc3-456d-9d43-8918a549cfea",
              "first_name": "John",
              "last_name": "Smith",
              "email": "john@example.com",
              "phone": "0501234567",
              "address": "123 Main Street",
              "city": "Tel Aviv",
              "state": "Center",
              "country": "Israel",
              "status": "active",
              "received": "website_form",
              "profileImageLink": "https://example.com/profile.jpg",
              "next_call": "2024-02-01T10:00:00Z",
              "shopify_id": "12345",
              "woocommerce_id": "67890",
              "created_at": "2024-01-01T12:00:00Z",
              "updated_at": "2024-01-15T14:30:00Z",
              "customFields": [
                {
                  "key": "customer_type_1749674077124",
                  "content": "Business",
                  "label": "Customer Type",
                  "type": "select",
                  "area": "Customer Details"
                },
                {
                  "key": "priority_level_1749674482288",
                  "content": "High",
                  "label": "Priority Level",
                  "type": "text",
                  "area": "Customer Details"
                },
                {
                  "key": "notes_field_1749674482289",
                  "content": "N/A",
                  "label": "Additional Notes",
                  "type": "textarea",
                  "area": "Notes"
                }
              ]
            }
          ],
          "count": 1,
          "searched_phone": "0501234567"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing phone field or invalid request',
        example: JSON.stringify({
          "error": "Missing required field: phone"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          "error": "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          "error": "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          "error": "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const response = await fetch('https://dev.clickai.live/customers/get-customer/phone', {
    method: 'POST',
    headers: {
        'Authorization': 'Bearer your_api_key_here',
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        phone: "0501234567"
    })
});

const data = await response.json();

if (response.ok && data.success) {
    console.log(\`Found \${data.count} customer(s) with phone \${data.searched_phone}\`);
    
    data.data.forEach(customer => {
        console.log(\`Customer: \${customer.first_name} \${customer.last_name}\`);
        console.log(\`ID: \${customer.customer_id}\`);
        console.log(\`Email: \${customer.email}\`);
        console.log(\`Status: \${customer.status}\`);
        console.log(\`Address: \${customer.address}, \${customer.city}, \${customer.country}\`);
        
        // Display custom fields with values
        console.log('Custom Fields:');
        customer.customFields.forEach(field => {
            if (field.content !== "N/A") {
                console.log(\`  \${field.label}: \${field.content} (\${field.type})\`);
            }
        });
        
        // Display integration IDs if available
        if (customer.shopify_id !== 'N/A') {
            console.log(\`Shopify ID: \${customer.shopify_id}\`);
        }
        if (customer.woocommerce_id !== 'N/A') {
            console.log(\`WooCommerce ID: \${customer.woocommerce_id}\`);
        }
        console.log("---");
    });
    
    if (data.count === 0) {
        console.log("No customers found with this phone number");
    }
} else {
    console.error('Error:', data.error || 'Unknown error');
}`,
      python: `import requests
import json
from datetime import datetime

def search_customers_by_phone(api_key, phone_number):
    url = "https://dev.clickai.live/customers/get-customer/phone"
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    data = {
        "phone": phone_number
    }
    
    try:
        response = requests.post(url, headers=headers, json=data)
        result = response.json()
        
        if response.status_code == 200 and result.get("success"):
            customers = result["data"]
            count = result["count"]
            searched_phone = result["searched_phone"]
            
            print(f"Found {count} customer(s) with phone {searched_phone}")
            
            if count == 0:
                print("No customers found with this phone number")
                return []
            
            for i, customer in enumerate(customers, 1):
                print(f"\\n--- Customer {i} ---")
                print(f"Customer: {customer['first_name']} {customer['last_name']}")
                print(f"ID: {customer['customer_id']}")
                print(f"Email: {customer['email']}")
                print(f"Status: {customer['status']}")
                print(f"Address: {customer['address']}, {customer['city']}, {customer['country']}")
                
                # Display timestamps
                created_date = datetime.fromisoformat(customer['created_at'].replace('Z', '+00:00'))
                updated_date = datetime.fromisoformat(customer['updated_at'].replace('Z', '+00:00'))
                print(f"Created: {created_date.strftime('%Y-%m-%d %H:%M')}")
                print(f"Updated: {updated_date.strftime('%Y-%m-%d %H:%M')}")
                
                # Display custom fields with values
                print("\\nCustom Fields:")
                for field in customer["customFields"]:
                    if field["content"] != "N/A":
                        print(f"  {field['label']}: {field['content']} ({field['type']})")
                
                # Display integration IDs if available
                if customer.get("shopify_id") != "N/A":
                    print(f"\\nShopify ID: {customer['shopify_id']}")
                if customer.get("woocommerce_id") != "N/A":
                    print(f"WooCommerce ID: {customer['woocommerce_id']}")
            
            return customers
        else:
            print(f"Error {response.status_code}: {result.get('error', 'Unknown error')}")
            return None
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

# Usage
api_key = "your_api_key_here"
phone_number = "0501234567"
customers = search_customers_by_phone(api_key, phone_number)`,
      curl: `# Search for customers by phone number
curl -X POST "https://dev.clickai.live/customers/get-customer/phone" \\
     -H "Authorization: Bearer your_api_key_here" \\
     -H "Content-Type: application/json" \\
     -d '{
       "phone": "0501234567"
     }'`,
      powershell: `# PowerShell example for searching customers by phone
$apiKey = "your_api_key_here"
$url = "https://dev.clickai.live/customers/get-customer/phone"

$headers = @{
    "Authorization" = "Bearer $apiKey"
    "Content-Type" = "application/json"
}

$body = @{
    phone = "0501234567"
} | ConvertTo-Json

try {
    $response = Invoke-RestMethod -Uri $url -Method Post -Headers $headers -Body $body
    
    if ($response.success) {
        Write-Host "Found $($response.count) customer(s) with phone $($response.searched_phone)" -ForegroundColor Green
        
        if ($response.count -eq 0) {
            Write-Host "No customers found with this phone number" -ForegroundColor Yellow
        } else {
            $customerNum = 1
            foreach ($customer in $response.data) {
                Write-Host "\\n--- Customer $customerNum ---" -ForegroundColor Cyan
                Write-Host "Customer: $($customer.first_name) $($customer.last_name)"
                Write-Host "ID: $($customer.customer_id)"
                Write-Host "Email: $($customer.email)"
                Write-Host "Status: $($customer.status)"
                Write-Host "Address: $($customer.address), $($customer.city), $($customer.country)"
                
                Write-Host "\\nCustom Fields:" -ForegroundColor Yellow
                foreach ($field in $customer.customFields) {
                    if ($field.content -ne "N/A") {
                        Write-Host "  $($field.label): $($field.content) ($($field.type))"
                    }
                }
                
                if ($customer.shopify_id -ne "N/A") {
                    Write-Host "\\nShopify ID: $($customer.shopify_id)" -ForegroundColor Magenta
                }
                if ($customer.woocommerce_id -ne "N/A") {
                    Write-Host "WooCommerce ID: $($customer.woocommerce_id)" -ForegroundColor Magenta
                }
                $customerNum++
            }
        }
    }
} catch {
    Write-Host "Error: $($_.Exception.Message)" -ForegroundColor Red
}`,
      php: `<?php
function searchCustomersByPhone($apiKey, $phoneNumber) {
    $url = 'https://dev.clickai.live/customers/get-customer/phone';
    
    $data = array(
        'phone' => $phoneNumber
    );
    
    $curl = curl_init();
    
    curl_setopt_array($curl, array(
        CURLOPT_URL => $url,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_POST => true,
        CURLOPT_POSTFIELDS => json_encode($data),
        CURLOPT_HTTPHEADER => array(
            'Authorization: Bearer ' . $apiKey,
            'Content-Type: application/json'
        ),
    ));
    
    $response = curl_exec($curl);
    $httpCode = curl_getinfo($curl, CURLINFO_HTTP_CODE);
    curl_close($curl);
    
    $result = json_decode($response, true);
    
    if ($httpCode == 200 && isset($result['success']) && $result['success']) {
        $customers = $result['data'];
        $count = $result['count'];
        $searchedPhone = $result['searched_phone'];
        
        echo "Found $count customer(s) with phone $searchedPhone\\n";
        
        if ($count == 0) {
            echo "No customers found with this phone number\\n";
            return array();
        }
        
        foreach ($customers as $index => $customer) {
            $customerNum = $index + 1;
            echo "\\n--- Customer $customerNum ---\\n";
            echo "Customer: " . $customer['first_name'] . " " . $customer['last_name'] . "\\n";
            echo "ID: " . $customer['customer_id'] . "\\n";
            echo "Email: " . $customer['email'] . "\\n";
            echo "Status: " . $customer['status'] . "\\n";
            echo "Address: " . $customer['address'] . ", " . $customer['city'] . ", " . $customer['country'] . "\\n";
            
            // Display timestamps
            $createdDate = new DateTime($customer['created_at']);
            $updatedDate = new DateTime($customer['updated_at']);
            echo "Created: " . $createdDate->format('Y-m-d H:i') . "\\n";
            echo "Updated: " . $updatedDate->format('Y-m-d H:i') . "\\n";
            
            // Display custom fields with values
            echo "\\nCustom Fields:\\n";
            foreach ($customer['customFields'] as $field) {
                if ($field['content'] !== 'N/A') {
                    echo "  " . $field['label'] . ": " . $field['content'] . " (" . $field['type'] . ")\\n";
                }
            }
            
            // Display integration IDs if available
            if (isset($customer['shopify_id']) && $customer['shopify_id'] !== 'N/A') {
                echo "\\nShopify ID: " . $customer['shopify_id'] . "\\n";
            }
            if (isset($customer['woocommerce_id']) && $customer['woocommerce_id'] !== 'N/A') {
                echo "WooCommerce ID: " . $customer['woocommerce_id'] . "\\n";
            }
        }
        
        return $customers;
    } else {
        echo "Error $httpCode: " . ($result['error'] ?? 'Unknown error') . "\\n";
        return null;
    }
}

// Usage
$apiKey = 'your_api_key_here';
$phoneNumber = '0501234567';
$customers = searchCustomersByPhone($apiKey, $phoneNumber);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class CustomerSearchService
{
    private readonly HttpClient _httpClient;
    private readonly string _baseUrl = "https://dev.clickai.live";

    public CustomerSearchService()
    {
        _httpClient = new HttpClient();
    }

    public async Task<CustomerSearchResponse> SearchCustomersByPhoneAsync(string apiKey, string phoneNumber)
    {
        try
        {
            _httpClient.DefaultRequestHeaders.Clear();
            _httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");

            var requestData = new { phone = phoneNumber };
            var json = JsonConvert.SerializeObject(requestData);
            var content = new StringContent(json, Encoding.UTF8, "application/json");

            var response = await _httpClient.PostAsync($"{_baseUrl}/customers/get-customer/phone", content);
            var responseContent = await response.Content.ReadAsStringAsync();

            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<CustomerSearchResponse>(responseContent);
                
                if (result.Success)
                {
                    Console.WriteLine($"Found {result.Count} customer(s) with phone {result.SearchedPhone}");
                    
                    if (result.Count == 0)
                    {
                        Console.WriteLine("No customers found with this phone number");
                    }
                    else
                    {
                        for (int i = 0; i < result.Data.Length; i++)
                        {
                            var customer = result.Data[i];
                            Console.WriteLine($"\\n--- Customer {i + 1} ---");
                            Console.WriteLine($"Customer: {customer.FirstName} {customer.LastName}");
                            Console.WriteLine($"ID: {customer.CustomerId}");
                            Console.WriteLine($"Email: {customer.Email}");
                            Console.WriteLine($"Status: {customer.Status}");
                            Console.WriteLine($"Address: {customer.Address}, {customer.City}, {customer.Country}");
                            
                            Console.WriteLine("\\nCustom Fields:");
                            foreach (var field in customer.CustomFields)
                            {
                                if (field.Content != "N/A")
                                {
                                    Console.WriteLine($"  {field.Label}: {field.Content} ({field.Type})");
                                }
                            }
                            
                            if (customer.ShopifyId != "N/A")
                                Console.WriteLine($"\\nShopify ID: {customer.ShopifyId}");
                            if (customer.WooCommerceId != "N/A")
                                Console.WriteLine($"WooCommerce ID: {customer.WooCommerceId}");
                        }
                    }
                }
                return result;
            }
            else
            {
                var errorData = JsonConvert.DeserializeObject<ErrorResponse>(responseContent);
                Console.WriteLine($"Error {(int)response.StatusCode}: {errorData.Error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
            return null;
        }
    }
}

public class CustomerSearchResponse
{
    public bool Success { get; set; }
    public Customer[] Data { get; set; }
    public int Count { get; set; }
    [JsonProperty("searched_phone")]
    public string SearchedPhone { get; set; }
}

public class Customer
{
    [JsonProperty("customer_id")]
    public string CustomerId { get; set; }
    [JsonProperty("first_name")]
    public string FirstName { get; set; }
    [JsonProperty("last_name")]
    public string LastName { get; set; }
    public string Email { get; set; }
    public string Phone { get; set; }
    public string Address { get; set; }
    public string City { get; set; }
    public string State { get; set; }
    public string Country { get; set; }
    public string Status { get; set; }
    [JsonProperty("shopify_id")]
    public string ShopifyId { get; set; }
    [JsonProperty("woocommerce_id")]
    public string WooCommerceId { get; set; }
    [JsonProperty("customFields")]
    public CustomField[] CustomFields { get; set; }
}

public class CustomField
{
    public string Key { get; set; }
    public string Content { get; set; }
    public string Label { get; set; }
    public string Type { get; set; }
    public string Area { get; set; }
}

public class ErrorResponse
{
    public string Error { get; set; }
}`,
      ruby: `require 'net/http'
require 'json'
require 'date'

class CustomerSearchService
  BASE_URL = 'https://dev.clickai.live'

  def self.search_customers_by_phone(api_key, phone_number)
    uri = URI("#{BASE_URL}/customers/get-customer/phone")
    
    http = Net::HTTP.new(uri.host, uri.port)
    http.use_ssl = true

    request = Net::HTTP::Post.new(uri)
    request['Authorization'] = "Bearer #{api_key}"
    request['Content-Type'] = 'application/json'

    data = { phone: phone_number }
    request.body = data.to_json

    begin
      response = http.request(request)
      result = JSON.parse(response.body)

      case response.code.to_i
      when 200
        if result['success']
          customers = result['data']
          count = result['count']
          searched_phone = result['searched_phone']
          
          puts "Found #{count} customer(s) with phone #{searched_phone}"
          
          if count == 0
            puts "No customers found with this phone number"
            return []
          end
          
          customers.each_with_index do |customer, index|
            puts "\\n--- Customer #{index + 1} ---"
            puts "Customer: #{customer['first_name']} #{customer['last_name']}"
            puts "ID: #{customer['customer_id']}"
            puts "Email: #{customer['email']}"
            puts "Status: #{customer['status']}"
            puts "Address: #{customer['address']}, #{customer['city']}, #{customer['country']}"
            
            # Display timestamps
            created_date = DateTime.parse(customer['created_at'])
            updated_date = DateTime.parse(customer['updated_at'])
            puts "Created: #{created_date.strftime('%Y-%m-%d %H:%M')}"
            puts "Updated: #{updated_date.strftime('%Y-%m-%d %H:%M')}"
            
            # Display custom fields with values
            puts "\\nCustom Fields:"
            customer['customFields'].each do |field|
              if field['content'] != 'N/A'
                puts "  #{field['label']}: #{field['content']} (#{field['type']})"
              end
            end
            
            # Display integration IDs if available
            if customer['shopify_id'] != 'N/A'
              puts "\\nShopify ID: #{customer['shopify_id']}"
            end
            if customer['woocommerce_id'] != 'N/A'
              puts "WooCommerce ID: #{customer['woocommerce_id']}"
            end
          end
          
          return customers
        end
      else
        puts "Error #{response.code}: #{result['error'] || 'Unknown error'}"
        return nil
      end
    rescue StandardError => e
      puts "Request failed: #{e.message}"
      return nil
    end
  end
end

# Usage
api_key = 'your_api_key_here'
phone_number = '0501234567'
customers = CustomerSearchService.search_customers_by_phone(api_key, phone_number)`,
      java: `import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.net.URI;
import java.time.LocalDateTime;
import java.time.format.DateTimeFormatter;
import com.google.gson.Gson;
import com.google.gson.JsonObject;
import com.google.gson.JsonArray;

public class CustomerSearchAPI {
    private static final String BASE_URL = "https://dev.clickai.live";
    private static final HttpClient client = HttpClient.newHttpClient();
    private static final Gson gson = new Gson();

    public static CustomerSearchResult searchCustomersByPhone(String apiKey, String phoneNumber) {
        try {
            String jsonData = String.format("{\\"phone\\":\\"%s\\"}", phoneNumber);

            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(BASE_URL + "/customers/get-customer/phone"))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(jsonData))
                .build();

            HttpResponse<String> response = client.send(request, 
                HttpResponse.BodyHandlers.ofString());

            JsonObject result = gson.fromJson(response.body(), JsonObject.class);

            if (response.statusCode() == 200 && result.get("success").getAsBoolean()) {
                JsonArray customersData = result.getAsJsonArray("data");
                int count = result.get("count").getAsInt();
                String searchedPhone = result.get("searched_phone").getAsString();
                
                System.out.println("Found " + count + " customer(s) with phone " + searchedPhone);
                
                if (count == 0) {
                    System.out.println("No customers found with this phone number");
                    return new CustomerSearchResult(true, 0, searchedPhone);
                }
                
                for (int i = 0; i < customersData.size(); i++) {
                    JsonObject customer = customersData.get(i).getAsJsonObject();
                    System.out.println("\\n--- Customer " + (i + 1) + " ---");
                    System.out.println("Customer: " + customer.get("first_name").getAsString() + 
                        " " + customer.get("last_name").getAsString());
                    System.out.println("ID: " + customer.get("customer_id").getAsString());
                    System.out.println("Email: " + customer.get("email").getAsString());
                    System.out.println("Status: " + customer.get("status").getAsString());
                    System.out.println("Address: " + customer.get("address").getAsString() + 
                        ", " + customer.get("city").getAsString() + 
                        ", " + customer.get("country").getAsString());
                    
                    // Display timestamps
                    String createdAt = customer.get("created_at").getAsString();
                    String updatedAt = customer.get("updated_at").getAsString();
                    System.out.println("Created: " + formatTimestamp(createdAt));
                    System.out.println("Updated: " + formatTimestamp(updatedAt));
                    
                    // Display custom fields with values
                    System.out.println("\\nCustom Fields:");
                    JsonArray customFields = customer.getAsJsonArray("customFields");
                    for (int j = 0; j < customFields.size(); j++) {
                        JsonObject field = customFields.get(j).getAsJsonObject();
                        String content = field.get("content").getAsString();
                        if (!content.equals("N/A")) {
                            System.out.println("  " + field.get("label").getAsString() + 
                                ": " + content + " (" + field.get("type").getAsString() + ")");
                        }
                    }
                    
                    // Display integration IDs if available
                    String shopifyId = customer.get("shopify_id").getAsString();
                    String wooCommerceId = customer.get("woocommerce_id").getAsString();
                    if (!shopifyId.equals("N/A")) {
                        System.out.println("\\nShopify ID: " + shopifyId);
                    }
                    if (!wooCommerceId.equals("N/A")) {
                        System.out.println("WooCommerce ID: " + wooCommerceId);
                    }
                }
                
                return new CustomerSearchResult(true, count, searchedPhone);
            } else {
                String error = result.has("error") ? result.get("error").getAsString() : "Unknown error";
                System.out.println("Error " + response.statusCode() + ": " + error);
                return new CustomerSearchResult(false, 0, phoneNumber);
            }

        } catch (Exception e) {
            System.out.println("Request failed: " + e.getMessage());
            return new CustomerSearchResult(false, 0, phoneNumber);
        }
    }
    
    private static String formatTimestamp(String timestamp) {
        return timestamp.replace("T", " ").replace("Z", "");
    }

    public static void main(String[] args) {
        String apiKey = "your_api_key_here";
        String phoneNumber = "0501234567";
        
        CustomerSearchResult result = searchCustomersByPhone(apiKey, phoneNumber);
    }

    static class CustomerSearchResult {
        boolean success;
        int count;
        String searchedPhone;

        CustomerSearchResult(boolean success, int count, String searchedPhone) {
            this.success = success;
            this.count = count;
            this.searchedPhone = searchedPhone;
        }
    }
}`
    }
  },

  'customers-delete-customer': {
    id: 'customers-delete-customer',
    title: 'Delete customer',
    category: 'Customers',
    categoryId: 'customers',
    method: 'DELETE',
    path: '/customers/delete-customer',
    description: 'Delete a customer from your organization. This action cannot be undone.',
    parameters: [
      {
        name: 'customer_id',
        type: 'string',
        required: true,
        description: 'The unique identifier of the customer to delete'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success',
        example: JSON.stringify({
          success: true,
          message: 'Customer deleted successfully'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/customers/delete-customer';

fetch(url, {
  method: 'DELETE',
  headers: {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ customer_id: 'cust_01H9XK2F5Z6G7ABCDEFGHI' })
})
.then(response => response.json())
.then(data => console.log(data));`,
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/customers/delete-customer'

response = requests.delete(url, 
    headers={'Authorization': f'Bearer {api_key}'}, 
    json={'customer_id': 'cust_01H9XK2F5Z6G7ABCDEFGHI'})
print(response.json())`,
      curl: `curl -X DELETE "https://dev.clickai.live/customers/delete-customer" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"customer_id": "cust_01H9XK2F5Z6G7ABCDEFGHI"}'`,
      php: `<?php
$api_key = 'your_api_key_here';
$data = ['customer_id' => 'cust_01H9XK2F5Z6G7ABCDEFGHI'];

$ch = curl_init('https://dev.clickai.live/customers/delete-customer');
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, 'DELETE');
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

echo curl_exec($ch);
curl_close($ch);
?>`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/customers/delete-customer')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Delete.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = {customer_id: 'cust_01H9XK2F5Z6G7ABCDEFGHI'}.to_json

puts http.request(request).body`
    }
  },

  // Products endpoints - additional
  'products-custom-fields': {
    id: 'products-custom-fields',
    title: 'Get custom fields',
    category: 'Products',
    categoryId: 'products',
    method: 'GET',
    path: '/products/custom-fields',
    description: 'Retrieve all custom fields configured for products in your organization.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success',
        example: JSON.stringify({
          success: true,
          data: [
            {
              key: '1750878964687_1750878975688',
              area: 'פרטי מוצר',
              type: 'select',
              label: 'קטגוריית מוצר',
              options: ['אלקטרוניקה', 'בגדים', 'ביתיים'],
              required: false
            }
          ]
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/products/custom-fields';

fetch(url, {
  method: 'GET',
  headers: {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => console.log(data));`,
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/products/custom-fields'

response = requests.get(url, 
    headers={'Authorization': f'Bearer {api_key}'})
print(response.json())`,
      curl: `curl -X GET "https://dev.clickai.live/products/custom-fields" \\
  -H "Authorization: Bearer your_api_key_here"`,
      php: `<?php
$api_key = 'your_api_key_here';

$ch = curl_init('https://dev.clickai.live/products/custom-fields');
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Authorization: Bearer ' . $api_key
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

echo curl_exec($ch);
curl_close($ch);
?>`,
      ruby: `require 'net/http'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/products/custom-fields')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = "Bearer #{api_key}"

puts http.request(request).body`
    }
  },

  // Team endpoints
  'team-list': {
    id: 'team-list',
    title: 'Get Team Members List',
    category: 'Team',
    categoryId: 'team',
    method: 'GET',
    path: '/team/list',
    description: 'Retrieves a complete list of all team members (users) in your organization. This endpoint returns basic profile information for all users associated with your organization, including their roles and contact details.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: false,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Team members retrieved',
        example: JSON.stringify({
          team: [
            {
              id: "a9ea0e8d-54e2-4499-a2d3-b8bea46ff7b2",
              display_name: "Jessica Rowley",
              email: "jessica@example.com",
              role: "admin",
              created_at: "2024-01-15T10:30:00Z",
              updated_at: "2024-01-15T10:30:00Z"
            },
            {
              id: "2c29759a-e549-4135-a94a-c6e844999c67",
              display_name: "John Doe",
              email: "exanple@example.com",
              role: "manager",
              created_at: "2024-01-20T14:15:00Z",
              updated_at: "2024-01-25T09:45:00Z"
            },
            {
              id: "debdb842-9c6b-4573-ab09-eba4caec7ee9",
              display_name: "test2",
              email: "test2@example.com",
              role: "user",
              created_at: "2024-02-01T11:20:00Z",
              updated_at: "2024-02-01T11:20:00Z"
            }
          ],
          count: 3
        }, null, 2)
      },
      {
        code: '401',
        description: 'Missing Authorization',
        example: JSON.stringify({
          error: "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Invalid API Key',
        example: JSON.stringify({
          error: "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Server Error',
        example: JSON.stringify({
          error: "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/team/list';

fetch(url, {
    method: 'GET',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    }
})
.then(response => response.json())
.then(data => {
    console.log(\`Found \${data.count} team members:\`);
    data.team.forEach(member => {
        console.log(\`- \${member.display_name} (\${member.email}) - \${member.role}\`);
    });
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/team/list"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

response = requests.get(url, headers=headers)

if response.status_code == 200:
    data = response.json()
    print(f"Found {data['count']} team members:")
    for member in data['team']:
        print(f"- {member['display_name']} ({member['email']}) - {member['role']}")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X GET "https://dev.clickai.live/team/list" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json"`,
      'curl-linux': `curl -X GET "https://dev.clickai.live/team/list" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/team/list';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    echo "Found " . $data['count'] . " team members:\\n";
    foreach ($data['team'] as $member) {
        echo "- " . $member['display_name'] . " (" . $member['email'] . ") - " . $member['role'] . "\\n";
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/team/list";
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            HttpResponseMessage response = await client.GetAsync(url);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic data = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine($"Found {data.count} team members:");
                foreach (var member in data.team)
                {
                    Console.WriteLine($"- {member.display_name} ({member.email}) - {member.role}");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/team/list')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  puts "Found #{data['count']} team members:"
  data['team'].each do |member|
    puts "- #{member['display_name']} (#{member['email']}) - #{member['role']}"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.Gson;
import com.google.gson.JsonObject;
import com.google.gson.JsonArray;

public class TeamListClient {
    private static final OkHttpClient client = new OkHttpClient();
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/team/list";
        
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                Gson gson = new Gson();
                JsonObject data = gson.fromJson(responseBody, JsonObject.class);
                JsonArray team = data.getAsJsonArray("team");
                int count = data.get("count").getAsInt();
                
                System.out.println("Found " + count + " team members:");
                for (int i = 0; i < team.size(); i++) {
                    JsonObject member = team.get(i).getAsJsonObject();
                    System.out.println("- " + member.get("display_name").getAsString() + 
                        " (" + member.get("email").getAsString() + ") - " + 
                        member.get("role").getAsString());
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  // Additional Products endpoints
  'products-get-product': {
    id: 'products-get-product',
    title: 'Get single product with variations',
    category: 'Products',
    categoryId: 'products',
    method: 'POST',
    path: '/product/get-product',
    description: 'Retrieves detailed information about a single product including all its variations (if any). This endpoint returns complete product data along with all product variations, making it ideal for product detail pages and comprehensive product information displays.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer {your_api_key}',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'application/json',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'id',
        type: 'string',
        required: true,
        description: 'Product UUID to retrieve',
        example: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'OK - Request successful',
        example: JSON.stringify({
          "success": true,
          "data": {
            "id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
            "title": "Gaming Laptop",
            "shopify_id": "N/A",
            "woocommerce_id": "N/A",
            "description": "Advanced gaming laptop with powerful graphics card",
            "product_link": "N/A",
            "product_type": "Electronics",
            "tags": ["Gaming", "Laptop", "RTX", "New"],
            "image_link": "N/A",
            "category": "Computers",
            "price": 4999.99,
            "compare_at_price": 5999.99,
            "has_variants": true,
            "variations": [
              {
                "id": "9ac6c3e6-9aad-4831-96e3-4cb81b7a547e",
                "price": 4999.99,
                "compare_at_price": 5499.99,
                "title": "RTX 4060 + 16GB RAM",
                "content": "Basic version with RTX 4060 and 16GB RAM",
                "image_link": "N/A"
              },
              {
                "id": "6fc66960-5a6b-4c27-b909-3a88f0a3ad4b",
                "price": 6999.99,
                "compare_at_price": 7999.99,
                "title": "RTX 4070 + 32GB RAM",
                "content": "Advanced version with RTX 4070 and 32GB RAM",
                "image_link": "N/A"
              },
              {
                "id": "4bda58b7-1e86-4b6c-9d9d-7dd44b35480c",
                "price": 9999.99,
                "compare_at_price": 11999.99,
                "title": "RTX 4080 + 32GB RAM + SSD 2TB",
                "content": "Premium version with RTX 4080, 32GB RAM and 2TB SSD",
                "image_link": "N/A"
              }
            ],
            "variations_count": 3
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing required field or invalid data',
        example: JSON.stringify({
          "error": "Missing required field: id"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          "error": "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          "error": "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Product not found',
        example: JSON.stringify({
          "error": "Product not found or does not belong to your organization"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          "error": "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/product/get-product';

const data = {
    id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
})
.then(response => response.json())
.then(result => {
    if (result.success) {
        const product = result.data;
        console.log(\`Product: \${product.title}\`);
        console.log(\`Base Price: ₪\${product.price}\`);
        console.log(\`Has Variants: \${product.has_variants}\`);
        
        if (product.variations.length > 0) {
            console.log(\`\\nVariations (\${product.variations_count}):\`);
            product.variations.forEach(variation => {
                console.log(\`- \${variation.title}: ₪\${variation.price}\`);
                if (variation.content !== "N/A") {
                    console.log(\`  Description: \${variation.content}\`);
                }
            });
        }
    } else {
        console.log('Product not found');
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/product/get-product"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

data = {
    "id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa"
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    if result['success']:
        product = result['data']
        print(f"Product: {product['title']}")
        print(f"Base Price: ₪{product['price']}")
        print(f"Has Variants: {product['has_variants']}")
        
        if product['variations']:
            print(f"\\nVariations ({product['variations_count']}):")
            for variation in product['variations']:
                print(f"- {variation['title']}: ₪{variation['price']}")
                if variation['content'] != "N/A":
                    print(f"  Description: {variation['content']}")
    else:
        print("Product not found")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      curl: `curl -X POST "https://dev.clickai.live/product/get-product" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"id\\":\\"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa\\"}"`,
      curlLinux: `curl -X POST "https://dev.clickai.live/product/get-product" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"id":"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa"}'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/product/get-product';

$data = json_encode([
    'id' => '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
]);

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, $data);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $result = json_decode($response, true);
    if ($result['success']) {
        $product = $result['data'];
        echo "Product: " . $product['title'] . "\\n";
        echo "Base Price: ₪" . $product['price'] . "\\n";
        echo "Has Variants: " . ($product['has_variants'] ? 'Yes' : 'No') . "\\n";
        
        if (!empty($product['variations'])) {
            echo "\\nVariations (" . $product['variations_count'] . "):\\n";
            foreach ($product['variations'] as $variation) {
                echo "- " . $variation['title'] . ": ₪" . $variation['price'] . "\\n";
                if ($variation['content'] !== "N/A") {
                    echo "  Description: " . $variation['content'] . "\\n";
                }
            }
        }
    } else {
        echo "Product not found\\n";
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/product/get-product";
        
        var requestData = new { id = "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa" };
        string jsonData = JsonConvert.SerializeObject(requestData);
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic result = JsonConvert.DeserializeObject(responseContent);
                if (result.success == true)
                {
                    var product = result.data;
                    Console.WriteLine($"Product: {product.title}");
                    Console.WriteLine($"Base Price: ₪{product.price}");
                    Console.WriteLine($"Has Variants: {product.has_variants}");
                    
                    if (product.variations != null && product.variations.Count > 0)
                    {
                        Console.WriteLine($"\\nVariations ({product.variations_count}):");
                        foreach (var variation in product.variations)
                        {
                            Console.WriteLine($"- {variation.title}: ₪{variation.price}");
                            if (variation.content.ToString() != "N/A")
                            {
                                Console.WriteLine($"  Description: {variation.content}");
                            }
                        }
                    }
                }
                else
                {
                    Console.WriteLine("Product not found");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/product/get-product')

data = {
  id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  if result['success']
    product = result['data']
    puts "Product: #{product['title']}"
    puts "Base Price: ₪#{product['price']}"
    puts "Has Variants: #{product['has_variants']}"
    
    if product['variations'] && !product['variations'].empty?
      puts "\\nVariations (#{product['variations_count']}):"
      product['variations'].each do |variation|
        puts "- #{variation['title']}: ₪#{variation['price']}"
        if variation['content'] != "N/A"
          puts "  Description: #{variation['content']}"
        end
      end
    end
  else
    puts "Product not found"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.Gson;
import com.google.gson.JsonObject;
import com.google.gson.JsonArray;

public class ProductDetailClient {
    private static final OkHttpClient client = new OkHttpClient();
    private static final Gson gson = new Gson();
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/product/get-product";
        
        JsonObject requestData = new JsonObject();
        requestData.addProperty("id", "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa");
        
        RequestBody body = RequestBody.create(
            requestData.toString(),
            MediaType.get("application/json")
        );
        
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .post(body)
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject result = gson.fromJson(responseBody, JsonObject.class);
                
                if (result.get("success").getAsBoolean()) {
                    JsonObject product = result.getAsJsonObject("data");
                    System.out.println("Product: " + product.get("title").getAsString());
                    System.out.println("Base Price: ₪" + product.get("price").getAsDouble());
                    System.out.println("Has Variants: " + product.get("has_variants").getAsBoolean());
                    
                    JsonArray variations = product.getAsJsonArray("variations");
                    if (variations != null && variations.size() > 0) {
                        System.out.println("\\nVariations (" + product.get("variations_count").getAsInt() + "):");
                        for (int i = 0; i < variations.size(); i++) {
                            JsonObject variation = variations.get(i).getAsJsonObject();
                            System.out.println("- " + variation.get("title").getAsString() + 
                                ": ₪" + variation.get("price").getAsDouble());
                            if (!variation.get("content").getAsString().equals("N/A")) {
                                System.out.println("  Description: " + variation.get("content").getAsString());
                            }
                        }
                    }
                } else {
                    System.out.println("Product not found");
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'products-edit-product': {
    id: 'products-edit-product',
    title: 'Edit existing product with variations',
    category: 'Products',
    categoryId: 'products',
    method: 'POST',
    path: '/products/edit-product',
    description: 'Updates an existing product and its variations. This endpoint allows partial updates - only the fields provided in the request will be updated. For product variations, only existing variations can be edited; to add new variations, use the `/products/add-variations` endpoint.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer {your_api_key}',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'application/json',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'id',
        type: 'string',
        required: true,
        description: 'Product UUID to update',
        example: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
      },
      {
        name: 'title',
        type: 'string',
        required: false,
        description: 'Product name/title',
        example: 'Gaming Laptop Updated'
      },
      {
        name: 'shopify_id',
        type: 'string',
        required: false,
        description: 'Shopify integration ID',
        example: 'shopify_123456'
      },
      {
        name: 'woocommerce_id',
        type: 'string',
        required: false,
        description: 'WooCommerce integration ID',
        example: 'wc_789012'
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Product description',
        example: 'Updated description with additional details'
      },
      {
        name: 'product_link',
        type: 'string',
        required: false,
        description: 'Direct link to product page',
        example: 'https://example.com/product/gaming-laptop'
      },
      {
        name: 'product_type',
        type: 'string',
        required: false,
        description: 'Product type/category',
        example: 'Electronics'
      },
      {
        name: 'tags',
        type: 'array',
        required: false,
        description: 'Array of product tags',
        example: '["Gaming", "Laptop", "RTX", "Updated"]'
      },
      {
        name: 'image_link',
        type: 'string',
        required: false,
        description: 'Product image URL',
        example: 'https://example.com/images/laptop.jpg'
      },
      {
        name: 'category',
        type: 'string',
        required: false,
        description: 'Product category',
        example: 'Computers & Technology'
      },
      {
        name: 'price',
        type: 'number',
        required: false,
        description: 'Product base price',
        example: '4799.99'
      },
      {
        name: 'compare_at_price',
        type: 'number',
        required: false,
        description: 'Original/compare price',
        example: '5699.99'
      },
      {
        name: 'variations',
        type: 'array',
        required: false,
        description: 'Array of variation objects to update',
        example: '[{"id": "variation-uuid", "title": "Updated Variation", "price": 4999.99}]'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'OK - Product updated successfully',
        example: JSON.stringify({
          "success": true,
          "message": "Product updated successfully",
          "product_id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing required field or invalid data',
        example: JSON.stringify({
          "error": "Missing required field: id"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          "error": "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          "error": "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Product or variation not found',
        example: JSON.stringify({
          "error": "Product not found or does not belong to your organization"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          "error": "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/products/edit-product';

const data = {
    id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
    title: 'Gaming Laptop Updated',
    price: 4799.99,
    description: 'Updated description with additional details',
    variations: [
        {
            id: '9ac6c3e6-9aad-4831-96e3-4cb81b7a547e',
            title: 'RTX 4060 + 16GB RAM - Updated',
            price: 4799.99,
            content: 'Updated basic version'
        }
    ]
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
})
.then(response => response.json())
.then(result => {
    if (result.success) {
        console.log(\`Product \${result.product_id} updated successfully!\`);
    } else {
        console.log('Update failed');
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/products/edit-product"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Update product with variations
data = {
    "id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
    "title": "Gaming Laptop Updated",
    "price": 4799.99,
    "description": "Updated description with additional details",
    "variations": [
        {
            "id": "9ac6c3e6-9aad-4831-96e3-4cb81b7a547e",
            "title": "RTX 4060 + 16GB RAM - Updated",
            "price": 4799.99,
            "content": "Updated basic version"
        }
    ]
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    if result['success']:
        print(f"Product {result['product_id']} updated successfully!")
    else:
        print("Update failed")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      curl: `curl -X POST "https://dev.clickai.live/products/edit-product" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"id\\":\\"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa\\",\\"title\\":\\"Gaming Laptop Updated\\",\\"price\\":4799.99,\\"variations\\":[{\\"id\\":\\"9ac6c3e6-9aad-4831-96e3-4cb81b7a547e\\",\\"title\\":\\"RTX 4060 + 16GB RAM - Updated\\",\\"price\\":4799.99}]}"`,
      curlLinux: `curl -X POST "https://dev.clickai.live/products/edit-product" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
    "title": "Gaming Laptop Updated",
    "price": 4799.99,
    "variations": [
      {
        "id": "9ac6c3e6-9aad-4831-96e3-4cb81b7a547e",
        "title": "RTX 4060 + 16GB RAM - Updated",
        "price": 4799.99
      }
    ]
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/products/edit-product';

$data = [
    'id' => '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
    'title' => 'Gaming Laptop Updated',
    'price' => 4799.99,
    'description' => 'Updated description with additional details',
    'variations' => [
        [
            'id' => '9ac6c3e6-9aad-4831-96e3-4cb81b7a547e',
            'title' => 'RTX 4060 + 16GB RAM - Updated',
            'price' => 4799.99,
            'content' => 'Updated basic version'
        ]
    ]
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $result = json_decode($response, true);
    if ($result['success']) {
        echo "Product " . $result['product_id'] . " updated successfully!\\n";
    } else {
        echo "Update failed\\n";
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/products/edit-product";
        
        var requestData = new {
            id = "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
            title = "Gaming Laptop Updated",
            price = 4799.99,
            description = "Updated description with additional details",
            variations = new[] {
                new {
                    id = "9ac6c3e6-9aad-4831-96e3-4cb81b7a547e",
                    title = "RTX 4060 + 16GB RAM - Updated",
                    price = 4799.99,
                    content = "Updated basic version"
                }
            }
        };
        
        string jsonData = JsonConvert.SerializeObject(requestData);
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic result = JsonConvert.DeserializeObject(responseContent);
                if (result.success == true)
                {
                    Console.WriteLine($"Product {result.product_id} updated successfully!");
                }
                else
                {
                    Console.WriteLine("Update failed");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/products/edit-product')

data = {
  id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
  title: 'Gaming Laptop Updated',
  price: 4799.99,
  description: 'Updated description with additional details',
  variations: [
    {
      id: '9ac6c3e6-9aad-4831-96e3-4cb81b7a547e',
      title: 'RTX 4060 + 16GB RAM - Updated',
      price: 4799.99,
      content: 'Updated basic version'
    }
  ]
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  if result['success']
    puts "Product #{result['product_id']} updated successfully!"
  else
    puts "Update failed"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.Gson;
import com.google.gson.JsonObject;
import com.google.gson.JsonArray;

public class ProductEditClient {
    private static final OkHttpClient client = new OkHttpClient();
    private static final Gson gson = new Gson();
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/products/edit-product";
        
        JsonObject requestData = new JsonObject();
        requestData.addProperty("id", "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa");
        requestData.addProperty("title", "Gaming Laptop Updated");
        requestData.addProperty("price", 4799.99);
        requestData.addProperty("description", "Updated description with additional details");
        
        JsonArray variations = new JsonArray();
        JsonObject variation = new JsonObject();
        variation.addProperty("id", "9ac6c3e6-9aad-4831-96e3-4cb81b7a547e");
        variation.addProperty("title", "RTX 4060 + 16GB RAM - Updated");
        variation.addProperty("price", 4799.99);
        variation.addProperty("content", "Updated basic version");
        variations.add(variation);
        
        requestData.add("variations", variations);
        
        RequestBody body = RequestBody.create(
            requestData.toString(),
            MediaType.get("application/json")
        );
        
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .post(body)
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject result = gson.fromJson(responseBody, JsonObject.class);
                
                if (result.get("success").getAsBoolean()) {
                    System.out.println("Product " + result.get("product_id").getAsString() + " updated successfully!");
                } else {
                    System.out.println("Update failed");
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'products-add-variations': {
    id: 'products-add-variations',
    title: 'Add new variations to existing product',
    category: 'Products',
    categoryId: 'products',
    method: 'POST',
    path: '/products/add-variations',
    description: 'Adds new variations to an existing product. This endpoint validates that the product belongs to your organization before adding variations. If the product\'s `has_variants` field is currently `false`, it will be automatically updated to `true` after adding variations.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer {your_api_key}',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'application/json',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'product_id',
        type: 'string',
        required: true,
        description: 'Product UUID to add variations to',
        example: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
      },
      {
        name: 'variations',
        type: 'array',
        required: true,
        description: 'Array of variation objects to add',
        example: '[{"title": "RTX 4090 + 64GB RAM", "content": "Premium configuration", "price": 14999.99}]'
      },
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Variation title/name (required in each variation object)',
        example: 'RTX 4090 + 64GB RAM + SSD 4TB'
      },
      {
        name: 'content',
        type: 'string',
        required: true,
        description: 'Variation description (required in each variation object)',
        example: 'Premium version with RTX 4090 and 64GB RAM and SSD 4TB'
      },
      {
        name: 'shopify_id',
        type: 'string',
        required: false,
        description: 'Shopify variation ID (optional in each variation object)',
        example: 'shopify_12345'
      },
      {
        name: 'woocommerce_id',
        type: 'string',
        required: false,
        description: 'WooCommerce variation ID (optional in each variation object)',
        example: 'wc_67890'
      },
      {
        name: 'price',
        type: 'number',
        required: false,
        description: 'Variation price (optional in each variation object)',
        example: '14999.99'
      },
      {
        name: 'compare_at_price',
        type: 'number',
        required: false,
        description: 'Original/compare price for variation (optional in each variation object)',
        example: '16999.99'
      },
      {
        name: 'image_link',
        type: 'string',
        required: false,
        description: 'Variation-specific image URL (optional in each variation object)',
        example: 'https://example.com/rtx4090.jpg'
      },
      {
        name: 'stock_amount',
        type: 'integer',
        required: false,
        description: 'Stock quantity (optional in each variation object)',
        example: '5'
      },
      {
        name: 'stock_policy',
        type: 'string',
        required: false,
        description: 'Stock policy (allow/deny) (optional in each variation object)',
        example: 'allow'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Created - Variations added successfully',
        example: JSON.stringify({
          "success": true,
          "message": "Successfully added 2 variation(s) to product",
          "product_id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
          "variations": [
            {
              "id": "new-variation-uuid-1",
              "product_id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
              "title": "RTX 4090 + 64GB RAM + SSD 4TB",
              "content": "Premium version with RTX 4090 and 64GB RAM and SSD 4TB",
              "shopify_id": "N/A",
              "woocommerce_id": "N/A",
              "price": 14999.99,
              "compare_at_price": 16999.99,
              "image_link": "https://example.com/rtx4090.jpg",
              "stock_amount": 5,
              "stock_policy": "allow",
              "created_at": "2024-01-15T10:30:00Z",
              "updated_at": "2024-01-15T10:30:00Z"
            },
            {
              "id": "new-variation-uuid-2",
              "product_id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
              "title": "RTX 4070 Ti + 32GB RAM + SSD 1TB",
              "content": "Advanced version with RTX 4070 Ti and 32GB RAM",
              "shopify_id": "12345",
              "woocommerce_id": "67890",
              "price": 8999.99,
              "compare_at_price": 9999.99,
              "image_link": "N/A",
              "stock_amount": 0,
              "stock_policy": "N/A",
              "created_at": "2024-01-15T10:30:00Z",
              "updated_at": "2024-01-15T10:30:00Z"
            }
          ]
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing required field or invalid data',
        example: JSON.stringify({
          "error": "Missing required field: product_id"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          "error": "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          "error": "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Product not found',
        example: JSON.stringify({
          "error": "Product not found or does not belong to your organization"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          "error": "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/products/add-variations';

const data = {
    product_id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
    variations: [
        {
            title: 'RTX 4090 + 64GB RAM + SSD 4TB',
            content: 'Premium version with RTX 4090 and 64GB RAM and SSD 4TB',
            price: 14999.99,
            compare_at_price: 16999.99,
            image_link: 'https://example.com/rtx4090.jpg',
            stock_amount: 5
        },
        {
            title: 'RTX 4070 Ti + 32GB RAM + SSD 1TB',
            content: 'Advanced version with RTX 4070 Ti and 32GB RAM',
            price: 8999.99,
            compare_at_price: 9999.99
        }
    ]
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
})
.then(response => response.json())
.then(result => {
    if (result.success) {
        console.log(\`Successfully added \${result.variations.length} variations!\`);
        result.variations.forEach(variation => {
            console.log(\`- \${variation.title} (ID: \${variation.id})\`);
        });
    } else {
        console.log('Failed to add variations');
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/products/add-variations"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Add multiple variations
data = {
    "product_id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
    "variations": [
        {
            "title": "RTX 4090 + 64GB RAM + SSD 4TB",
            "content": "Premium version with RTX 4090 and 64GB RAM and SSD 4TB",
            "price": 14999.99,
            "compare_at_price": 16999.99,
            "image_link": "https://example.com/rtx4090.jpg",
            "stock_amount": 5
        },
        {
            "title": "RTX 4070 Ti + 32GB RAM + SSD 1TB",
            "content": "Advanced version with RTX 4070 Ti and 32GB RAM",
            "price": 8999.99,
            "compare_at_price": 9999.99
        }
    ]
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 201:
    result = response.json()
    if result['success']:
        print(f"Successfully added {len(result['variations'])} variations!")
        for variation in result['variations']:
            print(f"- {variation['title']} (ID: {variation['id']})")
    else:
        print("Failed to add variations")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      curl: `curl -X POST "https://dev.clickai.live/products/add-variations" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"product_id\\":\\"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa\\",\\"variations\\":[{\\"title\\":\\"RTX 4090 + 64GB RAM + SSD 4TB\\",\\"content\\":\\"Premium version with RTX 4090 and 64GB RAM and SSD 4TB\\",\\"price\\":14999.99,\\"compare_at_price\\":16999.99,\\"image_link\\":\\"https://example.com/rtx4090.jpg\\"},{\\"title\\":\\"RTX 4070 Ti + 32GB RAM + SSD 1TB\\",\\"content\\":\\"Advanced version with RTX 4070 Ti and 32GB RAM\\",\\"price\\":8999.99,\\"compare_at_price\\":9999.99}]}"`,
      curlLinux: `curl -X POST "https://dev.clickai.live/products/add-variations" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "product_id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
    "variations": [
      {
        "title": "RTX 4090 + 64GB RAM + SSD 4TB",
        "content": "Premium version with RTX 4090 and 64GB RAM and SSD 4TB",
        "price": 14999.99,
        "compare_at_price": 16999.99,
        "image_link": "https://example.com/rtx4090.jpg"
      },
      {
        "title": "RTX 4070 Ti + 32GB RAM + SSD 1TB",
        "content": "Advanced version with RTX 4070 Ti and 32GB RAM",
        "price": 8999.99,
        "compare_at_price": 9999.99
      }
    ]
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/products/add-variations';

$data = [
    'product_id' => '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
    'variations' => [
        [
            'title' => 'RTX 4090 + 64GB RAM + SSD 4TB',
            'content' => 'Premium version with RTX 4090 and 64GB RAM and SSD 4TB',
            'price' => 14999.99,
            'compare_at_price' => 16999.99,
            'image_link' => 'https://example.com/rtx4090.jpg',
            'stock_amount' => 5
        ],
        [
            'title' => 'RTX 4070 Ti + 32GB RAM + SSD 1TB',
            'content' => 'Advanced version with RTX 4070 Ti and 32GB RAM',
            'price' => 8999.99,
            'compare_at_price' => 9999.99
        ]
    ]
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $result = json_decode($response, true);
    if ($result['success']) {
        echo "Successfully added " . count($result['variations']) . " variations!\\n";
        foreach ($result['variations'] as $variation) {
            echo "- " . $variation['title'] . " (ID: " . $variation['id'] . ")\\n";
        }
    } else {
        echo "Failed to add variations\\n";
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/products/add-variations";
        
        var requestData = new {
            product_id = "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
            variations = new[] {
                new {
                    title = "RTX 4090 + 64GB RAM + SSD 4TB",
                    content = "Premium version with RTX 4090 and 64GB RAM and SSD 4TB",
                    price = 14999.99,
                    compare_at_price = 16999.99,
                    image_link = "https://example.com/rtx4090.jpg",
                    stock_amount = 5
                },
                new {
                    title = "RTX 4070 Ti + 32GB RAM + SSD 1TB",
                    content = "Advanced version with RTX 4070 Ti and 32GB RAM",
                    price = 8999.99,
                    compare_at_price = 9999.99
                }
            }
        };
        
        string jsonData = JsonConvert.SerializeObject(requestData);
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.StatusCode == System.Net.HttpStatusCode.Created)
            {
                dynamic result = JsonConvert.DeserializeObject(responseContent);
                if (result.success == true)
                {
                    Console.WriteLine($"Successfully added {result.variations.Count} variations!");
                    foreach (var variation in result.variations)
                    {
                        Console.WriteLine($"- {variation.title} (ID: {variation.id})");
                    }
                }
                else
                {
                    Console.WriteLine("Failed to add variations");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/products/add-variations')

data = {
  product_id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
  variations: [
    {
      title: 'RTX 4090 + 64GB RAM + SSD 4TB',
      content: 'Premium version with RTX 4090 and 64GB RAM and SSD 4TB',
      price: 14999.99,
      compare_at_price: 16999.99,
      image_link: 'https://example.com/rtx4090.jpg',
      stock_amount: 5
    },
    {
      title: 'RTX 4070 Ti + 32GB RAM + SSD 1TB',
      content: 'Advanced version with RTX 4070 Ti and 32GB RAM',
      price: 8999.99,
      compare_at_price: 9999.99
    }
  ]
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)

if response.code == '201'
  result = JSON.parse(response.body)
  if result['success']
    puts "Successfully added #{result['variations'].length} variations!"
    result['variations'].each do |variation|
      puts "- #{variation['title']} (ID: #{variation['id']})"
    end
  else
    puts "Failed to add variations"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.Gson;
import com.google.gson.JsonObject;
import com.google.gson.JsonArray;

public class AddVariationsClient {
    private static final OkHttpClient client = new OkHttpClient();
    private static final Gson gson = new Gson();
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/products/add-variations";
        
        JsonObject requestData = new JsonObject();
        requestData.addProperty("product_id", "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa");
        
        JsonArray variations = new JsonArray();
        
        JsonObject variation1 = new JsonObject();
        variation1.addProperty("title", "RTX 4090 + 64GB RAM + SSD 4TB");
        variation1.addProperty("content", "Premium version with RTX 4090 and 64GB RAM and SSD 4TB");
        variation1.addProperty("price", 14999.99);
        variation1.addProperty("compare_at_price", 16999.99);
        variation1.addProperty("image_link", "https://example.com/rtx4090.jpg");
        variation1.addProperty("stock_amount", 5);
        variations.add(variation1);
        
        JsonObject variation2 = new JsonObject();
        variation2.addProperty("title", "RTX 4070 Ti + 32GB RAM + SSD 1TB");
        variation2.addProperty("content", "Advanced version with RTX 4070 Ti and 32GB RAM");
        variation2.addProperty("price", 8999.99);
        variation2.addProperty("compare_at_price", 9999.99);
        variations.add(variation2);
        
        requestData.add("variations", variations);
        
        RequestBody body = RequestBody.create(
            requestData.toString(),
            MediaType.get("application/json")
        );
        
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .post(body)
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.code() == 201) {
                JsonObject result = gson.fromJson(responseBody, JsonObject.class);
                
                if (result.get("success").getAsBoolean()) {
                    JsonArray resultVariations = result.getAsJsonArray("variations");
                    System.out.println("Successfully added " + resultVariations.size() + " variations!");
                    
                    for (int i = 0; i < resultVariations.size(); i++) {
                        JsonObject variation = resultVariations.get(i).getAsJsonObject();
                        System.out.println("- " + variation.get("title").getAsString() + 
                                         " (ID: " + variation.get("id").getAsString() + ")");
                    }
                } else {
                    System.out.println("Failed to add variations");
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'products-delete-variation': {
    id: 'products-delete-variation',
    title: 'Delete variation',
    category: 'Products',
    categoryId: 'products',
    method: 'DELETE',
    path: '/products/delete-variation',
    description: 'Delete a specific variation from a product.',
    parameters: [
      {
        name: 'product_id',
        type: 'string',
        required: true,
        description: 'The unique identifier of the product'
      },
      {
        name: 'variation_id',
        type: 'string',
        required: true,
        description: 'The unique identifier of the variation to delete'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success',
        example: JSON.stringify({
          success: true,
          message: 'Variation deleted successfully'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const productId = 'prod_01H9XK2F5Z6G7ABCDEFGHI';
const variationId = 'var_01H9XK2F5Z6G7ABCDEFGHI';
const url = \`https://dev.clickai.live/products/\${productId}/variations/\${variationId}\`;

fetch(url, {
  method: 'DELETE',
  headers: {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => {
  if (data.success) {
    console.log('Variation deleted successfully');
  } else {
    console.error('Error:', data.error);
  }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = 'your_api_key_here'
product_id = 'prod_01H9XK2F5Z6G7ABCDEFGHI'
variation_id = 'var_01H9XK2F5Z6G7ABCDEFGHI'
url = f'https://dev.clickai.live/products/delete-variation'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

response = requests.delete(url, headers=headers)

if response.status_code == 200:
    data = response.json()
    if data['success']:
        print('Variation deleted successfully')
    else:
        print('Error:', data['error'])
else:
    print(f'HTTP Error {response.status_code}: {response.text}')`,
      curl: `curl -X DELETE "https://dev.clickai.live/products/prod_01H9XK2F5Z6G7ABCDEFGHI/variations/var_01H9XK2F5Z6G7ABCDEFGHI" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"`,
      php: `<?php
$api_key = 'your_api_key_here';
$product_id = 'prod_01H9XK2F5Z6G7ABCDEFGHI';
$variation_id = 'var_01H9XK2F5Z6G7ABCDEFGHI';
$url = "https://dev.clickai.live/products/$product_id/variations/$variation_id";

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, 'DELETE');
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    if ($data['success']) {
        echo "Variation deleted successfully";
    } else {
        echo "Error: " . $data['error'];
    }
} else {
    echo "Error $http_code: $response";
}
?>`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
product_id = 'prod_01H9XK2F5Z6G7ABCDEFGHI'
variation_id = 'var_01H9XK2F5Z6G7ABCDEFGHI'
url = URI("https://dev.clickai.live/products/#{product_id}/variations/#{variation_id}")

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Delete.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  if data['success']
    puts "Variation deleted successfully"
  else
    puts "Error: #{data['error']}"
  end
else
  puts "HTTP Error #{response.code}: #{response.body}"
end`
    }
  },

  'products-create-product': {
    id: 'products-create-product',
    title: 'Create new product with variations',
    category: 'Products',
    categoryId: 'products',
    method: 'POST',
    path: '/products/create-product',
    description: 'Creates a new product in your organization with optional variations. This endpoint allows you to create both simple products and complex products with multiple variations. The product will be assigned to your organization based on your API key.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer {your_api_key}'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Product title/name',
        example: 'מחשב נייד גיימינג'
      },
      {
        name: 'shopify_id',
        type: 'string',
        required: false,
        description: 'Shopify integration ID'
      },
      {
        name: 'woocommerce_id',
        type: 'string',
        required: false,
        description: 'WooCommerce integration ID'
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Product description',
        example: 'מחשב נייד מתקדם עבור גיימרים'
      },
      {
        name: 'product_link',
        type: 'string',
        required: false,
        description: 'Direct link to product page'
      },
      {
        name: 'product_type',
        type: 'string',
        required: false,
        description: 'Product type/category',
        example: 'אלקטרוניקה'
      },
      {
        name: 'tags',
        type: 'array or string',
        required: false,
        description: 'Product tags (array of strings or single string)',
        example: '["גיימינג", "RTX", "מתקדם"]'
      },
      {
        name: 'image_link',
        type: 'string',
        required: false,
        description: 'Product image URL'
      },
      {
        name: 'category',
        type: 'string',
        required: false,
        description: 'Product category',
        example: 'מחשבים'
      },
      {
        name: 'price',
        type: 'number',
        required: false,
        description: 'Product price (must be positive)',
        example: '2999.99'
      },
      {
        name: 'compare_at_price',
        type: 'number',
        required: false,
        description: 'Original/compare price (must be positive)',
        example: '3499.99'
      },
      {
        name: 'variations',
        type: 'array',
        required: false,
        description: 'Array of variation objects with title, content, price, etc.',
        example: '[{"title": "RTX 3070 + 16GB RAM", "content": "גרסה בסיסית", "price": 8999.99}]'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Created - Product created successfully',
        example: JSON.stringify({
          success: true,
          message: "Product created successfully with 2 variation(s)",
          product: {
            id: "f47ac10b-58cc-4372-a567-0e02b2c3d479",
            title: "מחשב נייד גיימינג",
            shopify_id: null,
            woocommerce_id: null,
            description: "מחשב נייד מתקדם עבור גיימרים",
            product_link: null,
            product_type: "אלקטרוניקה",
            tags: ["גיימינג", "RTX", "מתקדם"],
            image_link: "https://example.com/gaming-laptop.jpg",
            category: "מחשבים",
            price: null,
            compare_at_price: null,
            has_variants: true,
            org_id: "550e8400-e29b-41d4-a716-446655440000",
            created_at: "2024-01-15T10:30:00Z",
            updated_at: "2024-01-15T10:30:00Z"
          },
          variations: [
            {
              id: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
              product_id: "f47ac10b-58cc-4372-a567-0e02b2c3d479",
              title: "RTX 3070 + 16GB RAM + SSD 1TB",
              content: "גרסה בסיסית עם RTX 3070 ו-16GB RAM ו-SSD 1TB",
              shopify_id: null,
              woocommerce_id: null,
              price: 8999.99,
              compare_at_price: 9999.99,
              image_link: "https://example.com/rtx3070.jpg",
              stock_amount: 10,
              stock_policy: "allow",
              created_at: "2024-01-15T10:30:00Z",
              updated_at: "2024-01-15T10:30:00Z"
            }
          ]
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing required field',
        example: JSON.stringify({
          error: 'Missing required field: title'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/products/create-product';

const productData = {
    title: 'מחשב נייד גיימינג',
    description: 'מחשב נייד מתקדם עבור גיימרים',
    category: 'מחשבים',
    product_type: 'אלקטרוניקה',
    tags: ['גיימינג', 'RTX', 'מתקדם'],
    variations: [
        {
            title: 'RTX 3070 + 16GB RAM + SSD 1TB',
            content: 'גרסה בסיסית עם RTX 3070',
            price: 8999.99,
            compare_at_price: 9999.99,
            stock_amount: 10,
            stock_policy: 'allow'
        },
        {
            title: 'RTX 4080 + 32GB RAM + SSD 2TB',
            content: 'גרסה מתקדמת עם RTX 4080',
            price: 12999.99,
            compare_at_price: 14999.99,
            stock_amount: 5,
            stock_policy: 'allow'
        }
    ]
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(productData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log(\`Product created: \${data.product.title}\`);
        console.log(\`Product ID: \${data.product.id}\`);
        if (data.variations) {
            console.log(\`Created \${data.variations.length} variations\`);
        }
    } else {
        console.error('Failed to create product');
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/products/create-product"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Create product with variations
product_data = {
    "title": "מחשב נייד גיימינג",
    "description": "מחשב נייד מתקדם עבור גיימרים",
    "category": "מחשבים",
    "product_type": "אלקטרוניקה",
    "tags": ["גיימינג", "RTX", "מתקדם"],
    "variations": [
        {
            "title": "RTX 3070 + 16GB RAM + SSD 1TB",
            "content": "גרסה בסיסית עם RTX 3070",
            "price": 8999.99,
            "compare_at_price": 9999.99,
            "stock_amount": 10,
            "stock_policy": "allow"
        },
        {
            "title": "RTX 4080 + 32GB RAM + SSD 2TB",
            "content": "גרסה מתקדמת עם RTX 4080",
            "price": 12999.99,
            "compare_at_price": 14999.99,
            "stock_amount": 5,
            "stock_policy": "allow"
        }
    ]
}

response = requests.post(url, headers=headers, json=product_data)

if response.status_code == 201:
    data = response.json()
    if data['success']:
        print(f"Product created: {data['product']['title']}")
        print(f"Product ID: {data['product']['id']}")
        if 'variations' in data:
            print(f"Created {len(data['variations'])} variations")
    else:
        print("Failed to create product")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      curl: `curl -X POST "https://dev.clickai.live/products/create-product" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"title\\":\\"מחשב נייד גיימינג\\",\\"description\\":\\"מחשב נייד מתקדם עבור גיימרים\\",\\"category\\":\\"מחשבים\\",\\"variations\\":[{\\"title\\":\\"RTX 3070 + 16GB RAM\\",\\"content\\":\\"גרסה בסיסית\\",\\"price\\":8999.99,\\"stock_amount\\":10}]}"`,
      curlLinux: `curl -X POST "https://dev.clickai.live/products/create-product" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "מחשב נייד גיימינג",
    "description": "מחשב נייד מתקדם עבור גיימרים",
    "category": "מחשבים",
    "product_type": "אלקטרוניקה",
    "tags": ["גיימינג", "RTX"],
    "variations": [
      {
        "title": "RTX 3070 + 16GB RAM + SSD 1TB",
        "content": "גרסה בסיסית עם RTX 3070",
        "price": 8999.99,
        "compare_at_price": 9999.99,
        "stock_amount": 10,
        "stock_policy": "allow"
      }
    ]
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/products/create-product';

$product_data = [
    'title' => 'מחשב נייד גיימינג',
    'description' => 'מחשב נייד מתקדם עבור גיימרים',
    'category' => 'מחשבים',
    'product_type' => 'אלקטרוניקה',
    'tags' => ['גיימינג', 'RTX', 'מתקדם'],
    'variations' => [
        [
            'title' => 'RTX 3070 + 16GB RAM + SSD 1TB',
            'content' => 'גרסה בסיסית עם RTX 3070',
            'price' => 8999.99,
            'compare_at_price' => 9999.99,
            'stock_amount' => 10,
            'stock_policy' => 'allow'
        ],
        [
            'title' => 'RTX 4080 + 32GB RAM + SSD 2TB',
            'content' => 'גרסה מתקדמת עם RTX 4080',
            'price' => 12999.99,
            'compare_at_price' => 14999.99,
            'stock_amount' => 5,
            'stock_policy' => 'allow'
        ]
    ]
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($product_data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $data = json_decode($response, true);
    if ($data['success']) {
        echo "Product created: " . $data['product']['title'] . "\\n";
        echo "Product ID: " . $data['product']['id'] . "\\n";
        if (isset($data['variations'])) {
            echo "Created " . count($data['variations']) . " variations\\n";
        }
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`
    }
  },

  // Calendar endpoints
  'calendar-get-break-hours': {
    id: 'calendar-get-break-hours',
    title: 'Get break hours',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'POST',
    path: '/calendar/get-break-hours-by-date',
    description: 'Retrieves the break hours (unavailable time periods) for a specific user on a given date. This endpoint returns the actual break periods when the user is not available for appointments, rather than working hours.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer {your_api_key}'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'type_id',
        type: 'string',
        required: true,
        description: 'Appointment type ID (for validation)',
        example: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
      },
      {
        name: 'date',
        type: 'string',
        required: true,
        description: 'Date in YYYY-MM-DD format',
        example: '2024-01-15'
      },
      {
        name: 'user_id',
        type: 'string',
        required: true,
        description: 'User ID to get break hours for',
        example: 'user-uuid-here'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Break hours retrieved',
        example: JSON.stringify({
          success: true,
          data: {
            user_id: "user-uuid-here",
            break_hours: [
              "12:00-13:00",
              "15:30-16:00"
            ]
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing required field',
        example: JSON.stringify({
          error: "Field 'user_id' is required"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key or API key not found'
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - User or appointment type not found',
        example: JSON.stringify({
          error: 'User does not exist in your organization'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/calendar/get-break-hours-by-date';

const requestData = {
    type_id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
    date: '2024-01-15',
    user_id: 'user-uuid-here'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        const breakHours = data.data.break_hours;
        const userId = data.data.user_id;
        
        console.log(\`Break hours for user \${userId}:\`);
        if (breakHours.length > 0) {
            breakHours.forEach(breakPeriod => {
                console.log(\`- \${breakPeriod}\`);
            });
        } else {
            console.log('- No break hours scheduled');
        }
    } else {
        console.error('Failed to get break hours');
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/calendar/get-break-hours-by-date"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

data = {
    "type_id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
    "date": "2024-01-15",
    "user_id": "user-uuid-here"
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    if result['success']:
        break_hours = result['data']['break_hours']
        user_id = result['data']['user_id']
        
        print(f"Break hours for user {user_id}:")
        if break_hours:
            for break_period in break_hours:
                print(f"- {break_period}")
        else:
            print("- No break hours scheduled")
    else:
        print("Failed to get break hours")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      curl: `curl -X POST "https://dev.clickai.live/calendar/get-break-hours-by-date" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"type_id\\":\\"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa\\",\\"date\\":\\"2024-01-15\\",\\"user_id\\":\\"user-uuid-here\\"}"`,
      curlLinux: `curl -X POST "https://dev.clickai.live/calendar/get-break-hours-by-date" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type_id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
    "date": "2024-01-15",
    "user_id": "user-uuid-here"
  }'`,
      php: `<?php
$apiKey = 'your_api_key_here';
$url = 'https://dev.clickai.live/calendar/get-break-hours-by-date';

$data = array(
    'type_id' => '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
    'date' => '2024-01-15',
    'user_id' => 'user-uuid-here'
);

$options = array(
    'http' => array(
        'header' => array(
            "Authorization: Bearer $apiKey",
            "Content-Type: application/json"
        ),
        'method' => 'POST',
        'content' => json_encode($data)
    )
);

$context = stream_context_create($options);
$response = file_get_contents($url, false, $context);

if ($response !== FALSE) {
    $result = json_decode($response, true);
    
    if ($result['success']) {
        $breakHours = $result['data']['break_hours'];
        $userId = $result['data']['user_id'];
        
        echo "Break hours for user $userId:\\n";
        if (!empty($breakHours)) {
            foreach ($breakHours as $breakPeriod) {
                echo "- $breakPeriod\\n";
            }
        } else {
            echo "- No break hours scheduled\\n";
        }
    } else {
        echo "Failed to get break hours\\n";
    }
} else {
    echo "Request failed\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/calendar/get-break-hours-by-date";
        
        var requestData = new
        {
            type_id = "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
            date = "2024-01-15",
            user_id = "user-uuid-here"
        };
        
        string jsonData = JsonConvert.SerializeObject(requestData);
        var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseBody = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic result = JsonConvert.DeserializeObject(responseBody);
                
                if (result.success == true)
                {
                    var breakHours = result.data.break_hours;
                    string userId = result.data.user_id;
                    
                    Console.WriteLine($"Break hours for user {userId}:");
                    if (breakHours.Count > 0)
                    {
                        foreach (var breakPeriod in breakHours)
                        {
                            Console.WriteLine($"- {breakPeriod}");
                        }
                    }
                    else
                    {
                        Console.WriteLine("- No break hours scheduled");
                    }
                }
                else
                {
                    Console.WriteLine("Failed to get break hours");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseBody}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/calendar/get-break-hours-by-date')

data = {
  type_id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
  date: '2024-01-15',
  user_id: 'user-uuid-here'
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  
  if result['success']
    break_hours = result['data']['break_hours']
    user_id = result['data']['user_id']
    
    puts "Break hours for user #{user_id}:"
    if break_hours.any?
      break_hours.each do |break_period|
        puts "- #{break_period}"
      end
    else
      puts "- No break hours scheduled"
    end
  else
    puts "Failed to get break hours"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.*;
import java.io.IOException;

public class GetBreakHours {
    private static final OkHttpClient client = new OkHttpClient();
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/calendar/get-break-hours-by-date";
        
        JsonObject requestData = new JsonObject();
        requestData.addProperty("type_id", "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa");
        requestData.addProperty("date", "2024-01-15");
        requestData.addProperty("user_id", "user-uuid-here");
        
        RequestBody body = RequestBody.create(requestData.toString(), JSON);
        Request request = new Request.Builder()
            .url(url)
            .post(body)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject result = JsonParser.parseString(responseBody).getAsJsonObject();
                if (result.get("success").getAsBoolean()) {
                    JsonObject data = result.getAsJsonObject("data");
                    JsonArray breakHours = data.getAsJsonArray("break_hours");
                    String userId = data.get("user_id").getAsString();
                    
                    System.out.println("Break hours for user " + userId + ":");
                    if (breakHours.size() > 0) {
                        for (int i = 0; i < breakHours.size(); i++) {
                            String breakPeriod = breakHours.get(i).getAsString();
                            System.out.println("- " + breakPeriod);
                        }
                    } else {
                        System.out.println("- No break hours scheduled");
                    }
                } else {
                    System.out.println("Failed to get break hours");
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'calendar-get-appointment-users': {
    id: 'calendar-get-appointment-users',
    title: 'Get Appointment Users',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'POST',
    path: '/calendar/get-appointment-users',
    description: 'Retrieves all users who have access to view a specific appointment type. This endpoint returns user information based on the appointment type\'s access permissions - either all users in the organization (if public) or only specific users with restricted access.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'type_id',
        type: 'string',
        required: true,
        description: 'The unique ID of the appointment type',
        example: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Users retrieved',
        example: JSON.stringify({
          success: true,
          users: [
            {
              user_id: "user-uuid-1",
              display_name: "Yossi Cohen"
            },
            {
              user_id: "user-uuid-2",
              display_name: "Rachel Levy"
            },
            {
              user_id: "user-uuid-3",
              display_name: "Michal Green"
            }
          ]
        }, null, 2)
      },
      {
        code: '400',
        description: 'Missing Request Body',
        example: JSON.stringify({
          error: "Request body is required"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Missing type_id',
        example: JSON.stringify({
          error: "Appointment type ID (type_id) is required"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Missing Authorization',
        example: JSON.stringify({
          error: "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Invalid API Key',
        example: JSON.stringify({
          error: "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Appointment Type Not Found',
        example: JSON.stringify({
          error: "Appointment type not found or does not belong to your organization"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Server Error',
        example: JSON.stringify({
          error: "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/calendar/get-appointment-users';

const data = {
    type_id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
})
.then(response => response.json())
.then(result => {
    if (result.success) {
        const users = result.users;
        console.log(\`Found \${users.length} users with access:\`);
        users.forEach(user => {
            console.log(\`- \${user.display_name} (\${user.user_id})\`);
        });
    } else {
        console.error('Failed to get appointment users');
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/calendar/get-appointment-users"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

data = {
    "type_id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa"
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    if result['success']:
        users = result['users']
        print(f"Found {len(users)} users with access:")
        for user in users:
            print(f"- {user['display_name']} ({user['user_id']})")
    else:
        print("Failed to get appointment users")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/calendar/get-appointment-users" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"type_id\\":\\"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/calendar/get-appointment-users" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"type_id":"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa"}'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/calendar/get-appointment-users';

$data = [
    'type_id' => '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $result = json_decode($response, true);
    if ($result['success']) {
        $users = $result['users'];
        echo "Found " . count($users) . " users with access:\\n";
        foreach ($users as $user) {
            echo "- " . $user['display_name'] . " (" . $user['user_id'] . ")\\n";
        }
    } else {
        echo "Failed to get appointment users\\n";
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/calendar/get-appointment-users";
        
        var requestData = new
        {
            type_id = "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa"
        };
        
        string jsonData = JsonConvert.SerializeObject(requestData);
        var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic result = JsonConvert.DeserializeObject(responseContent);
                if (result.success == true)
                {
                    var users = result.users;
                    Console.WriteLine($"Found {users.Count} users with access:");
                    foreach (var user in users)
                    {
                        Console.WriteLine($"- {user.display_name} ({user.user_id})");
                    }
                }
                else
                {
                    Console.WriteLine("Failed to get appointment users");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/calendar/get-appointment-users')

data = {
  type_id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  if result['success']
    users = result['users']
    puts "Found #{users.length} users with access:"
    users.each do |user|
      puts "- #{user['display_name']} (#{user['user_id']})"
    end
  else
    puts "Failed to get appointment users"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.JsonObject;
import com.google.gson.JsonParser;
import com.google.gson.JsonArray;

public class AppointmentUsersClient {
    private static final OkHttpClient client = new OkHttpClient();
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/calendar/get-appointment-users";
        
        JsonObject requestData = new JsonObject();
        requestData.addProperty("type_id", "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa");
        
        RequestBody body = RequestBody.create(requestData.toString(), JSON);
        Request request = new Request.Builder()
            .url(url)
            .post(body)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject result = JsonParser.parseString(responseBody).getAsJsonObject();
                if (result.get("success").getAsBoolean()) {
                    JsonArray users = result.getAsJsonArray("users");
                    System.out.println("Found " + users.size() + " users with access:");
                    
                    for (int i = 0; i < users.size(); i++) {
                        JsonObject user = users.get(i).getAsJsonObject();
                        String displayName = user.get("display_name").getAsString();
                        String userId = user.get("user_id").getAsString();
                        System.out.println("- " + displayName + " (" + userId + ")");
                    }
                } else {
                    System.out.println("Failed to get appointment users");
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'calendar-get-appointment-types': {
    id: 'calendar-get-appointment-types',
    title: 'Get Appointment Types',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'GET',
    path: '/calendar/get-appointment-types',
    description: 'Retrieves all appointment types configured for your organization with their access permissions. This endpoint returns appointment type details including user access rights - either public (visible to all users) or restricted (visible to specific users only).',
    parameters: [],
    responses: [
      {
        code: '200',
        description: 'Success - Appointment types retrieved',
        example: JSON.stringify({
          success: true,
          data: [
            {
              id: "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
              name: "Business Consultation",
              color_code: "#FF5733",
              duration: 60,
              tag: "Consultation",
              tag_color_code: "#33FF57",
              product_id: "product-uuid-here",
              price: 150.00,
              can_all_users_see: true,
              access_users: []
            },
            {
              id: "02d1f443-g6c5-5g8f-b6eg-g761de4g90bb",
              name: "Private Meeting",
              color_code: "#3366CC",
              duration: 30,
              tag: "Private",
              tag_color_code: "#CC3366",
              product_id: "another-product-uuid",
              price: 75.50,
              can_all_users_see: false,
              access_users: [
                {
                  user_id: "user-uuid-1",
                  display_name: "Yossi Cohen"
                },
                {
                  user_id: "user-uuid-2",
                  display_name: "Rachel Levy"
                }
              ]
            }
          ],
          count: 2
        }, null, 2)
      },
      {
        code: '401',
        description: 'Missing Authorization',
        example: JSON.stringify({
          error: "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Invalid API Key',
        example: JSON.stringify({
          error: "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Server Error',
        example: JSON.stringify({
          error: "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/calendar/get-appointment-types';

fetch(url, {
    method: 'GET',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    }
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log(\`Found \${data.count} appointment types:\`);
        data.data.forEach(appointmentType => {
            const priceInfo = appointmentType.price ? \` - ₪\${appointmentType.price}\` : '';
            console.log(\`- \${appointmentType.name} (\${appointmentType.duration} min)\${priceInfo}\`);
            if (appointmentType.can_all_users_see) {
                console.log('  Visible to all users');
            } else {
                console.log(\`  Restricted to \${appointmentType.access_users.length} users\`);
            }
        });
    } else {
        console.error('Failed to get appointment types');
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/calendar/get-appointment-types"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

response = requests.get(url, headers=headers)

if response.status_code == 200:
    data = response.json()
    if data['success']:
        print(f"Found {data['count']} appointment types:")
        for appointment_type in data['data']:
            price_info = f" - ₪{appointment_type['price']}" if appointment_type['price'] else ""
            print(f"- {appointment_type['name']} ({appointment_type['duration']} min){price_info}")
            if appointment_type['can_all_users_see']:
                print("  Visible to all users")
            else:
                print(f"  Restricted to {len(appointment_type['access_users'])} users")
    else:
        print("Failed to get appointment types")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X GET "https://dev.clickai.live/calendar/get-appointment-types" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json"`,
      'curl-linux': `curl -X GET "https://dev.clickai.live/calendar/get-appointment-types" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json"`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/calendar/get-appointment-types';

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    if ($data['success']) {
        echo "Found " . $data['count'] . " appointment types:\\n";
        foreach ($data['data'] as $appointmentType) {
            $priceInfo = $appointmentType['price'] ? " - ₪" . $appointmentType['price'] : "";
            echo "- " . $appointmentType['name'] . " (" . $appointmentType['duration'] . " min)" . $priceInfo . "\\n";
            if ($appointmentType['can_all_users_see']) {
                echo "  Visible to all users\\n";
            } else {
                echo "  Restricted to " . count($appointmentType['access_users']) . " users\\n";
            }
        }
    } else {
        echo "Failed to get appointment types\\n";
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/calendar/get-appointment-types";
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            HttpResponseMessage response = await client.GetAsync(url);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic data = JsonConvert.DeserializeObject(responseContent);
                if (data.success == true)
                {
                    Console.WriteLine($"Found {data.count} appointment types:");
                    foreach (var appointmentType in data.data)
                    {
                        string priceInfo = appointmentType.price != null ? $" - ₪{appointmentType.price}" : "";
                        Console.WriteLine($"- {appointmentType.name} ({appointmentType.duration} min){priceInfo}");
                        if (appointmentType.can_all_users_see == true)
                        {
                            Console.WriteLine("  Visible to all users");
                        }
                        else
                        {
                            Console.WriteLine($"  Restricted to {appointmentType.access_users.Count} users");
                        }
                    }
                }
                else
                {
                    Console.WriteLine("Failed to get appointment types");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/calendar/get-appointment-types')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Get.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'

response = http.request(request)

if response.code == '200'
  data = JSON.parse(response.body)
  if data['success']
    puts "Found #{data['count']} appointment types:"
    data['data'].each do |appointment_type|
      price_info = appointment_type['price'] ? " - ₪#{appointment_type['price']}" : ""
      puts "- #{appointment_type['name']} (#{appointment_type['duration']} min)#{price_info}"
      if appointment_type['can_all_users_see']
        puts "  Visible to all users"
      else
        puts "  Restricted to #{appointment_type['access_users'].length} users"
      end
    end
  else
    puts "Failed to get appointment types"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.JsonObject;
import com.google.gson.JsonParser;
import com.google.gson.JsonArray;

public class AppointmentTypesClient {
    private static final OkHttpClient client = new OkHttpClient();
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/calendar/get-appointment-types";
        
        Request request = new Request.Builder()
            .url(url)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject data = JsonParser.parseString(responseBody).getAsJsonObject();
                if (data.get("success").getAsBoolean()) {
                    int count = data.get("count").getAsInt();
                    System.out.println("Found " + count + " appointment types:");
                    
                    JsonArray appointmentTypes = data.getAsJsonArray("data");
                    for (int i = 0; i < appointmentTypes.size(); i++) {
                        JsonObject appointmentType = appointmentTypes.get(i).getAsJsonObject();
                        String name = appointmentType.get("name").getAsString();
                        int duration = appointmentType.get("duration").getAsInt();
                        boolean canAllUsersSee = appointmentType.get("can_all_users_see").getAsBoolean();
                        
                        String priceInfo = "";
                        if (appointmentType.has("price") && !appointmentType.get("price").isJsonNull()) {
                            double price = appointmentType.get("price").getAsDouble();
                            priceInfo = " - ₪" + price;
                        }
                        
                        System.out.println("- " + name + " (" + duration + " min)" + priceInfo);
                        if (canAllUsersSee) {
                            System.out.println("  Visible to all users");
                        } else {
                            JsonArray accessUsers = appointmentType.getAsJsonArray("access_users");
                            System.out.println("  Restricted to " + accessUsers.size() + " users");
                        }
                    }
                } else {
                    System.out.println("Failed to get appointment types");
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'calendar-schedule-meeting': {
    id: 'calendar-schedule-meeting',
    title: 'Schedule Meeting',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'POST',
    path: '/calendar/schedule-meeting',
    description: 'Creates a new appointment with comprehensive validation and automatic conflict resolution. Includes organization membership verification, user authorization for appointment types, working hours validation, conflict detection, and automatic customer creation for new clients.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'appointment_type_id',
        type: 'string',
        required: true,
        description: 'ID of the appointment type (UUID format)',
        example: '1b9842cf-72f4-417c-b998-12a0049f33ac'
      },
      {
        name: 'date',
        type: 'string',
        required: true,
        description: 'Date in YYYY-MM-DD format',
        example: '2025-06-30'
      },
      {
        name: 'start_time',
        type: 'string',
        required: true,
        description: 'Start time in HH:MM format',
        example: '12:30'
      },
      {
        name: 'end_time',
        type: 'string',
        required: true,
        description: 'End time in HH:MM format',
        example: '13:30'
      },
      {
        name: 'user_id',
        type: 'string',
        required: true,
        description: 'ID of the user who will conduct the appointment (UUID format)',
        example: '2c29759a-e549-4135-a94a-c6e844999c67'
      },
      {
        name: 'customer_id',
        type: 'string',
        required: false,
        description: 'ID of existing customer (mutually exclusive with lead_id and manual customer data)',
        example: '091cd08c-5dc3-456d-9d43-8918a549cfea'
      },
      {
        name: 'lead_id',
        type: 'string',
        required: false,
        description: 'ID of existing lead (mutually exclusive with customer_id and manual customer data)',
        example: 'a7be24f6-c304-4279-abc0-9fdb2fb2092d'
      },
      {
        name: 'first_name',
        type: 'string',
        required: false,
        description: 'First name for new customer (required with last_name and phone if no customer_id/lead_id)',
        example: 'John'
      },
      {
        name: 'last_name',
        type: 'string',
        required: false,
        description: 'Last name for new customer (required with first_name and phone if no customer_id/lead_id)',
        example: 'Doe'
      },
      {
        name: 'phone',
        type: 'string',
        required: false,
        description: 'Phone number for new customer (required with first_name and last_name if no customer_id/lead_id)',
        example: '0501234567'
      },
      {
        name: 'email',
        type: 'string',
        required: false,
        description: 'Email address (optional for new customers)',
        example: 'john.doe@email.com'
      },
      {
        name: 'title',
        type: 'string',
        required: false,
        description: 'Custom appointment title (auto-generated if not provided)',
        example: 'Special Consultation'
      },
      {
        name: 'location',
        type: 'string',
        required: false,
        description: 'Appointment location',
        example: 'Office Room 1'
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: 'Additional appointment description',
        example: 'Initial consultation meeting'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Created - Appointment scheduled successfully',
        example: JSON.stringify({
          success: true,
          data: {
            appointment_id: '12345678-1234-1234-1234-123456789012',
            appointment_type: 'Consultation',
            user_id: '2c29759a-e549-4135-a94a-c6e844999c67',
            date: '2025-06-30',
            start_time: '12:30',
            end_time: '13:30',
            duration_minutes: 60,
            title: 'Consultation With John Doe',
            location: 'Office Room 1',
            participant_id: '87654321-4321-4321-4321-210987654321',
            participant_type: 'customer'
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Invalid input data or validation failure',
        example: JSON.stringify({
          error: 'Duration must match appointment type'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Authorization failure or forbidden access',
        example: JSON.stringify({
          error: 'User not authorized for this appointment type'
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Resource not found',
        example: JSON.stringify({
          error: 'Appointment type not found'
        }, null, 2)
      },
      {
        code: '409',
        description: 'Conflict - Time conflict with existing appointment',
        example: JSON.stringify({
          error: 'Time conflict with existing appointment'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Server Error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const url = 'https://dev.clickai.live/calendar/schedule-meeting';

const scheduleAppointment = async (appointmentData) => {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer your_api_key_here',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(appointmentData)
        });
        
        const result = await response.json();
        
        if (response.ok) {
            console.log('Appointment created:', result.data);
            return result.data;
        } else {
            console.error('Error:', result.error);
            throw new Error(result.error);
        }
    } catch (error) {
        console.error('Network error:', error.message);
        throw error;
    }
};

// Example: Schedule with existing customer
const appointmentData = {
    appointment_type_id: "1b9842cf-72f4-417c-b998-12a0049f33ac",
    date: "2025-06-30",
    start_time: "12:30",
    end_time: "13:30",
    user_id: "2c29759a-e549-4135-a94a-c6e844999c67",
    customer_id: "091cd08c-5dc3-456d-9d43-8918a549cfea",
    location: "Main Office"
};

scheduleAppointment(appointmentData);`,
      python: `import requests

url = "https://dev.clickai.live/calendar/schedule-meeting"

headers = {
    "Authorization": "Bearer your_api_key_here",
    "Content-Type": "application/json"
}

# Example: Schedule with new customer
data = {
    "appointment_type_id": "1b9842cf-72f4-417c-b998-12a0049f33ac",
    "date": "2025-06-30",
    "start_time": "12:30",
    "end_time": "13:30",
    "user_id": "2c29759a-e549-4135-a94a-c6e844999c67",
    "first_name": "John",
    "last_name": "Doe",
    "phone": "0501234567",
    "email": "john.doe@email.com"
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 201:
    result = response.json()
    print(f"Appointment created successfully!")
    print(f"Appointment ID: {result['data']['appointment_id']}")
    print(f"Title: {result['data']['title']}")
else:
    error = response.json()
    print(f"Error: {error['error']}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/calendar/schedule-meeting" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"appointment_type_id\\":\\"1b9842cf-72f4-417c-b998-12a0049f33ac\\",\\"date\\":\\"2025-06-30\\",\\"start_time\\":\\"12:30\\",\\"end_time\\":\\"13:30\\",\\"user_id\\":\\"2c29759a-e549-4135-a94a-c6e844999c67\\",\\"lead_id\\":\\"a7be24f6-c304-4279-abc0-9fdb2fb2092d\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/calendar/schedule-meeting" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "appointment_type_id": "1b9842cf-72f4-417c-b998-12a0049f33ac",
    "date": "2025-06-30",
    "start_time": "12:30",
    "end_time": "13:30",
    "user_id": "2c29759a-e549-4135-a94a-c6e844999c67",
    "customer_id": "091cd08c-5dc3-456d-9d43-8918a549cfea"
  }'`,
      php: `<?php
$url = 'https://dev.clickai.live/calendar/schedule-meeting';

$data = [
    'appointment_type_id' => '1b9842cf-72f4-417c-b998-12a0049f33ac',
    'date' => '2025-06-30',
    'start_time' => '12:30',
    'end_time' => '13:30',
    'user_id' => '2c29759a-e549-4135-a94a-c6e844999c67',
    'lead_id' => 'a7be24f6-c304-4279-abc0-9fdb2fb2092d',
    'location' => 'Meeting Room A'
];

$options = [
    'http' => [
        'header' => [
            'Authorization: Bearer your_api_key_here',
            'Content-Type: application/json'
        ],
        'method' => 'POST',
        'content' => json_encode($data)
    ]
];

$context = stream_context_create($options);
$response = file_get_contents($url, false, $context);

if ($response !== FALSE) {
    $result = json_decode($response, true);
    if (isset($result['success']) && $result['success']) {
        echo "Appointment created successfully!\\n";
        echo "Appointment ID: " . $result['data']['appointment_id'] . "\\n";
        echo "Title: " . $result['data']['title'] . "\\n";
    } else {
        echo "Error: " . $result['error'] . "\\n";
    }
} else {
    echo "Request failed\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class AppointmentScheduler
{
    private readonly HttpClient httpClient;
    
    public AppointmentScheduler()
    {
        httpClient = new HttpClient();
        httpClient.DefaultRequestHeaders.Add("Authorization", "Bearer your_api_key_here");
    }
    
    public async Task<object> ScheduleAppointmentAsync(object appointmentData)
    {
        string url = "https://dev.clickai.live/calendar/schedule-meeting";
        
        string json = JsonConvert.SerializeObject(appointmentData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine("Appointment created successfully!");
                return result;
            }
            else
            {
                var error = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine($"Error: {error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return null;
        }
    }
}`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

url = URI('https://dev.clickai.live/calendar/schedule-meeting')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = 'Bearer your_api_key_here'
request['Content-Type'] = 'application/json'

request.body = {
  appointment_type_id: '1b9842cf-72f4-417c-b998-12a0049f33ac',
  date: '2025-06-30',
  start_time: '12:30',
  end_time: '13:30',
  user_id: '2c29759a-e549-4135-a94a-c6e844999c67',
  first_name: 'John',
  last_name: 'Doe',
  phone: '0501234567',
  email: 'john.doe@email.com'
}.to_json

response = http.request(request)

if response.code == '201'
  result = JSON.parse(response.body)
  puts "Appointment created successfully!"
  puts "Appointment ID: #{result['data']['appointment_id']}"
  puts "Title: #{result['data']['title']}"
else
  error = JSON.parse(response.body)
  puts "Error: #{error['error']}"
end`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;

public class AppointmentScheduler {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    
    public AppointmentScheduler() {
        this.httpClient = HttpClient.newHttpClient();
        this.objectMapper = new ObjectMapper();
    }
    
    public void scheduleAppointment(Map<String, Object> appointmentData) {
        try {
            String url = "https://dev.clickai.live/calendar/schedule-meeting";
            String json = objectMapper.writeValueAsString(appointmentData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer your_api_key_here")
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 201) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                Map<String, Object> data = (Map<String, Object>) result.get("data");
                System.out.println("Appointment created successfully!");
                System.out.println("Appointment ID: " + data.get("appointment_id"));
                System.out.println("Title: " + data.get("title"));
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
        }
    }
}`
    }
  },

  'calendar-appointment-type-details': {
    id: 'calendar-appointment-type-details',
    title: 'Get Appointment Type Details',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'POST',
    path: '/calendar/appointment-type-details',
    description: 'Retrieves detailed information about a specific appointment type by its ID. This endpoint returns complete appointment type details including user access permissions and product pricing information. The appointment type must belong to your organization.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'id',
        type: 'string',
        required: true,
        description: 'The unique ID of the appointment type',
        example: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Appointment type details retrieved',
        example: JSON.stringify({
          success: true,
          data: {
            id: "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
            name: "Business Consultation",
            color_code: "#FF5733",
            duration: 60,
            tag: "Consultation",
            tag_color_code: "#33FF57",
            product_id: "product-uuid-here",
            price: 150.00,
            can_all_users_see: true,
            access_users: []
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Missing Request Body',
        example: JSON.stringify({
          error: "Request body is required"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Missing ID',
        example: JSON.stringify({
          error: "Appointment type ID is required"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Missing Authorization',
        example: JSON.stringify({
          error: "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Invalid API Key',
        example: JSON.stringify({
          error: "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Wrong Organization',
        example: JSON.stringify({
          error: "Appointment type does not belong to your organization"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found',
        example: JSON.stringify({
          error: "Appointment type not found"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Server Error',
        example: JSON.stringify({
          error: "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/calendar/appointment-type-details';

const data = {
    id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
})
.then(response => response.json())
.then(result => {
    if (result.success) {
        const appointment = result.data;
        const priceInfo = appointment.price ? \` - ₪\${appointment.price}\` : '';
        console.log(\`Appointment Type: \${appointment.name} (\${appointment.duration} min)\${priceInfo}\`);
        console.log(\`Color: \${appointment.color_code}\`);
        if (appointment.tag) {
            console.log(\`Tag: \${appointment.tag} (\${appointment.tag_color_code})\`);
        }
        
        if (appointment.can_all_users_see) {
            console.log('Visible to all users');
        } else {
            console.log(\`Restricted to \${appointment.access_users.length} users:\`);
            appointment.access_users.forEach(user => {
                console.log(\`  - \${user.display_name} (\${user.user_id})\`);
            });
        }
    } else {
        console.error('Failed to get appointment type details');
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/calendar/appointment-type-details"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

data = {
    "id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa"
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    if result['success']:
        appointment = result['data']
        price_info = f" - ₪{appointment['price']}" if appointment['price'] else ""
        print(f"Appointment Type: {appointment['name']} ({appointment['duration']} min){price_info}")
        print(f"Color: {appointment['color_code']}")
        if appointment['tag']:
            print(f"Tag: {appointment['tag']} ({appointment['tag_color_code']})")
        
        if appointment['can_all_users_see']:
            print("Visible to all users")
        else:
            print(f"Restricted to {len(appointment['access_users'])} users:")
            for user in appointment['access_users']:
                print(f"  - {user['display_name']} ({user['user_id']})")
    else:
        print("Failed to get appointment type details")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/calendar/appointment-type-details" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"id\\":\\"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/calendar/appointment-type-details" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"id":"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa"}'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/calendar/appointment-type-details';

$data = [
    'id' => '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $result = json_decode($response, true);
    if ($result['success']) {
        $appointment = $result['data'];
        $priceInfo = $appointment['price'] ? " - ₪" . $appointment['price'] : "";
        echo "Appointment Type: " . $appointment['name'] . " (" . $appointment['duration'] . " min)" . $priceInfo . "\\n";
        echo "Color: " . $appointment['color_code'] . "\\n";
        if ($appointment['tag']) {
            echo "Tag: " . $appointment['tag'] . " (" . $appointment['tag_color_code'] . ")\\n";
        }
        
        if ($appointment['can_all_users_see']) {
            echo "Visible to all users\\n";
        } else {
            echo "Restricted to " . count($appointment['access_users']) . " users:\\n";
            foreach ($appointment['access_users'] as $user) {
                echo "  - " . $user['display_name'] . " (" . $user['user_id'] . ")\\n";
            }
        }
    } else {
        echo "Failed to get appointment type details\\n";
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/calendar/appointment-type-details";
        
        var requestData = new
        {
            id = "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa"
        };
        
        string jsonData = JsonConvert.SerializeObject(requestData);
        var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic result = JsonConvert.DeserializeObject(responseContent);
                if (result.success == true)
                {
                    var appointment = result.data;
                    string priceInfo = appointment.price != null ? $" - ₪{appointment.price}" : "";
                    Console.WriteLine($"Appointment Type: {appointment.name} ({appointment.duration} min){priceInfo}");
                    Console.WriteLine($"Color: {appointment.color_code}");
                    if (appointment.tag != null)
                    {
                        Console.WriteLine($"Tag: {appointment.tag} ({appointment.tag_color_code})");
                    }
                    
                    if (appointment.can_all_users_see == true)
                    {
                        Console.WriteLine("Visible to all users");
                    }
                    else
                    {
                        Console.WriteLine($"Restricted to {appointment.access_users.Count} users:");
                        foreach (var user in appointment.access_users)
                        {
                            Console.WriteLine($"  - {user.display_name} ({user.user_id})");
                        }
                    }
                }
                else
                {
                    Console.WriteLine("Failed to get appointment type details");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/calendar/appointment-type-details')

data = {
  id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa'
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  if result['success']
    appointment = result['data']
    price_info = appointment['price'] ? " - ₪#{appointment['price']}" : ""
    puts "Appointment Type: #{appointment['name']} (#{appointment['duration']} min)#{price_info}"
    puts "Color: #{appointment['color_code']}"
    if appointment['tag']
      puts "Tag: #{appointment['tag']} (#{appointment['tag_color_code']})"
    end
    
    if appointment['can_all_users_see']
      puts "Visible to all users"
    else
      puts "Restricted to #{appointment['access_users'].length} users:"
      appointment['access_users'].each do |user|
        puts "  - #{user['display_name']} (#{user['user_id']})"
      end
    end
  else
    puts "Failed to get appointment type details"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.JsonObject;
import com.google.gson.JsonParser;
import com.google.gson.JsonArray;

public class AppointmentTypeDetailsClient {
    private static final OkHttpClient client = new OkHttpClient();
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/calendar/appointment-type-details";
        
        JsonObject requestData = new JsonObject();
        requestData.addProperty("id", "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa");
        
        RequestBody body = RequestBody.create(requestData.toString(), JSON);
        Request request = new Request.Builder()
            .url(url)
            .post(body)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject result = JsonParser.parseString(responseBody).getAsJsonObject();
                if (result.get("success").getAsBoolean()) {
                    JsonObject appointment = result.getAsJsonObject("data");
                    String name = appointment.get("name").getAsString();
                    int duration = appointment.get("duration").getAsInt();
                    String colorCode = appointment.get("color_code").getAsString();
                    boolean canAllUsersSee = appointment.get("can_all_users_see").getAsBoolean();
                    
                    String priceInfo = "";
                    if (appointment.has("price") && !appointment.get("price").isJsonNull()) {
                        double price = appointment.get("price").getAsDouble();
                        priceInfo = " - ₪" + price;
                    }
                    
                    System.out.println("Appointment Type: " + name + " (" + duration + " min)" + priceInfo);
                    System.out.println("Color: " + colorCode);
                    
                    if (appointment.has("tag") && !appointment.get("tag").isJsonNull()) {
                        String tag = appointment.get("tag").getAsString();
                        String tagColor = appointment.get("tag_color_code").getAsString();
                        System.out.println("Tag: " + tag + " (" + tagColor + ")");
                    }
                    
                    if (canAllUsersSee) {
                        System.out.println("Visible to all users");
                    } else {
                        JsonArray accessUsers = appointment.getAsJsonArray("access_users");
                        System.out.println("Restricted to " + accessUsers.size() + " users:");
                        for (int i = 0; i < accessUsers.size(); i++) {
                            JsonObject user = accessUsers.get(i).getAsJsonObject();
                            String displayName = user.get("display_name").getAsString();
                            String userId = user.get("user_id").getAsString();
                            System.out.println("  - " + displayName + " (" + userId + ")");
                        }
                    }
                } else {
                    System.out.println("Failed to get appointment type details");
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'calendar-get-user-working-hours': {
    id: 'calendar-get-user-working-hours',
    title: 'Get User Working Hours',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'POST',
    path: '/calendar/get-user-working-hours',
    description: 'Retrieves the working hours for a specific user from the employee_availability table. This endpoint returns the weekly schedule showing which days the user is available and their working hours for each day.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'user_id',
        type: 'string',
        required: true,
        description: 'User ID to get working hours for',
        example: '2c29759a-e549-4135-a94a-c6e844999c67'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Working hours retrieved',
        example: JSON.stringify({
          success: true,
          data: {
            user_id: '2c29759a-e549-4135-a94a-c6e844999c67',
            working_hours: {
              sunday: {
                start: '09:00',
                end: '17:00'
              },
              monday: {
                start: '08:30',
                end: '16:30'
              },
              tuesday: null,
              wednesday: {
                start: '09:00',
                end: '17:00'
              },
              thursday: {
                start: '09:00',
                end: '17:00'
              },
              friday: {
                start: '09:00',
                end: '14:00'
              },
              saturday: null
            }
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Missing Required Field',
        example: JSON.stringify({
          error: "Field 'user_id' is required"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Missing Authorization',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Invalid API Key or User Not in Organization',
        example: JSON.stringify({
          error: 'Forbidden: User does not belong to your organization'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Server Error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/calendar/get-user-working-hours';

const requestData = {
    user_id: '2c29759a-e549-4135-a94a-c6e844999c67'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        const workingHours = data.data.working_hours;
        console.log('Working hours:');
        Object.entries(workingHours).forEach(([day, hours]) => {
            if (hours) {
                console.log(\`- \${day}: \${hours.start} - \${hours.end}\`);
            } else {
                console.log(\`- \${day}: Not available\`);
            }
        });
    }
})
.catch(error => console.error('Error:', error));`,
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/calendar/get-user-working-hours'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

data = {
    'user_id': '2c29759a-e549-4135-a94a-c6e844999c67'
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    if result['success']:
        working_hours = result['data']['working_hours']
        print('Working hours:')
        for day, hours in working_hours.items():
            if hours:
                print(f"- {day}: {hours['start']} - {hours['end']}")
            else:
                print(f"- {day}: Not available")
else:
    print(f'Error {response.status_code}: {response.json()}')`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/calendar/get-user-working-hours" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"user_id\\":\\"2c29759a-e549-4135-a94a-c6e844999c67\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/calendar/get-user-working-hours" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "2c29759a-e549-4135-a94a-c6e844999c67"
  }'`,
      php: `<?php
$apiKey = 'your_api_key_here';
$url = 'https://dev.clickai.live/calendar/get-user-working-hours';

$data = array(
    'user_id' => '2c29759a-e549-4135-a94a-c6e844999c67'
);

$options = array(
    'http' => array(
        'header' => array(
            "Authorization: Bearer $apiKey",
            "Content-Type: application/json"
        ),
        'method' => 'POST',
        'content' => json_encode($data)
    )
);

$context = stream_context_create($options);
$response = file_get_contents($url, false, $context);

if ($response !== FALSE) {
    $result = json_decode($response, true);
    if ($result['success']) {
        $workingHours = $result['data']['working_hours'];
        echo "Working hours:\\n";
        foreach ($workingHours as $day => $hours) {
            $dayName = ucfirst($day);
            if ($hours) {
                echo "- $dayName: {$hours['start']} - {$hours['end']}\\n";
            } else {
                echo "- $dayName: Not available\\n";
            }
        }
    }
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/calendar/get-user-working-hours";
        
        var requestData = new
        {
            user_id = "2c29759a-e549-4135-a94a-c6e844999c67"
        };
        
        string jsonData = JsonConvert.SerializeObject(requestData);
        var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseBody = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic result = JsonConvert.DeserializeObject(responseBody);
                if (result.success == true)
                {
                    var workingHours = result.data.working_hours;
                    Console.WriteLine("Working hours:");
                    foreach (var dayProperty in workingHours)
                    {
                        string day = dayProperty.Name;
                        var hours = dayProperty.Value;
                        if (hours != null && hours.Type != Newtonsoft.Json.Linq.JTokenType.Null)
                        {
                            Console.WriteLine($"- {day}: {hours.start} - {hours.end}");
                        }
                        else
                        {
                            Console.WriteLine($"- {day}: Not available");
                        }
                    }
                }
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/calendar/get-user-working-hours')

data = {
  user_id: '2c29759a-e549-4135-a94a-c6e844999c67'
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  if result['success']
    working_hours = result['data']['working_hours']
    puts "Working hours:"
    working_hours.each do |day, hours|
      if hours
        puts "- #{day}: #{hours['start']} - #{hours['end']}"
      else
        puts "- #{day}: Not available"
      end
    end
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.*;
import java.io.IOException;

public class GetUserWorkingHours {
    private static final OkHttpClient client = new OkHttpClient();
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/calendar/get-user-working-hours";
        
        JsonObject requestData = new JsonObject();
        requestData.addProperty("user_id", "2c29759a-e549-4135-a94a-c6e844999c67");
        
        RequestBody body = RequestBody.create(requestData.toString(), JSON);
        Request request = new Request.Builder()
            .url(url)
            .post(body)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            if (response.isSuccessful()) {
                JsonObject result = JsonParser.parseString(response.body().string()).getAsJsonObject();
                if (result.get("success").getAsBoolean()) {
                    JsonObject workingHours = result.getAsJsonObject("data").getAsJsonObject("working_hours");
                    System.out.println("Working hours:");
                    String[] days = {"sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"};
                    
                    for (String day : days) {
                        JsonElement dayElement = workingHours.get(day);
                        if (!dayElement.isJsonNull()) {
                            JsonObject hours = dayElement.getAsJsonObject();
                            System.out.println("- " + day + ": " + hours.get("start").getAsString() + " - " + hours.get("end").getAsString());
                        } else {
                            System.out.println("- " + day + ": Not available");
                        }
                    }
                }
            }
        }
    }
}`
    }
  },

  'calendar-get-free-time-slot': {
    id: 'calendar-get-free-time-slot',
    title: 'Get Free Time Slots',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'POST',
    path: '/calendar/get-free-time-slot',
    description: 'Returns available time slots for a specific appointment type on a specific date. This endpoint analyzes working hours, existing appointments, and break times for all authorized users to determine when new appointments can be scheduled.',
    parameters: [
      {
        name: 'appointment_type_id',
        type: 'string',
        required: true,
        description: 'The ID of the appointment type'
      },
      {
        name: 'date',
        type: 'string',
        required: true,
        description: 'Date in YYYY-MM-DD format'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Available slots returned',
        example: JSON.stringify({
          success: true,
          data: {
            success: true,
            date: "2024-01-15",
            appointment_type_id: "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
            appointment_type_name: "Business Consultation",
            duration_minutes: 60,
            available_slots: [
              {
                user_id: "user-uuid-1",
                display_name: "Yossi Cohen",
                slots: [
                  { start: "09:00", end: "10:00" },
                  { start: "14:00", end: "15:00" },
                  { start: "16:30", end: "17:30" }
                ]
              },
              {
                user_id: "user-uuid-2",
                display_name: "Sarah Levy",
                slots: [
                  { start: "10:30", end: "11:30" },
                  { start: "13:00", end: "14:00" }
                ]
              }
            ]
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing required fields',
        example: JSON.stringify({
          error: "Field 'appointment_type_id' is required"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Missing Authorization',
        example: JSON.stringify({
          error: "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Invalid API Key',
        example: JSON.stringify({
          error: "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Server Error',
        example: JSON.stringify({
          error: "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const getFreeTimeSlots = async (appointmentTypeId, date) => {
    const apiKey = 'your_api_key_here';
    const url = 'https://dev.clickai.live/calendar/get-free-time-slot';
    
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': \`Bearer \${apiKey}\`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                appointment_type_id: appointmentTypeId,
                date: date
            })
        });
        
        const result = await response.json();
        
        if (response.ok && result.success) {
            const slotsData = result.data;
            console.log(\`Available slots for \${slotsData.appointment_type_name} on \${slotsData.date}:\`);
            console.log(\`Duration: \${slotsData.duration_minutes} minutes\`);
            
            slotsData.available_slots.forEach(user => {
                console.log(\`\\n\${user.display_name} (\${user.user_id}):\`);
                if (user.slots.length > 0) {
                    user.slots.forEach(slot => {
                        console.log(\`  \${slot.start} - \${slot.end}\`);
                    });
                } else {
                    console.log('  No available slots');
                }
            });
            
            return slotsData;
        } else {
            console.error('Error:', result.error || 'Failed to get free time slots');
            return null;
        }
    } catch (error) {
        console.error('Request failed:', error);
        return null;
    }
};

// Usage
getFreeTimeSlots('01c0f332-f5b4-4f7e-a5df-f650cd3f89aa', '2024-01-15');`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/calendar/get-free-time-slot"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

data = {
    "appointment_type_id": "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
    "date": "2024-01-15"
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    if result['success']:
        slots_data = result['data']
        print(f"Available slots for {slots_data['appointment_type_name']} on {slots_data['date']}:")
        print(f"Duration: {slots_data['duration_minutes']} minutes")
        
        for user in slots_data['available_slots']:
            print(f"\\n{user['display_name']} ({user['user_id']}):")
            if user['slots']:
                for slot in user['slots']:
                    print(f"  {slot['start']} - {slot['end']}")
            else:
                print("  No available slots")
    else:
        print("Failed to get free time slots")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/calendar/get-free-time-slot" ^
     -H "Authorization: Bearer your_api_key_here" ^
     -H "Content-Type: application/json" ^
     -d "{\\"appointment_type_id\\":\\"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa\\",\\"date\\":\\"2024-01-15\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/calendar/get-free-time-slot" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"appointment_type_id":"01c0f332-f5b4-4f7e-a5df-f650cd3f89aa","date":"2024-01-15"}'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/calendar/get-free-time-slot';

$data = [
    'appointment_type_id' => '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
    'date' => '2024-01-15'
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $result = json_decode($response, true);
    if ($result['success']) {
        $slotsData = $result['data'];
        echo "Available slots for " . $slotsData['appointment_type_name'] . " on " . $slotsData['date'] . ":\\n";
        echo "Duration: " . $slotsData['duration_minutes'] . " minutes\\n";
        
        foreach ($slotsData['available_slots'] as $user) {
            echo "\\n" . $user['display_name'] . " (" . $user['user_id'] . "):\\n";
            if (!empty($user['slots'])) {
                foreach ($user['slots'] as $slot) {
                    echo "  " . $slot['start'] . " - " . $slot['end'] . "\\n";
                }
            } else {
                echo "  No available slots\\n";
            }
        }
    } else {
        echo "Failed to get free time slots\\n";
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/calendar/get-free-time-slot";
        
        var requestData = new
        {
            appointment_type_id = "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa",
            date = "2024-01-15"
        };
        
        var json = JsonConvert.SerializeObject(requestData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic result = JsonConvert.DeserializeObject(responseContent);
                if (result.success == true)
                {
                    var slotsData = result.data;
                    Console.WriteLine($"Available slots for {slotsData.appointment_type_name} on {slotsData.date}:");
                    Console.WriteLine($"Duration: {slotsData.duration_minutes} minutes");
                    
                    foreach (var user in slotsData.available_slots)
                    {
                        Console.WriteLine($"\\n{user.display_name} ({user.user_id}):");
                        if (user.slots.Count > 0)
                        {
                            foreach (var slot in user.slots)
                            {
                                Console.WriteLine($"  {slot.start} - {slot.end}");
                            }
                        }
                        else
                        {
                            Console.WriteLine("  No available slots");
                        }
                    }
                }
                else
                {
                    Console.WriteLine("Failed to get free time slots");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/calendar/get-free-time-slot')

data = {
  appointment_type_id: '01c0f332-f5b4-4f7e-a5df-f650cd3f89aa',
  date: '2024-01-15'
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  if result['success']
    slots_data = result['data']
    puts "Available slots for #{slots_data['appointment_type_name']} on #{slots_data['date']}:"
    puts "Duration: #{slots_data['duration_minutes']} minutes"
    
    slots_data['available_slots'].each do |user|
      puts "\\n#{user['display_name']} (#{user['user_id']}):"
      if user['slots'].any?
        user['slots'].each do |slot|
          puts "  #{slot['start']} - #{slot['end']}"
        end
      else
        puts "  No available slots"
      end
    end
  else
    puts "Failed to get free time slots"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.JsonObject;
import com.google.gson.JsonParser;
import com.google.gson.JsonArray;

public class FreeTimeSlotsClient {
    private static final OkHttpClient client = new OkHttpClient();
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/calendar/get-free-time-slot";
        
        JsonObject requestData = new JsonObject();
        requestData.addProperty("appointment_type_id", "01c0f332-f5b4-4f7e-a5df-f650cd3f89aa");
        requestData.addProperty("date", "2024-01-15");
        
        RequestBody body = RequestBody.create(
            requestData.toString(),
            MediaType.parse("application/json")
        );
        
        Request request = new Request.Builder()
            .url(url)
            .post(body)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject result = JsonParser.parseString(responseBody).getAsJsonObject();
                if (result.get("success").getAsBoolean()) {
                    JsonObject slotsData = result.getAsJsonObject("data");
                    String appointmentTypeName = slotsData.get("appointment_type_name").getAsString();
                    String date = slotsData.get("date").getAsString();
                    int duration = slotsData.get("duration_minutes").getAsInt();
                    
                    System.out.println("Available slots for " + appointmentTypeName + " on " + date + ":");
                    System.out.println("Duration: " + duration + " minutes");
                    
                    JsonArray availableSlots = slotsData.getAsJsonArray("available_slots");
                    for (int i = 0; i < availableSlots.size(); i++) {
                        JsonObject user = availableSlots.get(i).getAsJsonObject();
                        String displayName = user.get("display_name").getAsString();
                        String userId = user.get("user_id").getAsString();
                        
                        System.out.println("\\n" + displayName + " (" + userId + "):");
                        
                        JsonArray slots = user.getAsJsonArray("slots");
                        if (slots.size() > 0) {
                            for (int j = 0; j < slots.size(); j++) {
                                JsonObject slot = slots.get(j).getAsJsonObject();
                                String start = slot.get("start").getAsString();
                                String end = slot.get("end").getAsString();
                                System.out.println("  " + start + " - " + end);
                            }
                        } else {
                            System.out.println("  No available slots");
                        }
                    }
                } else {
                    System.out.println("Failed to get free time slots");
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'calendar-get-user-appointment-by-date': {
    id: 'calendar-get-user-appointment-by-date',
    title: 'Get User Appointments by Date',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'POST',
    path: '/appointment/get-user-appointment-by-date',
    description: 'Retrieves all calendar events/appointments for a specific user on a given date. This endpoint returns detailed information about each appointment including participant details from both leads and customers.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'user_id',
        type: 'string',
        required: true,
        description: 'User ID to get appointments for',
        example: '2c29759a-e549-4135-a94a-c6e844999c67'
      },
      {
        name: 'date',
        type: 'string',
        required: true,
        description: 'Date in YYYY-MM-DD format',
        example: '2025-06-29'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Appointments retrieved',
        example: JSON.stringify({
          success: true,
          data: {
            user_id: '2c29759a-e549-4135-a94a-c6e844999c67',
            date: '2025-06-29',
            events: [
              {
                id: 'event-456',
                title: 'Meeting with client',
                location: 'Main office',
                start_time: '09:00',
                end_time: '10:00',
                event_type: 'meeting',
                participants: [
                  {
                    lead_id: 'lead-789',
                    name: 'John Cohen',
                    phone: '0501234567',
                    email: 'john@example.com'
                  },
                  {
                    customer_id: 'customer-101',
                    name: 'Rachel Levy',
                    phone: '0527654321'
                  }
                ]
              }
            ]
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Missing Required Field',
        example: JSON.stringify({
          error: "Field 'user_id' is required"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Missing Authorization',
        example: JSON.stringify({
          error: 'Missing or invalid Authorization header. Expected format: Bearer <api_key>'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Invalid API Key or User Not in Organization',
        example: JSON.stringify({
          error: 'Forbidden: User does not belong to your organization'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Server Error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/appointment/get-user-appointment-by-date';

const requestData = {
    user_id: '2c29759a-e549-4135-a94a-c6e844999c67',
    date: '2025-06-29'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        const events = data.data.events;
        console.log(\`Found \${events.length} appointments\`);
        events.forEach(event => {
            console.log(\`- \${event.title} (\${event.start_time}-\${event.end_time})\`);
        });
    }
})
.catch(error => console.error('Error:', error));`,
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/appointment/get-user-appointment-by-date'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

data = {
    'user_id': '2c29759a-e549-4135-a94a-c6e844999c67',
    'date': '2025-06-29'
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    if result['success']:
        events = result['data']['events']
        print(f'Found {len(events)} appointments')
        for event in events:
            print(f"- {event['title']} ({event['start_time']}-{event['end_time']})")
else:
    print(f'Error {response.status_code}: {response.json()}')`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/appointment/get-user-appointment-by-date" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"user_id\\":\\"2c29759a-e549-4135-a94a-c6e844999c67\\",\\"date\\":\\"2025-06-29\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/appointment/get-user-appointment-by-date" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "2c29759a-e549-4135-a94a-c6e844999c67",
    "date": "2025-06-29"
  }'`,
      php: `<?php
$apiKey = 'your_api_key_here';
$url = 'https://dev.clickai.live/appointment/get-user-appointment-by-date';

$data = array(
    'user_id' => '2c29759a-e549-4135-a94a-c6e844999c67',
    'date' => '2025-06-29'
);

$options = array(
    'http' => array(
        'header' => array(
            "Authorization: Bearer $apiKey",
            "Content-Type: application/json"
        ),
        'method' => 'POST',
        'content' => json_encode($data)
    )
);

$context = stream_context_create($options);
$response = file_get_contents($url, false, $context);

if ($response !== FALSE) {
    $result = json_decode($response, true);
    if ($result['success']) {
        $events = $result['data']['events'];
        echo "Found " . count($events) . " appointments\\n";
        foreach ($events as $event) {
            echo "- {$event['title']} ({$event['start_time']}-{$event['end_time']})\\n";
        }
    }
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/appointment/get-user-appointment-by-date";
        
        var requestData = new
        {
            user_id = "2c29759a-e549-4135-a94a-c6e844999c67",
            date = "2025-06-29"
        };
        
        string jsonData = JsonConvert.SerializeObject(requestData);
        var content = new StringContent(jsonData, Encoding.UTF8, "application/json");
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseBody = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic result = JsonConvert.DeserializeObject(responseBody);
                if (result.success == true)
                {
                    var events = result.data.events;
                    Console.WriteLine($"Found {events.Count} appointments");
                    foreach (var eventItem in events)
                    {
                        Console.WriteLine($"- {eventItem.title} ({eventItem.start_time}-{eventItem.end_time})");
                    }
                }
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/appointment/get-user-appointment-by-date')

data = {
  user_id: '2c29759a-e549-4135-a94a-c6e844999c67',
  date: '2025-06-29'
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  if result['success']
    events = result['data']['events']
    puts "Found #{events.length} appointments"
    events.each do |event|
      puts "- #{event['title']} (#{event['start_time']}-#{event['end_time']})"
    end
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.*;
import java.io.IOException;

public class GetUserAppointments {
    private static final OkHttpClient client = new OkHttpClient();
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/appointment/get-user-appointment-by-date";
        
        JsonObject requestData = new JsonObject();
        requestData.addProperty("user_id", "2c29759a-e549-4135-a94a-c6e844999c67");
        requestData.addProperty("date", "2025-06-29");
        
        RequestBody body = RequestBody.create(requestData.toString(), JSON);
        Request request = new Request.Builder()
            .url(url)
            .post(body)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            if (response.isSuccessful()) {
                JsonObject result = JsonParser.parseString(response.body().string()).getAsJsonObject();
                if (result.get("success").getAsBoolean()) {
                    JsonArray events = result.getAsJsonObject("data").getAsJsonArray("events");
                    System.out.println("Found " + events.size() + " appointments");
                    for (int i = 0; i < events.size(); i++) {
                        JsonObject event = events.get(i).getAsJsonObject();
                        System.out.println("- " + event.get("title").getAsString() + 
                            " (" + event.get("start_time").getAsString() + "-" + 
                            event.get("end_time").getAsString() + ")");
                    }
                }
            }
        }
    }
}`
    }
  },

  'calendar-get-double-free-time-slot': {
    id: 'calendar-get-double-free-time-slot',
    title: 'Get Double Free Time Slots',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'POST',
    path: '/calendar/get-double-free-time-slot',
    description: 'Finds available time combinations for two different appointment types with overlapping schedules. This endpoint is useful for scheduling appointments that require two different services or practitioners to work together with a specified minimum overlap.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'appointment_type_id_1',
        type: 'string',
        required: true,
        description: 'ID of the first appointment type (UUID format)',
        example: '794bad64-cf3f-441c-b679-25a3949e3361'
      },
      {
        name: 'appointment_type_id_2',
        type: 'string',
        required: true,
        description: 'ID of the second appointment type (UUID format)',
        example: '506becd3-64c6-4ed7-a905-768a5b05d0d4'
      },
      {
        name: 'date',
        type: 'string',
        required: true,
        description: 'Date in YYYY-MM-DD format',
        example: '2025-01-15'
      },
      {
        name: 'overlap_percentage',
        type: 'number',
        required: true,
        description: 'Minimum overlap percentage required between services (0.0-100.0)',
        example: '60'
      },
      {
        name: 'dismiss_overlap',
        type: 'number',
        required: false,
        description: 'Time overlap threshold for filtering similar combinations (0.0-100.0)',
        example: '80'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Available combinations found',
        example: JSON.stringify({
          success: true,
          data: {
            success: true,
            date: '2025-01-15',
            appointment_type_1_name: 'Manicure',
            appointment_type_2_name: 'Massage',
            required_overlap: 60.0,
            available_combinations: [
              {
                users: 'Sarah Cohen + David Levy',
                time: '10:00 - 12:00',
                user1_details: {
                  name: 'Sarah Cohen',
                  user_id: 'uuid1',
                  time: '10:00 - 11:30',
                  service: 'Manicure'
                },
                user2_details: {
                  name: 'David Levy',
                  user_id: 'uuid2',
                  time: '10:30 - 12:00',
                  service: 'Massage'
                },
                overlap_percentage: 66.7
              }
            ]
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Invalid parameters',
        example: JSON.stringify({
          error: 'Overlap percentage must be between 0 and 100'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Invalid API key',
        example: JSON.stringify({
          error: 'Invalid API key'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Server Error',
        example: JSON.stringify({
          error: 'Internal server error'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/calendar/get-double-free-time-slot';

const requestData = {
    appointment_type_id_1: '794bad64-cf3f-441c-b679-25a3949e3361',
    appointment_type_id_2: '506becd3-64c6-4ed7-a905-768a5b05d0d4',
    date: '2025-01-15',
    overlap_percentage: 60,
    dismiss_overlap: 80  // Optional
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        const combinations = data.data.available_combinations;
        console.log(\`Found \${combinations.length} available combinations:\`);
        
        combinations.forEach(combo => {
            console.log(\`\${combo.users} at \${combo.time}\`);
            console.log(\`Overlap: \${combo.overlap_percentage}%\`);
        });
    }
})
.catch(error => console.error('Error:', error));`,
      python: `import requests

api_key = 'your_api_key_here'
url = 'https://dev.clickai.live/calendar/get-double-free-time-slot'

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json'
}

data = {
    'appointment_type_id_1': '794bad64-cf3f-441c-b679-25a3949e3361',
    'appointment_type_id_2': '506becd3-64c6-4ed7-a905-768a5b05d0d4',
    'date': '2025-01-15',
    'overlap_percentage': 60,
    'dismiss_overlap': 80  # Optional
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    combinations = result['data']['available_combinations']
    print(f'Found {len(combinations)} available combinations:')
    
    for combo in combinations:
        print(f"- {combo['users']} at {combo['time']}")
        print(f"  Overlap: {combo['overlap_percentage']}%")
else:
    print(f'Error {response.status_code}: {response.json()}')`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/calendar/get-double-free-time-slot" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"appointment_type_id_1\\":\\"794bad64-cf3f-441c-b679-25a3949e3361\\",\\"appointment_type_id_2\\":\\"506becd3-64c6-4ed7-a905-768a5b05d0d4\\",\\"date\\":\\"2025-01-15\\",\\"overlap_percentage\\":60,\\"dismiss_overlap\\":80}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/calendar/get-double-free-time-slot" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "appointment_type_id_1": "794bad64-cf3f-441c-b679-25a3949e3361",
    "appointment_type_id_2": "506becd3-64c6-4ed7-a905-768a5b05d0d4",
    "date": "2025-01-15",
    "overlap_percentage": 60,
    "dismiss_overlap": 80
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/calendar/get-double-free-time-slot';

$data = [
    'appointment_type_id_1' => '794bad64-cf3f-441c-b679-25a3949e3361',
    'appointment_type_id_2' => '506becd3-64c6-4ed7-a905-768a5b05d0d4',
    'date' => '2025-01-15',
    'overlap_percentage' => 60,
    'dismiss_overlap' => 80  // Optional
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $result = json_decode($response, true);
    $combinations = $result['data']['available_combinations'];
    
    echo "Found " . count($combinations) . " available combinations:\\n";
    
    foreach ($combinations as $combo) {
        echo "- {$combo['users']} at {$combo['time']}\\n";
        echo "  Overlap: {$combo['overlap_percentage']}%\\n";
    }
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

class Program
{
    private static readonly HttpClient client = new HttpClient();
    
    static async Task Main(string[] args)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/calendar/get-double-free-time-slot";
        
        var requestData = new
        {
            appointment_type_id_1 = "794bad64-cf3f-441c-b679-25a3949e3361",
            appointment_type_id_2 = "506becd3-64c6-4ed7-a905-768a5b05d0d4",
            date = "2025-01-15",
            overlap_percentage = 60,
            dismiss_overlap = 80  // Optional
        };
        
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        var json = JsonConvert.SerializeObject(requestData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        try
        {
            HttpResponseMessage response = await client.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                dynamic result = JsonConvert.DeserializeObject(responseContent);
                var combinations = result.data.available_combinations;
                
                Console.WriteLine($"Found {combinations.Count} available combinations:");
                
                foreach (var combo in combinations)
                {
                    Console.WriteLine($"- {combo.users} at {combo.time}");
                    Console.WriteLine($"  Overlap: {combo.overlap_percentage}%");
                }
            }
            else
            {
                Console.WriteLine($"Error {response.StatusCode}: {responseContent}");
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Request failed: {ex.Message}");
        }
    }
}`,
      ruby: `require 'net/http'
require 'json'

api_key = 'your_api_key_here'
url = URI('https://dev.clickai.live/calendar/get-double-free-time-slot')

data = {
  appointment_type_id_1: '794bad64-cf3f-441c-b679-25a3949e3361',
  appointment_type_id_2: '506becd3-64c6-4ed7-a905-768a5b05d0d4',
  date: '2025-01-15',
  overlap_percentage: 60,
  dismiss_overlap: 80  # Optional
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  combinations = result['data']['available_combinations']
  
  puts "Found #{combinations.length} available combinations:"
  
  combinations.each do |combo|
    puts "- #{combo['users']} at #{combo['time']}"
    puts "  Overlap: #{combo['overlap_percentage']}%"
  end
else
  puts "Error #{response.code}: #{response.body}"
end`,
      java: `import okhttp3.*;
import com.google.gson.JsonObject;
import com.google.gson.JsonParser;
import com.google.gson.JsonArray;

public class DoubleFreeSlotsClient {
    private static final OkHttpClient client = new OkHttpClient();
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");
    
    public static void main(String[] args) throws Exception {
        String apiKey = "your_api_key_here";
        String url = "https://dev.clickai.live/calendar/get-double-free-time-slot";
        
        String json = "{"
            + "\\"appointment_type_id_1\\":\\"794bad64-cf3f-441c-b679-25a3949e3361\\","
            + "\\"appointment_type_id_2\\":\\"506becd3-64c6-4ed7-a905-768a5b05d0d4\\","
            + "\\"date\\":\\"2025-01-15\\","
            + "\\"overlap_percentage\\":60,"
            + "\\"dismiss_overlap\\":80"
            + "}";
        
        RequestBody body = RequestBody.create(json, JSON);
        Request request = new Request.Builder()
            .url(url)
            .post(body)
            .addHeader("Authorization", "Bearer " + apiKey)
            .addHeader("Content-Type", "application/json")
            .build();
            
        try (Response response = client.newCall(request).execute()) {
            String responseBody = response.body().string();
            
            if (response.isSuccessful()) {
                JsonObject result = JsonParser.parseString(responseBody).getAsJsonObject();
                JsonArray combinations = result.getAsJsonObject("data")
                                              .getAsJsonArray("available_combinations");
                
                System.out.println("Found " + combinations.size() + " available combinations:");
                
                for (int i = 0; i < combinations.size(); i++) {
                    JsonObject combo = combinations.get(i).getAsJsonObject();
                    System.out.println("- " + combo.get("users").getAsString() + 
                                     " at " + combo.get("time").getAsString());
                    System.out.println("  Overlap: " + combo.get("overlap_percentage").getAsFloat() + "%");
                }
            } else {
                System.out.println("Error " + response.code() + ": " + responseBody);
            }
        }
    }
}`
    }
  },

  'calendar-create-event-type': {
    id: 'calendar-create-event-type',
    title: 'Create Appointment Type',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'POST',
    path: '/calendar/create-event-type',
    description: 'Creates a new appointment type with access permissions. This endpoint allows you to define new types of appointments that can be scheduled in your calendar system, including their duration, appearance, and user access permissions.',
    parameters: [
      {
        name: 'name',
        type: 'string',
        required: true,
        description: 'The name of the appointment type'
      },
      {
        name: 'duration',
        type: 'integer',
        required: true,
        description: 'Duration in minutes (must be positive)'
      },
      {
        name: 'can_all_users_see',
        type: 'boolean',
        required: true,
        description: 'Whether all users can access this appointment type'
      },
      {
        name: 'user_ids',
        type: 'array',
        required: false,
        description: 'Required if can_all_users_see is false. Array of user UUIDs'
      },
      {
        name: 'color_code',
        type: 'string',
        required: false,
        description: 'Hex color code for visual representation (e.g., "#3498db")'
      },
      {
        name: 'tag',
        type: 'string',
        required: false,
        description: 'Tag label for categorization'
      },
      {
        name: 'tag_color_code',
        type: 'string',
        required: false,
        description: 'Hex color code for the tag (e.g., "#2980b9")'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Created - Appointment type created successfully',
        example: JSON.stringify({
          success: true,
          data: {
            appointment_type_id: "f47ac10b-58cc-4372-a567-0e02b2c3d479",
            name: "General Consultation",
            color_code: "#3498db",
            duration: 60,
            tag: "Consultation",
            tag_color_code: "#2980b9",
            can_all_users_see: true,
            organization_id: "org_12345",
            created_at: "2024-12-31T10:30:00Z",
            access_users: [],
            total_access_users: 0
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Validation errors',
        example: JSON.stringify({
          error: "Field 'name' is required"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - User access error',
        example: JSON.stringify({
          error: "User f47ac10b-58cc-4372-a567-0e02b2c3d479 does not exist or does not belong to your organization"
        }, null, 2)
      },
      {
        code: '409',
        description: 'Conflict - Duplicate name',
        example: JSON.stringify({
          error: "Appointment type with name 'General Consultation' already exists in your organization"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/calendar/create-event-type';

const appointmentTypeData = {
    name: 'General Consultation',
    color_code: '#3498db',
    duration: 60,
    tag: 'Consultation',
    tag_color_code: '#2980b9',
    can_all_users_see: true
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(appointmentTypeData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Appointment type created:', data.data.appointment_type_id);
        console.log('Name:', data.data.name);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/calendar/create-event-type"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Public appointment type (all users can see)
data = {
    "name": "General Consultation",
    "color_code": "#3498db",
    "duration": 60,
    "tag": "Consultation",
    "tag_color_code": "#2980b9",
    "can_all_users_see": True
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 201:
    result = response.json()
    print(f"Appointment type created: {result['data']['appointment_type_id']}")
    print(f"Name: {result['data']['name']}")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/calendar/create-event-type" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"name\\":\\"General Consultation\\",\\"color_code\\":\\"#3498db\\",\\"duration\\":60,\\"tag\\":\\"Consultation\\",\\"tag_color_code\\":\\"#2980b9\\",\\"can_all_users_see\\":true}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/calendar/create-event-type" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "General Consultation",
    "color_code": "#3498db",
    "duration": 60,
    "tag": "Consultation",
    "tag_color_code": "#2980b9",
    "can_all_users_see": true
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/calendar/create-event-type';

$data = [
    'name' => 'Quick Chat',
    'duration' => 15,
    'can_all_users_see' => true
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $result = json_decode($response, true);
    echo "Appointment type created: " . $result['data']['appointment_type_id'] . "\\n";
} else {
    $error = json_decode($response, true);
    echo "Error $http_code: " . $error['error'] . "\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class AppointmentTypeCreator
{
    private readonly HttpClient httpClient;
    
    public AppointmentTypeCreator()
    {
        httpClient = new HttpClient();
        httpClient.DefaultRequestHeaders.Add("Authorization", "Bearer your_api_key_here");
    }
    
    public async Task<object> CreateAppointmentTypeAsync(object appointmentTypeData)
    {
        string url = "https://dev.clickai.live/calendar/create-event-type";
        
        string json = JsonConvert.SerializeObject(appointmentTypeData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine("Appointment type created successfully!");
                return result;
            }
            else
            {
                var error = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine($"Error: {error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return null;
        }
    }
}`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

url = URI('https://dev.clickai.live/calendar/create-event-type')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = 'Bearer your_api_key_here'
request['Content-Type'] = 'application/json'

request.body = {
  name: 'Customer Support',
  duration: 20,
  color_code: '#f39c12',
  tag: 'Support',
  can_all_users_see: true
}.to_json

response = http.request(request)

if response.code == '201'
  result = JSON.parse(response.body)
  puts "Appointment type created: #{result['data']['appointment_type_id']}"
else
  error = JSON.parse(response.body)
  puts "Error: #{error['error']}"
end`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;

public class AppointmentTypeCreator {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    
    public AppointmentTypeCreator() {
        this.httpClient = HttpClient.newHttpClient();
        this.objectMapper = new ObjectMapper();
    }
    
    public void createAppointmentType(Map<String, Object> appointmentTypeData) {
        try {
            String url = "https://dev.clickai.live/calendar/create-event-type";
            String json = objectMapper.writeValueAsString(appointmentTypeData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer your_api_key_here")
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 201) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                Map<String, Object> data = (Map<String, Object>) result.get("data");
                System.out.println("Appointment type created: " + data.get("appointment_type_id"));
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
        }
    }
}

data = {
  'name' => 'פגישת ייעוץ עסקי',
  'duration' => 90,
  'description' => 'פגישת ייעוץ מקיפה לעסקים',
  'price' => 250.00,
  'color' => '#3b82f6'
}

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer #{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

puts http.request(request).body`
    }
  },

  'calendar-edit-event-type': {
    id: 'calendar-edit-event-type',
    title: 'Edit Appointment Type',
    category: 'Calendar',
    categoryId: 'calendar',
    method: 'POST',
    path: '/calendar/edit-event-type',
    description: 'Updates an existing appointment type\'s properties. This endpoint allows you to modify specific fields of an appointment type while preserving other settings. You must provide at least one field to update along with the appointment type ID.',
    parameters: [
      {
        name: 'event_type_id',
        type: 'string',
        required: true,
        description: 'The ID of the appointment type to update (UUID)'
      },
      {
        name: 'name',
        type: 'string',
        required: false,
        description: 'New name for the appointment type'
      },
      {
        name: 'duration',
        type: 'integer',
        required: false,
        description: 'New duration in minutes (must be positive)'
      },
      {
        name: 'color_code',
        type: 'string',
        required: false,
        description: 'New hex color code for visual representation'
      },
      {
        name: 'tag',
        type: 'string',
        required: false,
        description: 'New tag label for categorization'
      },
      {
        name: 'tag_color_code',
        type: 'string',
        required: false,
        description: 'New hex color code for the tag'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Appointment type updated successfully',
        example: JSON.stringify({
          success: true,
          data: {
            appointment_type_id: "379b1708-241d-47fa-9ca2-306fb15070a8",
            name: "Advanced Test Consultation",
            color_code: "#e74c3c",
            duration: 90,
            tag: "Premium",
            tag_color_code: "#9b59b6",
            can_all_users_see: true,
            organization_id: "org_12345",
            created_at: "2024-12-31T10:30:00Z",
            updated_fields: ["name", "duration", "color_code"]
          },
          message: "Appointment type updated successfully. Updated fields: name, duration, color_code"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Validation errors',
        example: JSON.stringify({
          error: "Field 'event_type_id' is required"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found',
        example: JSON.stringify({
          error: "Appointment type not found or does not belong to your organization"
        }, null, 2)
      },
      {
        code: '409',
        description: 'Conflict - Duplicate name',
        example: JSON.stringify({
          error: "Appointment type with name 'Advanced Test Consultation' already exists in your organization"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/calendar/edit-event-type';

const updateData = {
    event_type_id: '379b1708-241d-47fa-9ca2-306fb15070a8',
    name: 'Advanced Test Consultation',
    color_code: '#e74c3c',
    tag: 'Premium'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(updateData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Appointment type updated:', data.data.appointment_type_id);
        console.log('Updated fields:', data.data.updated_fields.join(', '));
        console.log('Message:', data.message);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/calendar/edit-event-type"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Update name and duration
data = {
    "event_type_id": "379b1708-241d-47fa-9ca2-306fb15070a8",
    "name": "Advanced Test Consultation",
    "duration": 90
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    print(f"Updated appointment type: {result['data']['appointment_type_id']}")
    print(f"Updated fields: {', '.join(result['data']['updated_fields'])}")
    print(f"Message: {result['message']}")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/calendar/edit-event-type" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"event_type_id\\":\\"379b1708-241d-47fa-9ca2-306fb15070a8\\",\\"name\\":\\"Advanced Test Consultation\\",\\"duration\\":90}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/calendar/edit-event-type" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "event_type_id": "379b1708-241d-47fa-9ca2-306fb15070a8",
    "name": "Advanced Test Consultation",
    "duration": 90,
    "color_code": "#e74c3c"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/calendar/edit-event-type';

$data = [
    'event_type_id' => '379b1708-241d-47fa-9ca2-306fb15070a8',
    'name' => 'Advanced Test Consultation',
    'duration' => 90,
    'tag' => 'Premium'
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $result = json_decode($response, true);
    echo "Updated appointment type: " . $result['data']['appointment_type_id'] . "\\n";
    echo "Updated fields: " . implode(', ', $result['data']['updated_fields']) . "\\n";
    echo "Message: " . $result['message'] . "\\n";
} else {
    $error = json_decode($response, true);
    echo "Error $http_code: " . $error['error'] . "\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class AppointmentTypeEditor
{
    private readonly HttpClient httpClient;
    
    public AppointmentTypeEditor()
    {
        httpClient = new HttpClient();
        httpClient.DefaultRequestHeaders.Add("Authorization", "Bearer your_api_key_here");
    }
    
    public async Task<object> EditAppointmentTypeAsync(object updateData)
    {
        string url = "https://dev.clickai.live/calendar/edit-event-type";
        
        string json = JsonConvert.SerializeObject(updateData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine("Appointment type updated successfully!");
                return result;
            }
            else
            {
                var error = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine($"Error: {error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return null;
        }
    }
}`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

url = URI('https://dev.clickai.live/calendar/edit-event-type')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = 'Bearer your_api_key_here'
request['Content-Type'] = 'application/json'

request.body = {
  event_type_id: '379b1708-241d-47fa-9ca2-306fb15070a8',
  name: 'Advanced Test Consultation',
  duration: 90,
  tag: 'Premium',
  tag_color_code: '#9b59b6'
}.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  puts "Updated appointment type: #{result['data']['appointment_type_id']}"
  puts "Updated fields: #{result['data']['updated_fields'].join(', ')}"
  puts "Message: #{result['message']}"
else
  error = JSON.parse(response.body)
  puts "Error: #{error['error']}"
end`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;

public class AppointmentTypeEditor {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    
    public AppointmentTypeEditor() {
        this.httpClient = HttpClient.newHttpClient();
        this.objectMapper = new ObjectMapper();
    }
    
    public void editAppointmentType(Map<String, Object> updateData) {
        try {
            String url = "https://dev.clickai.live/calendar/edit-event-type";
            String json = objectMapper.writeValueAsString(updateData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer your_api_key_here")
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 200) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                Map<String, Object> data = (Map<String, Object>) result.get("data");
                System.out.println("Updated appointment type: " + data.get("appointment_type_id"));
                System.out.println("Message: " + result.get("message"));
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
        }
    }
}`
    }
  },

  // Tasks endpoints
  'tasks-create-task': {
    id: 'tasks-create-task',
    title: 'Create Task',
    category: 'Tasks',
    categoryId: 'tasks',
    method: 'POST',
    path: '/tasks/create-task',
    description: 'Creates a new task in the system. Tasks can be general assignments or associated with specific leads or customers. The endpoint validates all user permissions and entity relationships before creating the task.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'The title/subject of the task',
        example: 'Follow up with lead'
      },
      {
        name: 'description',
        type: 'string',
        required: true,
        description: 'Detailed description of the task',
        example: 'Call the lead to discuss our proposal'
      },
      {
        name: 'priority',
        type: 'string',
        required: true,
        description: 'Task priority: "Low", "Medium", or "High"',
        example: 'High'
      },
      {
        name: 'assign_to',
        type: 'string',
        required: true,
        description: 'User ID to assign the task to (UUID format)',
        example: '2c29759a-e549-4135-a94a-c6e844999c67'
      },
      {
        name: 'due_date',
        type: 'string',
        required: true,
        description: 'Due date and time in format "YYYY-MM-DD HH:MM"',
        example: '2025-01-15 10:00'
      },
      {
        name: 'created_by',
        type: 'string',
        required: true,
        description: 'User ID of the task creator (UUID format)',
        example: '2c29759a-e549-4135-a94a-c6e844999c67'
      },
      {
        name: 'lead_id',
        type: 'string',
        required: false,
        description: 'Optional lead ID to associate with the task (UUID format)',
        example: 'a7be24f6-c304-4279-abc0-9fdb2fb2092d'
      },
      {
        name: 'customer_id',
        type: 'string',
        required: false,
        description: 'Optional customer ID to associate with the task (UUID format)',
        example: '091cd08c-5dc3-456d-9d43-8918a549cfea'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Created - Task created successfully',
        example: JSON.stringify({
          success: true,
          data: {
            task_id: "550e8400-e29b-41d4-a716-446655440000",
            title: "Follow up with lead",
            description: "Call the lead to discuss our proposal",
            priority: "High",
            status: "pending",
            assigned_to: "2c29759a-e549-4135-a94a-c6e844999c67",
            created_by: "2c29759a-e549-4135-a94a-c6e844999c67",
            due_date: "2025-01-15 10:00",
            type: "lead",
            lead_id: "a7be24f6-c304-4279-abc0-9fdb2fb2092d",
            customer_id: null,
            created_at: "2024-12-31T10:30:00Z"
          },
          message: "Task created successfully"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing Required Field',
        example: JSON.stringify({
          error: "Field 'title' is required"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Invalid Priority',
        example: JSON.stringify({
          error: "Priority must be one of: Low, Medium, High"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Invalid Date Format',
        example: JSON.stringify({
          error: "Invalid due_date format. Expected YYYY-MM-DD HH:MM"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid User',
        example: JSON.stringify({
          error: "Assigned to user does not exist or does not belong to your organization"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Invalid Lead/Customer',
        example: JSON.stringify({
          error: "Lead not found or does not belong to your organization"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/tasks/create-task';

const taskData = {
    title: 'Customer support',
    description: 'Help customer with technical issue',
    priority: 'Medium',
    assign_to: '2c29759a-e549-4135-a94a-c6e844999c67',
    due_date: '2025-01-10 14:30',
    created_by: '2c29759a-e549-4135-a94a-c6e844999c67',
    customer_id: '091cd08c-5dc3-456d-9d43-8918a549cfea'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(taskData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Task created:', data.data.task_id);
        console.log('Assigned to:', data.data.assigned_to);
        console.log('Type:', data.data.type);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/tasks/create-task"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Task with lead association
data = {
    "title": "Follow up with lead",
    "description": "Call the lead to discuss our proposal",
    "priority": "High",
    "assign_to": "2c29759a-e549-4135-a94a-c6e844999c67",
    "due_date": "2025-01-15 10:00",
    "created_by": "2c29759a-e549-4135-a94a-c6e844999c67",
    "lead_id": "a7be24f6-c304-4279-abc0-9fdb2fb2092d"
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 201:
    result = response.json()
    print(f"Task created: {result['data']['task_id']}")
    print(f"Title: {result['data']['title']}")
    print(f"Type: {result['data']['type']}")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/tasks/create-task" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"title\\":\\"Follow up with lead\\",\\"description\\":\\"Call the lead to discuss our proposal\\",\\"priority\\":\\"High\\",\\"assign_to\\":\\"2c29759a-e549-4135-a94a-c6e844999c67\\",\\"due_date\\":\\"2025-01-15 10:00\\",\\"created_by\\":\\"2c29759a-e549-4135-a94a-c6e844999c67\\",\\"lead_id\\":\\"a7be24f6-c304-4279-abc0-9fdb2fb2092d\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/tasks/create-task" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Follow up with lead",
    "description": "Call the lead to discuss our proposal",
    "priority": "High",
    "assign_to": "2c29759a-e549-4135-a94a-c6e844999c67",
    "due_date": "2025-01-15 10:00",
    "created_by": "2c29759a-e549-4135-a94a-c6e844999c67",
    "lead_id": "a7be24f6-c304-4279-abc0-9fdb2fb2092d"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/tasks/create-task';

$data = [
    'title' => 'Team meeting preparation',
    'description' => 'Prepare agenda for next team meeting',
    'priority' => 'Low',
    'assign_to' => '2c29759a-e549-4135-a94a-c6e844999c67',
    'due_date' => '2025-01-08 09:00',
    'created_by' => '2c29759a-e549-4135-a94a-c6e844999c67'
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $result = json_decode($response, true);
    echo "Task created: " . $result['data']['task_id'] . "\\n";
    echo "Title: " . $result['data']['title'] . "\\n";
    echo "Priority: " . $result['data']['priority'] . "\\n";
} else {
    $error = json_decode($response, true);
    echo "Error $http_code: " . $error['error'] . "\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class TaskCreator
{
    private readonly HttpClient httpClient;
    
    public TaskCreator()
    {
        httpClient = new HttpClient();
        httpClient.DefaultRequestHeaders.Add("Authorization", "Bearer your_api_key_here");
    }
    
    public async Task<object> CreateTaskAsync(object taskData)
    {
        string url = "https://dev.clickai.live/tasks/create-task";
        
        string json = JsonConvert.SerializeObject(taskData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine("Task created successfully!");
                return result;
            }
            else
            {
                var error = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine($"Error: {error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return null;
        }
    }
}`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

url = URI('https://dev.clickai.live/tasks/create-task')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = 'Bearer your_api_key_here'
request['Content-Type'] = 'application/json'

request.body = {
  title: 'Lead qualification',
  description: 'Research and qualify the new lead',
  priority: 'High',
  assign_to: '2c29759a-e549-4135-a94a-c6e844999c67',
  due_date: '2025-01-14 11:00',
  created_by: '2c29759a-e549-4135-a94a-c6e844999c67',
  lead_id: 'a7be24f6-c304-4279-abc0-9fdb2fb2092d'
}.to_json

response = http.request(request)

if response.code == '201'
  result = JSON.parse(response.body)
  puts "Task created: #{result['data']['task_id']}"
  puts "Title: #{result['data']['title']}"
  puts "Type: #{result['data']['type']}"
else
  error = JSON.parse(response.body)
  puts "Error: #{error['error']}"
end`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;

public class TaskCreator {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    
    public TaskCreator() {
        this.httpClient = HttpClient.newHttpClient();
        this.objectMapper = new ObjectMapper();
    }
    
    public void createTask(Map<String, Object> taskData) {
        try {
            String url = "https://dev.clickai.live/tasks/create-task";
            String json = objectMapper.writeValueAsString(taskData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer your_api_key_here")
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 201) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                Map<String, Object> data = (Map<String, Object>) result.get("data");
                System.out.println("Task created: " + data.get("task_id"));
                System.out.println("Title: " + data.get("title"));
                System.out.println("Type: " + data.get("type"));
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
        }
    }
}`
    }
  },

  'tasks-edit-task': {
    id: 'tasks-edit-task',
    title: 'Edit Task',
    category: 'Tasks',
    categoryId: 'tasks',
    method: 'POST',
    path: '/tasks/edit-task',
    description: 'Updates an existing task in the system. Allows modification of task properties including title, due date, assignee, priority, and status. The endpoint validates that the task belongs to your organization and applies all necessary field validations before updating.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'task_id',
        type: 'string',
        required: true,
        description: 'The ID of the task to update (UUID format)',
        example: 'a1c1073c-188d-4aca-917a-25d4d91621b2'
      },
      {
        name: 'name',
        type: 'string',
        required: false,
        description: 'New title/subject for the task (at least one optional field required)',
        example: 'Updated task title'
      },
      {
        name: 'due_date',
        type: 'string',
        required: false,
        description: 'New due date and time in format "YYYY-MM-DD HH:MM" (at least one optional field required)',
        example: '2025-01-20 15:30'
      },
      {
        name: 'assign_to',
        type: 'string',
        required: false,
        description: 'New user ID to assign the task to (UUID format) (at least one optional field required)',
        example: '2c29759a-e549-4135-a94a-c6e844999c67'
      },
      {
        name: 'priority',
        type: 'string',
        required: false,
        description: 'New task priority: "Low", "Medium", or "High" (at least one optional field required)',
        example: 'High'
      },
      {
        name: 'status',
        type: 'string',
        required: false,
        description: 'New task status: "pending", "in_progress", "completed", or "cancelled" (at least one optional field required)',
        example: 'in_progress'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Task updated successfully',
        example: JSON.stringify({
          success: true,
          data: {
            task_id: "a1c1073c-188d-4aca-917a-25d4d91621b2",
            title: "Updated task title",
            assigned_to: "2c29759a-e549-4135-a94a-c6e844999c67",
            due_date: "2025-01-20 15:30",
            priority: "High",
            status: "in_progress",
            description: "Call the lead to discuss our proposal",
            created_by: "2c29759a-e549-4135-a94a-c6e844999c67",
            lead_id: "a7be24f6-c304-4279-abc0-9fdb2fb2092d",
            customer_id: null,
            type: "lead",
            created_at: "2024-12-31T10:30:00Z",
            updated_at: "2025-01-01T14:25:00Z",
            updated_fields: ["tasktitle", "priority", "status"]
          },
          message: "Task updated successfully. Updated fields: tasktitle, priority, status"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing Task ID',
        example: JSON.stringify({
          error: "Field 'task_id' is required"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - No Fields to Update',
        example: JSON.stringify({
          error: "At least one of the following fields is required: name, due_date, assign_to, priority, status"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Invalid Priority',
        example: JSON.stringify({
          error: "Priority must be one of: Low, Medium, High"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Invalid Status',
        example: JSON.stringify({
          error: "Status must be one of: pending, in_progress, completed, cancelled"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Invalid Date Format',
        example: JSON.stringify({
          error: "Invalid due_date format. Expected YYYY-MM-DD HH:MM"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Task Doesn\'t Belong to Organization',
        example: JSON.stringify({
          error: "Task does not belong to your organization"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Task Not Found',
        example: JSON.stringify({
          error: "Task not found"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/tasks/edit-task';

const updateData = {
    task_id: 'a1c1073c-188d-4aca-917a-25d4d91621b2',
    status: 'completed',
    priority: 'Low'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(updateData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Task updated:', data.data.task_id);
        console.log('Status:', data.data.status);
        console.log('Priority:', data.data.priority);
        console.log('Updated fields:', data.data.updated_fields);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/tasks/edit-task"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Update task title and priority
data = {
    "task_id": "a1c1073c-188d-4aca-917a-25d4d91621b2",
    "name": "Updated follow-up task",
    "priority": "High"
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    print(f"Task updated: {result['data']['task_id']}")
    print(f"Updated fields: {', '.join(result['data']['updated_fields'])}")
    print(f"New title: {result['data']['title']}")
    print(f"New priority: {result['data']['priority']}")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/tasks/edit-task" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"task_id\\":\\"a1c1073c-188d-4aca-917a-25d4d91621b2\\",\\"name\\":\\"Updated task title\\",\\"priority\\":\\"High\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/tasks/edit-task" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "task_id": "a1c1073c-188d-4aca-917a-25d4d91621b2",
    "name": "Updated task title",
    "due_date": "2025-01-20 15:30",
    "priority": "High",
    "status": "in_progress"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/tasks/edit-task';

$data = [
    'task_id' => 'a1c1073c-188d-4aca-917a-25d4d91621b2',
    'assign_to' => '2c29759a-e549-4135-a94a-c6e844999c67',
    'due_date' => '2025-01-25 10:00'
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $result = json_decode($response, true);
    echo "Task updated: " . $result['data']['task_id'] . "\\n";
    echo "Updated fields: " . implode(', ', $result['data']['updated_fields']) . "\\n";
} else {
    $error = json_decode($response, true);
    echo "Error $http_code: " . $error['error'] . "\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class TaskEditor
{
    private readonly HttpClient httpClient;
    
    public TaskEditor()
    {
        httpClient = new HttpClient();
        httpClient.DefaultRequestHeaders.Add("Authorization", "Bearer your_api_key_here");
    }
    
    public async Task<object> EditTaskAsync(object updateData)
    {
        string url = "https://dev.clickai.live/tasks/edit-task";
        
        string json = JsonConvert.SerializeObject(updateData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine("Task updated successfully!");
                return result;
            }
            else
            {
                var error = JsonConvert.DeserializeObject(responseContent);
                Console.WriteLine($"Error: {error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return null;
        }
    }
}`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

url = URI('https://dev.clickai.live/tasks/edit-task')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = 'Bearer your_api_key_here'
request['Content-Type'] = 'application/json'

request.body = {
  task_id: 'a1c1073c-188d-4aca-917a-25d4d91621b2',
  priority: 'Medium',
  status: 'in_progress'
}.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  puts "Task updated: #{result['data']['task_id']}"
  puts "Updated fields: #{result['data']['updated_fields'].join(', ')}"
  puts "Priority: #{result['data']['priority']}"
  puts "Status: #{result['data']['status']}"
else
  error = JSON.parse(response.body)
  puts "Error: #{error['error']}"
end`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;

public class TaskEditor {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    
    public TaskEditor() {
        this.httpClient = HttpClient.newHttpClient();
        this.objectMapper = new ObjectMapper();
    }
    
    public void editTask(Map<String, Object> updateData) {
        try {
            String url = "https://dev.clickai.live/tasks/edit-task";
            String json = objectMapper.writeValueAsString(updateData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer your_api_key_here")
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 200) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                Map<String, Object> data = (Map<String, Object>) result.get("data");
                System.out.println("Task updated: " + data.get("task_id"));
                System.out.println("Updated fields: " + data.get("updated_fields"));
                System.out.println("Title: " + data.get("title"));
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
        }
    }
}`
    }
  },

  'tasks-delete-task': {
    id: 'tasks-delete-task',
    title: 'Delete Task',
    category: 'Tasks',
    categoryId: 'tasks',
    method: 'POST',
    path: '/tasks/delete-task',
    description: 'Permanently deletes a task from the system. This operation is irreversible and completely removes the task from the database. The endpoint validates that the task belongs to your organization before performing the deletion.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'task_id',
        type: 'string',
        required: true,
        description: 'The ID of the task to delete (UUID format)',
        example: 'a1c1073c-188d-4aca-917a-25d4d91621b2'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Success - Task deleted successfully',
        example: JSON.stringify({
          success: true,
          message: 'Task deleted successfully',
          deleted_task_id: 'a1c1073c-188d-4aca-917a-25d4d91621b2'
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing Task ID',
        example: JSON.stringify({
          error: "Field 'task_id' is required"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - No Data Provided',
        example: JSON.stringify({
          error: 'No data provided'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Invalid Authorization Header',
        example: JSON.stringify({
          error: 'Invalid authorization header format'
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Invalid API Key',
        example: JSON.stringify({
          error: 'Invalid API key'
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Task Doesn\'t Belong to Organization',
        example: JSON.stringify({
          error: 'Task does not belong to your organization'
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Task Not Found',
        example: JSON.stringify({
          error: 'Task not found'
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error - Failed to Delete',
        example: JSON.stringify({
          error: 'Failed to delete task'
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/tasks/delete-task';

const deleteData = {
    task_id: 'a1c1073c-188d-4aca-917a-25d4d91621b2'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(deleteData)
})
.then(response => response.json())
.then(data => {
    if (data.success) {
        console.log('Task deleted successfully:', data.deleted_task_id);
        console.log('Message:', data.message);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/tasks/delete-task"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Delete task
data = {
    "task_id": "a1c1073c-188d-4aca-917a-25d4d91621b2"
}

response = requests.post(url, headers=headers, json=data)

if response.status_code == 200:
    result = response.json()
    print(f"Task deleted successfully: {result['deleted_task_id']}")
    print(f"Message: {result['message']}")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/tasks/delete-task" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"task_id\\":\\"a1c1073c-188d-4aca-917a-25d4d91621b2\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/tasks/delete-task" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "task_id": "a1c1073c-188d-4aca-917a-25d4d91621b2"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/tasks/delete-task';

$data = [
    'task_id' => 'a1c1073c-188d-4aca-917a-25d4d91621b2'
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $result = json_decode($response, true);
    echo "Task deleted successfully: " . $result['deleted_task_id'] . "\\n";
    echo "Message: " . $result['message'] . "\\n";
} else {
    $error = json_decode($response, true);
    echo "Error $http_code: " . $error['error'] . "\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class TaskDeleter
{
    private readonly HttpClient httpClient;
    
    public TaskDeleter()
    {
        httpClient = new HttpClient();
        httpClient.DefaultRequestHeaders.Add("Authorization", "Bearer your_api_key_here");
    }
    
    public async Task<bool> DeleteTaskAsync(string taskId)
    {
        string url = "https://dev.clickai.live/tasks/delete-task";
        
        var deleteData = new { task_id = taskId };
        string json = JsonConvert.SerializeObject(deleteData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Task deleted successfully: {result.deleted_task_id}");
                Console.WriteLine($"Message: {result.message}");
                return true;
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
                return false;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return false;
        }
    }
}`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

url = URI('https://dev.clickai.live/tasks/delete-task')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = 'Bearer your_api_key_here'
request['Content-Type'] = 'application/json'

request.body = {
  task_id: 'a1c1073c-188d-4aca-917a-25d4d91621b2'
}.to_json

response = http.request(request)

if response.code == '200'
  result = JSON.parse(response.body)
  puts "Task deleted successfully: #{result['deleted_task_id']}"
  puts "Message: #{result['message']}"
else
  error = JSON.parse(response.body)
  puts "Error: #{error['error']}"
end`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;

public class TaskDeleter {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    
    public TaskDeleter() {
        this.httpClient = HttpClient.newHttpClient();
        this.objectMapper = new ObjectMapper();
    }
    
    public boolean deleteTask(String taskId) {
        try {
            String url = "https://dev.clickai.live/tasks/delete-task";
            
            Map<String, Object> deleteData = new HashMap<>();
            deleteData.put("task_id", taskId);
            
            String json = objectMapper.writeValueAsString(deleteData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer your_api_key_here")
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 200) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Task deleted successfully: " + result.get("deleted_task_id"));
                System.out.println("Message: " + result.get("message"));
                return true;
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
                return false;
            }
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
            return false;
        }
    }
}`
    }
  },

  // Contacts endpoints
  'contacts-create-contact': {
    id: 'contacts-create-contact',
    title: 'Create New Contact',
    category: 'Contacts',
    categoryId: 'contacts',
    method: 'POST',
    path: '/contacts/create-new-contact',
    description: 'Creates a new contact in the system. This endpoint supports creating contacts with basic information and includes duplicate checking by phone number.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'name',
        type: 'string',
        required: true,
        description: 'Contact\'s full name (must not be empty)',
        example: 'John Doe'
      },
      {
        name: 'phone',
        type: 'string',
        required: true,
        description: 'Phone number in international format (10-15 digits, no \'+\' prefix, must NOT start with \'0\')',
        example: '972501234567'
      },
      {
        name: 'profile_picture_url',
        type: 'string',
        required: false,
        description: 'URL to contact\'s profile picture (must be valid URL if provided)',
        example: 'https://example.com/profile.jpg'
      },
      {
        name: 'bot_status',
        type: 'string',
        required: true,
        description: 'Bot interaction status (must be true or false)',
        example: 'true'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Contact created successfully',
        example: JSON.stringify({
          success: true,
          data: {
            contact_id: "a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6",
            name: "John Doe",
            phone: "972501234567",
            profile_picture_url: "https://example.com/profile.jpg",
            bot_status: true,
            is_open: true,
            organization_id: "org-12345678",
            created_at: "2024-01-15T10:30:00Z"
          },
          message: "Contact created successfully"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Validation Error',
        example: JSON.stringify({
          error: "Phone must be in international format (without local '0' prefix). Example: 972501234567 instead of 0501234567"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Invalid API Key',
        example: JSON.stringify({
          error: "Invalid API key"
        }, null, 2)
      },
      {
        code: '409',
        description: 'Duplicate Contact',
        example: JSON.stringify({
          error: "Contact with phone number 972501234567 already exists in your organization",
          existing_contact: {
            contact_id: "a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6",
            name: "Existing User",
            phone: "972501234567"
          }
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          error: "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/contacts/create-new-contact';

const contactData = {
    name: 'Jane Smith',
    phone: '972509876543', // Valid international format
    bot_status: false,
    profile_picture_url: 'https://example.com/jane.jpg'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(contactData)
})
.then(response => response.json())
.then(data => {
    if (response.status === 201) {
        console.log('Contact created:', data.data);
    } else if (response.status === 409) {
        console.log('Duplicate contact found:', data.existing_contact);
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/contacts/create-new-contact"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# Valid international format
contact_data = {
    "name": "John Doe",
    "phone": "972501234567",  # Valid international format
    "bot_status": True,
    "profile_picture_url": "https://example.com/profile.jpg"
}

response = requests.post(url, json=contact_data, headers=headers)

if response.status_code == 201:
    data = response.json()
    print("Contact created:", data['data']['contact_id'])
elif response.status_code == 409:
    print("Duplicate contact:", response.json()['existing_contact'])
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/contacts/create-new-contact" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"name\\":\\"John Doe\\",\\"phone\\":\\"972501234567\\",\\"bot_status\\":true}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/contacts/create-new-contact" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John Doe",
    "phone": "972501234567",
    "bot_status": true,
    "profile_picture_url": "https://example.com/profile.jpg"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/contacts/create-new-contact';

$contact_data = [
    'name' => 'Sarah Miller',
    'phone' => '972531234567', // Valid international format
    'bot_status' => true,
    'profile_picture_url' => 'https://example.com/sarah.jpg'
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($contact_data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 201) {
    $data = json_decode($response, true);
    echo "Contact created: " . $data['data']['contact_id'] . "\\n";
    echo "Name: " . $data['data']['name'] . "\\n";
} elseif ($http_code == 409) {
    $error = json_decode($response, true);
    echo "Duplicate contact: " . $error['existing_contact']['name'] . "\\n";
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class ContactCreator
{
    private readonly HttpClient httpClient;
    
    public ContactCreator()
    {
        httpClient = new HttpClient();
    }
    
    public async Task<bool> CreateContactAsync(string name, string phone, bool botStatus, string profilePictureUrl = null)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/contacts/create-new-contact";
        
        var contactData = new
        {
            name = name,
            phone = phone, // Must be international format without leading 0
            bot_status = botStatus,
            profile_picture_url = profilePictureUrl
        };
        
        string json = JsonConvert.SerializeObject(contactData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        httpClient.DefaultRequestHeaders.Clear();
        httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Contact created: {result.data.contact_id}");
                Console.WriteLine($"Name: {result.data.name}");
                Console.WriteLine($"Phone: {result.data.phone}");
                return true;
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.Conflict)
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Duplicate contact: {error.existing_contact.name}");
                return false;
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
                return false;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return false;
        }
    }
}`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

url = URI('https://dev.clickai.live/contacts/create-new-contact')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = 'Bearer your_api_key_here'
request['Content-Type'] = 'application/json'

contact_data = {
  name: 'Emma Wilson',
  phone: '972551234567', # Valid international format
  bot_status: false,
  profile_picture_url: 'https://example.com/emma.jpg'
}

request.body = contact_data.to_json

response = http.request(request)

case response.code
when '201'
  result = JSON.parse(response.body)
  puts "Contact created: #{result['data']['contact_id']}"
  puts "Name: #{result['data']['name']}"
when '409'
  error = JSON.parse(response.body)
  puts "Duplicate contact: #{error['existing_contact']['name']}"
else
  error = JSON.parse(response.body)
  puts "Error: #{error['error']}"
end`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;

public class ContactCreator {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    
    public ContactCreator() {
        this.httpClient = HttpClient.newHttpClient();
        this.objectMapper = new ObjectMapper();
    }
    
    public boolean createContact(String name, String phone, boolean botStatus, String profilePictureUrl) {
        try {
            String url = "https://dev.clickai.live/contacts/create-new-contact";
            
            Map<String, Object> contactData = new HashMap<>();
            contactData.put("name", name);
            contactData.put("phone", phone); // Must be international format
            contactData.put("bot_status", botStatus);
            if (profilePictureUrl != null) {
                contactData.put("profile_picture_url", profilePictureUrl);
            }
            
            String json = objectMapper.writeValueAsString(contactData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer your_api_key_here")
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 201) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                Map<String, Object> data = (Map<String, Object>) result.get("data");
                System.out.println("Contact created: " + data.get("contact_id"));
                System.out.println("Name: " + data.get("name"));
                return true;
            } else if (response.statusCode() == 409) {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                Map<String, Object> existingContact = (Map<String, Object>) error.get("existing_contact");
                System.out.println("Duplicate contact: " + existingContact.get("name"));
                return false;
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
                return false;
            }
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
            return false;
        }
    }
}`
    }
  },

  'contacts-find-by-phone': {
    id: 'contacts-find-by-phone',
    title: 'Find Contact by Phone Number',
    category: 'Contacts',
    categoryId: 'contacts',
    method: 'POST',
    path: '/contacts/find-by-phone',
    description: 'Searches for an existing contact in the system by phone number. This endpoint searches for contacts within the same organization and returns contact details if found.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'phone',
        type: 'string',
        required: true,
        description: 'Phone number to search for (must not be empty)',
        example: '972501234567'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Contact found successfully',
        example: JSON.stringify({
          success: true,
          data: {
            id: "a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6",
            name: "John Doe",
            phone: "972501234567",
            profile_picture_url: "https://example.com/profile.jpg",
            bot_status: true
          },
          message: "Contact found successfully"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Missing Phone Number',
        example: JSON.stringify({
          error: "Field 'phone' is required"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Invalid API Key',
        example: JSON.stringify({
          error: "Invalid API key"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Contact Not Found',
        example: JSON.stringify({
          error: "Contact not found",
          message: "No contact found with phone number 972501234567 in your organization"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          error: "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/contacts/find-by-phone';

const searchData = {
    phone: '972546230070'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(searchData)
})
.then(response => response.json())
.then(data => {
    if (response.status === 200) {
        console.log('Contact found:', data.data);
        console.log('Name:', data.data.name);
        console.log('Phone:', data.data.phone);
    } else if (response.status === 404) {
        console.log('Contact not found');
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/contacts/find-by-phone"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

search_data = {
    "phone": "972501234567"
}

response = requests.post(url, json=search_data, headers=headers)

if response.status_code == 200:
    data = response.json()
    contact = data['data']
    print(f"Contact found: {contact['name']}")
    print(f"ID: {contact['id']}")
    print(f"Phone: {contact['phone']}")
    print(f"Bot Status: {contact['bot_status']}")
elif response.status_code == 404:
    print("Contact not found")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/contacts/find-by-phone" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"phone\\":\\"972501234567\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/contacts/find-by-phone" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone": "972501234567"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/contacts/find-by-phone';

$search_data = [
    'phone' => '972531234567'
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($search_data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    $contact = $data['data'];
    echo "Contact found: " . $contact['name'] . "\\n";
    echo "ID: " . $contact['id'] . "\\n";
    echo "Phone: " . $contact['phone'] . "\\n";
} elseif ($http_code == 404) {
    echo "Contact not found\\n";
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class ContactSearcher
{
    private readonly HttpClient httpClient;
    
    public ContactSearcher()
    {
        httpClient = new HttpClient();
    }
    
    public async Task<object> FindContactByPhoneAsync(string phone)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/contacts/find-by-phone";
        
        var searchData = new { phone = phone };
        
        string json = JsonConvert.SerializeObject(searchData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        httpClient.DefaultRequestHeaders.Clear();
        httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Contact found: {result.data.name}");
                Console.WriteLine($"ID: {result.data.id}");
                Console.WriteLine($"Phone: {result.data.phone}");
                Console.WriteLine($"Bot Status: {result.data.bot_status}");
                return result.data;
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.NotFound)
            {
                Console.WriteLine("Contact not found");
                return null;
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return null;
        }
    }
}`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

url = URI('https://dev.clickai.live/contacts/find-by-phone')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = 'Bearer your_api_key_here'
request['Content-Type'] = 'application/json'

search_data = {
  phone: '972551234567'
}

request.body = search_data.to_json

response = http.request(request)

case response.code
when '200'
  result = JSON.parse(response.body)
  contact = result['data']
  puts "Contact found: #{contact['name']}"
  puts "ID: #{contact['id']}"
  puts "Phone: #{contact['phone']}"
  puts "Bot Status: #{contact['bot_status']}"
when '404'
  puts "Contact not found"
else
  error = JSON.parse(response.body)
  puts "Error: #{error['error']}"
end`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;

public class ContactSearcher {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    
    public ContactSearcher() {
        this.httpClient = HttpClient.newHttpClient();
        this.objectMapper = new ObjectMapper();
    }
    
    public Map<String, Object> findContactByPhone(String phone) {
        try {
            String url = "https://dev.clickai.live/contacts/find-by-phone";
            
            Map<String, Object> searchData = new HashMap<>();
            searchData.put("phone", phone);
            
            String json = objectMapper.writeValueAsString(searchData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer your_api_key_here")
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 200) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                Map<String, Object> contact = (Map<String, Object>) result.get("data");
                System.out.println("Contact found: " + contact.get("name"));
                System.out.println("ID: " + contact.get("id"));
                System.out.println("Phone: " + contact.get("phone"));
                return contact;
            } else if (response.statusCode() == 404) {
                System.out.println("Contact not found");
                return null;
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
                return null;
            }
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
            return null;
        }
    }
}`
    }
  },

  'contacts-find-by-name': {
    id: 'contacts-find-by-name',
    title: 'Find Contact by Name',
    category: 'Contacts',
    categoryId: 'contacts',
    method: 'POST',
    path: '/contacts/find-by-name',
    description: 'Searches for existing contacts in the system by name. This endpoint performs partial name matching within the same organization and returns matching contact details. Supports both full name search and partial matches.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'name',
        type: 'string',
        required: true,
        description: 'Name to search for (supports partial matching, minimum 2 characters)',
        example: 'John Doe'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Contacts found successfully',
        example: JSON.stringify({
          success: true,
          data: [
            {
              id: "a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6",
              name: "John Doe",
              phone: "972501234567",
              profile_picture_url: "https://example.com/profile1.jpg",
              bot_status: true
            },
            {
              id: "b2c3d4e5-f6g7-8h9i-0j1k-l2m3n4o5p6q7",
              name: "John Smith",
              phone: "972502345678",
              profile_picture_url: "https://example.com/profile2.jpg",
              bot_status: false
            }
          ],
          count: 2,
          message: "Found 2 contacts matching 'John'"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Missing or Invalid Name Parameter',
        example: JSON.stringify({
          error: "Field 'name' is required and must be at least 2 characters"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Invalid API Key',
        example: JSON.stringify({
          error: "Invalid API key"
        }, null, 2)
      },
      {
        code: '404',
        description: 'No Contacts Found',
        example: JSON.stringify({
          error: "No contacts found",
          message: "No contacts found matching 'XYZ' in your organization"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          error: "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const url = 'https://dev.clickai.live/contacts/find-by-name';

const searchData = {
    name: 'John'
};

fetch(url, {
    method: 'POST',
    headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(searchData)
})
.then(response => response.json())
.then(data => {
    if (response.status === 200) {
        console.log(\`Found \${data.count} contacts:\`);
        data.data.forEach(contact => {
            console.log(\`- \${contact.name} (\${contact.phone})\`);
        });
    } else if (response.status === 404) {
        console.log('No contacts found');
    } else {
        console.error('Error:', data.error);
    }
})
.catch(error => console.error('Request failed:', error));`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/contacts/find-by-name"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

search_data = {
    "name": "John"
}

response = requests.post(url, json=search_data, headers=headers)

if response.status_code == 200:
    data = response.json()
    contacts = data['data']
    print(f"Found {data['count']} contacts:")
    for contact in contacts:
        print(f"- {contact['name']} ({contact['phone']})")
        print(f"  ID: {contact['id']}")
        print(f"  Bot Status: {contact['bot_status']}")
        print()
elif response.status_code == 404:
    print("No contacts found")
else:
    print(f"Error {response.status_code}: {response.json()}")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/contacts/find-by-name" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"name\\":\\"John\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/contacts/find-by-name" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John"
  }'`,
      php: `<?php
$api_key = 'your_api_key_here';
$url = 'https://dev.clickai.live/contacts/find-by-name';

$search_data = [
    'name' => 'John'
];

$headers = [
    'Authorization: Bearer ' . $api_key,
    'Content-Type: application/json'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($search_data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$http_code = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($http_code == 200) {
    $data = json_decode($response, true);
    $contacts = $data['data'];
    echo "Found " . $data['count'] . " contacts:\\n";
    foreach ($contacts as $contact) {
        echo "- " . $contact['name'] . " (" . $contact['phone'] . ")\\n";
        echo "  ID: " . $contact['id'] . "\\n";
    }
} elseif ($http_code == 404) {
    echo "No contacts found\\n";
} else {
    echo "Error $http_code: $response\\n";
}
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class ContactNameSearcher
{
    private readonly HttpClient httpClient;
    
    public ContactNameSearcher()
    {
        httpClient = new HttpClient();
    }
    
    public async Task<object> FindContactsByNameAsync(string name)
    {
        string apiKey = "your_api_key_here";
        string url = "https://dev.clickai.live/contacts/find-by-name";
        
        var searchData = new { name = name };
        
        string json = JsonConvert.SerializeObject(searchData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        httpClient.DefaultRequestHeaders.Clear();
        httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Found {result.count} contacts:");
                foreach (var contact in result.data)
                {
                    Console.WriteLine($"- {contact.name} ({contact.phone})");
                    Console.WriteLine($"  ID: {contact.id}");
                    Console.WriteLine($"  Bot Status: {contact.bot_status}");
                }
                return result.data;
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.NotFound)
            {
                Console.WriteLine("No contacts found");
                return null;
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
                return null;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return null;
        }
    }
}`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

url = URI('https://dev.clickai.live/contacts/find-by-name')

http = Net::HTTP.new(url.host, url.port)
request = Net::HTTP::Post.new(url)
request['Authorization'] = 'Bearer your_api_key_here'
request['Content-Type'] = 'application/json'

search_data = {
  name: 'John'
}

request.body = search_data.to_json

response = http.request(request)

case response.code
when '200'
  result = JSON.parse(response.body)
  contacts = result['data']
  puts "Found #{result['count']} contacts:"
  contacts.each do |contact|
    puts "- #{contact['name']} (#{contact['phone']})"
    puts "  ID: #{contact['id']}"
    puts "  Bot Status: #{contact['bot_status']}"
    puts
  end
when '404'
  puts "No contacts found"
else
  error = JSON.parse(response.body)
  puts "Error: #{error['error']}"
end`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;
import java.util.List;

public class ContactNameSearcher {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    
    public ContactNameSearcher() {
        this.httpClient = HttpClient.newHttpClient();
        this.objectMapper = new ObjectMapper();
    }
    
    public List<Map<String, Object>> findContactsByName(String name) {
        try {
            String url = "https://dev.clickai.live/contacts/find-by-name";
            
            Map<String, Object> searchData = new HashMap<>();
            searchData.put("name", name);
            
            String json = objectMapper.writeValueAsString(searchData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer your_api_key_here")
                .header("Content-Type", "application/json")
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 200) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                List<Map<String, Object>> contacts = (List<Map<String, Object>>) result.get("data");
                System.out.println("Found " + result.get("count") + " contacts:");
                for (Map<String, Object> contact : contacts) {
                    System.out.println("- " + contact.get("name") + " (" + contact.get("phone") + ")");
                    System.out.println("  ID: " + contact.get("id"));
                }
                return contacts;
            } else if (response.statusCode() == 404) {
                System.out.println("No contacts found");
                return null;
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
                return null;
            }
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
            return null;
        }
    }
}`
    }
  },

  'customers-add-call-log': {
    id: 'customers-add-call-log',
    title: 'Add call log to customer',
    category: 'Customers',
    categoryId: 'customers',
    method: 'POST',
    path: '/customers/add-call-log',
    description: 'Adds a comprehensive call log entry to a customer record. Supports detailed call documentation including transcription, analysis, scoring, and recording links for customer relationship management and quality tracking.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Must be application/json',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'customer_id',
        type: 'string',
        required: true,
        description: 'UUID of the customer to add call log to',
        example: 'e768ecd5-a2dd-41fe-8823-4846de51b9f9'
      },
      {
        name: 'title',
        type: 'string',
        required: true,
        description: 'Title/subject of the call',
        example: 'Sales Call'
      },
      {
        name: 'description',
        type: 'string',
        required: true,
        description: 'Detailed description of the call purpose and outcome',
        example: 'Call with client regarding price quote and product details'
      },
      {
        name: 'duration',
        type: 'integer',
        required: true,
        description: 'Call duration in minutes (must be valid integer)',
        example: '25'
      },
      {
        name: 'call_date',
        type: 'string',
        required: true,
        description: 'Call date and time in YYYY-MM-DD HH:MM format',
        example: '2024-01-15 14:30'
      },
      {
        name: 'conversation_transcription',
        type: 'string',
        required: false,
        description: 'Full or partial transcription of the conversation',
        example: 'Client expressed interest in the product and asked about pricing options'
      },
      {
        name: 'conversation_analysis',
        type: 'string',
        required: false,
        description: 'Analysis of the conversation and customer sentiment',
        example: 'Strong potential client, positive engagement, ready to move forward'
      },
      {
        name: 'created_by',
        type: 'string',
        required: false,
        description: 'UUID of the user who created this call log',
        example: 'f1a2b3c4-d5e6-7890-1234-567890abcdef'
      },
      {
        name: 'score',
        type: 'integer',
        required: false,
        description: 'Call quality or success score (typically 1-10)',
        example: '9'
      },
      {
        name: 'call_record_link',
        type: 'string',
        required: false,
        description: 'URL link to the call recording file',
        example: 'https://example.com/recordings/call-12345.mp3'
      }
    ],
    responses: [
      {
        code: '201',
        description: 'Created - Call log added successfully',
        example: JSON.stringify({
          "success": true,
          "data": {
            "id": "abcd1234-5678-90ef-ghij-klmnopqrstuv",
            "customer_id": "e768ecd5-a2dd-41fe-8823-4846de51b9f9",
            "title": "Sales Call",
            "description": "Call with client regarding price quote",
            "duration": 25,
            "call_date": "2024-01-15 14:30",
            "conversation_transcription": "Client expressed interest in the product",
            "conversation_analysis": "Strong potential client",
            "created_by": "f1a2b3c4-d5e6-7890-1234-567890abcdef",
            "score": 9,
            "call_record_link": "https://example.com/recordings/call-12345.mp3",
            "created_at": "2024-01-15T14:35:00Z"
          }
        }, null, 2)
      },
      {
        code: '400',
        description: 'Bad Request - Missing required field or invalid data',
        example: JSON.stringify({
          "error": "Field 'title' is required"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Unauthorized - Missing or invalid Authorization header',
        example: JSON.stringify({
          "error": "Missing or invalid Authorization header. Expected format: Bearer <api_key>"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Forbidden - Invalid API key',
        example: JSON.stringify({
          "error": "Invalid API key or API key not found"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Not Found - Customer not found',
        example: JSON.stringify({
          "error": "Customer not found or does not belong to your organization"
        }, null, 2)
      },
      {
        code: '422',
        description: 'Unprocessable Entity - Invalid field format',
        example: JSON.stringify({
          "error": "Invalid call_date format. Expected format: YYYY-MM-DD HH:MM"
        }, null, 2)
      },
      {
        code: '500',
        description: 'Internal Server Error',
        example: JSON.stringify({
          "error": "Internal server error"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `// Example 1: Basic call log with required fields only
const response = await fetch('https://dev.clickai.live/customers/add-call-log', {
    method: 'POST',
    headers: {
        'Authorization': 'Bearer your_api_key_here',
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        customer_id: 'e768ecd5-a2dd-41fe-8823-4846de51b9f9',
        title: 'Sales Call',
        description: 'Call with client regarding price quote',
        duration: 25,
        call_date: '2024-01-15 14:30'
    })
});

const data = await response.json();

if (response.status === 201 && data.success) {
    console.log(\`Call log created successfully! ID: \${data.data.id}\`);
    console.log(\`Customer ID: \${data.data.customer_id}\`);
    console.log(\`Duration: \${data.data.duration} minutes\`);
} else {
    console.error('Error:', data.error || 'Unknown error');
}

// Example 2: Complete call log with all optional fields
const completeResponse = await fetch('https://dev.clickai.live/customers/add-call-log', {
    method: 'POST',
    headers: {
        'Authorization': 'Bearer your_api_key_here',
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        customer_id: 'e768ecd5-a2dd-41fe-8823-4846de51b9f9',
        title: 'Sales Call',
        description: 'Call with client regarding price quote and product details',
        duration: 25,
        call_date: '2024-01-15 14:30',
        conversation_transcription: 'Client expressed interest in the product and asked about pricing options',
        conversation_analysis: 'Strong potential client, positive engagement, ready to move forward',
        score: 9,
        call_record_link: 'https://example.com/recordings/call-12345.mp3'
    })
});

const completeData = await completeResponse.json();
console.log('Complete call log:', completeData);`,
      python: `import requests
from datetime import datetime

def add_call_log_basic(api_key, customer_id, title, description, duration, call_date):
    """Add basic call log with required fields only"""
    url = "https://dev.clickai.live/customers/add-call-log"
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    call_data = {
        "customer_id": customer_id,
        "title": title,
        "description": description,
        "duration": duration,
        "call_date": call_date
    }
    
    try:
        response = requests.post(url, json=call_data, headers=headers)
        
        if response.status_code == 201:
            result = response.json()
            print(f"Call log created successfully! ID: {result['data']['id']}")
            print(f"Customer ID: {result['data']['customer_id']}")
            print(f"Duration: {result['data']['duration']} minutes")
            return result
        else:
            error = response.json()
            print(f"Error {response.status_code}: {error['error']}")
            return None
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

def add_call_log_complete(api_key, customer_id, title, description, duration, call_date, 
                         transcription=None, analysis=None, score=None, record_link=None):
    """Add complete call log with all optional fields"""
    url = "https://dev.clickai.live/customers/add-call-log"
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    call_data = {
        "customer_id": customer_id,
        "title": title,
        "description": description,
        "duration": duration,
        "call_date": call_date
    }
    
    # Add optional fields if provided
    if transcription:
        call_data["conversation_transcription"] = transcription
    if analysis:
        call_data["conversation_analysis"] = analysis
    if score is not None:
        call_data["score"] = score
    if record_link:
        call_data["call_record_link"] = record_link
    
    try:
        response = requests.post(url, json=call_data, headers=headers)
        
        if response.status_code == 201:
            result = response.json()
            print(f"Complete call log created successfully! ID: {result['data']['id']}")
            print(f"Customer ID: {result['data']['customer_id']}")
            print(f"Duration: {result['data']['duration']} minutes")
            if result['data'].get('score'):
                print(f"Score: {result['data']['score']}")
            return result
        elif response.status_code == 400:
            error = response.json()
            print(f"Validation error: {error['error']}")
            return None
        elif response.status_code == 404:
            print("Customer not found or doesn't belong to your organization")
            return None
        else:
            error = response.json()
            print(f"Error {response.status_code}: {error['error']}")
            return None
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

# Usage examples
api_key = "your_api_key_here"
customer_id = "e768ecd5-a2dd-41fe-8823-4846de51b9f9"

# Example 1: Basic call log
basic_result = add_call_log_basic(
    api_key, customer_id,
    "Sales Call",
    "Call with client regarding price quote",
    25,
    "2024-01-15 14:30"
)

# Example 2: Complete call log
complete_result = add_call_log_complete(
    api_key, customer_id,
    "Follow-up Call",
    "Follow-up on previous order and discussion of next steps",
    15,
    "2024-01-16 09:15",
    transcription="Client asked about order status and expressed interest in additional products",
    analysis="Satisfied customer, interested in expanding relationship",
    score=9,
    record_link="https://example.com/recordings/call-67890.mp3"
)`,
      curl: `# Example 1: Basic call log with required fields only
curl -X POST "https://dev.clickai.live/customers/add-call-log" \\
     -H "Authorization: Bearer your_api_key_here" \\
     -H "Content-Type: application/json" \\
     -d '{
       "customer_id": "e768ecd5-a2dd-41fe-8823-4846de51b9f9",
       "title": "Sales Call",
       "description": "Call with client regarding price quote",
       "duration": 25,
       "call_date": "2024-01-15 14:30"
     }'

# Example 2: Complete call log with all optional fields
curl -X POST "https://dev.clickai.live/customers/add-call-log" \\
     -H "Authorization: Bearer your_api_key_here" \\
     -H "Content-Type: application/json" \\
     -d '{
       "customer_id": "e768ecd5-a2dd-41fe-8823-4846de51b9f9",
       "title": "Follow-up Call",
       "description": "Follow-up on previous order",
       "duration": 15,
       "call_date": "2024-01-16 09:15",
       "conversation_transcription": "Client asked about order status",
       "conversation_analysis": "Satisfied customer, interested in more products",
       "score": 9,
       "call_record_link": "https://example.com/recordings/call-67890.mp3"
     }'`,
      powershell: `# PowerShell example for adding call logs
$apiKey = "your_api_key_here"
$url = "https://dev.clickai.live/customers/add-call-log"

$headers = @{
    "Authorization" = "Bearer $apiKey"
    "Content-Type" = "application/json"
}

# Example 1: Basic call log
$basicCallBody = @{
    customer_id = "e768ecd5-a2dd-41fe-8823-4846de51b9f9"
    title = "Sales Call"
    description = "Call with client regarding price quote"
    duration = 25
    call_date = "2024-01-15 14:30"
} | ConvertTo-Json

try {
    $response = Invoke-RestMethod -Uri $url -Method POST -Headers $headers -Body $basicCallBody
    
    if ($response.success) {
        Write-Host "Call log created successfully! ID: $($response.data.id)" -ForegroundColor Green
        Write-Host "Customer ID: $($response.data.customer_id)"
        Write-Host "Duration: $($response.data.duration) minutes"
    }
} catch {
    $errorDetails = $_.ErrorDetails.Message | ConvertFrom-Json
    Write-Host "Error: $($errorDetails.error)" -ForegroundColor Red
}

# Example 2: Complete call log with all fields
$completeCallBody = @{
    customer_id = "e768ecd5-a2dd-41fe-8823-4846de51b9f9"
    title = "Follow-up Call"
    description = "Follow-up on previous order and discussion of next steps"
    duration = 15
    call_date = "2024-01-16 09:15"
    conversation_transcription = "Client asked about order status and expressed interest in additional products"
    conversation_analysis = "Satisfied customer, interested in expanding relationship"
    score = 9
    call_record_link = "https://example.com/recordings/call-67890.mp3"
} | ConvertTo-Json

try {
    $completeResponse = Invoke-RestMethod -Uri $url -Method POST -Headers $headers -Body $completeCallBody
    
    if ($completeResponse.success) {
        Write-Host "\\nComplete call log created successfully! ID: $($completeResponse.data.id)" -ForegroundColor Green
        Write-Host "Score: $($completeResponse.data.score)" -ForegroundColor Cyan
    }
} catch {
    $errorDetails = $_.ErrorDetails.Message | ConvertFrom-Json
    Write-Host "Complete call log error: $($errorDetails.error)" -ForegroundColor Red
}`,
      php: `<?php
function addCallLogBasic($apiKey, $customerId, $title, $description, $duration, $callDate) {
    $url = 'https://dev.clickai.live/customers/add-call-log';
    
    $callData = [
        'customer_id' => $customerId,
        'title' => $title,
        'description' => $description,
        'duration' => $duration,
        'call_date' => $callDate
    ];

    $options = [
        'http' => [
            'header' => [
                "Authorization: Bearer $apiKey",
                'Content-Type: application/json'
            ],
            'method' => 'POST',
            'content' => json_encode($callData)
        ]
    ];

    $context = stream_context_create($options);
    $result = file_get_contents($url, false, $context);

    if ($result !== FALSE) {
        $response = json_decode($result, true);
        if (isset($response['success']) && $response['success']) {
            echo "Call log created successfully!\\n";
            echo "ID: " . $response['data']['id'] . "\\n";
            echo "Customer ID: " . $response['data']['customer_id'] . "\\n";
            echo "Duration: " . $response['data']['duration'] . " minutes\\n";
            return $response;
        } else {
            echo "Error: " . $response['error'] . "\\n";
            return null;
        }
    } else {
        echo "Failed to make request\\n";
        return null;
    }
}

function addCallLogComplete($apiKey, $customerId, $title, $description, $duration, $callDate, 
                           $transcription = null, $analysis = null, $score = null, $recordLink = null) {
    $url = 'https://dev.clickai.live/customers/add-call-log';
    
    $callData = [
        'customer_id' => $customerId,
        'title' => $title,
        'description' => $description,
        'duration' => $duration,
        'call_date' => $callDate
    ];
    
    // Add optional fields if provided
    if ($transcription !== null) $callData['conversation_transcription'] = $transcription;
    if ($analysis !== null) $callData['conversation_analysis'] = $analysis;
    if ($score !== null) $callData['score'] = $score;
    if ($recordLink !== null) $callData['call_record_link'] = $recordLink;

    $options = [
        'http' => [
            'header' => [
                "Authorization: Bearer $apiKey",
                'Content-Type: application/json'
            ],
            'method' => 'POST',
            'content' => json_encode($callData)
        ]
    ];

    $context = stream_context_create($options);
    $result = file_get_contents($url, false, $context);

    if ($result !== FALSE) {
        $response = json_decode($result, true);
        if (isset($response['success']) && $response['success']) {
            echo "Complete call log created successfully!\\n";
            echo "ID: " . $response['data']['id'] . "\\n";
            echo "Customer ID: " . $response['data']['customer_id'] . "\\n";
            echo "Duration: " . $response['data']['duration'] . " minutes\\n";
            if (isset($response['data']['score'])) {
                echo "Score: " . $response['data']['score'] . "\\n";
            }
            return $response;
        } else {
            echo "Error: " . $response['error'] . "\\n";
            return null;
        }
    } else {
        echo "Failed to make request\\n";
        return null;
    }
}

// Usage examples
$apiKey = 'your_api_key_here';
$customerId = 'e768ecd5-a2dd-41fe-8823-4846de51b9f9';

// Example 1: Basic call log
echo "Creating basic call log...\\n";
$basicResult = addCallLogBasic(
    $apiKey, $customerId,
    'Sales Call',
    'Call with client regarding price quote',
    25,
    '2024-01-15 14:30'
);

// Example 2: Complete call log
echo "\\nCreating complete call log...\\n";
$completeResult = addCallLogComplete(
    $apiKey, $customerId,
    'Follow-up Call',
    'Follow-up on previous order and discussion of next steps',
    15,
    '2024-01-16 09:15',
    'Client asked about order status and expressed interest in additional products',
    'Satisfied customer, interested in expanding relationship',
    9,
    'https://example.com/recordings/call-67890.mp3'
);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Text.Json;
using System.Threading.Tasks;

public class CustomerCallLogService
{
    private readonly HttpClient _httpClient;
    private readonly string _apiKey;

    public CustomerCallLogService(string apiKey)
    {
        _httpClient = new HttpClient();
        _apiKey = apiKey;
    }

    public async Task<bool> AddCallLogBasic(string customerId, string title, string description, 
        int duration, string callDate)
    {
        var url = "https://dev.clickai.live/customers/add-call-log";
        
        var callData = new
        {
            customer_id = customerId,
            title = title,
            description = description,
            duration = duration,
            call_date = callDate
        };

        return await SendCallLogRequest(callData, url);
    }

    public async Task<bool> AddCallLogComplete(string customerId, string title, string description, 
        int duration, string callDate, string transcription = null, string analysis = null, 
        int? score = null, string recordLink = null)
    {
        var url = "https://dev.clickai.live/customers/add-call-log";
        
        var callData = new
        {
            customer_id = customerId,
            title = title,
            description = description,
            duration = duration,
            call_date = callDate,
            conversation_transcription = transcription,
            conversation_analysis = analysis,
            score = score,
            call_record_link = recordLink
        };

        return await SendCallLogRequest(callData, url);
    }

    private async Task<bool> SendCallLogRequest(object callData, string url)
    {
        var json = JsonSerializer.Serialize(callData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        _httpClient.DefaultRequestHeaders.Clear();
        _httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {_apiKey}");

        try
        {
            var response = await _httpClient.PostAsync(url, content);
            var responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.StatusCode == System.Net.HttpStatusCode.Created)
            {
                var result = JsonSerializer.Deserialize<JsonElement>(responseContent);
                var data = result.GetProperty("data");
                Console.WriteLine($"Call log created successfully! ID: {data.GetProperty("id")}");
                Console.WriteLine($"Customer ID: {data.GetProperty("customer_id")}");
                Console.WriteLine($"Duration: {data.GetProperty("duration")} minutes");
                if (data.TryGetProperty("score", out var scoreElement))
                {
                    Console.WriteLine($"Score: {scoreElement}");
                }
                return true;
            }
            else
            {
                var error = JsonSerializer.Deserialize<JsonElement>(responseContent);
                Console.WriteLine($"Error {(int)response.StatusCode}: {error.GetProperty("error")}");
                return false;
            }
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return false;
        }
    }
}

class Program
{
    static async Task Main(string[] args)
    {
        var service = new CustomerCallLogService("your_api_key_here");
        string customerId = "e768ecd5-a2dd-41fe-8823-4846de51b9f9";
        
        // Example 1: Basic call log
        Console.WriteLine("Creating basic call log...");
        bool basicSuccess = await service.AddCallLogBasic(
            customerId,
            "Sales Call",
            "Call with client regarding price quote",
            25,
            "2024-01-15 14:30"
        );
        
        // Example 2: Complete call log
        Console.WriteLine("\\nCreating complete call log...");
        bool completeSuccess = await service.AddCallLogComplete(
            customerId,
            "Follow-up Call",
            "Follow-up on previous order and discussion of next steps",
            15,
            "2024-01-16 09:15",
            "Client asked about order status and expressed interest in additional products",
            "Satisfied customer, interested in expanding relationship",
            9,
            "https://example.com/recordings/call-67890.mp3"
        );
        
        Console.WriteLine($"\\nBasic call log: {(basicSuccess ? "Success" : "Failed")}");
        Console.WriteLine($"Complete call log: {(completeSuccess ? "Success" : "Failed")}");
    }
}`,
      ruby: `require 'net/http'
require 'json'
require 'uri'

class CustomerCallLogService
  def initialize(api_key)
    @api_key = api_key
    @base_url = 'https://dev.clickai.live'
  end

  def add_call_log_basic(customer_id, title, description, duration, call_date)
    call_data = {
      customer_id: customer_id,
      title: title,
      description: description,
      duration: duration,
      call_date: call_date
    }
    
    send_call_log_request(call_data)
  end

  def add_call_log_complete(customer_id, title, description, duration, call_date, options = {})
    call_data = {
      customer_id: customer_id,
      title: title,
      description: description,
      duration: duration,
      call_date: call_date
    }
    
    # Add optional fields
    call_data[:conversation_transcription] = options[:transcription] if options[:transcription]
    call_data[:conversation_analysis] = options[:analysis] if options[:analysis]
    call_data[:score] = options[:score] if options[:score]
    call_data[:call_record_link] = options[:record_link] if options[:record_link]
    
    send_call_log_request(call_data)
  end

  private

  def send_call_log_request(call_data)
    uri = URI("#{@base_url}/customers/add-call-log")
    
    http = Net::HTTP.new(uri.host, uri.port)
    http.use_ssl = true

    request = Net::HTTP::Post.new(uri)
    request['Authorization'] = "Bearer #{@api_key}"
    request['Content-Type'] = 'application/json'
    request.body = call_data.to_json
    
    begin
      response = http.request(request)
      result = JSON.parse(response.body)
      
      if response.code == '201'
        data = result['data']
        puts "Call log created successfully! ID: #{data['id']}"
        puts "Customer ID: #{data['customer_id']}"
        puts "Duration: #{data['duration']} minutes"
        puts "Score: #{data['score']}" if data['score']
        true
      else
        puts "Error #{response.code}: #{result['error']}"
        false
      end
    rescue StandardError => e
      puts "Exception: #{e.message}"
      false
    end
  end
end

# Usage examples
service = CustomerCallLogService.new('your_api_key_here')
customer_id = 'e768ecd5-a2dd-41fe-8823-4846de51b9f9'

# Example 1: Basic call log
puts "Creating basic call log..."
basic_success = service.add_call_log_basic(
  customer_id,
  'Sales Call',
  'Call with client regarding price quote',
  25,
  '2024-01-15 14:30'
)

# Example 2: Complete call log
puts "\\nCreating complete call log..."
complete_success = service.add_call_log_complete(
  customer_id,
  'Follow-up Call',
  'Follow-up on previous order and discussion of next steps',
  15,
  '2024-01-16 09:15',
  {
    transcription: 'Client asked about order status and expressed interest in additional products',
    analysis: 'Satisfied customer, interested in expanding relationship',
    score: 9,
    record_link: 'https://example.com/recordings/call-67890.mp3'
  }
)

puts "\\nBasic call log: #{basic_success ? 'Success' : 'Failed'}"
puts "Complete call log: #{complete_success ? 'Success' : 'Failed'}"`,
      java: `import java.io.IOException;
import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.time.Duration;
import com.google.gson.Gson;
import com.google.gson.JsonObject;

public class CustomerCallLogService {
    private final HttpClient httpClient;
    private final String apiKey;
    private final Gson gson;

    public CustomerCallLogService(String apiKey) {
        this.httpClient = HttpClient.newBuilder()
                .connectTimeout(Duration.ofSeconds(30))
                .build();
        this.apiKey = apiKey;
        this.gson = new Gson();
    }

    public boolean addCallLogBasic(String customerId, String title, String description, 
                                 int duration, String callDate) {
        JsonObject callData = new JsonObject();
        callData.addProperty("customer_id", customerId);
        callData.addProperty("title", title);
        callData.addProperty("description", description);
        callData.addProperty("duration", duration);
        callData.addProperty("call_date", callDate);

        return sendCallLogRequest(callData);
    }

    public boolean addCallLogComplete(String customerId, String title, String description, 
                                    int duration, String callDate, String transcription, 
                                    String analysis, Integer score, String recordLink) {
        JsonObject callData = new JsonObject();
        callData.addProperty("customer_id", customerId);
        callData.addProperty("title", title);
        callData.addProperty("description", description);
        callData.addProperty("duration", duration);
        callData.addProperty("call_date", callDate);
        
        if (transcription != null) callData.addProperty("conversation_transcription", transcription);
        if (analysis != null) callData.addProperty("conversation_analysis", analysis);
        if (score != null) callData.addProperty("score", score);
        if (recordLink != null) callData.addProperty("call_record_link", recordLink);

        return sendCallLogRequest(callData);
    }

    private boolean sendCallLogRequest(JsonObject callData) {
        try {
            HttpRequest request = HttpRequest.newBuilder()
                    .uri(URI.create("https://dev.clickai.live/customers/add-call-log"))
                    .timeout(Duration.ofSeconds(30))
                    .header("Authorization", "Bearer " + apiKey)
                    .header("Content-Type", "application/json")
                    .POST(HttpRequest.BodyPublishers.ofString(gson.toJson(callData)))
                    .build();

            HttpResponse<String> response = httpClient.send(request, 
                    HttpResponse.BodyHandlers.ofString());

            if (response.statusCode() == 201) {
                JsonObject result = gson.fromJson(response.body(), JsonObject.class);
                JsonObject data = result.getAsJsonObject("data");
                System.out.println("Call log created successfully! ID: " + data.get("id").getAsString());
                System.out.println("Customer ID: " + data.get("customer_id").getAsString());
                System.out.println("Duration: " + data.get("duration").getAsInt() + " minutes");
                if (data.has("score") && !data.get("score").isJsonNull()) {
                    System.out.println("Score: " + data.get("score").getAsInt());
                }
                return true;
            } else {
                JsonObject error = gson.fromJson(response.body(), JsonObject.class);
                System.out.println("Error " + response.statusCode() + ": " + error.get("error").getAsString());
                return false;
            }
            
        } catch (IOException | InterruptedException e) {
            System.out.println("Exception: " + e.getMessage());
            return false;
        }
    }
    
    public static void main(String[] args) {
        CustomerCallLogService service = new CustomerCallLogService("your_api_key_here");
        String customerId = "e768ecd5-a2dd-41fe-8823-4846de51b9f9";
        
        // Example 1: Basic call log
        System.out.println("Creating basic call log...");
        boolean basicSuccess = service.addCallLogBasic(
            customerId,
            "Sales Call",
            "Call with client regarding price quote",
            25,
            "2024-01-15 14:30"
        );
        
        // Example 2: Complete call log
        System.out.println("\\nCreating complete call log...");
        boolean completeSuccess = service.addCallLogComplete(
            customerId,
            "Follow-up Call",
            "Follow-up on previous order and discussion of next steps",
            15,
            "2024-01-16 09:15",
            "Client asked about order status and expressed interest in additional products",
            "Satisfied customer, interested in expanding relationship",
            9,
            "https://example.com/recordings/call-67890.mp3"
        );
        
        System.out.println("\\nBasic call log: " + (basicSuccess ? "Success" : "Failed"));
        System.out.println("Complete call log: " + (completeSuccess ? "Success" : "Failed"));
    }
}`
    }
  },

  // Chats endpoints
  'chats-send-message': {
    id: 'chats-send-message',
    title: 'Send Message to Contact',
    category: 'Chats',
    categoryId: 'chats',
    method: 'POST',
    path: '/chats/send-message',
    description: 'Sends a message to a contact through ClickAI\'s external messaging service. The endpoint receives a contact ID and message content, verifies the contact belongs to the organization, and sends the message through the external messaging service.',
    headers: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        description: 'Bearer token for authentication',
        example: 'Bearer your_api_key_here'
      },
      {
        name: 'Content-Type',
        type: 'string',
        required: true,
        description: 'Content type of the request',
        example: 'application/json'
      }
    ],
    parameters: [
      {
        name: 'contact_id',
        type: 'string',
        required: true,
        description: 'ID of the contact to send message to (must be valid UUID)',
        example: 'a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6'
      },
      {
        name: 'message',
        type: 'string',
        required: true,
        description: 'Message content to send (must not be empty)',
        example: 'Hello, how are you?'
      }
    ],
    responses: [
      {
        code: '200',
        description: 'Message sent successfully',
        example: JSON.stringify({
          success: true,
          message: "Message sent successfully",
          contact: {
            id: "a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6",
            name: "John Doe",
            phone: "972501234567"
          },
          sent_message: "Hello, how are you?"
        }, null, 2)
      },
      {
        code: '400',
        description: 'Missing Parameters',
        example: JSON.stringify({
          error: "Field 'contact_id' is required"
        }, null, 2)
      },
      {
        code: '401',
        description: 'Invalid API Key',
        example: JSON.stringify({
          error: "Invalid API key"
        }, null, 2)
      },
      {
        code: '403',
        description: 'Contact Access Denied',
        example: JSON.stringify({
          error: "Contact does not belong to your organization"
        }, null, 2)
      },
      {
        code: '404',
        description: 'Contact Not Found',
        example: JSON.stringify({
          error: "Contact not found",
          message: "No contact found with ID a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6"
        }, null, 2)
      },
      {
        code: '500',
        description: 'External Service Error',
        example: JSON.stringify({
          error: "Failed to send message",
          message: "Unknown error from messaging service"
        }, null, 2)
      },
      {
        code: '503',
        description: 'Service Unavailable',
        example: JSON.stringify({
          error: "Failed to connect to messaging service"
        }, null, 2)
      },
      {
        code: '504',
        description: 'Service Timeout',
        example: JSON.stringify({
          error: "Message sending timeout - please try again"
        }, null, 2)
      }
    ],
    codeExamples: {
      javascript: `const apiKey = 'your_api_key_here';
const baseUrl = 'https://dev.clickai.live';

const headers = {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json'
};

async function sendMessageToContact(contactId, message) {
    const url = \`\${baseUrl}/chats/send-message\`;
    
    const messageData = {
        contact_id: contactId,
        message: message
    };
    
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(messageData)
        });
        
        const data = await response.json();
        
        if (response.status === 200) {
            console.log(\`Message sent to \${data.contact.name}\`);
            console.log(\`Phone: \${data.contact.phone}\`);
            console.log(\`Message: \${data.sent_message}\`);
            return { success: true, data: data };
        } else if (response.status === 403) {
            console.error('Contact does not belong to your organization');
            return { success: false, error: 'Access denied' };
        } else if (response.status === 404) {
            console.error('Contact not found');
            return { success: false, error: 'Contact not found' };
        } else if (response.status === 504) {
            console.error('Message sending timeout');
            return { success: false, error: 'Timeout' };
        } else {
            console.error('Error:', data.error);
            return { success: false, error: data.error };
        }
    } catch (error) {
        console.error('Request failed:', error);
        return { success: false, error: 'Network error' };
    }
}

// Usage example
sendMessageToContact('a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6', 'Hello from the system!');`,
      python: `import requests

api_key = "your_api_key_here"
url = "https://dev.clickai.live/chats/send-message"

headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

# First, find a contact
search_data = {"phone": "972501234567"}
search_response = requests.post(
    "https://dev.clickai.live/contacts/find-by-phone", 
    json=search_data, 
    headers=headers
)

if search_response.status_code == 200:
    contact = search_response.json()['data']
    contact_id = contact['id']
    
    # Send message to the contact
    message_data = {
        "contact_id": contact_id,
        "message": "Hello from the system! How are you?"
    }
    
    response = requests.post(url, json=message_data, headers=headers)
    
    if response.status_code == 200:
        result = response.json()
        print(f"Message sent to {result['contact']['name']}")
        print(f"Message: {result['sent_message']}")
    elif response.status_code == 403:
        print("Contact does not belong to your organization")
    elif response.status_code == 404:
        print("Contact not found")
    elif response.status_code == 504:
        print("Message sending timeout - try again")
    else:
        error = response.json()
        print(f"Error: {error['error']}")
else:
    print("Contact not found for phone number")`,
      'curl-windows': `curl -X POST "https://dev.clickai.live/chats/send-message" -H "Authorization: Bearer your_api_key_here" -H "Content-Type: application/json" -d "{\\"contact_id\\":\\"a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6\\",\\"message\\":\\"Hello, how are you?\\"}"`,
      'curl-linux': `curl -X POST "https://dev.clickai.live/chats/send-message" \\
  -H "Authorization: Bearer your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "contact_id": "a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6",
    "message": "Hello, how are you?"
  }'`,
      php: `<?php
function sendMessageToContact($apiKey, $contactId, $message) {
    $url = 'https://dev.clickai.live/chats/send-message';
    
    $messageData = [
        'contact_id' => $contactId,
        'message' => $message
    ];
    
    $headers = [
        'Authorization: Bearer ' . $apiKey,
        'Content-Type: application/json'
    ];
    
    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, $url);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($messageData));
    curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_TIMEOUT, 35); // Slightly longer than server timeout
    
    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    curl_close($ch);
    
    if ($httpCode == 200) {
        $data = json_decode($response, true);
        echo "Message sent successfully!\\n";
        echo "Contact: " . $data['contact']['name'] . " (" . $data['contact']['phone'] . ")\\n";
        echo "Message: " . $data['sent_message'] . "\\n";
        return $data;
    } elseif ($httpCode == 403) {
        echo "Error: Contact does not belong to your organization\\n";
    } elseif ($httpCode == 404) {
        echo "Error: Contact not found\\n";
    } elseif ($httpCode == 504) {
        echo "Error: Message sending timeout - please try again\\n";
    } else {
        $error = json_decode($response, true);
        echo "Error $httpCode: " . $error['error'] . "\\n";
    }
    
    return null;
}

// Usage example
$apiKey = 'your_api_key_here';
$contactId = 'a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6';
$message = 'Hello from PHP!';

sendMessageToContact($apiKey, $contactId, $message);
?>`,
      csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class MessageSender
{
    private readonly HttpClient httpClient;
    private readonly string apiKey;
    
    public MessageSender(string apiKey)
    {
        this.apiKey = apiKey;
        this.httpClient = new HttpClient();
        this.httpClient.Timeout = TimeSpan.FromSeconds(35); // Slightly longer than server timeout
    }
    
    public async Task<bool> SendMessageAsync(string contactId, string message)
    {
        string url = "https://dev.clickai.live/chats/send-message";
        
        var messageData = new
        {
            contact_id = contactId,
            message = message
        };
        
        string json = JsonConvert.SerializeObject(messageData);
        var content = new StringContent(json, Encoding.UTF8, "application/json");
        
        httpClient.DefaultRequestHeaders.Clear();
        httpClient.DefaultRequestHeaders.Add("Authorization", $"Bearer {apiKey}");
        
        try
        {
            var response = await httpClient.PostAsync(url, content);
            string responseContent = await response.Content.ReadAsStringAsync();
            
            if (response.IsSuccessStatusCode)
            {
                var result = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine("Message sent successfully!");
                Console.WriteLine($"Contact: {result.contact.name} ({result.contact.phone})");
                Console.WriteLine($"Message: {result.sent_message}");
                return true;
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.Forbidden)
            {
                Console.WriteLine("Error: Contact does not belong to your organization");
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.NotFound)
            {
                Console.WriteLine("Error: Contact not found");
            }
            else if (response.StatusCode == System.Net.HttpStatusCode.GatewayTimeout)
            {
                Console.WriteLine("Error: Message sending timeout - please try again");
            }
            else
            {
                var error = JsonConvert.DeserializeObject<dynamic>(responseContent);
                Console.WriteLine($"Error: {error.error}");
            }
            
            return false;
        }
        catch (TaskCanceledException)
        {
            Console.WriteLine("Error: Request timeout");
            return false;
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Exception: {ex.Message}");
            return false;
        }
    }
}`,
      ruby: `require 'net/http'
require 'uri'
require 'json'

class MessageSender
  def initialize(api_key)
    @api_key = api_key
    @base_url = 'https://dev.clickai.live'
  end
  
  def send_message(contact_id, message)
    url = URI("#{@base_url}/chats/send-message")
    
    http = Net::HTTP.new(url.host, url.port)
    http.read_timeout = 35 # Slightly longer than server timeout
    
    request = Net::HTTP::Post.new(url)
    request['Authorization'] = "Bearer #{@api_key}"
    request['Content-Type'] = 'application/json'
    
    message_data = {
      contact_id: contact_id,
      message: message
    }
    
    request.body = message_data.to_json
    
    begin
      response = http.request(request)
      
      case response.code
      when '200'
        result = JSON.parse(response.body)
        puts "Message sent successfully!"
        puts "Contact: #{result['contact']['name']} (#{result['contact']['phone']})"
        puts "Message: #{result['sent_message']}"
        true
      when '403'
        puts "Error: Contact does not belong to your organization"
        false
      when '404'
        puts "Error: Contact not found"
        false
      when '504'
        puts "Error: Message sending timeout - please try again"
        false
      else
        error = JSON.parse(response.body)
        puts "Error: #{error['error']}"
        false
      end
    rescue Net::TimeoutError
      puts "Error: Request timeout"
      false
    rescue => e
      puts "Exception: #{e.message}"
      false
    end
  end
end

# Usage example
sender = MessageSender.new('your_api_key_here')
success = sender.send_message('a1b2c3d4-e5f6-7g8h-9i0j-k1l2m3n4o5p6', 'Hello from Ruby!')`,
      java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.time.Duration;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.Map;
import java.util.HashMap;

public class MessageSender {
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    private final String apiKey;
    
    public MessageSender(String apiKey) {
        this.apiKey = apiKey;
        this.httpClient = HttpClient.newBuilder()
            .connectTimeout(Duration.ofSeconds(35))
            .build();
        this.objectMapper = new ObjectMapper();
    }
    
    public boolean sendMessage(String contactId, String message) {
        try {
            String url = "https://dev.clickai.live/chats/send-message";
            
            Map<String, Object> messageData = new HashMap<>();
            messageData.put("contact_id", contactId);
            messageData.put("message", message);
            
            String json = objectMapper.writeValueAsString(messageData);
            
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(url))
                .header("Authorization", "Bearer " + apiKey)
                .header("Content-Type", "application/json")
                .timeout(Duration.ofSeconds(35))
                .POST(HttpRequest.BodyPublishers.ofString(json))
                .build();
            
            HttpResponse<String> response = httpClient.send(request, 
                HttpResponse.BodyHandlers.ofString());
            
            if (response.statusCode() == 200) {
                Map<String, Object> result = objectMapper.readValue(response.body(), Map.class);
                Map<String, Object> contact = (Map<String, Object>) result.get("contact");
                
                System.out.println("Message sent successfully!");
                System.out.println("Contact: " + contact.get("name") + " (" + contact.get("phone") + ")");
                System.out.println("Message: " + result.get("sent_message"));
                return true;
            } else if (response.statusCode() == 403) {
                System.out.println("Error: Contact does not belong to your organization");
            } else if (response.statusCode() == 404) {
                System.out.println("Error: Contact not found");
            } else if (response.statusCode() == 504) {
                System.out.println("Error: Message sending timeout - please try again");
            } else {
                Map<String, Object> error = objectMapper.readValue(response.body(), Map.class);
                System.out.println("Error: " + error.get("error"));
            }
            
            return false;
        } catch (Exception e) {
            System.out.println("Exception: " + e.getMessage());
            return false;
        }
    }
}`
    }
  }
};

// Get endpoint data by ID
export const getEndpointData = (endpointId: string): EndpointData | null => {
  return endpointsData[endpointId] || null;
};

// Get all endpoints in a flat array
export const getAllEndpoints = (): { id: string; name: string }[] => {
  const allEndpoints: { id: string; name: string }[] = [];
  categories.forEach(category => {
    category.endpoints.forEach(endpoint => {
      allEndpoints.push(endpoint);
    });
  });
  return allEndpoints;
};

// Get category by ID
export const getCategoryById = (categoryId: string): CategoryData | null => {
  return categories.find(cat => cat.id === categoryId) || null;
};

// Get endpoints by category
export const getEndpointsByCategory = (categoryId: string): { id: string; name: string }[] => {
  const category = getCategoryById(categoryId);
  return category ? category.endpoints : [];
}; 